import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { PaymentConfirmationPreview, partyAndRowsForInvoiceDeposit } from "./paymentConfirmation";
import DraggableModal from "./DraggableModal";
import { LOGO_B64 } from "./SalesReports";
import BulkDeleteDialog from "./BulkDeleteDialog";
import BulkStatusDialog from "./BulkStatusDialog";
import SearchSelect from "./SearchSelect";
import EmailDialog from "./EmailDialog";
import ButtonMenu from "./ButtonMenu";
import DocPreviewModal from "./DocPreviewModal";
import { buildInboundFiveBrothersHtml, buildOutboundACSSweetLeafHtml, copyHtmlToClipboard } from "./emailHtml";
import { defaultOpenSeasonIds, buildSeasonFilterOptions, seasonFilterPlaceholder, resolveSeasonId } from "./seasonFilter";
import useDragReorder, { DragHandle } from "./useDragReorder";
import ExportButton from "./ExportButton";
import { DELIVERY_STATUSES, DISPOSITION_STATUSES, PENDING_STATUSES, deriveDeliveryStatus, allocationMatchesLine, resolvePendingStage, buildSlPassingIndex, matchLineToInvoiceLines, twinRiverPalletStatus, twinRiverProductStatus } from "./saleStatusCalc";
import { num2, intOr, s as xs } from "./xlsxExport";
import { loadCompanyProfile, composeEmailBody, pickEmailTemplate } from "./CompanyProfile";
import { appendDisclosureByAccount, isFourSeasonsClientName, fourSeasonsRoutingBlockedMessage, appendProtectionNotice, filterContactsByCategory } from "./emailHelpers";
import { PROTECTION_DEFECTS, PROTECTION_DEFECT_SEVERITY, selectedProtectionDefects, selectedPalletDefects, protectionDefectsEmailText, hasProtectionResponses, ProtectionResponsesReadOnly } from "./protectionReport";
import { buildQualityReportBlob, qualityReportFilename, QualityReportPreview } from "./qualityReport";
import { REJECTION_IMPACT_NOTE_ES, rejectionNoticeSentence, buildRejectionReportBlob, rejectionReportFilename, RejectionReportPreview, RejectionBadge, rejectedCasesForInvoice, RejectionDetailsReadOnly } from "./rejectionReport";
import FileDropZone from "./FileDropZone";
import { ProtectionReportPreview, buildProtectionReportBlob, protectionReportFilename } from "./protectionReport";
import { buildBlackberrySpecAtOriginBlob, BLACKBERRY_SPEC_FILENAME } from "./blackberrySpecAtOrigin";
import { buildBlueberrySpecAtOriginBlob, BLUEBERRY_SPEC_FILENAME } from "./blueberrySpecAtOrigin";
import { saveDoc, loadDoc } from "./gfiDB";
import { SWEETLEAF_LOGO_B64, GFI_ISOTIPO_B64, SL_ISOTIPO_B64 } from "./brandLogos";
import {
  isConfigured as driveConfigured,
  isSignedIn as driveSignedIn,
  signIn as driveSignIn,
  ensurePath as driveEnsurePath,
  ensureFolder as driveEnsureFolder,
  folderViewUrl as driveFolderViewUrl,
  rootPathSegments,
  seasonFolderName,
  embarqueFolderName,
  invoiceFolderName,
  uploadFile as driveUploadFile,
  buildLocalPath,
  openLocalFolder,
} from "../googleDrive";
import { htmlToPdfBlob, safeFilename } from "../pdfRenderer";
import { loadFromSupabase, saveToSupabase, saveManyToSupabase, deleteFromSupabase, getCached } from "../supabaseDB";
import useDocumentTitle from "../useDocumentTitle";
import { computeLotBalances, lotBalanceMismatches } from "./lotBalanceCheck";

/* Per the user spec the Invoices Summary export breaks "Advance
   Financing Fee" and "Other Fees" out into their own columns. The
   bucket "Other Fees" here means anything that is neither the Advance
   Financing Fee nor in the canonical fee list. The whole-fees-bucket
   "Fees" column collects everything except Advance Financing Fee. */
function isAdvanceFinancingFeeInv(fe) {
  var n = fe && fe.feeType ? String(fe.feeType).trim().toLowerCase() : "";
  return n === "advance financing fee";
}
var INV_KNOWN_FEE_NAMES = [
  "pti labeling fee", "qc inspection", "warehouse fees", "warehouse fee",
  "wire transfer fee", "late fee", "factoring fee", "freight",
];
function isKnownFeeName(fe) {
  var n = fe && fe.feeType ? String(fe.feeType).trim().toLowerCase() : "";
  return INV_KNOWN_FEE_NAMES.indexOf(n) >= 0;
}
function feeNameInv(fe) {
  if (!fe) return "";
  return fe.feeType === "__custom__" ? (fe.feeCustom || "Custom Fee") : (fe.feeType || "Fee");
}

/* Full presentation/pack/label for a line — same fallback contract
   as BillRegistry.resolveProductDesc: explicit productDescription
   first, otherwise look up the product by productId in gfi-products
   and rebuild it. Avoids the "shows only commodity" bug that the
   simple `productDescription || productName` fallback caused. */
function resolveInvLineDesc(line) {
  if (!line) return "";
  if (line.productDescription) return line.productDescription;
  if (!line.productId) return "";
  var prod = (function () { try { return JSON.parse(localStorage.getItem("gfi-products") || "[]"); } catch (e) { return []; } })().find(function (p) { return String(p.id) === String(line.productId) || String(p._uuid) === String(line.productId); });
  if (!prod) return "";
  var cat = (prod.category === "Organic" || prod.category === "OG") ? "OG" : "CV";
  return (prod.description || (prod.size || "") + " " + (prod.unitsCase || "") + "u " + cat + " " + (prod.labelInitials || "")).trim();
}

/* Build the list of Export Cert entries for a printed doc. Spec:
     - Only ORGANIC product lines on the invoice count (USDA cert
       applies to OG only — CV lines stay out of the printed block).
     - Cert lookup priority per invoice line:
         line.exportCertNumber → line.exportCertManual
         → line.exportCertificate / line.importCertificate (legacy
           aliases some imports used)
         → matching shipment line's cert (when the invoice line
           inherited from a bill that didn't propagate the field).
     - Group certs by product (productBase / productName). If EVERY
       organic product carries the same single cert, the output
       collapses to one line `[cert]`. Otherwise each entry reads
       `<Product> — <cert>` so the auditor sees which cert covers
       which commodity.
   Returns [] when no organic lines have a cert — caller renders
   "—" or skips the row. */
function buildExportCertEntries(invoice, shipment) {
  var lines = (invoice && invoice.lines) || [];
  var shipLines = (shipment && shipment.lines) || [];
  /* shipment-line cert lookup keyed by lowercased product name —
     fallback when the invoice line itself lost the cert during the
     bill → invoice handoff. */
  var shipCertByProduct = {};
  shipLines.forEach(function (sl) {
    var key = String(sl.productBase || sl.productName || "").toLowerCase().trim();
    if (!key) return;
    var c = sl.exportCertNumber || sl.exportCertManual || "";
    if (c && !shipCertByProduct[key]) shipCertByProduct[key] = c;
  });
  var perProduct = {};
  var allCerts = {};
  lines.forEach(function (l) {
    var cat = String(l.category || "").toLowerCase();
    if (cat !== "organic" && cat !== "og") return;
    var product = (l.productBase || l.productName || "").trim();
    if (!product) return;
    var cert = (l.exportCertNumber || l.exportCertManual || l.exportCertificate || l.importCertificate || shipCertByProduct[product.toLowerCase()] || "").trim();
    if (!cert) return;
    if (!perProduct[product]) perProduct[product] = {};
    perProduct[product][cert] = true;
    allCerts[cert] = true;
  });
  var products = Object.keys(perProduct);
  if (products.length === 0) return [];
  var uniqueCerts = Object.keys(allCerts);
  /* Every organic product shares the same cert → collapse to a
     single line (matches the user-described format). */
  if (uniqueCerts.length === 1) return [uniqueCerts[0]];
  return products.map(function (p) {
    return p + " — " + Object.keys(perProduct[p]).join(", ");
  });
}

/* ======================== TRANSLATIONS ======================== */
const T = {
  en: {
    title: "Invoice Registry", add: "New invoice", edit: "Edit invoice",
    search: "Search by PU#, client, lot, or PO...",
    showing: "Showing", of: "of", total: "invoices", noResults: "No invoices match",
    invoiceNum: "Invoice #", puNum: "PU #", date: "Date", dueDate: "Due Date",
    client: "Client", selectClient: "Select client",
    clientRef: "Customer Ref # (PO)", clientRefColLine1: "Customer", clientRefColLine2: "Ref # (PO)", lot: "Lot", shipper: "Shipper",
    pickupDate: "Pickup Date (BOL)", pickupTime: "Pickup Time (BOL)",
    pickupHint: "From the driver's BOL — ask the warehouse to note the exact pickup time. Used to estimate the ETA shown on the Active Shipments dashboard card.",
    thermograph: "Temp Recorder # (Logger)",
    arrivalDateLabel: "Arrival Date", arrivalTimeLabel: "Arrival Time",
    selectPO: "Select PO from Client Pricing...", enterPOManually: "✏️ Enter manually...",
    manualPOPlaceholder: "Manual PO #", noAvailablePOs: "No POs available for this client",
    poTypeFixed: "Fixed", poTypeSemifixed: "Semi-fixed", poTypeConsignment: "Cons.",
    product: "Product", description: "Type/Cond/Var/Package", category: "Cat",
    cases: "Qty", pallets: "Pallets", salePrice: "Price", amount: "Amount", deductions: "Deductions", paidCol: "Paid",
    lineComm: "Comm %", lineCommAmt: "Comm $", lineNetAmt: "Net Amount",
    palletAllocations: "Pallet Allocations", addFromOtherLot: "+ Add pallet from another lot",
    allocCases: "Cases", palletNumCol: "Pallet #", tagIdCol: "Tag ID", varietyCol: "Variety", growerCol: "Grower", weightCol: "Weight",
    populateFromLot: "Populate from Lot",
    confirmRelot: "Changing this lot will erase its current pallet allocations. Continue?",
    noPalletsToAdd: "No pallets available to add",
    emailInboundFB: "Inbound Five Brothers", emailOutboundACS: "Outbound American Cold Storage",
    emailCustomer: "Email Customer",
    emailWarehouse: "Email Warehouse",
    emailWarehouseTitle: "Email warehouse",
    emailWarehouseDefaultSubject: "Inbound_{lot}_{pu}",
    emailNoWarehouse: "This invoice has no linked warehouse or warehouse has no contacts",
    emailShipperProtection: "Email Shipper — Protection Notice",
    emailShipperProtectionUpdate: "Email Shipper — Protection Status Update",
    emailNoShipperContacts: "This shipment's supplier has no contacts with an email on file",
    resetToPending: "Reset to pending — clears this pallet's status back to the auto-computed Pending Pick Up/Delivery",
    protectionEmailSent: "Protection Notice sent",
    protectionEmailNotSent: "Protection Notice NOT sent to shipper",
    protectionEmailReminderPrefix: "This invoice now has a protected pallet but the Protection Notice hasn't been sent to the shipper yet. Use the ⋮ menu → \"",
    protectionEmailReminderSuffix: "\" before closing this invoice.",
    emailCustomerTitle: "Email customer",
    emailCustomerDocInbound: "Inbound",
    emailCustomerDocMaster: "Master Inbound",
    emailCustomerDocPacking: "Packing List",
    emailCustomerDocInvoice: "Invoice",
    emailCustomerDefaultSubject: "Inbound_{pu}_PO_{po}",
    emailCustomerSubjectInbound: "Inbound_{pu}_PO_{po}",
    emailCustomerSubjectPick: "Pick_Ticket_{pu}_PO_{po}",
    emailCustomerKindInbound: "Inbound",
    emailCustomerKindPick: "Pick Ticket",
    emailCustomerDefaultBody: "Hi,\n\nPlease find attached the documents for the upcoming delivery.\n\nPU#: {pu}\nPO#: {po}\nLOT: {lot}\n\nThanks,\nGround Fresh International",
    emailReminderPickup: "Send Pickup Reminder",
    emailReminderKindPickup: "Pickup Reminder",
    emailReminderSubjectPickup: "Reminder_PU_{pu}_PO_{po}",
    emailReminderDefaultBody: "Hi,\n\nAs of today, PU# {pu} / PO# {po} hasn't been picked up yet. Could you please confirm when it will be picked up?\n\nThanks,\nGround Fresh International",
    emailClientProtectionReminder: "Send Protection Status Reminder",
    emailClientProtectionReminderKind: "Protection Status Reminder",
    emailClientProtectionReminderSubject: "Protection Status Reminder — PU# {pu} / PO# {po}",
    emailClientProtectionReminderBody: "Hi,\n\nWe're following up on the Protection status reported for the shipment below — could you confirm the final outcome when you have it?\n\nPU#: {pu}\nPO#: {po}\nLOT: {lot}\n\nThanks,\nGround Fresh International",
    emailNoClient: "This invoice has no client assigned",
    emailNoShipment: "This invoice has no linked shipment — required to build the documents",
    emailHtmlCopied: "HTML copied to clipboard — paste into Outlook",
    emailHtmlError: "Couldn't copy HTML — browser blocked clipboard access",
    copyToSL: "Copy to SweetLeaf",
    copyToSLSuccess: "Copied to SweetLeaf",
    copyToSLExists: "Already copied to SweetLeaf",
    paymentsSection: "Payments", originalTotal: "Original Total", currentBalance: "Current Balance",
    noPayments: "No payments recorded", paymentRef: "Reference", paymentMethodCol: "Method",
    editLockedTitle: "Editing locked",
    editLockedMsg: "This record has payments applied. To edit, first delete the payments.",
    editLockedHint: "Click any payment below to open it in Payments and delete it.",
    lotNum: "Lot #",
    lessAdvance: "Less Advance", otherFees: "Other Fees", lessCredits: "Less Credits",
    totalDue: "TOTAL DUE", remarks: "Remarks",
    subtotal: "Subtotal", lessPaid: "Less Amount Paid",
    billTo: "BILL TO",
    save: "Save", cancel: "Cancel", delete: "Delete", editBtn: "Edit", close: "Close",
    invoiceActivity: "Invoice Activity", linkedLot: "Open Linked Lot", linkedBill: "Open Linked Bill", linkedPO: "Open PO",
    confirmDel: "Delete this invoice?", yes: "Yes, delete", no: "Cancel",
    confirmExit: "Unsaved changes. Exit?", yesExit: "Yes, exit", noExit: "Keep editing",
    saved: "Invoice saved", deleted: "Invoice deleted",
    all: "All", allClients: "All clients", allStatuses: "All statuses", allSaleStatuses: "All sale statuses",
    statusDraft: "Draft", statusSent: "Sent", statusPaid: "Paid", statusOverdue: "Overdue", status: "Status",
    printPreview: "Print / PDF", sourceBill: "Source Bill",
    preview: "Preview", printPdf: "Print PDF", more: "More actions",
    driveCreateFolder: "Create Drive Folder",
    driveCreating: "Creating...",
    driveOpenFolder: "Open Folder",
    driveSignInNeeded: "Sign in to Google Drive first",
    driveNotConfigured: "REACT_APP_GOOGLE_CLIENT_ID not set in .env.local",
    driveNoShipment: "Can't find the linked shipment — create the shipment's folder first",
    driveError: "Drive error",
    openLocalFolder: "Open Folder",
    openLocalFolderHint: "Shortcut downloaded. IMPORTANT: rename the file — change the extension from .download to .url — then double-click it to open the folder. Path also copied to clipboard.",
    noInventory: "No available inventory",
    selectLot: "Select lot (from received shipments)",
    saleStatus: "Sale Status",
    goodDelivery: "Good Delivery", protection: "Protection", pas: "PAS", partialProtection: "Partial Protection",
    pendingPickUp: "Pending Pick Up", pendingDelivery: "Pending Delivery",
    protectionResponseLabel: "Client Response — Protection (from SweetLeaf)",
    protectionResponseEditableLabel: "Client Response — Protection",
    protectionResponseNotePlaceholder: "Note (optional)",
    protectionResponseClear: "Clear",
    protectionReminderDueBanner: function (n) { return "⚠ It's been " + n + " days since the client reported arrival with no protection response registered — a Protection Status Reminder is due. Use the kebab menu (⋮) to send one."; },
    protectionResponseCol: "Response", protectionResponseColLine1: "Protection", protectionResponseColLine2: "Outcome",
    willPayFull: "Full Pay", settled: "Settled", noResultYet: "No Result Yet",
    settled: "Settled", paidInFull: "Paid in Full",
    dumped: "Dumped", donated: "Donated", samples: "Samples", transfer: "Transfer",
    selectStatus: "Select status", palletStatusCol: "Status", computedFromPallets: "Computed from pallet status below",
    // Batch deposit modal
    recordDeposit: "Record Deposit", recordDeposits: "Record Deposits",
    batchDepositTitle: "Batch Deposit",
    concept: "Concept", conceptDeposit: "Deposit", conceptAdvance: "Advance",
    depositDate: "Deposit Date", depositMethod: "Method", depositReference: "Reference / Wire #",
    refColumn: "Reference",
    methodWire: "Wire", methodCheck: "Check", methodAch: "ACH", methodTransfer: "Transfer", methodCash: "Cash", methodOther: "Other",
    depositAmount: "Deposit Amount", totalDeposit: "Total Deposit",
    dueAmount: "Due Amount",
    noOpenInvoices: "Selected invoices have no outstanding balance.",
    depositSaved: "Deposit recorded",
    overApplyInv: "Deposit cannot exceed Due Amount",
    notes: "Notes",
    fees: "Fees & Deductions", feeType: "Fee Type", feeQty: "Qty", feeRate: "Rate", feeAmount: "Amount",
    selectFee: "Select fee", customFee: "Custom fee…", addFee: "Add fee",
    catCustoms: "Customs", catWarehouse: "Warehouse", catQC: "Quality/Inspection",
    catShipping: "Shipping", catFinancing: "Financing (non-operating)", catCertification: "Certification", catOther: "Other Fees",
    credits: "Credits", creditType: "Type", creditFree: "Free credit", creditFromInvoice: "From Invoice",
    creditConcept: "Concept", creditRefInvoice: "Invoice ref", creditAmount: "Amount", addCredit: "Add credit",
    grossSales: "Gross Sales",
    balanceTransfers: "Balance Transfers", btDirection: "Direction", btSend: "Send my negative to…", btReceive: "Receive negative from…",
    btOtherInvoice: "Other invoice", btAmount: "Amount", btCreate: "Create transfer", btType: "Type",
    btCredit: "Credit (received)", btDebit: "Debit (sent)", btCancel: "Cancel",
  },
  es: {
    title: "Registro de Facturas", add: "Nueva factura", edit: "Editar factura",
    search: "Buscar por PU#, cliente, lote o PO...",
    showing: "Mostrando", of: "de", total: "facturas", noResults: "Sin resultados",
    invoiceNum: "Factura #", puNum: "PU #", date: "Fecha", dueDate: "Fecha de Pago",
    client: "Cliente", selectClient: "Seleccionar cliente",
    clientRef: "Ref. Cliente (PO)", clientRefColLine1: "Cliente", clientRefColLine2: "Ref. (PO)", lot: "Lote", shipper: "Exportador",
    pickupDate: "Fecha de Recolección (BOL)", pickupTime: "Hora de Recolección (BOL)",
    pickupHint: "Del BOL del chofer — pídele a la bodega que anote la hora exacta de recolección. Se usa para estimar el ETA que se muestra en la tarjeta de Active Shipments del Dashboard.",
    thermograph: "No. de Temp Recorder (Logger)",
    arrivalDateLabel: "Fecha de Llegada", arrivalTimeLabel: "Hora de Llegada",
    selectPO: "Seleccionar PO de Precios al Cliente...", enterPOManually: "✏️ Capturar manualmente...",
    manualPOPlaceholder: "PO # manual", noAvailablePOs: "Sin POs disponibles para este cliente",
    poTypeFixed: "Fijo", poTypeSemifixed: "Semi-fijo", poTypeConsignment: "Cons.",
    product: "Producto", description: "Tipo/Cond/Var/Paquete", category: "Cat",
    cases: "Cant", pallets: "Pallets", salePrice: "Precio", amount: "Monto", deductions: "Deducciones", paidCol: "Pagado",
    lineComm: "Com %", lineCommAmt: "Com $", lineNetAmt: "Total Neto",
    palletAllocations: "Asignación de Pallets", addFromOtherLot: "+ Agregar pallet de otro lote",
    allocCases: "Cajas", palletNumCol: "Pallet #", tagIdCol: "ID Tag", varietyCol: "Variedad", growerCol: "Productor", weightCol: "Peso",
    populateFromLot: "Traer del Lote",
    confirmRelot: "Cambiar este lote borrará sus allocations actuales. ¿Continuar?",
    noPalletsToAdd: "No hay pallets disponibles para agregar",
    emailInboundFB: "Inbound Five Brothers", emailOutboundACS: "Outbound American Cold Storage",
    emailWarehouse: "Email Bodega",
    emailWarehouseTitle: "Email bodega",
    emailWarehouseDefaultSubject: "Inbound_{lot}_{pu}",
    emailNoWarehouse: "Este invoice no tiene bodega asignada o la bodega no tiene contactos",
    /* Pre-existing gap fixed 2026-09-14 — this whole emailCustomer*
       group was only ever defined in the `en` block, so the "Email
       Customer" kebab button rendered with a blank/invisible label in
       Spanish (the app's default language) since forever. Found live
       while testing the new "Email Shipper — Protection" feature,
       which sits in the exact same menu. */
    emailCustomer: "Email Cliente",
    emailCustomerTitle: "Email cliente",
    emailCustomerDocInbound: "Inbound",
    emailCustomerDocMaster: "Master Inbound",
    emailCustomerDocPacking: "Packing List",
    emailCustomerDocInvoice: "Invoice",
    emailCustomerDefaultSubject: "Inbound_{pu}_PO_{po}",
    emailCustomerSubjectInbound: "Inbound_{pu}_PO_{po}",
    emailCustomerSubjectPick: "Pick_Ticket_{pu}_PO_{po}",
    emailCustomerKindInbound: "Inbound",
    emailCustomerKindPick: "Pick Ticket",
    emailCustomerDefaultBody: "Hola,\n\nAdjunto los documentos para la próxima entrega.\n\nPU#: {pu}\nPO#: {po}\nLOTE: {lot}\n\nGracias,\nGround Fresh International",
    emailShipperProtection: "Enviar Aviso de Protección al Shipper",
    emailShipperProtectionUpdate: "Enviar Actualización de Protección al Shipper",
    emailNoShipperContacts: "El proveedor de este embarque no tiene contactos con correo registrado",
    resetToPending: "Regresar a pendiente — borra el status de este pallet y vuelve al valor automático Pendiente de Recolección/Entrega",
    protectionEmailSent: "Aviso de Protección enviado",
    protectionEmailNotSent: "Aviso de Protección NO enviado al shipper",
    protectionEmailReminderPrefix: "Este invoice ahora tiene un pallet protegido pero el Aviso de Protección aún no se ha enviado al shipper. Usa el menú ⋮ → \"",
    protectionEmailReminderSuffix: "\" antes de cerrar este invoice.",
    emailReminderPickup: "Enviar Recordatorio de Recolección",
    emailReminderKindPickup: "Recordatorio de Recolección",
    emailReminderSubjectPickup: "Recordatorio_PU_{pu}_PO_{po}",
    emailReminderDefaultBody: "Hola,\n\nA la fecha de hoy, el PU# {pu} / PO# {po} aún no ha sido recolectado. ¿Podría confirmarnos cuándo se estaría recolectando?\n\nGracias,\nGround Fresh International",
    emailClientProtectionReminder: "Enviar Recordatorio de Estatus de Protección",
    emailClientProtectionReminderKind: "Recordatorio de Estatus de Protección",
    emailClientProtectionReminderSubject: "Recordatorio de Estatus de Protección — PU# {pu} / PO# {po}",
    emailClientProtectionReminderBody: "Hola,\n\nLes damos seguimiento al estatus de Protección reportado en el siguiente embarque — ¿podrían confirmarnos el resultado final en cuanto lo tengan?\n\nPU#: {pu}\nPO#: {po}\nLOTE: {lot}\n\nSaludos,\nGround Fresh International",
    emailHtmlCopied: "HTML copiado al portapapeles — pega en Outlook",
    emailHtmlError: "No se pudo copiar HTML — tu navegador bloqueó el portapapeles",
    copyToSL: "Copiar a SweetLeaf",
    copyToSLSuccess: "Copiado a SweetLeaf",
    copyToSLExists: "Ya fue copiado a SweetLeaf",
    paymentsSection: "Pagos", originalTotal: "Total Original", currentBalance: "Saldo Actual",
    noPayments: "Sin pagos registrados", paymentRef: "Referencia", paymentMethodCol: "Método",
    editLockedTitle: "Edición bloqueada",
    editLockedMsg: "Este registro tiene pagos aplicados. Para editarlo, primero elimina los pagos.",
    editLockedHint: "Click en un pago para abrirlo en Pagos y eliminarlo.",
    lotNum: "Lote #",
    lessAdvance: "Menos Anticipo", otherFees: "Otros Gastos", lessCredits: "Menos Créditos",
    totalDue: "TOTAL A PAGAR", remarks: "Observaciones",
    subtotal: "Subtotal", lessPaid: "Menos Pagos Realizados",
    billTo: "FACTURAR A",
    save: "Guardar", cancel: "Cancelar", delete: "Eliminar", editBtn: "Editar", close: "Cerrar",
    invoiceActivity: "Actividad de la Factura", linkedLot: "Abrir Lote Vinculado", linkedBill: "Abrir Bill Vinculado", linkedPO: "Abrir PO",
    confirmDel: "¿Eliminar esta factura?", yes: "Sí, eliminar", no: "Cancelar",
    confirmExit: "Cambios sin guardar. ¿Salir?", yesExit: "Sí, salir", noExit: "Seguir editando",
    saved: "Factura guardada", deleted: "Factura eliminada",
    all: "Todos", allClients: "Todos", allStatuses: "Todos", allSaleStatuses: "Todos los estados",
    statusDraft: "Borrador", statusSent: "Enviada", statusPaid: "Pagada", statusOverdue: "Vencida", status: "Estado",
    printPreview: "Imprimir / PDF", sourceBill: "Bill Origen",
    preview: "Vista previa", printPdf: "Imprimir / PDF", more: "Más acciones",
    driveCreateFolder: "Crear Carpeta en Drive",
    driveCreating: "Creando...",
    driveOpenFolder: "Abrir Carpeta",
    driveSignInNeeded: "Primero inicia sesión en Google Drive",
    driveNotConfigured: "Falta REACT_APP_GOOGLE_CLIENT_ID en .env.local",
    driveNoShipment: "No se encuentra el embarque ligado — crea primero la carpeta del embarque",
    driveError: "Error de Drive",
    openLocalFolder: "Abrir Carpeta",
    openLocalFolderHint: "Acceso directo descargado. IMPORTANTE: renombra el archivo — cambia la extensión de .download a .url — y luego haz doble clic para abrir la carpeta. Ruta también copiada al portapapeles.",
    noInventory: "Sin inventario disponible",
    selectLot: "Seleccionar lote (embarques recibidos)",
    saleStatus: "Status de Venta",
    goodDelivery: "Good Delivery", protection: "Protección", pas: "PAS", partialProtection: "Protección Parcial",
    pendingPickUp: "Pendiente de Recolección", pendingDelivery: "Pendiente de Entrega",
    protectionResponseLabel: "Respuesta del Cliente — Protección (desde SweetLeaf)",
    protectionResponseEditableLabel: "Respuesta del Cliente — Protección",
    protectionResponseNotePlaceholder: "Nota (opcional)",
    protectionResponseClear: "Limpiar",
    protectionReminderDueBanner: function (n) { return "⚠ Han pasado " + n + " días desde que el cliente reportó la llegada sin registrar respuesta de protección — toca enviar un Recordatorio de Estatus. Usa el menú (⋮) para enviarlo."; },
    protectionResponseCol: "Respuesta", protectionResponseColLine1: "Protección", protectionResponseColLine2: "Resultado",
    willPayFull: "Pago Completo", settled: "Precio Ajustado", noResultYet: "Aún sin Resultado",
    settled: "Settled", paidInFull: "Paid in Full",
    dumped: "Dumped", donated: "Donated", samples: "Samples", transfer: "Transferencia",
    selectStatus: "Seleccionar status", palletStatusCol: "Status", computedFromPallets: "Calculado de los pallets de abajo",
    // Batch deposit modal
    recordDeposit: "Registrar Depósito", recordDeposits: "Registrar Depósitos",
    batchDepositTitle: "Depósito en Lote",
    concept: "Concepto", conceptDeposit: "Depósito", conceptAdvance: "Adelanto de cliente",
    depositDate: "Fecha del Depósito", depositMethod: "Método", depositReference: "Referencia / # Transferencia",
    refColumn: "Referencia",
    methodWire: "Transferencia", methodCheck: "Cheque", methodAch: "ACH", methodTransfer: "Transferencia", methodCash: "Efectivo", methodOther: "Otro",
    depositAmount: "Monto del Depósito", totalDeposit: "Total del Depósito",
    dueAmount: "Por Cobrar",
    noOpenInvoices: "Las facturas seleccionadas no tienen saldo pendiente.",
    depositSaved: "Depósito registrado",
    overApplyInv: "El depósito no puede exceder el saldo pendiente",
    notes: "Notas",
    fees: "Cargos y Deducciones", feeType: "Tipo", feeQty: "Cant", feeRate: "Tarifa", feeAmount: "Monto",
    selectFee: "Seleccionar cargo", customFee: "Cargo personalizado…", addFee: "Agregar cargo",
    catCustoms: "Aduana", catWarehouse: "Bodega", catQC: "Calidad/Inspección",
    catShipping: "Envío", catFinancing: "Financiamiento (no operativo)", catCertification: "Certificación", catOther: "Otros Gastos",
    credits: "Créditos", creditType: "Tipo", creditFree: "Crédito libre", creditFromInvoice: "De factura",
    creditConcept: "Concepto", creditRefInvoice: "Ref. factura", creditAmount: "Monto", addCredit: "Agregar crédito",
    grossSales: "Venta Bruta",
    balanceTransfers: "Transferencias de Saldo", btDirection: "Dirección", btSend: "Enviar mi saldo negativo a…", btReceive: "Recibir saldo negativo de…",
    btOtherInvoice: "Otra factura", btAmount: "Monto", btCreate: "Crear transferencia", btType: "Tipo",
    btCredit: "Crédito (recibido)", btDebit: "Débito (enviado)", btCancel: "Cancelar",
  },
};

/* ======================== CONSTANTS ======================== */
var STORAGE_KEY = "gfi-invoices";
var INV_STATUSES = ["draft", "sent", "paid"];
var INV_STATUS_STYLES = {
  draft: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.25)" },
  sent: { bg: "rgba(42,122,111,0.10)", text: "#2A7A6F", border: "rgba(42,122,111,0.25)" },
  paid: { bg: "rgba(16,185,129,0.10)", text: "#059669", border: "rgba(16,185,129,0.25)" },
};
var SALE_STATUSES = ["goodDelivery", "protection", "pas", "dumped", "donated", "samples", "transfer"];
/* Status Venta filter list — 2026-09-27 (Arturo: "hay status que no me
   aparecen en el filtro como pending delivery y pending pick up").
   pendingPickUp/pendingDelivery/partialProtection are never stored on
   a line (computed live only — see the Status column render below), so
   SALE_STATUSES alone left them unfilterable. Filter dropdown only —
   SALE_STATUSES itself stays the manual per-line editable list. */
var FILTER_SALE_STATUSES = PENDING_STATUSES.concat(SALE_STATUSES.slice(0, 3)).concat(["partialProtection"]).concat(SALE_STATUSES.slice(3));
var SALE_STATUS_COLORS = {
  goodDelivery: { bg: "rgba(16,185,129,0.12)", text: "#059669", border: "rgba(16,185,129,0.3)" },
  protection: { bg: "rgba(239,68,68,0.12)", text: "#DC2626", border: "rgba(239,68,68,0.3)" },
  /* PAS = Price After Sale. Market condition (oversupply), NOT a
     quality issue. Less severe than Protection but more severe than
     Partial Protection. Orange to distinguish from red and amber. */
  pas: { bg: "rgba(245,158,11,0.12)", text: "#D97706", border: "rgba(245,158,11,0.3)" },
  /* Computed only (2026-09-12, see saleStatusCalc.js) — a line whose
     matched pallets are a mix of goodDelivery and protection/pas. Never
     manually selectable, hence its own muted amber rather than reusing
     pas's orange (which IS manually selectable and means something
     different). */
  partialProtection: { bg: "rgba(217,119,6,0.10)", text: "#B45309", border: "rgba(217,119,6,0.25)" },
  /* Pre-delivery lifecycle stages (2026-09-12, saleStatusCalc.js
     resolvePendingStage) — display-only, never manually selectable.
     Neutral slate so they read as "no info yet", not good or bad. */
  pendingPickUp: { bg: "rgba(100,116,139,0.10)", text: "#64748B", border: "rgba(100,116,139,0.25)" },
  pendingDelivery: { bg: "rgba(37,99,235,0.10)", text: "#2563EB", border: "rgba(37,99,235,0.25)" },
  dumped: { bg: "rgba(239,68,68,0.10)", text: "#DC2626", border: "rgba(239,68,68,0.25)" },
  donated: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.25)" },
  samples: { bg: "rgba(139,92,246,0.10)", text: "#7C3AED", border: "rgba(139,92,246,0.25)" },
  /* Transfer = cases moved to another invoice/lot. Excluded from gross.
     Blue for "informational, no revenue on this invoice". */
  transfer: { bg: "rgba(59,130,246,0.10)", text: "#2563EB", border: "rgba(59,130,246,0.25)" },
};
/* 2026-10-21, Arturo: "matea toda la app" — same rectangular/3D-bevel
   recipe as ShipmentRegistry.js's TEXAS_QC_BEVEL, applied to every
   SALE_STATUS_COLORS/PROTECTION_RESPONSE_COLORS badge in this file
   (both table cells and the detail/edit views) so Sale Status pills
   read the same squared, beveled way everywhere in the app. */
var SALE_STATUS_BEVEL = "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.12)";
/* 2026-09-14 — Arturo: SweetLeaf's client-response tracking (Four
   Seasons' reply to the Protection Status Reminder — will pay in full,
   or will carry a credit) "trasladalo de manera backward a su espejo
   invoice de ground fresh... este status se origina en sweetleaf y se
   distribuye hacia ground-fresh" — the one field on this pair that
   flows SL→GFI instead of the usual GFI→SL, since FS's reply only ever
   reaches Arturo through the SweetLeaf module. Read-only here (mirrors
   SweetLeafPortal.js's own SALE_STATUS read-only convention, just
   reversed) — see the push in SweetLeafPortal.js's handleSave. */
/* 2026-10-01 — renamed "hasCredit" to "settled" (Arturo: "settled
   significa que el precio se ajusto"), same rename applied identically
   in SweetLeafPortal.js's own copy of this set — same slot/color/role,
   just the correct concept (an adjusted price, not literally credit). */
var PROTECTION_RESPONSE_FINAL_STATUSES = ["willPayFull", "settled"];
var PROTECTION_RESPONSE_COLORS = {
  willPayFull: { bg: "rgba(16,185,129,0.12)", text: "#059669", border: "rgba(16,185,129,0.3)" },
  /* Red, not blue — an adjusted/settled price means less money than
     originally agreed, same severity family as Protection/PAS. */
  settled: { bg: "rgba(239,68,68,0.12)", text: "#DC2626", border: "rgba(239,68,68,0.3)" },
  /* Not a final answer — client just doesn't have a number yet
     (2026-09-23). Same amber as SweetLeafPortal.js's copy. */
  noResultYet: { bg: "rgba(217,119,6,0.10)", text: "#B45309", border: "rgba(217,119,6,0.3)" },
};
/* 2026-10-01 — Ground-Fresh's own Protection Status Reminder schedule
   (Arturo: "necesito enviar alertas de proteccion desde ground fresh a
   clientes que no sean four seasons tanto automatizadas como
   manuales"). Unlike SweetLeafPortal.js's flat single-interval+max-
   count schedule (one config for all of Four Seasons), this is
   2-phase and configurable PER CLIENT (ClientRegistry.js's new
   protectionReminderFirstIntervalDays/FirstCount/SecondIntervalDays,
   falling back to 5/4/3 — Arturo's own example: 4 reminders x 5 days
   = the 20-day fruit payment term, then every 3 days indefinitely
   until a final response):
     count < firstCount  -> due = clientArrivalDate + (count+1)*firstIntervalDays
     count >= firstCount -> due = lastSent(or last "still no result") + secondIntervalDays, uncapped
   `client` is the resolved client record (may be null/undefined — the
   3 schedule fields then fall back to the defaults below). Mirrors
   SweetLeafPortal.js's computeProtectionReminderStatus's final-
   response / noResultYet-rebaseline logic exactly, just 2-phase.
   Four Seasons is always skipped here — that client is handled by
   SweetLeafPortal.js's own equivalent feature instead (REGLA DE
   ENVÍO DE EMAILS). Server-side twin: protection-reminder-send-gf.js
   duplicates this same math in CommonJS, same reasoning
   protection-reminder-send.js already duplicates its browser twin. */
function computeProtectionReminderStatusGf(inv, client, todayStr) {
  var hasProtection = (inv.palletAllocations || []).some(function (a) { return a && (a.saleStatus === "protection" || a.saleStatus === "pas"); })
    || (inv.lines || []).some(function (l) { return l && (l.saleStatus === "protection" || l.saleStatus === "pas"); });
  if (!hasProtection) return null;
  var clientName = (client && (client.shortName || client.fullName)) || inv.clientName || "";
  if (isFourSeasonsClientName(clientName)) return null;
  var isFinalResponse = inv.protectionResponseAt && PROTECTION_RESPONSE_FINAL_STATUSES.indexOf(inv.protectionResponseStatus) >= 0;
  if (isFinalResponse) return null;
  if (!inv.clientArrivalDate) return null;
  var arrival = new Date(inv.clientArrivalDate + "T00:00:00");
  if (isNaN(arrival.getTime())) return null;
  var today = new Date((todayStr || new Date().toISOString().slice(0, 10)) + "T00:00:00");
  var daysSinceArrival = Math.floor((today - arrival) / 86400000);
  if (daysSinceArrival < 0) return null;
  var firstIntervalDays = (client && client.protectionReminderFirstIntervalDays > 0) ? client.protectionReminderFirstIntervalDays : 5;
  var firstCount = (client && client.protectionReminderFirstCount >= 0) ? client.protectionReminderFirstCount : 4;
  var secondIntervalDays = (client && client.protectionReminderSecondIntervalDays > 0) ? client.protectionReminderSecondIntervalDays : 3;
  var count = parseInt(inv.protectionReminderAutoCount, 10) || 0;
  var lastSent = inv.protectionReminderSentAt ? new Date(inv.protectionReminderSentAt) : null;
  var lastSentDateOnly = lastSent && !isNaN(lastSent.getTime()) ? new Date(lastSent.getFullYear(), lastSent.getMonth(), lastSent.getDate()) : null;
  var dueDate;
  if (count < firstCount) {
    dueDate = new Date(arrival);
    dueDate.setDate(dueDate.getDate() + (count + 1) * firstIntervalDays);
  } else {
    var baseline = lastSentDateOnly && lastSentDateOnly > arrival ? lastSentDateOnly : arrival;
    if (inv.protectionResponseStatus === "noResultYet" && inv.protectionResponseAt) {
      var respAt = new Date(inv.protectionResponseAt);
      var respDateOnly = !isNaN(respAt.getTime()) ? new Date(respAt.getFullYear(), respAt.getMonth(), respAt.getDate()) : null;
      if (respDateOnly && respDateOnly > baseline) baseline = respDateOnly;
    }
    dueDate = new Date(baseline);
    dueDate.setDate(dueDate.getDate() + secondIntervalDays);
  }
  return { isDue: today >= dueDate, daysSinceArrival: daysSinceArrival, phase: count < firstCount ? 1 : 2 };
}
var inp = { padding: "7px 8px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", transition: "all 0.2s", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };
/* Brushed Steel "recessed field on a plate" treatment — duplicated
   per-file from ShipmentRegistry.js's filterFieldPlate, same
   convention as BillRegistry.js (never a shared import). Passed to
   every SearchSelect/date-input inside the Invoices Filters popover. */
var filterFieldPlate = {
  background: "linear-gradient(180deg, #f2f3f5 0%, #d7dadd 100%)",
  border: "1px solid #9aa0a8",
  borderRadius: 0,
  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.15), inset 0 -1px 0 rgba(255,255,255,0.6)",
  minHeight: 25,
};
/* Embossed uppercase field label used inside the plate-styled popup
   dialogs (InvoiceFeeEditDialog) — duplicated from BillRegistry.js's
   plateFieldLabel, same convention as filterFieldPlate above. */
var plateFieldLabel = { fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", marginBottom: 3 };
/* 2026-10-20, Arturo: "baj0 esta misma logica que acabamos de
   hacer... agrega las placas de datos" — same embossed plate-button
   recipe as BillRegistry.js's plateActionBtnStyle, duplicated per
   this repo's "never a shared import" convention. Used on the new
   Links/Invoice Activity/Edit strip in both DetailPanel and
   InvoiceForm, mirroring where Bill's own header strip lives. */
var plateActionBtnStyle = { padding: "6px 12px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" };
/* 2026-10-20, Arturo: "baj0 esta misma logica... agrega las placas de
   datos, headers, fonts, tarjetas en 2 columnas" — same dark-silver
   plate title bar as BillRegistry.js's sectionHeaderStyle, duplicated
   per this repo's convention. Each card below pairs this as a title
   strip with a plain content box underneath (borderTop:"none" so the
   two read as one plate), same recipe as Bill's Fees/Bill Summary
   cards — never collapsible here (no open/toggle state exists on this
   screen), same exemption Bill itself gives its own Shipment Info
   card. */
var sectionHeaderStyle = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", border: "1px solid #7d838c", padding: "9px 12px", borderRadius: 0, marginBottom: 0 };
var plateCardBodyStyle = { padding: "12px 14px", background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderTop: "none" };
/* 2026-10-20, Arturo: "invoice info va en una placa sin lineas
   divisorias, esto ya lo hicimos" — same unified-metal-surface fix
   BillRegistry.js already has for its Shipment Info card: header and
   body share ONE continuous textured plate (readOnlyPlateBodyStyle,
   duplicated verbatim) instead of two separately-bordered boxes
   stacked on top of each other, which is what drew the seam line the
   old plateCardBodyStyle version had. Only Invoice Info (the one
   read-only, never-collapsed info grid, same exemption Bill gives
   Shipment Info) uses this; Fees/Credits/Transfers/Summary keep the
   plain bordered plateCardBodyStyle since those aren't being
   complained about. */
var readOnlyPlateBodyStyle = { background: "repeating-linear-gradient(180deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 3px), linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderTop: "none", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -1px 0 rgba(0,0,0,0.15)" };
var readOnlyPlateLabelStyle = { color: "#2a3542", fontSize: 10 };
var readOnlyPlateValueStyle = { color: "#000000", fontWeight: 700 };
/* 2026-10-20, Arturo: "la placa debe contener la informacion del bill"
   — same BILL_STATUS_STYLES colors as BillRegistry.js (duplicated,
   this repo's convention), used to show the linked Bill's own status
   inline in the Links strip, not just a button that navigates away. */
var LINKED_BILL_STATUS_STYLES = {
  draft: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.25)" },
  finalized: { bg: "rgba(42,122,111,0.10)", text: "#2A7A6F", border: "rgba(42,122,111,0.25)" },
  paid: { bg: "rgba(16,185,129,0.10)", text: "#059669", border: "rgba(16,185,129,0.25)" },
};
function linkedBillStatusLabel(status, lang) {
  var labels = lang === "es"
    ? { draft: "Borrador", finalized: "Finalizado", paid: "Pagado" }
    : { draft: "Draft", finalized: "Finalized", paid: "Paid" };
  return labels[status] || labels.draft;
}
/* Same colored metal-plate status-transition buttons as BillRegistry.js's
   statusPlateBtnStyle (duplicated, this repo's convention) — the
   Draft/Sent/Paid buttons in InvoiceForm's sub-header were still flat
   transparent rgba tints before this pass. */
var INV_STATUS_PLATE_COLORS = {
  teal: { grad: "linear-gradient(180deg, #6FBFB2 0%, #2A7A6F 55%, #1C5048 100%)", border: "#123D37" },
  green: { grad: "linear-gradient(180deg, #6EE0B0 0%, #059669 55%, #03694A 100%)", border: "#024A34" },
  gray: { grad: "linear-gradient(180deg, #9CA3AF 0%, #6B7280 55%, #4B5563 100%)", border: "#374151" },
  /* 2026-10-20, Arturo: "protection y has rejections... cambialos a
     color solido con efecto 3d, el font de otro color... rojo
     metalico solido" — same 3-stop metal-plate recipe as teal/green/
     gray above, extended with the hues the title-bar status/
     rejections badges actually need. */
  red: { grad: "linear-gradient(180deg, #F08080 0%, #DC2626 55%, #9B1C1C 100%)", border: "#7F1D1D" },
  amber: { grad: "linear-gradient(180deg, #FBBF77 0%, #D97706 55%, #92400E 100%)", border: "#78350F" },
  blue: { grad: "linear-gradient(180deg, #93C5FD 0%, #2563EB 55%, #1E3A8A 100%)", border: "#1E3A8A" },
  /* 2026-10-21, Arturo: "la placa de este boton cambiale de color por
     uno naranja metalico solido porque no lo encontraba" — the
     Client-reported data button, distinct from amber (gold-brown) so
     it reads as true orange. */
  orange: { grad: "linear-gradient(180deg, #FDBA74 0%, #EA580C 55%, #9A3412 100%)", border: "#7C2D12" },
};
/* 2026-10-21, Arturo: "los botones de accion draft y paid no los
   cambiaste de diseño y tamaño" — these transition buttons sat right
   next to plateActionBtnStyle-styled buttons (Open Linked Bill,
   Invoice Activity) in the same Links-strip row but were noticeably
   smaller (padding 3px 10px / fontSize 10 vs. that style's 6px 12px /
   11) — bumped to match exactly, keeping the colored per-status
   gradient (that part was always correct/intentional). */
function statusPlateBtnStyle(colorKey, marginLeft) {
  var c = INV_STATUS_PLATE_COLORS[colorKey] || INV_STATUS_PLATE_COLORS.gray;
  return { padding: "6px 12px", borderRadius: 0, border: "1px solid " + c.border, background: c.grad, boxShadow: "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.15)", color: "#ffffff", textShadow: "0 1px 1px rgba(0,0,0,0.3)", cursor: "pointer", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", marginLeft: marginLeft || 0 };
}
/* Same metal-plate recipe as statusPlateBtnStyle but for a
   non-interactive badge (cursor:default, no hover semantics) — the
   title-bar status/rejections pills. "los fonts como si estuvieran
   grabados en el metal" — white, engraved-looking text (dark
   text-shadow pressed into the plate), same as the buttons above. */
function metallicBadgeStyle(colorKey) {
  var c = INV_STATUS_PLATE_COLORS[colorKey] || INV_STATUS_PLATE_COLORS.gray;
  return { padding: "3px 10px", borderRadius: 0, border: "1px solid " + c.border, background: c.grad, boxShadow: "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.15)", color: "#ffffff", textShadow: "0 1px 1px rgba(0,0,0,0.4)", cursor: "default", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" };
}
/* Maps a live sale-status key to the metallic color family it reads
   as (red = a real problem, amber = a milder/partial one, green =
   confirmed good, gray/blue = nothing confirmed yet). */
function statusColorKeyFor(status) {
  if (status === "protection" || status === "pas" || status === "dumped") return "red";
  if (status === "partialProtection") return "amber";
  if (status === "goodDelivery") return "green";
  if (status === "pendingDelivery") return "blue";
  return "gray";
}

/* GFI Company Info */
var GFI = { name: "Ground-Fresh International", address: "152 W Hoffman Ave Ste. 5", city: "Lindenhurst, NY 11757-4041", phone: "631-5650034" };

/* ======================== HELPERS ======================== */
function loadLS(key, fb) { try { var d = localStorage.getItem(key); return d ? JSON.parse(d) : fb; } catch { return fb; } }
/* Tiny local read of SweetLeaf's settings — just enough for
   emailHelpers.js's appendDisclosureByAccount() to recognize a
   SweetLeaf-branded send (brand === "sl" here, an account named
   "sweetleaf" in company.emailAccounts) and pull its disclosure text.
   Mirrors SweetLeafPortal.js's own getSLSettings() minimally; not
   importing that file to avoid a cross-module coupling this file
   doesn't otherwise have. */
function readSlSettingsForDisclosure() { return loadLS("gfi-sl-settings", {}); }
/* Paid amount with historical-import fallback: prefer sum(payments[])
   when present; otherwise fall back to amountPaid (used by bulk-paid
   imports that intentionally have no individual payment records). */
function paidAmount(rec) {
  var sum = ((rec && rec.payments) || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  if (sum > 0) return sum;
  return parseFloat(rec && rec.amountPaid) || 0;
}
function parseTermDays(term) {
  if (term === undefined || term === null || term === "") return null;
  var m = String(term).match(/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}
function addDaysToISO(isoDate, days) {
  if (!isoDate) return "";
  var d = new Date(isoDate + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + (parseInt(days, 10) || 0));
  return d.toISOString().substring(0, 10);
}
function saveLS(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch (e) { console.error("[Invoices] storage error", key, e); return false; } }
function nextPaymentId(items) { var nums = (items || []).map(function(i) { return parseInt((i.id || "").replace("PAY-", ""), 10); }).filter(function(n) { return !isNaN(n); }); return "PAY-" + String(Math.max(0, ...nums) + 1).padStart(4, "0"); }
function todayISO() { return new Date().toISOString().substring(0, 10); }
var PAYMENT_METHODS = ["wire", "check", "ach", "transfer", "cash", "other"];
function getClients() { return loadLS("gfi-clients", []); }
function getBills() { return loadLS("gfi-bills", []); }
function getShipments() { return loadLS("gfi-shipments", []); }
/* 2026-10-19, Arturo: "los avisos de proteccion de four seasons y twin
   river son distintos... si quiero reportar algo de twin river al
   shipper desde la factura, utiliza la logica de four seasons y no
   copia lo ya previamente capturado en el pallet que populo esa
   factura... y en las facturas de twin river no muestra tampoco ese
   porcentaje de regression". Twin River/ClariFresh defects are
   captured once, at the Shipment (ShipmentRegistry.js's Pallet
   Manifest, palletManifest[i].qcDefects) — this Invoice must display
   that data read-only for Twin River, never make Arturo re-type it
   via the Four-Seasons-style manual picker below. Read-only mirror of
   ShipmentRegistry.js's qcTol-prefixed / TOLERANCE_CATEGORY_COLORS labels,
   duplicated per this codebase's "don't share across registries"
   convention (same precedent as TOLERANCE_CATEGORY_COLORS itself
   already being duplicated in BillRegistry.js/ShipperPortal.js). */
var QC_TOL_LABELS = {
  decay: { en: "Decay", es: "Pudrición" },
  mold: { en: "Mold", es: "Moho" },
  insect: { en: "Insect", es: "Insecto" },
  leaky: { en: "Leaky", es: "Fuga de jugo" },
  shrivel: { en: "Shrivel", es: "Deshidratación" },
  soft: { en: "Soft", es: "Blanda" },
  brokenSkin: { en: "Broken skin", es: "Piel rota" },
  attachedRemains: { en: "Attached remains", es: "Restos adheridos" },
  floralRemains: { en: "Floral remains", es: "Restos florales" },
  offColor: { en: "Off color", es: "Color fuera de estándar" },
  berryDamage: { en: "Berry damage", es: "Daño en la baya" },
  redBerries: { en: "Red berries", es: "Bayas rojas" },
  greenBerries: { en: "Green berries", es: "Bayas verdes" },
  scars: { en: "Scars", es: "Cicatrices" },
  dryCell: { en: "Dry cell", es: "Célula seca" },
  redCell: { en: "Red cell", es: "Célula roja" },
  regression: { en: "Regression", es: "Regresión" },
  mechanicalDamage: { en: "Mechanical Damage", es: "Daño Mecánico" },
  whiteCells: { en: "White cells", es: "Células blancas" },
  redGreenDryTips: { en: "Red/green or dry tips", es: "Puntas rojas/verdes o secas" },
  attachedCore: { en: "Attached core", es: "Núcleo adherido" },
  /* 2026-10-21, Arturo: "Findings" — bugs/worms/insects found in the
     fruit, not a Condition/Quality tolerance defect. */
  bugs: { en: "Bugs", es: "Bichos" },
  worms: { en: "Worms", es: "Gusanos" },
  insects: { en: "Insects", es: "Insectos" },
};
var QC_CAT_COLORS = {
  severeCondition: { bg: "rgba(220,38,38,0.08)", border: "rgba(220,38,38,0.3)", text: "#B91C1C" },
  condition: { bg: "rgba(217,119,6,0.08)", border: "rgba(217,119,6,0.3)", text: "#B45309" },
  quality: { bg: "rgba(31,78,121,0.08)", border: "rgba(31,78,121,0.3)", text: "#1F4E79" },
  /* "cualquier finding encontrado es de severidad media alta" — same
     medium-high tone as ShipmentRegistry.js's TOLERANCE_CATEGORY_COLORS.findings. */
  findings: { bg: "rgba(234,88,12,0.12)", border: "rgba(234,88,12,0.4)", text: "#C2410C" },
};
var QC_CAT_LABELS = {
  severeCondition: { en: "Severe Condition", es: "Condición Severa" },
  condition: { en: "Condition", es: "Condición" },
  quality: { en: "Quality", es: "Calidad" },
  findings: { en: "Findings", es: "Hallazgos" },
};
function qcTolLabel(key, lang) {
  var l = QC_TOL_LABELS[key];
  if (l) return l[lang] || l.en;
  return key;
}
function qcCatLabel(cat, lang) {
  var l = QC_CAT_LABELS[cat];
  if (l) return l[lang] || l.en;
  return cat;
}
function getProducts() { return loadLS("gfi-products", []); }
function getSuppliers() { return loadLS("gfi-suppliers", []); }
function getFixedPOs() { return loadLS("gfi-fixed-pos", []); }
function getConsignmentPOs() { return loadLS("gfi-consignment-pos", []); }

/* ISO week number — used by the new Week filter on the invoice
   list. Mirrors BillRegistry's getISOWeek so the two modules agree
   on which week a given date belongs to. */
function getISOWeekInv(dateStr) {
  if (!dateStr) return "";
  var d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  var w1 = new Date(d.getFullYear(), 0, 4);
  return String(1 + Math.round(((d - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7)).padStart(2, "0");
}

/* Read/write PO linkage directly to localStorage. The Fixed/
   Consignment Pricing window globals only exist while their tabs
   are mounted — invoices save from a different module, so we go
   straight to the source key here. CompositeId is "F:<id>" for a
   Fixed PO line and "C:<id>" for a Consignment line, matching
   the convention CombinedWeeklySummary already uses. */
function poStorageKeyFor(compositeId) {
  if (!compositeId) return null;
  var src = String(compositeId).split(":")[0];
  return src === "F" ? "gfi-fixed-pos" : src === "C" ? "gfi-consignment-pos" : null;
}
function poIdFor(compositeId) {
  if (!compositeId) return "";
  var parts = String(compositeId).split(":");
  parts.shift();
  return parts.join(":");
}
/* Sync the linkage between an invoice and the PO line it points to.
   Writes BACK to the Pricing module so the PO row reflects:
     - linkedInvoiceId  → invoice's stable id
     - puNumber         → invoice's PU# (the human-readable invoice
                          number Arturo recognizes)
     - saleStatus       → PO Status (2026-09-18, Arturo's 3-status
                          split): "invoiced" once linked, "pending"
                          when unlinking. Purely a link-presence flag
                          now — Fixed/ConsignmentPricing displays it
                          read-only and actually recomputes it live
                          from linkedInvoiceId rather than trusting
                          this stored value, so this write just keeps
                          the row honest at rest for any other reader.

   Args:
     prevCompositeId — PO link the invoice carried previously
                       (empty string on first save)
     newCompositeId  — PO link the invoice now carries
                       (empty string when manual / unlinked)
     invoiceData     — { id, puNumber, status } for the just-saved
                       invoice. Status drives the new PO saleStatus.

   When prev != new, both POs are touched: prev unlinked + reset,
   new linked + stamped. Both updates persist with a fresh
   updatedAt timestamp.

   2026-09-12 — Arturo: "hoy dia tengo que ir y asignarlos manualmente
   despues de crear una factura" (today I have to go assign them
   manually after creating an invoice) — the auto-link was already
   implemented but only ever wrote to this device's localStorage,
   never to Supabase. It looked like it worked in the moment, then
   silently vanished on the next reload/different device the instant
   loadFromSupabase re-fetched fixed_pos/consignment_pos and
   overwrote the local-only patch. Confirmed against real production
   data: only 2 of 35 real PO rows ever got linkedInvoiceId written,
   while 29 have puNumber set through Arturo's manual after-the-fact
   fix. Now persists through saveToSupabase (source of truth) and
   keeps the localStorage mirror in sync too, same as every other
   write in this app. Never throws — linkage is explicitly best-
   effort, callers fire this without blocking the invoice save/delete
   itself (see the two call sites below). */
async function syncPoLinkage(prevCompositeId, newCompositeId, invoiceData) {
  function tableFor(compositeId) {
    var key = poStorageKeyFor(compositeId);
    return key === "gfi-fixed-pos" ? "fixed_pos" : key === "gfi-consignment-pos" ? "consignment_pos" : null;
  }
  function lsKeyFor(table) { return table === "fixed_pos" ? "gfi-fixed-pos" : "gfi-consignment-pos"; }
  async function persistPO(table, existing, mutator) {
    try {
      var merged = Object.assign({}, existing, mutator(existing), { updatedAt: new Date().toISOString() });
      await saveToSupabase(table, merged);
      try {
        var lsKey = lsKeyFor(table);
        var list = JSON.parse(localStorage.getItem(lsKey) || "[]");
        var found = list.some(function (p) { return String(p.id) === String(existing.id); });
        var next = found
          ? list.map(function (p) { return String(p.id) === String(existing.id) ? merged : p; })
          : list.concat([merged]);
        localStorage.setItem(lsKey, JSON.stringify(next));
      } catch (e2) {}
    } catch (e) {
      /* eslint-disable-next-line no-console */
      console.error("[syncPoLinkage] failed to persist PO " + table + ":" + existing.id + ":", e && e.message);
    }
  }
  var inv = invoiceData || {};
  var invId       = inv.id || "";
  var invPu       = inv.puNumber || "";
  var invStatus   = inv.status || "draft";
  var invTherm    = inv.thermograph || "";
  /* eslint-disable no-console */
  console.log("[syncPoLinkage] prev=" + prevCompositeId + " new=" + newCompositeId + " inv=" + invId + " pu=" + invPu + " status=" + invStatus);
  /* eslint-enable no-console */

  var RELEASE_PATCH = function () {
    /* palletsReceived/casesReceived cleared too (2026-09-18, PO
       2228528: Arturo found "real" quantities still showing after
       he deleted the linked invoice) — those were auto-filled from
       the now-deleted invoice's actual lines and are stale/meaningless
       without it; clearing them lets the Phase-3 auto-fill (which
       only ever fills an EMPTY field, never overwrites) repopulate
       correctly once a new invoice is linked. */
    return { linkedInvoiceId: "", puNumber: "", saleStatus: "pending", palletsReceived: "", casesReceived: "" };
  };
  var LINK_PATCH = function () {
    var patch = { linkedInvoiceId: invId, puNumber: invPu, saleStatus: "invoiced" };
    if (invTherm) patch.loggerNumber = invTherm;
    return patch;
  };

  var jobs = [];

  /* 2026-10-22, Arturo (real report, PO 7181 — 2 products/Blackberry +
     Blueberry): "al asignarlo en la factura me deja seleccionar solo
     uno de los dos" / "solo asigna que se recibieron las blacks... los
     blues no tienen/leen los pallets ni cajas recibidas". Root cause:
     a PO# with multiple products is really multiple SEPARATE rows in
     fixed_pos/consignment_pos (one per product, see FixedPricingRegistry.js's
     "Fase 1" comment) sharing the same poNumber — but this function
     only ever linked the ONE row the header's PO# picker pointed at
     (newCompositeId). The invoice's OTHER product line genuinely
     belongs to the same PO#, but its sibling PO row never got
     linkedInvoiceId/puNumber/saleStatus set, so it stayed "Pending
     Invoice" forever and FixedPricingRegistry.js's Phase-3 auto-fill
     (gated on `if (!r.linkedInvoiceId) return`) never populated its
     Pallets/Cases Received either.

     Fixed by linking/unlinking by SIBLING GROUP instead of by single
     row: whichever PO row the invoice is now pointed at via
     newCompositeId, every OTHER row in the same table sharing its
     poNumber + client — and not already claimed by a DIFFERENT
     invoice — gets linked to this same invoice too. Symmetrically on
     release, every PO row (in either table) still linked to THIS
     invoice gets freed, not just the one newCompositeId used to
     point at (covers the case where the picked row changed, or the
     invoice was deleted). */
  if (invId) {
    ["fixed_pos", "consignment_pos"].forEach(function (table) {
      jobs.push((async function () {
        var freshList = await loadFromSupabase(table, { force: true });
        var keepId = newCompositeId && tableFor(newCompositeId) === table ? poIdFor(newCompositeId) : null;
        var toRelease = freshList.filter(function (p) {
          if (keepId && String(p.id) === String(keepId)) return false;
          return String(p.linkedInvoiceId || "") === String(invId);
        });
        for (var i = 0; i < toRelease.length; i++) {
          await persistPO(table, toRelease[i], RELEASE_PATCH);
        }
      })());
    });
  } else if (prevCompositeId && prevCompositeId !== newCompositeId) {
    /* No invoice id (shouldn't normally happen, but mirrors the old
       behavior defensively) — fall back to releasing just the single
       previously-linked row. */
    var prevTable = tableFor(prevCompositeId);
    var prevId = poIdFor(prevCompositeId);
    if (prevTable && prevId) {
      jobs.push((async function () {
        var freshList = await loadFromSupabase(prevTable, { force: true });
        var existing = freshList.find(function (p) { return String(p.id) === String(prevId); });
        if (existing) await persistPO(prevTable, existing, RELEASE_PATCH);
      })());
    }
  }

  if (newCompositeId) {
    var table = tableFor(newCompositeId);
    var id = poIdFor(newCompositeId);
    if (table && id) {
      jobs.push((async function () {
        var freshList = await loadFromSupabase(table, { force: true });
        var picked = freshList.find(function (p) { return String(p.id) === String(id); });
        if (!picked) return;
        var siblings = freshList.filter(function (p) {
          if (String(p.id) === String(id)) return true;
          var samePo = p.poNumber && picked.poNumber && String(p.poNumber) === String(picked.poNumber);
          if (!samePo) return false;
          var sameClient = (p.clientId && picked.clientId && String(p.clientId) === String(picked.clientId))
            || (p.clientName && picked.clientName && p.clientName === picked.clientName);
          if (!sameClient) return false;
          var otherLink = p.linkedInvoiceId;
          return !otherLink || String(otherLink) === String(invId);
        });
        for (var i = 0; i < siblings.length; i++) {
          await persistPO(table, siblings[i], LINK_PATCH);
        }
      })());
    }
  }

  await Promise.all(jobs);
}

function fmtDate(d) { if (!d) return "\u2014"; var dt = new Date(d + "T00:00:00"); return dt.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }); }
/* Full ISO timestamp (e.g. protectionEmailSentAt) \u2192 local date + time,
   MM/DD/YYYY h:mm AM/PM (Arturo: "agrega la hora de envio" \u2014 the date-
   only badge didn't say WHEN the notice went out, just the day). */
function fmtDateTime(iso) {
  if (!iso) return "\u2014";
  var dt = new Date(iso);
  if (isNaN(dt.getTime())) return "\u2014";
  return dt.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) + " " + dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
function fmtMoney(n) { var v = parseFloat(n) || 0; return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function nextPU(invoices) {
  var nums = invoices.map(function(i) { return parseInt(i.puNumber, 10); }).filter(function(n) { return !isNaN(n); });
  return nums.length > 0 ? String(Math.max(...nums) + 1) : "17303";
}
function nextId(items) {
  var nums = items.map(function(i) { return parseInt((i.id || "").replace("INV-", ""), 10); }).filter(function(n) { return !isNaN(n); });
  return "INV-" + String((Math.max(0, ...nums) + 1)).padStart(4, "0");
}

function resolveClientName(clientId) {
  if (!clientId) return "";
  var c = getClients().find(function(x) { return String(x.id) === String(clientId); });
  if (!c) return "";
  return c.shortName || c.fullName || c.name || c.companyName || "";
}
function resolveClientFull(clientId) {
  if (!clientId) return null;
  return getClients().find(function(x) { return String(x.id) === String(clientId); }) || null;
}

/* Lots available as line sources for an invoice. Previously this
   filtered `status === "received" || "billed"`, which silently
   dropped every shipper-portal-promoted shipment — acceptPortal-
   Submission writes them with status="draft" (ShipmentRegistry line
   ~3999), so even a fully-billed portal lot like 58027 never showed
   up in the dropdown until someone manually flipped the status.
   Showing all three statuses (draft / received / billed) matches
   the user's ask "TODOS los embarques sin importar su origen" and
   restores parity with BillRegistry, which already looks up
   shipments by lot regardless of status. */
function getAvailableLots() {
  return getShipments();
}

/* Shared SearchSelect imported from ./SearchSelect (project-wide). */

/* ======================== TOAST ======================== */
function Toast({ message, onClose }) {
  var [fading, setFading] = useState(false);
  useEffect(function() {
    var fadeT = setTimeout(function() { setFading(true); }, 2700);
    var closeT = setTimeout(function() { onClose(); }, 3000);
    return function() { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [message]);
  return createPortal(<div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#1a2636", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 999999, boxShadow: "0 8px 32px rgba(0,0,0,0.2)", animation: fading ? "fadeOut 0.3s ease forwards" : undefined }}>{message}</div>, document.body);
}

/* ======================== PRINT ======================== */
/* Builds the printable HTML for one invoice. Same navy look as Sales Reports \u2014
   LOGO_B64 image, #1B3A4B accents, Ground Fresh footer. The full <style> block
   is included so multi-print can extract & share it. */
function fmtDateLongInv(d) { if (!d) return ""; return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }

export function buildInvoicePrintHTML(invoice, client) {
  /* Per-line commission. Each line carries its own commissionRate
     (post-migration); legacy lines fall back to invoice.commissionRate.
     Sum reproduces the legacy single-rate total when every line shares
     the same rate (linearity). */
  var invDefaultCommRate = parseFloat(invoice.commissionRate) || 0;
  var totalCases = 0, totalPallets = 0, totalAmount = 0, totalFees = 0, totalCredits = 0, totalTransfers = 0, commissionAmt = 0;
  (invoice.lines || []).forEach(function(l) {
    totalCases += parseInt(l.cases, 10) || 0;
    totalPallets += parseInt(l.pallets, 10) || 0;
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    var lineAmt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
    var rate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
      ? invDefaultCommRate
      : (parseFloat(l.commissionRate) || 0);
    totalAmount += lineAmt;
    commissionAmt += lineAmt * (rate / 100);
  });
  /* commRate retained for the legacy header label only. The actual
     deduction breakdown below shows one row per line. */
  var commRate = invDefaultCommRate;
  (invoice.fees || []).forEach(function(fe) { totalFees += Math.abs(parseFloat(fe.amount) || 0); });
  (invoice.credits || []).forEach(function(cr) { totalCredits += Math.abs(parseFloat(cr.amount) || 0); });
  (invoice.balanceTransfers || []).forEach(function(t) { var amt = parseFloat(t.amount) || 0; if (t.type === "credit") totalTransfers += amt; else if (t.type === "debit") totalTransfers -= amt; });
  var paid = (invoice.payments || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  /* Net to client = gross − commission − fees + credits ± balance transfers.
     Fees are deductions GFI passes through (PTI, QC, warehouse, etc.) and
     SUBTRACT. Credits are adjustments owed to GFI and ADD. Transfers ±
     per type. Then minus payments. */
  var subtotal = totalAmount - commissionAmt - totalFees + totalCredits + totalTransfers;
  var due = subtotal - paid;

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Invoice ' + (invoice.puNumber || "") + '</title><style>';
  h += '@page{size:letter;margin:0.6in 0.7in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4}';
  h += '.page{max-width:700px;margin:0 auto}';
  h += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:3px solid #1B3A4B}';
  /* Bumped to 100 — matches other GFI docs so "International" reads. */
  h += '.logo img{height:100px}';
  h += '.doc-title{font-size:26px;font-weight:300;color:#1B3A4B;letter-spacing:2px;text-align:right}';
  h += '.meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:14px 0;padding:12px 16px;background:#f7f8fa;border-radius:6px;border-left:4px solid #1B3A4B}';
  h += '.meta-grid .label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700}';
  h += '.meta-grid .value{font-size:11px;font-weight:600;color:#1a1a1a;margin-top:1px}';
  h += '.bill-to{margin:10px 0 14px;padding:10px 16px;border:1px solid #e0e0e0;border-radius:6px}';
  h += '.bill-to .bt-label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px}';
  h += '.bill-to .bt-name{font-size:13px;font-weight:700;color:#1B3A4B}';
  h += '.bill-to .bt-addr{font-size:9px;color:#666;margin-top:2px;line-height:1.5}';
  h += 'table{width:100%;border-collapse:collapse}';
  h += '.items th{padding:7px 8px;font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#fff;background:#1B3A4B;font-weight:600}';
  h += '.items th:first-child{border-radius:4px 0 0 0}.items th:last-child{border-radius:0 4px 0 0}';
  h += '.items td{padding:6px 8px;border-bottom:1px solid #eee;font-size:10px;vertical-align:middle}';
  h += '.items tr:nth-child(even){background:#fafbfc}';
  h += '.r{text-align:right}.c{text-align:center}.mono{font-family:"Courier New",monospace;font-size:10px}';
  h += '.ded{color:#c0392b}';
  h += '.summary{margin-top:10px;display:flex;justify-content:flex-end}';
  h += '.summary-tbl{width:380px}';
  h += '.summary-tbl td{padding:5px 8px;font-size:10px}';
  h += '.summary-tbl .total td{font-size:13px;font-weight:700;color:#1B3A4B;padding-top:8px}';
  h += '.summary-tbl .due td{font-size:14px;font-weight:800;color:#1B3A4B;border-top:3px solid #1B3A4B;padding-top:10px}';
  h += '.footer{margin-top:20px;padding-top:10px;border-top:1px solid #ddd;font-size:8px;color:#999;text-align:center}';
  h += '</style></head><body><div class="page">';

  /* Header */
  h += '<div class="header"><div class="logo"><img src="' + LOGO_B64 + '" /></div>';
  h += '<div class="doc-title">INVOICE</div></div>';

  /* Meta grid */
  h += '<div class="meta-grid">';
  h += '<div><div class="label">Invoice #</div><div class="value">' + (invoice.puNumber || "\u2014") + '</div></div>';
  h += '<div><div class="label">Date</div><div class="value">' + fmtDateLongInv(invoice.date) + '</div></div>';
  if (invoice.lot) h += '<div><div class="label">Lot</div><div class="value">' + invoice.lot + '</div></div>';
  if (invoice.clientRef) h += '<div><div class="label">Customer Ref</div><div class="value">' + invoice.clientRef + '</div></div>';
  if (invoice.dueDate) h += '<div><div class="label">Due Date</div><div class="value">' + fmtDateLongInv(invoice.dueDate) + '</div></div>';
  if (invoice.clientInitials) h += '<div><div class="label">Customer ID</div><div class="value">' + invoice.clientInitials + '</div></div>';
  h += '</div>';

  /* Bill To */
  if (client) {
    var addrParts = [client.address || "", (client.city || "") + (client.state ? ", " + client.state : "") + (client.zip ? " " + client.zip : ""), client.country || "", client.phone || ""].filter(Boolean);
    h += '<div class="bill-to"><div class="bt-label">Bill To</div>';
    h += '<div class="bt-name">' + (client.fullName || client.shortName || client.name || "") + '</div>';
    if (addrParts.length > 0) h += '<div class="bt-addr">' + addrParts.join(" \u00b7 ") + '</div>';
    h += '</div>';
  }

  /* Product lines — Comm % column added so the client can see the
     rate applied per line. */
  h += '<table class="items"><thead><tr>';
  h += '<th style="text-align:left;width:32%">Description</th>';
  h += '<th class="c" style="width:7%">Pallets</th>';
  h += '<th class="c" style="width:7%">Cases</th>';
  h += '<th class="r" style="width:12%">Price</th>';
  h += '<th class="r" style="width:8%">Comm %</th>';
  h += '<th class="c" style="width:11%">Status</th>';
  h += '<th class="r" style="width:18%">Amount</th>';
  h += '</tr></thead><tbody>';
  (invoice.lines || []).forEach(function(l) {
    var c = parseInt(l.cases, 10) || 0;
    var plt = parseInt(l.pallets, 10) || 0;
    var p = parseFloat(l.salePrice) || 0;
    var amt = c * p;
    var desc = l.productDescription || (l.productName || l.productBase || "");
    var statusLabel = l.saleStatus === "goodDelivery" ? "Good Delivery" : l.saleStatus === "protection" ? "Protection" : l.saleStatus === "pas" ? "PAS" : l.saleStatus === "dumped" ? "Dumped" : l.saleStatus === "donated" ? "Donated" : l.saleStatus === "samples" ? "Samples" : l.saleStatus === "transfer" ? "Transfer" : "";
    /* Match SALE_STATUS_COLORS in InvoiceRegistry / BillRegistry — text
       color only; no bg in print to keep it ink-friendly. */
    var statusColor = l.saleStatus === "goodDelivery" ? "#059669" : l.saleStatus === "pas" ? "#D97706" : l.saleStatus === "protection" || l.saleStatus === "dumped" ? "#DC2626" : l.saleStatus === "donated" ? "#6B7280" : l.saleStatus === "samples" ? "#7C3AED" : l.saleStatus === "transfer" ? "#2563EB" : "#666";
    var statusHTML = statusLabel ? '<span style="color:' + statusColor + ';font-weight:700;font-size:9px">' + statusLabel + '</span>' : '<span style="color:#bbb">—</span>';
    var lineRate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
      ? invDefaultCommRate
      : (parseFloat(l.commissionRate) || 0);
    var commCell = lineRate > 0 ? lineRate + '%' : '—';
    h += '<tr><td><b>' + (l.productName || l.productBase || "") + '</b>';
    if (desc && desc !== (l.productName || l.productBase)) h += '<div style="font-size:8px;color:#666;margin-top:2px">' + desc + '</div>';
    h += '</td><td class="c mono">' + plt + '</td><td class="c mono">' + c + '</td><td class="r mono">$' + fmtMoneyNoSign(p) + '</td><td class="r mono">' + commCell + '</td><td class="c">' + statusHTML + '</td><td class="r mono">$' + fmtMoneyNoSign(amt) + '</td></tr>';
  });
  h += '</tbody></table>';

  /* Summary — Total Due = Gross − Commission + Fees − Credits + Transfers − Payments */
  h += '<div class="summary"><table class="summary-tbl">';
  h += '<tr><td class="r"><b>Gross Sales</b></td><td></td><td class="r mono"><b>$' + fmtMoneyNoSign(totalAmount) + '</b></td></tr>';
  if (commissionAmt > 0) {
    /* Per-line commission breakdown — one row per non-excluded line
       so the client can audit the rate × amount math, plus a total
       row. Mirrors the bill PDF treatment. */
    h += '<tr><td colspan="3" style="font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;padding-top:8px;border-bottom:1px solid #ddd">Commission</td></tr>';
    (invoice.lines || []).forEach(function(l) {
      if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
      var lineAmt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
      var rate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
        ? invDefaultCommRate
        : (parseFloat(l.commissionRate) || 0);
      if (!(rate > 0)) return;
      var lineComm = lineAmt * (rate / 100);
      var name = l.productName || l.productBase || "Line";
      h += '<tr><td class="r" style="color:#c0392b;font-size:9px">Commission · ' + name + ' (' + rate + '%)</td><td></td><td class="r mono" style="color:#c0392b">($' + fmtMoneyNoSign(lineComm) + ')</td></tr>';
    });
    h += '<tr><td class="r" style="font-size:9px;font-weight:700;color:#888;padding-top:4px;border-top:1px solid #ddd">Total Commission</td><td style="border-top:1px solid #ddd"></td><td class="r mono" style="font-size:9px;color:#c0392b;padding-top:4px;border-top:1px solid #ddd;font-weight:700">($' + fmtMoneyNoSign(commissionAmt) + ')</td></tr>';
  }
  if (totalFees > 0) {
    h += '<tr><td colspan="3" style="font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;padding-top:8px;border-bottom:1px solid #ddd">Fees &amp; Deductions</td></tr>';
    (invoice.fees || []).forEach(function(fe) {
      var amtAbs = Math.abs(parseFloat(fe.amount) || 0);
      if (!(amtAbs > 0)) return;
      var fname = fe.feeType === "__custom__" ? fe.feeCustom : (fe.feeType || "Fee");
      h += '<tr><td class="r" style="color:#c0392b">' + fname + '</td><td></td><td class="r mono" style="color:#c0392b">($' + fmtMoneyNoSign(amtAbs) + ')</td></tr>';
    });
    h += '<tr><td class="r" style="font-size:9px;font-weight:700;color:#888;padding-top:4px;border-top:1px solid #ddd">Total Deductions</td><td style="border-top:1px solid #ddd"></td><td class="r mono" style="font-size:9px;color:#c0392b;padding-top:4px;border-top:1px solid #ddd">($' + fmtMoneyNoSign(totalFees) + ')</td></tr>';
  }
  if (totalCredits > 0) {
    h += '<tr><td colspan="3" style="font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;padding-top:8px;border-bottom:1px solid #ddd">Credits</td></tr>';
    (invoice.credits || []).forEach(function(cr) {
      var amtAbs = Math.abs(parseFloat(cr.amount) || 0);
      if (!(amtAbs > 0)) return;
      var label = cr.type === "fromInvoice"
        ? ("Credit from PU# " + (cr.refPuNumber || "—"))
        : (cr.concept || "Credit");
      h += '<tr><td class="r" style="color:#059669">' + label + '</td><td></td><td class="r mono" style="color:#059669">+$' + fmtMoneyNoSign(amtAbs) + '</td></tr>';
    });
    h += '<tr><td class="r" style="font-size:9px;font-weight:700;color:#888;padding-top:4px;border-top:1px solid #ddd">Total Credits</td><td style="border-top:1px solid #ddd"></td><td class="r mono" style="font-size:9px;color:#059669;padding-top:4px;border-top:1px solid #ddd">+$' + fmtMoneyNoSign(totalCredits) + '</td></tr>';
  }
  /* Balance Transfers — cross-invoice linkage. + (credit) or − (debit). */
  if ((invoice.balanceTransfers || []).length > 0) {
    h += '<tr><td colspan="3" style="font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;padding-top:8px;border-bottom:1px solid #ddd">Balance Transfers</td></tr>';
    (invoice.balanceTransfers || []).forEach(function(tr) {
      var amt = parseFloat(tr.amount) || 0;
      if (!(amt > 0)) return;
      var isCredit = tr.type === "credit";
      var color = isCredit ? "#059669" : "#c0392b";
      var sign = isCredit ? "+" : "−";
      var label = (isCredit ? "Credit from PU# " : "Debit to PU# ") + (tr.otherPuNumber || "?");
      h += '<tr><td class="r" style="color:' + color + '">' + label + '</td><td></td><td class="r mono" style="color:' + color + '">' + sign + '$' + fmtMoneyNoSign(amt) + '</td></tr>';
    });
    var transfersAbs = Math.abs(totalTransfers);
    var ttColor = totalTransfers >= 0 ? "#059669" : "#c0392b";
    var ttSign = totalTransfers >= 0 ? "+" : "−";
    h += '<tr><td class="r" style="font-size:9px;font-weight:700;color:#888;padding-top:4px;border-top:1px solid #ddd">Net Transfers</td><td style="border-top:1px solid #ddd"></td><td class="r mono" style="font-size:9px;color:' + ttColor + ';padding-top:4px;border-top:1px solid #ddd">' + ttSign + '$' + fmtMoneyNoSign(transfersAbs) + '</td></tr>';
  }
  if (paid > 0) {
    h += '<tr class="total"><td class="r" colspan="2">Net</td><td class="r mono">$' + fmtMoneyNoSign(subtotal) + '</td></tr>';
    h += '<tr><td class="r" colspan="2" style="color:#c0392b">Less: Amount Paid</td><td class="r mono" style="color:#c0392b">($' + fmtMoneyNoSign(paid) + ')</td></tr>';
  }
  h += '<tr class="due"><td class="r" colspan="2">TOTAL DUE</td><td class="r mono">$' + fmtMoneyNoSign(due) + '</td></tr>';
  h += '</table></div>';

  h += '<div class="footer">Ground Fresh International, Inc. \u00b7 Lindenhurst, NY \u00b7 ground-fresh.com</div>';
  h += '</div></body></html>';
  return h;
}

function fmtMoneyNoSign(n) { var v = parseFloat(n) || 0; return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

/* 2026-10-21, Arturo: pasted a screenshot of BillRegistry.js's own
   "Bill Summary" card ("invoice summary debe verse asi") — same
   per-category fee breakdown (Commission / Customs / Warehouse /
   Financing / etc.) instead of a single lumped "- Fees & Deductions"
   line. Duplicated verbatim from BillRegistry.js's FEE_CATEGORY_ORDER/
   categorizeFeeName/calcBillFeesByCategory/FeeBreakdownLines, per this
   codebase's "never share between Registry files" convention — only
   the component/function names carry an "Inv" suffix to avoid any
   confusion with Bill's own copy if both files are open side by side. */
var FEE_CATEGORY_ORDER = ["customs", "warehouse", "qc", "shipping", "financing", "certification", "other"];
function categorizeFeeNameInv(name, feeCategoryMap) {
  var key = String(name || "").trim().toLowerCase();
  if (!key) return "other";
  if (feeCategoryMap && feeCategoryMap[key]) return feeCategoryMap[key];
  if (key.indexOf("financ") >= 0) return "financing";
  if (key.indexOf("custom") >= 0 || key.indexOf("aduana") >= 0 || key.indexOf("broker") >= 0) return "customs";
  if (key.indexOf("warehouse") >= 0 || key.indexOf("bodega") >= 0 || key.indexOf("repack") >= 0) return "warehouse";
  if (key.indexOf("qc") >= 0 || key.indexOf("inspect") >= 0 || key.indexOf("fumig") >= 0 || key.indexOf("quality") >= 0 || key.indexOf("calidad") >= 0) return "qc";
  if (key.indexOf("ship") >= 0 || key.indexOf("freight") >= 0 || key.indexOf("envio") >= 0) return "shipping";
  if (key.indexOf("cert") >= 0) return "certification";
  return "other";
}
function calcInvoiceFeesByCategory(fees, feeCategoryMap) {
  var byCategory = {};
  (fees || []).forEach(function (f) {
    var name = f.feeType === "__custom__" ? (f.feeCustom || "") : (f.feeType || "");
    var cat = categorizeFeeNameInv(name, feeCategoryMap);
    byCategory[cat] = (byCategory[cat] || 0) + Math.abs(parseFloat(f.amount) || 0);
  });
  return byCategory;
}
function FeeBreakdownLinesInv({ fees, feeCategoryMap, t }) {
  var byCategory = calcInvoiceFeesByCategory(fees, feeCategoryMap);
  var customCatsPresent = Object.keys(byCategory).filter(function (c) { return FEE_CATEGORY_ORDER.indexOf(c) === -1 && byCategory[c] > 0; }).sort();
  var cats = FEE_CATEGORY_ORDER.filter(function (c) { return byCategory[c] > 0; }).concat(customCatsPresent);
  if (cats.length === 0) return null;
  var catLabelKey = { customs: "catCustoms", warehouse: "catWarehouse", qc: "catQC", shipping: "catShipping", financing: "catFinancing", certification: "catCertification", other: "catOther" };
  return cats.map(function (c) {
    var isFinancing = c === "financing";
    return (
      <div key={c} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: isFinancing ? "#B45309" : "#DC2626" }}>
        <span>{"− "}{t[catLabelKey[c]] || c}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(byCategory[c])})</span>
      </div>
    );
  });
}

/* Multi-invoice print: reuses the shared <style> block from the first doc and
   concatenates each body with a hard page-break in between. Mirrors
   printMultipleSalesReports so behavior stays identical across modules. */
export function printMultipleInvoices(invoices, clients, onError) {
  if (!Array.isArray(invoices) || invoices.length === 0) return;
  var byId = {};
  (clients || []).forEach(function(c) { byId[String(c.id)] = c; });

  var htmls = invoices.map(function(inv) {
    var cli = byId[String(inv.clientId)] || null;
    return buildInvoicePrintHTML(inv, cli);
  });
  var styleMatch = htmls[0].match(/<style>([\s\S]*?)<\/style>/);
  var sharedCss = styleMatch ? styleMatch[1] : "";
  var bodies = htmls.map(function(html) {
    var m = html.match(/<body[^>]*>([\s\S]*?)<\/body>/);
    return m ? m[1] : "";
  });
  var today = new Date().toISOString().substring(0, 10);
  var doc = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Invoices - ' + today + '</title><style>' + sharedCss + ' .inv-pagebreak{page-break-before:always;break-before:page;height:0;margin:0;padding:0}</style></head><body>';
  bodies.forEach(function(body, i) {
    if (i > 0) doc += '<div class="inv-pagebreak"></div>';
    doc += body;
  });
  doc += '</body></html>';

  var w = window.open("", "_blank");
  if (!w) { if (onError) onError("popup-blocked"); return; }
  w.document.open();
  w.document.write(doc);
  w.document.close();
  setTimeout(function() { try { w.focus(); w.print(); } catch (e) {} }, 600);
}

/* ============= BRAND-AWARE SALES DOCS (English-only) =============
   Two printable docs × two brands = four variants:
     1. Pick Ticket / Inbound — product summary, picked at print time
     2. Packing List          — per-pallet detail
   Brands:
     - GFI       (Ground-Fresh International, navy #1B3A4B, LOGO_B64)
     - SweetLeaf (Sweetleaf Produce Inc.,    teal #2A7A6F, SWEETLEAF_LOGO_B64)
   The "kind" string passed to printInvoiceDocs follows
   "<brand>-<doc>" where brand=gfi|sl, doc=pick|inbound|packing.
   All four use inline CSS so print preserves formatting. Multi-print
   concatenates bodies separated by page-break. */

function escDocHtml(s) {
  if (s === undefined || s === null) return "";
  return String(s).replace(/[&<>"']/g, function (c) { return ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]; });
}
function fmtDocDate(d) { if (!d) return ""; return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }); }

/* Per-pallet weight mirror of ShipmentRegistry's calcPalletWeight.
   Inlined here so this file stays self-contained for printing. */
var DOC_SOZ = { "6oz": 6, "12oz": 12, "18oz": 18, "1 dry pint": 16, "1lb": 16, "2lb": 32 };
function docPalletWeight(p) {
  if (!p) return { lb: 0, kg: 0 };
  var oz = (DOC_SOZ[p.size] || 0) * (parseFloat(p.unitsCase) || 0) * (parseFloat(p.casesPallet) || 0);
  if (!oz) return { lb: 0, kg: 0 };
  var lb = oz / 16;
  return { lb: lb, kg: lb * 0.453592 };
}

/* Brand config table. The shipperName + shipperAddr are hard-coded
   per the spec rather than pulled from CompanyProfile because the
   docs are brand-specific (SweetLeaf is a trade name; the Company
   Profile holds GFI's legal entity). The contact phone/website still
   read from CompanyProfile so a single update propagates. */
function brandConfig(brandKey) {
  if (brandKey === "sl") {
    return {
      key: "sl",
      name: "Sweetleaf Produce Inc.",
      accent: "#2A7A6F",
      accentRgba: "rgba(42,122,111,0.06)",
      panelBg: "#eef5f4",
      logo: SWEETLEAF_LOGO_B64,
      shipperName: "Sweetleaf Produce Inc.",
      shipperAddr: "152 W Hoffman Ave Ste. 5, Lindenhurst, NY 11757-4041",
      footer: "Sweetleaf Produce Inc. · Lindenhurst, NY, USA",
      organicCert: false,
      tempRecorder: true,
      lotColumn: true,
    };
  }
  return {
    key: "gfi",
    name: "Ground-Fresh International Inc.",
    accent: "#1B3A4B",
    accentRgba: "rgba(27,58,75,0.06)",
    panelBg: "#eef4f5",
    logo: LOGO_B64,
    shipperName: "Ground-Fresh International Inc.",
    shipperAddr: "152 W Hoffman Ave Ste. 5, Lindenhurst, NY 11757-4041",
    footer: "Ground Fresh International Inc. · Lindenhurst, NY, USA · ground-fresh.com",
    organicCert: true,
    tempRecorder: false,
    lotColumn: false,
  };
}

/* Brand-aware stylesheet — accent color swaps per brand, everything
   else identical. Class names stable so the body markup is shared
   across the 4 doc variants. */
function brandDocCss(brand) {
  var c = brand.accent;
  var bg = brand.panelBg;
  var css = '@page{size:letter;margin:0.5in 0.55in}';
  css += '*{margin:0;padding:0;box-sizing:border-box}';
  css += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4}';
  css += '.page{max-width:730px;margin:0 auto}';
  css += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:14px;border-bottom:3px solid ' + c + ';margin-bottom:14px}';
  /* Bumped from 60 → 95. Both brand logos benefit: the GFI raster
     "Ground-Fresh International" wordmark becomes readable, and the
     SweetLeaf SVG (wider aspect ratio) still fits comfortably in the
     header next to the centered/right document title. */
  css += '.logo img{height:95px}';
  css += '.doc-title{font-size:22px;font-weight:300;color:' + c + ';letter-spacing:1.5px;text-align:right}';
  css += '.doc-title.center{text-align:center;flex:1}';
  css += '.doc-meta{font-size:9px;color:#666;text-align:right;margin-top:6px;line-height:1.6}';
  css += '.doc-meta b{color:' + c + ';font-weight:700}';
  css += '.party-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px}';
  css += '.party-block{padding:10px 14px;border:1px solid #e0e0e0;border-radius:6px;background:#fafbfc}';
  css += '.party-block .lbl{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px}';
  css += '.party-block .name{font-size:12px;font-weight:700;color:' + c + ';margin-bottom:3px}';
  css += '.party-block .line{font-size:10px;color:#444;line-height:1.5}';
  css += '.fields-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px 14px;margin-bottom:6px;padding:10px 14px;background:#f7f8fa;border-radius:6px;border-left:4px solid ' + c + '}';
  css += '.fields-grid .lbl{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700}';
  css += '.fields-grid .val{font-size:11px;font-weight:600;color:#1a1a1a;margin-top:1px;font-family:"Courier New",monospace}';
  /* ETA disclaimer — bumped to 10px because the prior 8px was almost
     invisible on screen and barely readable in print preview. */
  css += '.eta-note{font-size:10px;color:#666;font-style:italic;margin:4px 14px 12px;letter-spacing:0.3px}';
  css += 'table{width:100%;border-collapse:collapse}';
  css += '.items th{padding:7px 8px;font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#fff;background:' + c + ';font-weight:600}';
  css += '.items th:first-child{border-radius:4px 0 0 0}.items th:last-child{border-radius:0 4px 0 0}';
  css += '.items td{padding:6px 8px;border-bottom:1px solid #eee;font-size:10px;vertical-align:middle}';
  css += '.items tr:nth-child(even){background:#fafbfc}';
  css += '.r{text-align:right}.c{text-align:center}.mono{font-family:"Courier New",monospace}';
  css += '.totals{background:' + c + ';color:#fff;font-weight:800}';
  css += '.totals td{font-size:11px;color:#fff;padding:8px 8px;letter-spacing:0.5px;border-bottom:none}';
  css += '.summary{margin-top:14px;padding:10px 14px;background:' + bg + ';border-radius:6px;display:grid;grid-template-columns:repeat(4,1fr);gap:8px 14px}';
  css += '.summary .lbl{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:' + c + ';font-weight:700}';
  css += '.summary .val{font-size:14px;font-weight:800;color:' + c + ';font-family:"Courier New",monospace;margin-top:2px}';
  css += '.remarks{margin-top:14px;padding:12px 14px;border:1px dashed #ccc;border-radius:6px;min-height:60px}';
  css += '.remarks .lbl{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px}';
  css += '.cert-strip{margin-top:14px;padding:8px 14px;background:' + c + ';color:#fff;border-radius:6px;font-size:9px;text-align:center;letter-spacing:0.5px}';
  css += '.footer{margin-top:14px;padding-top:8px;border-top:1px solid #ddd;font-size:8px;color:#999;text-align:center}';
  return css;
}

/* Resolve the shipment that fed a given invoice line-set. Tries 3
   match strategies in order — strict, trimmed, then numeric — to
   tolerate the small inconsistencies that creep in over time
   (whitespace from imports, leading zeros, numeric vs string types).
   Returns null when none match. */
/* Identity match that tolerates BOTH the legacy id (rowToRecord sets
   record.id = row.legacy_id, which may be a Date.now() string from
   the LS era) AND the Supabase UUID (rowToRecord sets
   record._uuid = row.id). Foreign-key references stored on shipments
   / invoices / bills can be either form depending on when the parent
   row was created; without checking both, lookups fail silently on
   any row whose FK was written with the "other" id shape and the UI
   renders "—" / "Warehouse: —" / "No shipment found". */
function idMatches(record, ref) {
  if (!record || ref === undefined || ref === null || ref === "") return false;
  var s = String(ref);
  return String(record.id) === s || String(record._uuid) === s;
}
/* Pure lot lookup (exact → trimmed → numeric), factored out of
   findShipmentForInvoice below so any line/pallet that carries its
   OWN lot (a multi-lot invoice's lines/palletAllocations) can resolve
   the shipment it actually belongs to, instead of always the
   invoice's single "home" lot (2026-11-03, PU 17484: a multi-lot
   invoice — blackberry from lot 58083, blueberry from lot 58084 —
   had every line/pallet status check run against 58083's manifest
   only, so blueberry's real ClariFresh status, and even its OWN
   pallet numbers, were being looked up in the wrong shipment). */
function findShipmentByLot(lotRaw, shipments) {
  if (!lotRaw && lotRaw !== 0) return null;
  var lotStr = String(lotRaw);
  var list = shipments || [];
  /* 1. Exact string match — fast path that handles 95% of cases. */
  var hit = list.find(function (s) { return String(s.lot) === lotStr; });
  if (hit) return hit;
  /* 2. Trimmed match — guards against stray whitespace from Excel imports. */
  var trimmed = lotStr.trim();
  hit = list.find(function (s) { return String(s.lot || "").trim() === trimmed; });
  if (hit) return hit;
  /* 3. Numeric match — handles "57916" vs 57916 type mismatches. */
  var lotNum = parseInt(lotStr, 10);
  if (!isNaN(lotNum)) {
    hit = list.find(function (s) { var n = parseInt(s.lot, 10); return !isNaN(n) && n === lotNum; });
    if (hit) return hit;
  }
  return null;
}
function findShipmentForInvoice(invoice, shipments) {
  if (!invoice) return null;
  var lotRaw = invoice.lot || (invoice.lines && invoice.lines[0] && invoice.lines[0].lot) || "";
  if (!lotRaw && lotRaw !== 0) return null;
  return findShipmentByLot(lotRaw, shipments);
}
/* All distinct lots feeding this invoice (2026-11-03, PU 17484: "en el
   invoice info ... solo muestra el 58083 y debe mostrar todos" —
   Invoice Info's Lot field only ever showed the single home lot,
   invoice.lot, even once "+ Agregar de Lote" pulled in lines from a
   second shipment). Falls back to invoice.lot alone when no line
   carries its own lot (older/single-lot invoices), same as before. */
function uniqueInvoiceLots(invoiceLike) {
  var lots = (invoiceLike.lines || []).map(function (l) { return l.lot; });
  if (invoiceLike.lot) lots.push(invoiceLike.lot);
  var set = Array.from(new Set(lots.filter(function (v) { return v !== undefined && v !== null && v !== ""; }).map(String)));
  set.sort();
  return set;
}
/* 2026-10-20, Arturo: "la logica actual es que todas las facturas se
   actualiza su status manualmente y de ahi se distribuye al bill y
   embarque, esa logica debe ser solo para four seasons, para twin
   river el sales status cambia en el embarque y de ahi debe
   replicarse al bill y a la factura". Twin River never gets its
   status from the Invoice (no per-pallet saleStatus picked by hand,
   no manual push) — it's always computed live from the Shipment
   (texasQcMode "clarifresh", saleStatusCalc.js's twinRiverLotStatus,
   the 2026-10-15 exception to REGLA DE STATUS DE PALLET). Returns
   undefined for any non-Twin-River or non-ClariFresh invoice (caller
   falls back to the normal Four-Seasons-style derivation), so this
   never changes behavior for anyone else. */
/* 2026-10-30 — resolves the shipment a Twin-River/ClariFresh status
   check should run against, for callers that need a PER-PRODUCT
   status (twinRiverProductStatus). Returns undefined for non-Twin-
   River or non-ClariFresh.
   2026-11-03, PU 17484: this used to always resolve the INVOICE's
   single home lot (findShipmentForInvoice), applied uniformly to
   every line/pallet — on a multi-lot invoice (blackberry from lot
   58083, blueberry from 58084), the blueberry side got checked
   against 58083's manifest, always missing its real status (and
   occasionally matching a same-numbered pallet from the wrong lot).
   Now takes the lot explicitly, so every caller resolves against
   that LINE's or PALLET's own lot — see findShipmentByLot/
   uniqueInvoiceLots above. */
function resolveTwinRiverShipmentForLot(lot, invoiceLike, shipments) {
  var isTR = (resolveClientName(invoiceLike.clientId) || invoiceLike.clientName || "").toLowerCase().indexOf("twin river") >= 0;
  if (!isTR) return undefined;
  var shp = findShipmentByLot(lot, shipments);
  if (!shp || shp.texasQcMode !== "clarifresh") return undefined;
  return shp;
}
/* Find the bill associated with a given lot. Same tolerance ladder
   as findShipmentForInvoice (exact → trimmed → numeric) so the
   common "57916" / 57916 / " 57916 " mismatches don't drop the link. */
function findBillByLot(lot, bills) {
  if (!lot && lot !== 0) return null;
  var s = String(lot);
  var list = bills || [];
  var hit = list.find(function (b) { return String(b.lot) === s; });
  if (hit) return hit;
  var trimmed = s.trim();
  hit = list.find(function (b) { return String(b.lot || "").trim() === trimmed; });
  if (hit) return hit;
  var n = parseInt(s, 10);
  if (!isNaN(n)) {
    hit = list.find(function (b) { var bn = parseInt(b.lot, 10); return !isNaN(bn) && bn === n; });
    if (hit) return hit;
  }
  return null;
}

function resolveWarehouse(shipment, warehouses) {
  if (!shipment) return null;
  return (warehouses || []).find(function (x) { return idMatches(x, shipment.warehouseId); }) || null;
}

/* ──────── BUILDER 1 — PICK TICKET / INBOUND ────────
   opts: { brandKey: "gfi"|"sl", kind: "pick"|"inbound" }
   Same body for both Pick Ticket and Inbound — only the title changes.
   SweetLeaf inserts a Lot # column between Description and Pallets. */
export function buildPickTicketOrInboundHTML(invoice, ctx, opts) {
  opts = opts || {};
  var brand = brandConfig(opts.brandKey || "gfi");
  var kind = opts.kind === "pick" ? "pick" : "inbound";
  var titlePrefix = kind === "pick" ? "Pick Ticket" : "Inbound";
  var clients = ctx && ctx.clients ? ctx.clients : [];
  var shipments = ctx && ctx.shipments ? ctx.shipments : [];
  var warehouses = ctx && ctx.warehouses ? ctx.warehouses : [];
  var company = (ctx && ctx.company) || loadCompanyProfile();
  var client = clients.find(function (c) { return idMatches(c, invoice.clientId); }) || null;
  var shipment = findShipmentForInvoice(invoice, shipments);
  var wh = resolveWarehouse(shipment, warehouses);
  var whName = wh ? (wh.fullName || wh.shortName || wh.name || "") : (shipment ? (shipment.warehouseName || "") : "");
  var whAddr = wh ? [wh.address, (wh.city || "") + (wh.state ? ", " + wh.state : "") + (wh.zip ? " " + wh.zip : "")].filter(Boolean).join(", ") : "";
  var clientName = client ? (client.fullName || client.shortName || client.name || client.companyName || invoice.clientName || "") : (invoice.clientName || "");
  var clientAddr = client ? [client.address, (client.city || "") + (client.state ? ", " + client.state : "") + (client.zip || client.zipCode ? " " + (client.zip || client.zipCode) : "")].filter(Boolean).join(", ") : "";
  var clientPhone = client ? (client.phone || (client.phoneArea && client.phoneNumber ? "(" + client.phoneArea + ") " + client.phoneNumber : "")) : "";
  var customerId = client ? (client.initials || "") : "";
  /* Per FIX: main "Date" is the shipment's Arrival ETA, not Departure.
     Loading Date is now user-chosen for Pick Ticket (passed via
     opts.loadingDate from the remarks modal); Inbound falls back to
     the actual shipment departure date as before. */
  var etaDate = shipment ? (shipment.dateArrival || "") : "";
  var loadingDate = opts.loadingDate || (shipment ? (shipment.dateDeparture || "") : "");
  var lot = invoice.lot || (shipment ? shipment.lot : "");

  /* Aggregate invoice lines — Inbound/Pick Ticket shows product totals,
     not pallet-level detail. UOM defaults to "Flats". */
  var rows = (invoice.lines || []).filter(function (l) { return l.productName || l.productBase; }).map(function (l) {
    return {
      product: l.productBase || l.productName || "",
      desc: l.productDescription || ((l.productName || l.productBase || "") + " " + (l.size || "") + " " + (l.category || "")).trim(),
      pallets: parseInt(l.pallets, 10) || 0,
      cases: parseInt(l.cases, 10) || 0,
      lot: l.lot || lot,
    };
  });
  var totalPallets = rows.reduce(function (s, r) { return s + r.pallets; }, 0);
  var totalCases = rows.reduce(function (s, r) { return s + r.cases; }, 0);

  /* Filename per spec — different prefix per brand × kind. */
  var fname;
  if (brand.key === "sl") fname = "PU_" + (invoice.puNumber || "") + "_PO_" + (invoice.clientRef || "");
  else fname = (kind === "pick" ? "Pick_Ticket_" : "Inbound_") + (invoice.puNumber || "") + "_PO_" + (invoice.clientRef || "");

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escDocHtml(fname) + '</title><style>';
  h += brandDocCss(brand);
  h += '</style></head><body><div class="page">';

  h += '<div class="header"><div class="logo"><img src="' + brand.logo + '" /></div>';
  h += '<div><div class="doc-title">' + titlePrefix + ' ' + escDocHtml(invoice.puNumber || "") + '</div></div></div>';

  /* Two-column block: Shipper on the left, Warehouse on the right. */
  var shipperPhone = company.phone || "";
  h += '<div class="party-grid">';
  h += '<div class="party-block"><div class="lbl">From</div>';
  h += '<div class="name">' + escDocHtml(brand.shipperName) + '</div>';
  h += '<div class="line">' + escDocHtml(brand.shipperAddr) + '</div>';
  if (shipperPhone) h += '<div class="line">Tel: ' + escDocHtml(shipperPhone) + '</div>';
  h += '</div>';
  h += '<div class="party-block"><div class="lbl">Warehouse</div>';
  h += '<div class="name">' + escDocHtml(whName || "—") + '</div>';
  if (whAddr) h += '<div class="line">' + escDocHtml(whAddr) + '</div>';
  if (wh && wh.phone) h += '<div class="line">Tel: ' + escDocHtml(wh.phone) + '</div>';
  h += '</div></div>';

  /* Customer block (full width) */
  h += '<div class="party-block" style="margin-bottom:12px"><div class="lbl">Customer</div>';
  h += '<div class="name">' + escDocHtml(clientName || "—") + '</div>';
  if (clientAddr) h += '<div class="line">' + escDocHtml(clientAddr) + '</div>';
  if (clientPhone) h += '<div class="line">Tel: ' + escDocHtml(clientPhone) + '</div>';
  h += '</div>';

  /* Customs / cert references from the linked shipment + invoice.
     Entry # is one-per-shipment (shipment.entryNumber). Export Certs
     are computed per organic invoice line and grouped by product;
     buildExportCertEntries returns either [single cert] or
     ["Product — cert", ...] depending on whether all organic
     products share one cert. */
  var entryNumber = shipment ? (shipment.entryNumber || "") : "";
  var exportCertEntries = buildExportCertEntries(invoice, shipment);
  /* Rendering: span 2 for a single line (matches Customs Entry), or
     span 4 when there are multiple per-product lines so they wrap
     cleanly across the grid. */
  var certCellSpan = exportCertEntries.length <= 1 ? 2 : 4;
  var certCellBody = exportCertEntries.length === 0
    ? "—"
    : exportCertEntries.map(escDocHtml).join("<br>");

  /* Right-hand fields strip. Per FIX the headline "Date" is ETA. */
  h += '<div class="fields-grid">';
  h += '<div><div class="lbl">Date</div><div class="val">' + escDocHtml(fmtDocDate(etaDate) || "—") + '</div></div>';
  h += '<div><div class="lbl">' + (kind === "pick" ? "Pick Up" : "Inbound") + '</div><div class="val">' + escDocHtml(invoice.puNumber || "—") + '</div></div>';
  h += '<div><div class="lbl">Customer ID</div><div class="val">' + escDocHtml(customerId || "—") + '</div></div>';
  h += '<div><div class="lbl">Customer PO</div><div class="val">' + escDocHtml(invoice.clientRef || "—") + '</div></div>';
  h += '<div style="grid-column:span 4"><div class="lbl">Loading Date</div><div class="val">' + escDocHtml(fmtDocDate(loadingDate) || "—") + '</div></div>';
  h += '<div style="grid-column:span 2"><div class="lbl">Customs Entry #</div><div class="val mono">' + escDocHtml(entryNumber || "—") + '</div></div>';
  h += '<div style="grid-column:span ' + certCellSpan + '"><div class="lbl">Export Cert #</div><div class="val mono">' + certCellBody + '</div></div>';
  h += '</div>';
  /* ETA disclaimer per FIX — placed right under the fields block so
     the user reads it before the table. */
  h += '<div class="eta-note">* Subject to customs clearance</div>';

  /* Product table. SweetLeaf inserts a Lot # column between
     Description and Pallets per spec. Column widths recomputed when
     the lot column is present so the row stays balanced. */
  h += '<table class="items"><thead><tr>';
  if (brand.lotColumn) {
    h += '<th style="text-align:left;width:16%">Product</th>';
    h += '<th style="text-align:left;width:38%">Description</th>';
    h += '<th class="c" style="width:10%">Lot #</th>';
    h += '<th class="c" style="width:9%">Pallets</th>';
    h += '<th class="c" style="width:13%">Fill (Cases)</th>';
    h += '<th class="c" style="width:14%">UOM</th>';
  } else {
    h += '<th style="text-align:left;width:18%">Product</th>';
    h += '<th style="text-align:left;width:46%">Description</th>';
    h += '<th class="c" style="width:10%">Pallets</th>';
    h += '<th class="c" style="width:14%">Fill (Cases)</th>';
    h += '<th class="c" style="width:12%">UOM</th>';
  }
  h += '</tr></thead><tbody>';
  rows.forEach(function (r) {
    h += '<tr>';
    h += '<td><b>' + escDocHtml(r.product) + '</b></td>';
    h += '<td>' + escDocHtml(r.desc) + '</td>';
    if (brand.lotColumn) h += '<td class="c mono">' + escDocHtml(r.lot || "—") + '</td>';
    h += '<td class="c mono">' + r.pallets + '</td>';
    h += '<td class="c mono"><b>' + r.cases + '</b></td>';
    h += '<td class="c">Flats</td>';
    h += '</tr>';
  });
  /* TOTAL row — colspan adjusts for the optional Lot column. */
  if (brand.lotColumn) {
    h += '<tr class="totals"><td colspan="3" style="text-align:right">TOTAL</td>';
  } else {
    h += '<tr class="totals"><td colspan="2" style="text-align:right">TOTAL</td>';
  }
  h += '<td class="c mono">' + totalPallets + '</td>';
  h += '<td class="c mono">' + totalCases + '</td>';
  h += '<td class="c">Flats</td></tr>';
  h += '</tbody></table>';

  /* Remarks block — Pick Ticket only. Inbound omits it entirely
     because the fruit lands at the customer's warehouse and GFI is
     no longer responsible for the load; the receiver doesn't need a
     remarks field. Pick Ticket renders the lines the user chose in
     the remarks modal (mandatory + optional + free-text). */
  if (kind === "pick") {
    var remarks = Array.isArray(opts.remarks) ? opts.remarks : [];
    h += '<div class="remarks"><div class="lbl">Remarks</div>';
    if (remarks.length > 0) {
      /* All remarks render as alert banners — warehouse must see
         every line. Yellow #FFF3CD background, orange border, ⚠️
         icon, bold black text. print-color-adjust:exact forces
         browsers to keep the background fill in PDF output. */
      var bannerStyle = 'background:#FFF3CD;border:2px solid #E67E22;border-left:6px solid #C0392B;padding:10px 14px;margin:4px 0 10px;border-radius:6px;display:flex;align-items:center;gap:10px;-webkit-print-color-adjust:exact;print-color-adjust:exact;color-adjust:exact';
      var bannerIconStyle = 'font-size:18px;line-height:1;flex-shrink:0';
      var bannerTextStyle = 'font-size:13px;font-weight:800;color:#000;letter-spacing:0.3px;line-height:1.3';
      remarks.forEach(function (line) {
        h += '<div style="' + bannerStyle + '">';
        h += '<span style="' + bannerIconStyle + '">⚠️</span>';
        h += '<span style="' + bannerTextStyle + '">' + escDocHtml(line) + '</span>';
        h += '</div>';
      });
    }
    h += '</div>';
  }

  h += '<div class="footer">' + escDocHtml(brand.footer) + '</div>';
  h += '</div></body></html>';
  return h;
}

/* Backwards-compat alias — earlier code/tests might import the old
   name. Defaults to GFI brand + Inbound kind. */
export function buildInboundSheetHTML(invoice, ctx) {
  return buildPickTicketOrInboundHTML(invoice, ctx, { brandKey: "gfi", kind: "inbound" });
}

/* ──────── BUILDER 2 — PACKING LIST (per-pallet) ────────
   opts: { brandKey: "gfi"|"sl" }
   - GFI: includes Organic Cert strip at the bottom
   - SL : adds "Temp. Recorder #" to header meta, no Organic Cert. */
/* Resolves the packing-list row set for one invoice.
   NEW (palletAllocations present): one row per allocation, resolved
   live against each referenced shipment's pallet manifest by
   (lot, palletNum) — supports multi-lot invoices and reflects only the
   pallets/cases actually assigned to THIS invoice, not the whole lot.
   LEGACY (no allocations — pre-FASE-3 invoices): falls back to the
   old behavior, dumping the entire manifest of the single shipment
   findShipmentForInvoice resolves, so historical invoices keep
   rendering exactly as before until migrated (FASE 7). */
function buildPackingListRows(invoice, shipments) {
  var allocations = Array.isArray(invoice.palletAllocations)
    ? invoice.palletAllocations.filter(function (a) { return a && a.lot && a.palletNum && (parseInt(a.cases, 10) || 0) > 0; })
    : [];
  if (allocations.length > 0) {
    var shipByLot = {};
    (shipments || []).forEach(function (s) { if (s.lot != null) shipByLot[String(s.lot)] = s; });
    var rows = allocations.map(function (a) {
      var shp = shipByLot[String(a.lot)];
      var meta = shp ? (shp.palletManifest || []).find(function (p) { return String(p.palletNum) === String(a.palletNum); }) : null;
      var cases = parseInt(a.cases, 10) || 0;
      return {
        lot: a.lot, palletNum: a.palletNum,
        productBase: meta ? meta.productBase : "", variety: meta ? meta.variety : "", category: meta ? meta.category : "",
        unitsCase: meta ? meta.unitsCase : "", size: meta ? meta.size : "", labelInitials: meta ? meta.labelInitials : "",
        cases: cases,
        weight: docPalletWeight({ size: meta && meta.size, unitsCase: meta && meta.unitsCase, casesPallet: cases }),
        tagId: meta ? meta.tagId : "",
        julian: meta ? (meta.julianCode || meta.traceability || "") : "",
        grower: meta ? meta.growerCode : "",
      };
    });
    return { rows: rows, legacy: false };
  }
  return { rows: [], legacy: true };
}

export function buildPackingListHTML(invoice, ctx, opts) {
  opts = opts || {};
  var brand = brandConfig(opts.brandKey || "gfi");
  var clients = ctx && ctx.clients ? ctx.clients : [];
  var shipments = ctx && ctx.shipments ? ctx.shipments : [];
  var warehouses = ctx && ctx.warehouses ? ctx.warehouses : [];
  var company = (ctx && ctx.company) || loadCompanyProfile();
  var client = clients.find(function (c) { return idMatches(c, invoice.clientId); }) || null;
  /* Header-level fields (ETA, warehouse, entry #, thermograph) stay
     tied to ONE shipment — the primary/header lot — same as before.
     Multi-lot support (FASE 3-6) only affects the per-pallet table
     below and the Lot(s) line; a document only has one ship date. */
  var shipment = findShipmentForInvoice(invoice, shipments);
  var wh = resolveWarehouse(shipment, warehouses);
  var whAddr = wh ? [wh.address, (wh.city || "") + (wh.state ? ", " + wh.state : "") + (wh.zip ? " " + wh.zip : "")].filter(Boolean).join(", ") : "";
  var clientName = client ? (client.fullName || client.shortName || client.name || client.companyName || invoice.clientName || "") : (invoice.clientName || "");
  var clientContact = client && client.contacts && client.contacts.length > 0
    ? (client.contacts[0].name || (client.contacts[0].firstName ? client.contacts[0].firstName + " " + (client.contacts[0].lastName || "") : "")).trim()
    : (client ? (client.contact || "") : "");
  var clientPhone = client ? (client.phone || (client.phoneArea && client.phoneNumber ? "(" + client.phoneArea + ") " + client.phoneNumber : "")) : "";
  var rowSet = buildPackingListRows(invoice, shipments);
  var pallets = rowSet.rows;
  var isLegacy = rowSet.legacy;
  if (isLegacy) {
    /* Legacy fallback — identical to the pre-FASE-3 behavior: the
       whole manifest of the single resolved shipment. */
    pallets = shipment && Array.isArray(shipment.palletManifest)
      ? shipment.palletManifest.filter(function (p) { return p && (p.palletNum || p.productBase || p.casesPallet); }).map(function (p) {
          var cases = parseInt(p.casesPallet, 10) || 0;
          return {
            lot: shipment.lot, palletNum: p.palletNum,
            productBase: p.productBase, variety: p.variety, category: p.category,
            unitsCase: p.unitsCase, size: p.size, labelInitials: p.labelInitials,
            cases: cases, weight: docPalletWeight(p),
            tagId: p.tagId, julian: p.julianCode || p.traceability || "", grower: p.growerCode,
          };
        })
      : [];
  }
  var uniquePalletKeys = new Set(pallets.map(function (p) { return String(p.lot) + "::" + String(p.palletNum); }));
  var totalPallets = uniquePalletKeys.size;
  var totalCases = pallets.reduce(function (s, p) { return s + (p.cases || 0); }, 0);
  var totalKg = pallets.reduce(function (s, p) { return s + (p.weight ? p.weight.kg : 0); }, 0);
  var totalLb = pallets.reduce(function (s, p) { return s + (p.weight ? p.weight.lb : 0); }, 0);
  var uniqueLots = [...new Set(pallets.map(function (p) { return String(p.lot); }).filter(Boolean))].sort();
  var lotHeaderText = uniqueLots.length === 0 ? "—" : uniqueLots.length === 1 ? uniqueLots[0] : uniqueLots.join(", ");
  /* Per FIX: headline date = shipment ETA. */
  var etaDate = shipment ? (shipment.dateArrival || "") : "";
  /* SL header carries the Temp. Recorder # (thermograph) when present.
     Primary source: invoice.thermograph (2026-09-12 — captured directly
     on the Invoice, see ROW 2.5 in InvoiceForm; Arturo: "agregue el temp
     recorder a la order 17445 pero al imprimir el packing list no lo
     muestra" — this line used to read ONLY shipment.transport.thermograph,
     a field that's essentially never populated in practice — 1 of 146
     real shipments had anything in it, and even that was just "-").
     Falls back to the shipment-level field for older invoices that
     predate this field, so nothing already relying on it breaks. */
  var tempRec = invoice.thermograph || (shipment && shipment.transport ? (shipment.transport.thermograph || "") : "");
  /* Customs / export-cert references. Entry # is one-per-shipment;
     Export Certs are computed per organic invoice line (see
     buildExportCertEntries above) so the auditor sees which cert
     covers each commodity when they differ. */
  var entryNumber = shipment ? (shipment.entryNumber || "") : "";
  var exportCertEntries = buildExportCertEntries(invoice, shipment);

  /* Filename: brand-specific naming per spec. */
  var fname;
  if (brand.key === "sl") fname = "Packing_List_PO_" + (invoice.clientRef || "") + "_PU_" + (invoice.puNumber || "");
  else fname = "Packing_List_GFI_PU_" + (invoice.puNumber || "") + "_PO_" + (invoice.clientRef || "") + "_Lot_" + (invoice.lot || (shipment ? shipment.lot : ""));

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escDocHtml(fname) + '</title><style>';
  h += brandDocCss(brand);
  h += '</style></head><body><div class="page">';

  /* Header: logo left, centered title, meta right. */
  h += '<div class="header">';
  h += '<div class="logo"><img src="' + brand.logo + '" /></div>';
  h += '<div class="doc-title center">PACKING LIST</div>';
  h += '<div><div class="doc-meta"><b>Date:</b> ' + escDocHtml(fmtDocDate(etaDate) || "—") + '<br><b>PU#:</b> ' + escDocHtml(invoice.puNumber || "—") + '<br><b>PO#:</b> ' + escDocHtml(invoice.clientRef || "—");
  /* Pickup Date/Time (2026-09-12, Arturo) — recorded by hand from the
     driver's BOL once the client's truck actually picks up the load.
     Only printed once it's been recorded; omitted entirely (not a
     "—" placeholder) on older/unpicked-up invoices so this line
     doesn't clutter every packing list printed before pickup happens. */
  if (invoice.pickupDate) {
    h += '<br><b>Pickup:</b> ' + escDocHtml(fmtDocDate(invoice.pickupDate)) + (invoice.pickupTime ? ' ' + escDocHtml(invoice.pickupTime) : '');
  }
  h += '<br><b>' + (uniqueLots.length > 1 ? "Lots" : "Lot") + ':</b> ' + escDocHtml(lotHeaderText);
  if (brand.tempRecorder) h += '<br><b>Temp. Recorder #:</b> ' + escDocHtml(tempRec || "—");
  h += '<br><b>Entry #:</b> ' + escDocHtml(entryNumber || "—");
  /* When there are multiple per-product certs, emit one labeled
     line per entry so each "Product — cert" pair reads on its own
     row. Single-cert collapse keeps the legacy one-liner. */
  if (exportCertEntries.length === 0) {
    h += '<br><b>Export Cert:</b> —';
  } else if (exportCertEntries.length === 1) {
    h += '<br><b>Export Cert:</b> ' + escDocHtml(exportCertEntries[0]);
  } else {
    exportCertEntries.forEach(function (e) {
      h += '<br><b>Export Cert:</b> ' + escDocHtml(e);
    });
  }
  h += '</div></div>';
  h += '</div>';
  /* ETA disclaimer immediately under the header for visibility. */
  h += '<div class="eta-note" style="margin-top:0;margin-bottom:12px">* Subject to customs clearance</div>';
  /* Legacy banner — invoices saved before palletAllocations existed
     (FASE 3) have no per-pallet allocation, so this document falls back
     to dumping the whole linked shipment's manifest (old behavior).
     English-only like the rest of this generated document (CLAUDE.md:
     client-facing docs are always English regardless of UI language). */
  if (isLegacy) {
    h += '<div style="margin:0 0 12px;padding:6px 12px;background:#FFF3E0;color:#8A5300;border:1px solid #F0C36D;border-radius:5px;font-size:10px;font-weight:600">Legacy view — this invoice does not have per-pallet allocations</div>';
  }

  /* Shipper / Consignee parties */
  h += '<div class="party-grid">';
  h += '<div class="party-block"><div class="lbl">Shipper</div>';
  h += '<div class="name">' + escDocHtml(brand.shipperName) + '</div>';
  h += '<div class="line">' + escDocHtml(brand.shipperAddr) + '</div>';
  if (company.phone) h += '<div class="line">Tel: ' + escDocHtml(company.phone) + '</div>';
  h += '</div>';

  h += '<div class="party-block"><div class="lbl">Consignee</div>';
  h += '<div class="name">' + escDocHtml(clientName || "—") + '</div>';
  /* Use warehouse address as the delivery destination. Falls back to
     the client's own address. Attn / phone intentionally omitted per
     spec — the consignee block on the packing list stays as just
     name + address. */
  if (whAddr) h += '<div class="line">' + escDocHtml(whAddr) + '</div>';
  else if (client && client.address) h += '<div class="line">' + escDocHtml([client.address, (client.city || "") + (client.state ? ", " + client.state : "")].filter(Boolean).join(", ")) + '</div>';
  h += '</div></div>';

  /* Per-pallet table — sourced from invoice.palletAllocations (new) or
     the linked shipment's full manifest (legacy fallback, see
     buildPackingListRows above). */
  /* Julian (traceability) and Grower Code are internal-only —
     confidential per-grower/lot data that must not leave GFI on a
     customer-facing document. palletMetaFor/buildPackingListRows still
     resolve them (p.julian / p.grower) for other uses; this table
     simply doesn't paint those two columns anymore. */
  h += '<table class="items"><thead><tr>';
  h += '<th class="c" style="width:7%">Lot</th>';
  h += '<th class="c" style="width:8%">Pallet #</th>';
  h += '<th style="text-align:left;width:26%">Product</th>';
  h += '<th class="c" style="width:11%">Variety</th>';
  h += '<th class="c" style="width:7%">Label</th>';
  h += '<th class="c" style="width:10%">Cases</th>';
  h += '<th class="c" style="width:12%">Weight</th>';
  h += '<th class="c" style="width:19%">Tag ID</th>';
  h += '</tr></thead><tbody>';
  if (pallets.length === 0) {
    /* Distinguish the two empty cases so the user can debug:
         (a) no matching shipment found at all — usually a lot mismatch
         (b) shipment found but its palletManifest array is empty —
             user hasn't filled the manifest in ShipmentRegistry yet */
    var lotShown = invoice.lot || (invoice.lines && invoice.lines[0] && invoice.lines[0].lot) || "—";
    var emptyMsg = !shipment
      ? "No shipment found for LOT " + escDocHtml(lotShown) + ". Check that the lot number on this invoice matches a record in Shipment Registry."
      : "Shipment LOT " + escDocHtml(shipment.lot) + " has no pallet manifest. Open the shipment and fill the Pallet Manifest section.";
    h += '<tr><td colspan="8" style="text-align:center;color:#999;font-style:italic;padding:16px">' + emptyMsg + '</td></tr>';
  } else {
    pallets.forEach(function (p) {
      var typeLabel = p.category === "OG" ? "Organic" : p.category === "CV" ? "Conventional" : (p.category || "");
      /* Size/format string built from the pallet's own unitsCase+size
         (e.g. "12/6oz") — silently omitted when either is missing so
         legacy rows without that data fall back to plain product name. */
      var sizeStr = (p.unitsCase && p.size) ? (p.unitsCase + "/" + p.size) : "";
      /* USDA Organic note folded into the Product cell (GFI variant
         only — SL doesn't carry the organic cert reference at all per
         spec) since this layout has no separate Notes column. */
      var prodCell = escDocHtml((p.productBase || "—") + (sizeStr ? " " + sizeStr : "")) + ((brand.organicCert && p.category === "OG")
        ? ' <span style="color:' + brand.accent + ';font-weight:700">– Organic</span>'
        : (typeLabel ? ' <span style="color:#666">– ' + escDocHtml(typeLabel) + '</span>' : ""));
      h += '<tr>';
      h += '<td class="c mono">' + escDocHtml(p.lot || "—") + '</td>';
      h += '<td class="c mono"><b>' + escDocHtml(p.palletNum || "—") + '</b></td>';
      h += '<td>' + prodCell + '</td>';
      h += '<td class="c">' + escDocHtml(p.variety || "—") + '</td>';
      h += '<td class="c mono">' + escDocHtml(p.labelInitials || "—") + '</td>';
      h += '<td class="c mono"><b>' + (p.cases || "—") + '</b></td>';
      h += '<td class="c mono">' + (p.weight && p.weight.kg > 0 ? p.weight.kg.toFixed(1) + " kg" : "—") + '</td>';
      h += '<td class="c mono">' + escDocHtml(p.tagId || "—") + '</td>';
      h += '</tr>';
    });
    h += '<tr class="totals"><td colspan="5" style="text-align:right">TOTALS</td>';
    h += '<td class="c mono">' + totalCases + '</td>';
    h += '<td class="c mono">' + totalKg.toFixed(1) + ' kg</td>';
    h += '<td></td></tr>';
  }
  h += '</tbody></table>';

  /* Summary strip. Shipment Date kept as actual departure date —
     ETA is already in the header per FIX. */
  h += '<div class="summary">';
  h += '<div><div class="lbl">Total Pallets</div><div class="val">' + totalPallets + '</div></div>';
  h += '<div><div class="lbl">Total Cases</div><div class="val">' + totalCases + '</div></div>';
  h += '<div><div class="lbl">Total Gross Wt.</div><div class="val">' + totalKg.toFixed(1) + ' kg / ' + totalLb.toFixed(1) + ' lb</div></div>';
  h += '<div><div class="lbl">Shipment Date</div><div class="val">' + escDocHtml(fmtDocDate(shipment ? shipment.dateDeparture : "") || "—") + '</div></div>';
  h += '</div>';

  /* Cert + origin strip — GFI only. SweetLeaf intentionally omits. */
  if (brand.organicCert) {
    h += '<div class="cert-strip">Organic Cert: USDA / Primus Auditing – ' + escDocHtml(company.organicCert || "PA-OR-1186") + '  ·  Origin: Mexico</div>';
  }

  h += '<div class="footer">' + escDocHtml(brand.footer) + '</div>';
  h += '</div></body></html>';
  return h;
}

/* ──────── BUILDER 2.5 — MASTER INBOUND (combined doc) ────────
   Single PDF that stacks the Inbound sheet on top and the Packing
   List below it. Both halves are produced by the existing builders
   so the visual output stays identical to printing each one
   separately — we just splice the <body> contents into one
   document, deduping the shared <style> block. The two halves use
   the same `.page` wrapper / `brandDocCss` so a single copy of the
   stylesheet covers both. A page break separates them in print.

   opts: { brandKey: "gfi"|"sl" } */
export function buildMasterInboundHTML(invoice, ctx, opts) {
  opts = opts || {};
  var brandKey = opts.brandKey || "gfi";
  var brand = brandConfig(brandKey);
  /* Reuse the existing builders, then trim each half so the combined
     doc reads as one continuous document instead of two stacked
     PDFs. From the Inbound half we drop the Remarks block and the
     footer; from the Packing List half we keep ONLY the pallet
     table (no summary strip, no organic-cert strip, no second
     footer). One consolidated footer goes at the very end. */
  var inboundHtml = buildPickTicketOrInboundHTML(invoice, ctx, { brandKey: brandKey, kind: "inbound" });
  var packingHtml = buildPackingListHTML(invoice, ctx, { brandKey: brandKey });
  var styleMatch = inboundHtml.match(/<style>([\s\S]*?)<\/style>/);
  var sharedCss = styleMatch ? styleMatch[1] : "";
  var bodyMatch1 = inboundHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/);
  var bodyMatch2 = packingHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/);
  var inboundBody = bodyMatch1 ? bodyMatch1[1] : "";
  var packingBody = bodyMatch2 ? bodyMatch2[1] : "";
  /* Cut Inbound at the Remarks block — that and the footer that
     follows both belong to a standalone Inbound PDF, not the Master
     combo. Re-close the .page wrapper since we cut into it. */
  var remarksIdx = inboundBody.indexOf('<div class="remarks">');
  var inboundTop;
  if (remarksIdx >= 0) {
    inboundTop = inboundBody.substring(0, remarksIdx) + '</div>';
  } else {
    /* Fallback: just strip the footer if Remarks block is missing. */
    inboundTop = inboundBody.replace(/<div class="footer">[\s\S]*?<\/div>\s*<\/div>\s*$/, '</div>');
  }
  /* Pallet table only — from `<table class="items">` to just before
     the summary strip. Drops the bottom totals strip + organic cert
     strip + second footer. */
  var palletStart = packingBody.indexOf('<table class="items">');
  var palletEndMarkers = ['<div class="summary">', '<div class="cert-strip">', '<div class="footer">'];
  var palletEnd = -1;
  for (var pi = 0; pi < palletEndMarkers.length; pi++) {
    var idx = packingBody.indexOf(palletEndMarkers[pi]);
    if (idx > palletStart) { palletEnd = idx; break; }
  }
  if (palletEnd < 0) palletEnd = packingBody.length;
  var packingTableOnly = palletStart >= 0 ? packingBody.substring(palletStart, palletEnd) : "";

  var pu = invoice.puNumber || "";
  var po = invoice.clientRef || "";
  /* Filename matches the regular Inbound — "Master Inbound" is only
     an internal label in the app menu; the customer-facing PDF name
     stays plain "Inbound_*" so it slots in alongside the standalone
     Inbound PDFs on Drive / in customer inboxes. */
  var fname = (brandKey === "sl" ? "PU_" : "Inbound_") + pu + "_PO_" + po;
  /* No page-break here — Master Inbound is a SINGLE-page doc. The
     old `.mi-pagebreak { page-break-before: always }` forced the
     Packing List onto a second sheet even when both halves fit. The
     class is kept as a positional spacer only so the JSX below
     stays unchanged. */
  var extraCss = " .mi-pagebreak{height:0;margin:18px 0 0;padding:0}";
  /* Tighten the section header so it eats less vertical room. */
  extraCss += " .mi-section-header{margin:0 0 10px;padding-bottom:6px;border-bottom:2px solid #1a1a1a;font-size:18px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#1a1a1a;text-align:center}";
  /* Pallet-table compression scoped to the Master Inbound only —
     smaller font + slimmer row padding so the combined doc fits on
     one letter page. The standalone Packing List PDF is unaffected
     because it doesn't import this stylesheet. */
  extraCss += " body .items{font-size:10px}";
  extraCss += " body .items th{padding:5px 6px;font-size:9px}";
  extraCss += " body .items td{padding:4px 6px}";
  /* @page tightens the print margins slightly so a manifest with
     ~30 pallets still fits. Both halves use the same .page wrapper
     so this affects only the printed sheet, not the on-screen
     preview width. */
  extraCss += " @page{size:letter;margin:0.45in}";

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escDocHtml(fname) + '</title><style>';
  h += sharedCss + extraCss;
  h += '</style></head><body>';
  /* Inbound (top half) — header, parties, fields, eta-note,
     product table only. No remarks, no footer here. */
  h += inboundTop;
  /* Page break, then PACKING LIST header + pallet table, then ONE
     consolidated footer to close the doc. */
  h += '<div class="mi-pagebreak"></div>';
  h += '<div class="page"><div class="mi-section-header">PACKING LIST</div>' + packingTableOnly;
  h += '<div class="footer">' + escDocHtml(brand.footer) + '</div>';
  h += '</div></body></html>';
  return h;
}

/* ──────── BUILDER 3 — DRIVER SIGN-OFF SHEET (SweetLeaf) ────────
   Warehouse-fillable form. No logos, no branding apart from
   "Sweetleaf Produce Inc" in the Vendor field. 28 blank pallet slots
   (1-14 left column, 15-28 right column), each with two empty lines
   for Temp and Tag ID. Driver + shipper acknowledgement block at the
   bottom with print/sign lines. Pre-filled from the invoice: PU#,
   PO#, Total pallets (from the linked shipment's manifest, if any). */
export function buildDriverSignOffSheetHTML(invoice, ctx) {
  var shipments = ctx && ctx.shipments ? ctx.shipments : [];
  var shipment = findShipmentForInvoice(invoice, shipments);
  /* Total pallets — primary source is the invoice line items
     (each line carries a `pallets` field set when the user invoiced).
     Falls back to counting the linked shipment's manifest when the
     invoice lines don't have pallet counts (older invoices, imports). */
  var totalPallets = 0;
  (invoice.lines || []).forEach(function (l) {
    totalPallets += parseInt(l.pallets, 10) || 0;
  });
  if (totalPallets === 0 && shipment && Array.isArray(shipment.palletManifest)) {
    totalPallets = shipment.palletManifest.filter(function (p) { return p && (p.palletNum || p.productBase || p.casesPallet); }).length;
  }

  var fname = "Driver_SignOff_Sheet_PU_" + (invoice.puNumber || "") + "_PO_" + (invoice.clientRef || "");

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escDocHtml(fname) + '</title><style>';
  h += '@page{size:letter;margin:0.5in 0.55in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4}';
  h += '.page{max-width:730px;margin:0 auto}';
  /* No-branding title — bold centred header with a navy underline
     so the form still reads like an official doc. */
  h += '.doc-title{text-align:center;font-size:22px;font-weight:700;color:#1a1a1a;letter-spacing:2px;padding-bottom:8px;border-bottom:2px solid #1a1a1a;margin-bottom:14px}';
  /* Top meta strip — half pre-filled (PU#, PO#, Total Pallets,
     Vendor), half blank lines for the user to write in pen
     (Loading Date, Recorder #). */
  h += '.meta-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px 16px;margin-bottom:18px;padding:10px 14px;background:#f7f8fa;border:1px solid #d0d5dd;border-radius:6px}';
  h += '.meta-grid .lbl{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#6b7280;font-weight:700}';
  h += '.meta-grid .val{font-size:12px;font-weight:700;color:#1a1a1a;margin-top:3px;font-family:"Courier New",monospace;min-height:16px}';
  /* Empty fields get an underline so the user knows where to write. */
  h += '.meta-grid .blank{border-bottom:1px solid #1a1a1a;min-width:120px;display:inline-block;min-height:14px}';
  /* Pallet grid — two side-by-side columns, 14 rows each. Each row
     uses a fixed 5-column inner grid (label · "Temp:" · temp-line ·
     "Tag ID:" · tag-line) so the two columns align perfectly with
     each other when printed side-by-side. No borders/dividers — the
     space stays clean for handwriting. */
  h += '.pallets{display:grid;grid-template-columns:1fr 1fr;gap:8px 18px;margin-bottom:18px}';
  h += '.pallet-row{display:grid;grid-template-columns:70px 44px 1fr 52px 1fr;gap:6px;padding:6px 0;font-size:11px;align-items:baseline}';
  h += '.pallet-row .num{font-weight:700;color:#1a1a1a;font-family:"Courier New",monospace}';
  h += '.pallet-row .k{font-weight:600;color:#374151}';
  h += '.pallet-row .line{border-bottom:1px solid #1a1a1a;min-height:14px}';
  /* Signature block — two parties (shipper + driver) with statement
     paragraphs, then Print/Sign lines side by side under each. */
  h += '.sig{border-top:2px solid #1a1a1a;padding-top:14px;margin-top:8px}';
  h += '.sig-block{margin-bottom:18px;font-size:11px;line-height:1.7;color:#1a1a1a}';
  h += '.sig-block p{margin-bottom:6px}';
  h += '.sig-block .line{border-bottom:1px solid #1a1a1a;display:inline-block;min-width:200px;min-height:14px;margin:0 4px}';
  h += '.sig-row{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:8px;font-size:11px;font-weight:600}';
  h += '.sig-row .line{border-bottom:1px solid #1a1a1a;display:inline-block;width:80%;min-height:14px;margin-left:6px}';
  h += '</style></head><body><div class="page">';

  h += '<div class="doc-title">DRIVER SIGN-OFF SHEET</div>';

  /* Meta block — fixed values on the left, blank lines on the right. */
  h += '<div class="meta-grid">';
  h += '<div><div class="lbl">PU#</div><div class="val">' + escDocHtml(invoice.puNumber || "—") + '</div></div>';
  h += '<div><div class="lbl">PO#</div><div class="val">' + escDocHtml(invoice.clientRef || "—") + '</div></div>';
  h += '<div><div class="lbl">Total Pallets</div><div class="val">' + (totalPallets || "—") + '</div></div>';
  h += '<div><div class="lbl">Vendor</div><div class="val">Sweetleaf Produce Inc</div></div>';
  h += '<div><div class="lbl">Loading Date</div><div class="val"><span class="blank"></span></div></div>';
  h += '<div><div class="lbl">Recorder Number</div><div class="val"><span class="blank"></span></div></div>';
  h += '</div>';

  /* 28 blank pallet slots laid out row-major:
       Row 1: Pallet 1 | Pallet 2
       Row 2: Pallet 3 | Pallet 4
       …
       Row 14: Pallet 27 | Pallet 28
     CSS grid with 2 columns fills row-by-row naturally, so we just
     emit the 28 cells in numeric order and the grid does the rest. */
  function renderPalletCell(n) {
    /* 5-cell row matching the 5-column grid template so the parts
       line up across the left/right pallet columns identically. */
    var s = '<div class="pallet-row">';
    s += '<div class="num">Pallet ' + n + '</div>';
    s += '<div class="k">Temp:</div>';
    s += '<div class="line"></div>';
    s += '<div class="k">Tag ID:</div>';
    s += '<div class="line"></div>';
    s += '</div>';
    return s;
  }
  h += '<div class="pallets">';
  for (var i = 1; i <= 28; i++) h += renderPalletCell(i);
  h += '</div>';

  /* Signature / acknowledgement block. */
  h += '<div class="sig">';
  h += '<div class="sig-block"><p>I shipper <span class="line"></span> have put these pallets on the truck with the following pulp temps.</p>';
  h += '<div class="sig-row"><div>Print: <span class="line"></span></div><div>Sign: <span class="line"></span></div></div>';
  h += '</div>';
  h += '<div class="sig-block"><p>I driver <span class="line"></span> agree that the following pallets with the documented temps have been loaded on my truck.</p>';
  h += '<p>And will maintain the temperature for the duration of the trip.</p>';
  h += '<div class="sig-row"><div>Print: <span class="line"></span></div><div>Sign: <span class="line"></span></div></div>';
  h += '</div>';
  h += '</div>';

  h += '</div></body></html>';
  return h;
}

/* ──────── PRINT HELPER (single + bulk) ────────
   `kind` is one of: "gfi-pick" | "gfi-inbound" | "gfi-packing" |
                     "sl-pick"  | "sl-inbound"  | "sl-packing"
   Encodes brand + doc-type in a single string so the menu's onPick
   handler stays a thin pass-through. */
function printInvoiceDocs(invoices, kind, ctx, onError, extras) {
  if (!Array.isArray(invoices) || invoices.length === 0) return;
  var brandKey = kind.indexOf("sl-") === 0 ? "sl" : "gfi";
  var docKind = kind.split("-")[1];  /* pick | inbound | packing */
  /* extras.remarks + extras.loadingDate (Pick Ticket only) —
     assembled by the remarks modal, applied to every invoice in a
     bulk run. Loading Date overrides the auto-default
     (shipment.dateDeparture) used by Inbound. */
  var remarks = (extras && Array.isArray(extras.remarks)) ? extras.remarks : null;
  var loadingDateOverride = (extras && extras.loadingDate) || null;
  var build;
  if (docKind === "packing") {
    build = function (inv) { return buildPackingListHTML(inv, ctx, { brandKey: brandKey }); };
  } else if (docKind === "driversignoff") {
    /* Driver Sign-Off — SweetLeaf-only blank form, no brand styling
       beyond the "Sweetleaf Produce Inc" vendor field. */
    build = function (inv) { return buildDriverSignOffSheetHTML(inv, ctx); };
  } else if (docKind === "master") {
    /* Master Inbound — Inbound + Packing List stacked in one PDF. */
    build = function (inv) { return buildMasterInboundHTML(inv, ctx, { brandKey: brandKey }); };
  } else {
    build = function (inv) { return buildPickTicketOrInboundHTML(inv, ctx, { brandKey: brandKey, kind: docKind, remarks: remarks, loadingDate: loadingDateOverride }); };
  }
  var htmls = invoices.map(build);
  var styleMatch = htmls[0].match(/<style>([\s\S]*?)<\/style>/);
  var sharedCss = styleMatch ? styleMatch[1] : "";
  var bodies = htmls.map(function (html) { var m = html.match(/<body[^>]*>([\s\S]*?)<\/body>/); return m ? m[1] : ""; });

  /* Filename naming per spec, brand × doc × single/bulk. */
  var titleBase;
  if (invoices.length === 1) {
    var inv = invoices[0];
    var lot = inv.lot || (inv.lines && inv.lines[0] && inv.lines[0].lot) || "";
    if (docKind === "driversignoff") {
      titleBase = "Driver_SignOff_Sheet_PU_" + (inv.puNumber || "") + "_PO_" + (inv.clientRef || "");
    } else if (docKind === "master") {
      /* Master Inbound is customer-facing as a plain Inbound — only
         the in-app menu labels it "Master" to differentiate it from
         the standalone Inbound. Filename matches the regular one. */
      titleBase = (brandKey === "sl" ? "PU_" : "Inbound_") + (inv.puNumber || "") + "_PO_" + (inv.clientRef || "");
    } else if (docKind === "packing") {
      titleBase = brandKey === "sl"
        ? "Packing_List_PO_" + (inv.clientRef || "") + "_PU_" + (inv.puNumber || "")
        : "Packing_List_GFI_PU_" + (inv.puNumber || "") + "_PO_" + (inv.clientRef || "") + "_Lot_" + lot;
    } else if (brandKey === "sl") {
      /* SL pick/inbound share the same "PU_" filename pattern. */
      titleBase = "PU_" + (inv.puNumber || "") + "_PO_" + (inv.clientRef || "");
    } else {
      titleBase = (docKind === "pick" ? "Pick_Ticket_" : "Inbound_") + (inv.puNumber || "") + "_PO_" + (inv.clientRef || "");
    }
  } else {
    var iso = new Date().toISOString().slice(0, 10);
    var prefix;
    if (docKind === "driversignoff") prefix = "Driver_SignOff_Sheets";
    else if (docKind === "master") prefix = brandKey === "sl" ? "SL_Inbound_Sheets" : "Inbound_Sheets";
    else if (docKind === "packing") prefix = brandKey === "sl" ? "SL_Packing_Lists" : "Packing_Lists";
    else if (brandKey === "sl") prefix = "SL_" + (docKind === "pick" ? "Pick_Tickets" : "Inbound_Sheets");
    else prefix = docKind === "pick" ? "Pick_Tickets" : "Inbound_Sheets";
    titleBase = prefix + "_" + invoices.length + "_" + iso;
  }

  var doc = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escDocHtml(titleBase) + '</title>';
  doc += '<style>' + sharedCss + ' .gfi-doc-pagebreak{page-break-after:always;break-after:page;height:0}</style></head><body>';
  bodies.forEach(function (body, i) {
    doc += body;
    if (i < bodies.length - 1) doc += '<div class="gfi-doc-pagebreak"></div>';
  });
  doc += '</body></html>';

  var w = window.open("", "_blank");
  if (!w) { if (onError) onError("popup-blocked"); return; }
  w.document.open();
  w.document.write(doc);
  w.document.close();
  setTimeout(function () { try { w.focus(); w.print(); } catch (e) {} }, 600);
}

/* Drive-upload twin of printInvoiceDocs for a SINGLE invoice. Builds
   the same HTML, rasterises it, and drops the PDF into the invoice's
   client subfolder under the embarque tree on Drive.

   Why per-invoice (not a bulk PDF): each invoice has a different
   client → different Drive folder. Bulk prints can still call this
   in a sequential loop. */
/* Build a single invoice doc → { filename, blob, mimeType }.
   `kind` can be one of:
     - "gfi-pick" | "gfi-inbound" | "sl-pick" | "sl-inbound"
     - "gfi-packing" | "sl-packing"
     - "sl-driversignoff"
     - "invoice"  (the actual Invoice PDF — no brand prefix)
   The Drive upload + email attachment paths both consume this so the
   PDF a customer downloads from email matches what's archived on
   Drive. */
export async function buildInvoiceDocPdf(inv, kind, ctx, extras) {
  if (!inv || !ctx) throw new Error("buildInvoiceDocPdf: missing args");
  var html;
  var lot = inv.lot || (inv.lines && inv.lines[0] && inv.lines[0].lot) || "";
  var po = inv.clientRef || "";
  var pu = inv.puNumber || "";
  var filename;
  if (kind === "invoice") {
    var client = resolveClientFull(inv.clientId);
    html = buildInvoicePrintHTML(inv, client);
    filename = "Invoice_PU_" + pu + "_PO_" + po;
  } else {
    var brandKey = kind.indexOf("sl-") === 0 ? "sl" : "gfi";
    var docKind = kind.split("-")[1];
    var remarks = (extras && Array.isArray(extras.remarks)) ? extras.remarks : null;
    var loadingDateOverride = (extras && extras.loadingDate) || null;
    if (docKind === "packing") {
      html = buildPackingListHTML(inv, ctx, { brandKey: brandKey });
    } else if (docKind === "driversignoff") {
      html = buildDriverSignOffSheetHTML(inv, ctx);
    } else if (docKind === "master") {
      html = buildMasterInboundHTML(inv, ctx, { brandKey: brandKey });
    } else {
      html = buildPickTicketOrInboundHTML(inv, ctx, { brandKey: brandKey, kind: docKind, remarks: remarks, loadingDate: loadingDateOverride });
    }
    if (docKind === "driversignoff") {
      filename = "Driver_SignOff_Sheet_PU_" + pu + "_PO_" + po;
    } else if (docKind === "master") {
      /* Customer-facing filename — same as regular Inbound. The
         "Master" tag stays in the menu only. */
      filename = (brandKey === "sl" ? "PU_" : "Inbound_") + pu + "_PO_" + po;
    } else if (docKind === "packing") {
      filename = brandKey === "sl"
        ? "Packing_List_PO_" + po + "_PU_" + pu
        : "Packing_List_GFI_PU_" + pu + "_PO_" + po + "_Lot_" + lot;
    } else if (brandKey === "sl") {
      filename = "PU_" + pu + "_PO_" + po;
    } else {
      filename = (docKind === "pick" ? "Pick_Ticket_" : "Inbound_") + pu + "_PO_" + po;
    }
  }
  var pdfBlob = await htmlToPdfBlob(html);
  return { filename: safeFilename(filename) + ".pdf", blob: pdfBlob, mimeType: "application/pdf" };
}

async function uploadInvoiceDocToDrive(inv, kind, ctx, extras) {
  if (!inv || !ctx) throw new Error("uploadInvoiceDocToDrive: missing args");
  var { filename, blob } = await buildInvoiceDocPdf(inv, kind, ctx, extras);
  /* Resolve the Drive path. The invoice's linked shipment is the
     bridge to the season + shipper data the embarque folder name
     needs. */
  var shipments = ctx.shipments || [];
  var clients = ctx.clients || [];
  var company = ctx.company || {};
  var shipment = findShipmentForInvoice(inv, shipments);
  if (!shipment) throw new Error("No linked shipment for invoice " + inv.id + " — cannot resolve embarque folder");
  var suppliers = loadLS("gfi-suppliers", []);
  var seasons = loadLS("gfi-seasons", []);
  var sup = suppliers.find(function (s) { return idMatches(s, shipment.supplierId); });
  var shipperShort = (sup && (sup.shortName || sup.fullName)) || shipment.shipperName || "Unknown";
  var season = seasons.find(function (s) { return String(s.id) === String(shipment.seasonId); });
  var cli = clients.find(function (c) { return idMatches(c, inv.clientId); });
  var clientShort = (cli && (cli.shortName || cli.fullName)) || inv.clientName || "Unknown";
  var segments = rootPathSegments(company.driveRootPath).concat([
    seasonFolderName(season),
    embarqueFolderName(shipment, shipperShort),
    invoiceFolderName(inv, clientShort),
  ]);
  var folderId = await driveEnsurePath(segments);
  return driveUploadFile(folderId, filename, blob, { contentType: "application/pdf" });
}

/* ──────── PICK TICKET / INBOUND SELECTOR MODAL ────────
   Mandatory choice between Pick Ticket and Inbound before any
   pick-or-inbound print. Renders centered over the screen until
   the user picks one or cancels. */
function PickKindSelector({ brandLabel, lang, onPick, onCancel }) {
  var t = lang === "es"
    ? { title: "Tipo de documento", subtitle: brandLabel + " · selecciona uno", pick: "Pick Ticket", pickDesc: "El cliente recoge el producto", inbound: "Inbound", inboundDesc: "El producto llega a la bodega del cliente", cancel: "Cancelar" }
    : { title: "Document type", subtitle: brandLabel + " · pick one", pick: "Pick Ticket", pickDesc: "Customer picks up the product", inbound: "Inbound", inboundDesc: "Product arrives at customer warehouse", cancel: "Cancel" };
  /* zIndex bumped to 10006 — above DraggableModal (10001), email
     dialog (10003), preview modal (10004) and batch payment (10005)
     so this picker always lands on top regardless of what's open. */
  var overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10006 };
  var modalStyle = { background: "#ffffff", borderRadius: 10, padding: 22, minWidth: 380, maxWidth: 460, boxShadow: "0 24px 64px rgba(0,0,0,0.3)", border: "1px solid #d0d5dd" };
  var optionStyle = { display: "block", width: "100%", textAlign: "left", padding: "14px 16px", marginTop: 10, background: "#f7f8fa", border: "1px solid #d0d5dd", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#1a1a1a", transition: "background 0.15s" };
  return createPortal(
    <div style={overlayStyle} onClick={onCancel}>
      <div style={modalStyle} onClick={function (e) { e.stopPropagation(); }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a" }}>{t.title}</div>
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>{t.subtitle}</div>
        <button onClick={function () { onPick("pick"); }} onMouseEnter={function (e) { e.currentTarget.style.background = "#eef4f5"; }} onMouseLeave={function (e) { e.currentTarget.style.background = "#f7f8fa"; }} style={optionStyle}>
          <div style={{ fontWeight: 700 }}>{t.pick}</div>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>{t.pickDesc}</div>
        </button>
        <button onClick={function () { onPick("inbound"); }} onMouseEnter={function (e) { e.currentTarget.style.background = "#eef4f5"; }} onMouseLeave={function (e) { e.currentTarget.style.background = "#f7f8fa"; }} style={optionStyle}>
          <div style={{ fontWeight: 700 }}>{t.inbound}</div>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>{t.inboundDesc}</div>
        </button>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onCancel} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #d0d5dd", background: "#ffffff", color: "#6b7280", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.cancel}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ──────── PICK TICKET REMARKS MODAL ────────
   Only triggered for Pick Ticket (not Inbound). Three lines:
     1. Mandatory pre-checked: "SET LOADING TRUCK TEMPERATURE AT 35ºF"
        — checkbox is locked on, the user cannot uncheck it
     2. Optional: "Please Add Digital Template 4"
     3. Free-text input for any additional note
   Returns the assembled string lines to the caller so the Pick Ticket
   builder can drop them into the Remarks block. */
function PickTicketRemarksModal({ brandLabel, lang, defaultLoadingDate, onPrint, onCancel }) {
  /* Loading Date — required field. Defaults to ETA + 1 day (computed
     by the parent and passed in). User can override before printing.
     The Print button stays disabled until a value is present so it's
     impossible to ship a doc without one. */
  var [loadingDate, setLoadingDate] = useState(defaultLoadingDate || "");
  /* Line 1 is locked-on per spec — no state needed since the user
     cannot toggle it. Renders as a disabled checked checkbox. */
  var [digitalChecked, setDigitalChecked] = useState(false);
  var [customNote, setCustomNote] = useState("");
  var t = lang === "es"
    ? { title: "Notas del Pick Ticket", subtitle: brandLabel + " · revisa antes de imprimir", mandatory: "Obligatorio", required: "Requerido", optional: "Opcional", customLabel: "Nota adicional", customPlaceholder: "Escribe cualquier instrucción extra…", cancel: "Cancelar", print: "Imprimir", loadingDateLabel: "Loading Date", loadingDateHint: "Por defecto: día siguiente al ETA. Ajusta si hay feriado o retraso.", loadingDateMissing: "Loading Date es requerido" }
    : { title: "Pick Ticket Remarks", subtitle: brandLabel + " · review before printing", mandatory: "Mandatory", required: "Required", optional: "Optional", customLabel: "Custom note", customPlaceholder: "Type any extra instruction…", cancel: "Cancel", print: "Print", loadingDateLabel: "Loading Date", loadingDateHint: "Defaults to the day after Arrival ETA. Adjust for holidays or delays.", loadingDateMissing: "Loading Date is required" };

  /* Mandatory line is always included. The optional + custom lines
     only flow through when the user checks them / types text. */
  function handlePrint() {
    if (!loadingDate) return;  /* guard — button is disabled too */
    var lines = [];
    lines.push("SET LOADING TRUCK TEMPERATURE AT 35ºF");
    if (digitalChecked) lines.push("Please Add Digital Template 4");
    var trimmed = (customNote || "").trim();
    if (trimmed) lines.push(trimmed);
    onPrint(lines, loadingDate);
  }

  var canPrint = !!loadingDate;
  /* z-index above all other modals — opens after PickKindSelector. */
  var overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10006 };
  var modalStyle = { background: "#ffffff", borderRadius: 10, padding: 22, minWidth: 460, maxWidth: 520, boxShadow: "0 24px 64px rgba(0,0,0,0.3)", border: "1px solid #d0d5dd" };
  var rowStyle = { display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", marginTop: 10, background: "#f7f8fa", border: "1px solid #d0d5dd", borderRadius: 8 };
  var lockedRowStyle = { ...rowStyle, background: "#fef9e7", border: "1px solid #f5d878" };
  var labelStyle = { fontSize: 12, color: "#1a1a1a", fontWeight: 600, lineHeight: 1.4 };
  var pillStyle = { fontSize: 8, fontWeight: 700, padding: "2px 6px", borderRadius: 4, letterSpacing: "0.05em", textTransform: "uppercase", marginLeft: 6 };
  var dateRowStyle = { marginTop: 14, padding: "12px 14px", background: "#eef4f5", border: "1.5px solid #1B3A4B", borderRadius: 8 };
  return createPortal(
    <div style={overlayStyle} onClick={onCancel}>
      <div style={modalStyle} onClick={function (e) { e.stopPropagation(); }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a" }}>{t.title}</div>
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>{t.subtitle}</div>

        {/* Loading Date — required field, surfaced first so the user
            sees it before the remarks. Highlighted with navy border
            since it's the operational decision being captured. */}
        <div style={dateRowStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#1B3A4B", textTransform: "uppercase", letterSpacing: "0.06em" }}>{t.loadingDateLabel} <span style={{ ...pillStyle, background: "#b45309", color: "#fff" }}>{t.required}</span></div>
          </div>
          <input
            type="date"
            value={loadingDate}
            onChange={function (e) { setLoadingDate(e.target.value); }}
            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1.5px solid " + (loadingDate ? "#d0d5dd" : "#C0392B"), fontSize: 13, fontFamily: "inherit", color: "#1a1a1a", outline: "none", boxSizing: "border-box", fontWeight: 600 }}
          />
          <div style={{ fontSize: 10, color: "#6b7280", marginTop: 4, fontStyle: "italic" }}>{t.loadingDateHint}</div>
        </div>

        {/* Line 1 — mandatory, pre-checked, locked. */}
        <div style={lockedRowStyle}>
          <input type="checkbox" checked readOnly disabled style={{ marginTop: 3, cursor: "not-allowed", accentColor: "#b45309" }} />
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>SET LOADING TRUCK TEMPERATURE AT 35°F <span style={{ ...pillStyle, background: "#b45309", color: "#fff" }}>{t.required}</span></div>
            <div style={{ fontSize: 10, color: "#8b6914", marginTop: 3, fontStyle: "italic" }}>{lang === "es" ? "Se incluye siempre en el documento — no se puede desmarcar." : "Always included on the document — cannot be unchecked."}</div>
          </div>
        </div>

        {/* Line 2 — optional checkbox. */}
        <label style={{ ...rowStyle, cursor: "pointer" }}>
          <input type="checkbox" checked={digitalChecked} onChange={function (e) { setDigitalChecked(e.target.checked); }} style={{ marginTop: 3, accentColor: "#2A7A6F" }} />
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Please Add Digital Template 4 <span style={{ ...pillStyle, background: "#e5e7eb", color: "#6b7280" }}>{t.optional}</span></div>
          </div>
        </label>

        {/* Line 3 — free text. */}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.customLabel}</div>
          <textarea
            value={customNote}
            onChange={function (e) { setCustomNote(e.target.value); }}
            placeholder={t.customPlaceholder}
            rows={2}
            style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #d0d5dd", fontSize: 12, fontFamily: "inherit", color: "#1a1a1a", outline: "none", resize: "vertical", boxSizing: "border-box" }}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 18 }}>
          <div style={{ fontSize: 11, color: canPrint ? "transparent" : "#C0392B", fontWeight: 600 }}>{canPrint ? "" : "⚠ " + t.loadingDateMissing}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onCancel} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #d0d5dd", background: "#ffffff", color: "#6b7280", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.cancel}</button>
            <button onClick={handlePrint} disabled={!canPrint} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: canPrint ? "#1B3A4B" : "#9ca3af", color: "#fff", cursor: canPrint ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 700 }}>{"🖨"} {t.print}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ──────── DROPDOWN — "Print Documents" ────────
   Two sections (Ground-Fresh + SweetLeaf), 2 items each. The pick-
   or-inbound items emit the placeholder "<brand>-pickorinbound"; the
   parent receives that and pops the PickKindSelector before calling
   the print helper with the resolved kind. The "packing" items emit
   the final kind directly since they don't need user input. */
function PrintDocsDropdown({ lang, label, onPick, style, btnStyle: btnStyleOverride }) {
  var [open, setOpen] = useState(false);
  var rootRef = useRef(null);
  useEffect(function () {
    if (!open) return;
    function onDown(e) { if (rootRef.current && !rootRef.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-searchselect]'))) setOpen(false); }
    document.addEventListener("mousedown", onDown);
    return function () { document.removeEventListener("mousedown", onDown); };
  }, [open]);
  var menuStyle = { position: "absolute", top: "100%", right: 0, marginTop: 4, minWidth: 320, background: "#ffffff", border: "1.5px solid #d0d5dd", borderRadius: 8, boxShadow: "0 12px 32px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.08)", zIndex: 1000, overflow: "hidden" };
  var sectionHeaderGfi = { padding: "8px 14px 4px", fontSize: 9, fontWeight: 700, color: "#1B3A4B", textTransform: "uppercase", letterSpacing: "0.06em", background: "#f7f8fa", borderBottom: "1px solid #e5e7eb" };
  var sectionHeaderSl = { padding: "8px 14px 4px", fontSize: 9, fontWeight: 700, color: "#2A7A6F", textTransform: "uppercase", letterSpacing: "0.06em", background: "#eef5f4", borderBottom: "1px solid #d0e3e0", borderTop: "1px solid #d0e3e0" };
  var itemStyle = { display: "block", width: "100%", textAlign: "left", padding: "10px 14px", background: "#ffffff", border: "none", borderBottom: "1px solid #e5e7eb", cursor: "pointer", fontSize: 12, color: "#1a1a1a" };
  var itemHint = { fontSize: 10, color: "#6b7280", marginTop: 2 };
  /* Brushed Steel kebab menu (InvoiceRegistry.js list header): this
     trigger button is normally its own navy-outlined pill — same
     clash ExportButton.js had before its `btnStyle` override was
     added for the Bills/Shipments kebabs. `btnStyle` here lets the
     caller fully replace it so it blends into the plain kebab list
     items around it; every other existing caller (unchanged) keeps
     the original navy pill look. */
  var btnStyle = Object.assign({ padding: "6px 12px", borderRadius: 6, border: "1px solid #1B3A4B", background: "rgba(27,58,75,0.06)", color: "#1B3A4B", cursor: "pointer", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }, btnStyleOverride || {});
  var t = lang === "es"
    ? { btn: label || "Imprimir Documentos", gfiSection: "Ground-Fresh — Documentos de Venta", slSection: "SweetLeaf Produce — Documentos de Venta", pi: "Pick Ticket / Inbound", piDesc: "Elegir tipo al imprimir", packing: "Packing List", packingDesc: "Detalle por pallet del manifiesto", master: "Master Inbound", masterDesc: "Inbound + Packing List en un solo PDF", driver: "Driver Sign-Off Sheet", driverDesc: "Formato en blanco para que la bodega lo llene a mano" }
    : { btn: label || "Print Documents", gfiSection: "Ground-Fresh Sales Documents", slSection: "SweetLeaf Produce Documents", pi: "Pick Ticket / Inbound", piDesc: "Choose type when printing", packing: "Packing List", packingDesc: "Per-pallet manifest detail", master: "Master Inbound", masterDesc: "Inbound + Packing List in a single PDF", driver: "Driver Sign-Off Sheet", driverDesc: "Blank fill-in form for warehouse use" };
  function pick(kind) { setOpen(false); if (onPick) onPick(kind); }
  function MenuItem({ id, title, desc, last }) {
    return (
      <button onClick={function () { pick(id); }}
        onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
        onMouseLeave={function (e) { e.currentTarget.style.background = "#ffffff"; }}
        style={last ? { ...itemStyle, borderBottom: "none" } : itemStyle}>
        <div style={{ fontWeight: 700 }}>{title}</div>
        <div style={itemHint}>{desc}</div>
      </button>
    );
  }
  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block", ...(style || {}) }}>
      <button onClick={function () { setOpen(!open); }} style={btnStyle}>{"🖨"} {t.btn} <span style={{ fontSize: 10, opacity: 0.7 }}>{"▾"}</span></button>
      {open && (
        <div style={menuStyle}>
          <div style={sectionHeaderGfi}>{t.gfiSection}</div>
          <MenuItem id="gfi-pickorinbound" title={t.pi} desc={t.piDesc} />
          <MenuItem id="gfi-packing" title={t.packing} desc={t.packingDesc} />
          <MenuItem id="gfi-master" title={t.master} desc={t.masterDesc} />
          <div style={sectionHeaderSl}>{t.slSection}</div>
          <MenuItem id="sl-pickorinbound" title={t.pi} desc={t.piDesc} />
          <MenuItem id="sl-packing" title={t.packing} desc={t.packingDesc} />
          <MenuItem id="sl-master" title={t.master} desc={t.masterDesc} />
          <MenuItem id="sl-driversignoff" title={t.driver} desc={t.driverDesc} last />
        </div>
      )}
    </div>
  );
}

/* ──────── BRAND-SPECIFIC DOCS DROPDOWN ────────
   A single-brand variant of PrintDocsDropdown. Shows 2 items
   (Pick Ticket / Inbound · Packing List) for ONE brand. Used by the
   org-chart panel in the invoice detail header where each brand
   gets its own button. The combined PrintDocsDropdown stays in use
   for the bulk-action bar where horizontal space is tight. */
function BrandDocsDropdown({ brand, lang, onPick, style }) {
  var [open, setOpen] = useState(false);
  var rootRef = useRef(null);
  useEffect(function () {
    if (!open) return;
    function onDown(e) { if (rootRef.current && !rootRef.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-searchselect]'))) setOpen(false); }
    document.addEventListener("mousedown", onDown);
    return function () { document.removeEventListener("mousedown", onDown); };
  }, [open]);
  /* Color tokens hard-coded to brand requirement (navy vs teal). */
  var accent = brand === "sl" ? "#2A7A6F" : "#1B3A4B";
  var accentBg = brand === "sl" ? "rgba(42,122,111,0.06)" : "rgba(27,58,75,0.06)";
  var label = brand === "sl"
    ? (lang === "es" ? "SweetLeaf Docs" : "SweetLeaf Docs")
    : (lang === "es" ? "GFI Docs" : "GFI Docs");
  var pickPlaceholder = brand + "-pickorinbound";
  var packingKind = brand + "-packing";
  var masterKind = brand + "-master";
  var driverKind = brand + "-driversignoff";
  var t = lang === "es"
    ? { pi: "Pick Ticket / Inbound", piDesc: "Elegir tipo al imprimir", packing: "Packing List", packingDesc: "Detalle por pallet del manifiesto", master: "Master Inbound", masterDesc: "Inbound + Packing List en un solo PDF", driver: "Driver Sign-Off Sheet", driverDesc: "Formato en blanco para que la bodega lo llene a mano" }
    : { pi: "Pick Ticket / Inbound", piDesc: "Choose type when printing", packing: "Packing List", packingDesc: "Per-pallet manifest detail", master: "Master Inbound", masterDesc: "Inbound + Packing List in a single PDF", driver: "Driver Sign-Off Sheet", driverDesc: "Blank fill-in form for warehouse use" };
  var btnStyle = { padding: "5px 10px", borderRadius: 5, border: "1px solid " + accent, background: accentBg, color: accent, cursor: "pointer", fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" };
  var menuStyle = { position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", marginTop: 4, minWidth: 220, background: "#ffffff", border: "1.5px solid #d0d5dd", borderRadius: 8, boxShadow: "0 12px 32px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.08)", zIndex: 1000, overflow: "hidden" };
  var itemStyle = { display: "block", width: "100%", textAlign: "left", padding: "9px 12px", background: "#ffffff", border: "none", borderBottom: "1px solid #e5e7eb", cursor: "pointer", fontSize: 11, color: "#1a1a1a" };
  function pick(k) { setOpen(false); if (onPick) onPick(k); }
  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block", ...(style || {}) }}>
      <button onClick={function () { setOpen(!open); }} style={btnStyle}>{"🖨"} {label} <span style={{ fontSize: 9, opacity: 0.7 }}>{"▾"}</span></button>
      {open && (
        <div style={menuStyle}>
          <button onClick={function () { pick(pickPlaceholder); }}
            onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
            onMouseLeave={function (e) { e.currentTarget.style.background = "#ffffff"; }}
            style={itemStyle}>
            <div style={{ fontWeight: 700 }}>{t.pi}</div>
            <div style={{ fontSize: 9, color: "#6b7280", marginTop: 2 }}>{t.piDesc}</div>
          </button>
          <button onClick={function () { pick(packingKind); }}
            onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
            onMouseLeave={function (e) { e.currentTarget.style.background = "#ffffff"; }}
            style={itemStyle}>
            <div style={{ fontWeight: 700 }}>{t.packing}</div>
            <div style={{ fontSize: 9, color: "#6b7280", marginTop: 2 }}>{t.packingDesc}</div>
          </button>
          <button onClick={function () { pick(masterKind); }}
            onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
            onMouseLeave={function (e) { e.currentTarget.style.background = "#ffffff"; }}
            style={brand === "sl" ? itemStyle : { ...itemStyle, borderBottom: "none" }}>
            <div style={{ fontWeight: 700 }}>{t.master}</div>
            <div style={{ fontSize: 9, color: "#6b7280", marginTop: 2 }}>{t.masterDesc}</div>
          </button>
          {/* Driver Sign-Off — SweetLeaf only per spec. Blank form
              the warehouse fills by hand; no GFI counterpart yet. */}
          {brand === "sl" && (
            <button onClick={function () { pick(driverKind); }}
              onMouseEnter={function (e) { e.currentTarget.style.background = "#f3f4f6"; }}
              onMouseLeave={function (e) { e.currentTarget.style.background = "#ffffff"; }}
              style={{ ...itemStyle, borderBottom: "none" }}>
              <div style={{ fontWeight: 700 }}>{t.driver}</div>
              <div style={{ fontSize: 9, color: "#6b7280", marginTop: 2 }}>{t.driverDesc}</div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ──────── LOADING DOCS ORG CHART ────────
   Compact tree visual: a centered shipment icon up top, two soft-grey
   lines branching down to a GFI column on the left and a SweetLeaf
   column on the right. Each column shows the brand isotipo with the
   brand-coloured dropdown right below. Keeps the visual hierarchy
   "shipment splits into two brand outputs". Self-contained so a
   single render keeps print actions discoverable. */
function LoadingDocsOrgChart({ lang, onPick }) {
  /* Layout constants kept inline so the visual reads top-to-bottom:
       row 1 — icon at center (with white card border)
       row 2 — short vertical stub down
       row 3 — horizontal bar between the two branches
       row 4 — two short vertical stubs down to each column
       row 5 — two columns (isotipo + brand button)
     All connector lines use #d0d5dd (light grey) to feel subtle but
     present in print preview too. */
  var line = "#d0d5dd";
  var subtitle = lang === "es" ? "Documentos de Carga" : "Loading Docs";
  return (
    <div style={{ paddingTop: 6, paddingBottom: 4, display: "flex", flexDirection: "column", alignItems: "center", minWidth: 260 }}>
      <div style={{ fontSize: 8, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>{subtitle}</div>
      {/* Center shipment icon */}
      <div style={{ fontSize: 18, lineHeight: 1, padding: "3px 7px", background: "#ffffff", border: "1.5px solid " + line, borderRadius: 6 }} role="img" aria-label="shipment">{"🚚"}</div>
      {/* Vertical stub down from icon */}
      <div style={{ width: 1.5, height: 10, background: line }}></div>
      {/* Horizontal connector + two outer stubs going down to each branch */}
      <div style={{ position: "relative", width: 150, height: 10 }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1.5, background: line }}></div>
        <div style={{ position: "absolute", top: 0, left: 0, width: 1.5, height: 10, background: line }}></div>
        <div style={{ position: "absolute", top: 0, right: 0, width: 1.5, height: 10, background: line }}></div>
      </div>
      {/* Two branches — GFI left, SweetLeaf right */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 2, width: "100%" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
          <img src={GFI_ISOTIPO_B64} alt="Ground-Fresh" style={{ width: 32, height: 32, objectFit: "contain" }} />
          <BrandDocsDropdown brand="gfi" lang={lang} onPick={onPick} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
          <img src={SL_ISOTIPO_B64} alt="SweetLeaf" style={{ width: 32, height: 32, objectFit: "contain" }} />
          <BrandDocsDropdown brand="sl" lang={lang} onPick={onPick} />
        </div>
      </div>
    </div>
  );
}

/* ======================== BILL PREVIEW MODAL ========================
   Lightweight read-only preview of the bill that fed an invoice.
   Opened by clicking the LOT cell in the invoices list. Carries an
   "Open Bill" button that pops the Bills module in a new browser
   tab via the URL-hash deeplink (App.js handles the hash on mount). */
function BillPreviewModal({ bill, lang, onClose }) {
  var t = lang === "es"
    ? { title: "Bill", subtitle: "Vista previa", lot: "Lote", date: "Fecha", shipper: "Exportador", proforma: "Proforma", shipperInvoice: "Factura del Exportador", warehouse: "Bodega", product: "Producto", description: "Descripción", category: "Cat", pallets: "Pallets", cases: "Cant", price: "Precio (ref)", lineTotal: "Total", saleStatus: "Status Venta", paymentStatus: "Estado de Pago", noLines: "Sin líneas de producto", total: "Total", openBtn: "Abrir Bill", closeBtn: "Cerrar", statusDraft: "Borrador", statusFinalized: "Finalizada", statusAwaiting: "Pendiente Pago", statusPaid: "Pagada" }
    : { title: "Bill", subtitle: "Preview", lot: "Lot", date: "Date", shipper: "Shipper", proforma: "Proforma", shipperInvoice: "Shipper Invoice", warehouse: "Warehouse", product: "Product", description: "Description", category: "Cat", pallets: "Pallets", cases: "Qty", price: "Price (ref)", lineTotal: "Total", saleStatus: "Sale Status", paymentStatus: "Payment Status", noLines: "No product lines", total: "Total", openBtn: "Open Bill", closeBtn: "Close", statusDraft: "Draft", statusFinalized: "Finalized", statusAwaiting: "Awaiting Payment", statusPaid: "Paid" };

  /* Compute the same shape calcBillTotals does (we don't import it
     to avoid a circular dep; this is read-only and reproduces the
     line-level math used by the bill form). */
  var billDefaultRate = parseFloat(bill.commissionRate) || 0;
  var grossSales = 0, commAmt = 0;
  (bill.lines || []).forEach(function (l) {
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
    var rate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "") ? billDefaultRate : (parseFloat(l.commissionRate) || 0);
    grossSales += amt;
    commAmt += amt * (rate / 100);
  });
  var totalFees = 0; (bill.fees || []).forEach(function (f) { totalFees += parseFloat(f.amount) || 0; });
  var totalCredits = 0; (bill.credits || []).forEach(function (c) { totalCredits += parseFloat(c.amount) || 0; });
  var paid = (bill.payments || []).reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  var subtotal = grossSales - commAmt - totalFees + totalCredits;
  var balance = subtotal - paid;
  /* Payment status — same logic the Bills list uses (paid wins when
     balance ≈ 0 regardless of stored status). */
  var paymentStatus = bill.status || "draft";
  if (paymentStatus !== "draft" && paid > 0 && balance <= 0.01) paymentStatus = "paid";

  /* Resolve warehouse name via the shipment linked to this bill's
     lot — bills don't store warehouseId directly. */
  var whName = "";
  try {
    var shipments = JSON.parse(localStorage.getItem("gfi-shipments") || "[]");
    var shp = findBillByLot(bill.lot, shipments) || shipments.find(function (s) { return String(s.lot) === String(bill.lot); });
    if (shp) {
      whName = shp.warehouseName || "";
      if (!whName && shp.warehouseId) {
        var warehouses = JSON.parse(localStorage.getItem("gfi-warehouses") || "[]");
        var w = warehouses.find(function (x) { return idMatches(x, shp.warehouseId); });
        if (w) whName = w.fullName || w.shortName || w.name || "";
      }
    }
  } catch (e) {}

  function openInNewTab() {
    /* Deeplink: opens index.html in a new tab with #open=bill:ID;
       App.js parses the hash on mount, navigates to bills, then calls
       window.__gfiBillOpen(id). The preview modal stays open in the
       current tab (no auto-close per spec). */
    var url = window.location.origin + window.location.pathname + window.location.search + "#open=bill:" + encodeURIComponent(bill.id);
    window.open(url, "_blank");
  }

  var th = { padding: "6px 8px", textAlign: "left", fontWeight: 700, fontSize: 10, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "2px solid var(--color-border)" };
  var statusBadge = function (st) {
    var styles = {
      draft: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.25)", label: t.statusDraft },
      finalized: { bg: "rgba(37,99,235,0.10)", text: "#2563EB", border: "rgba(37,99,235,0.25)", label: t.statusFinalized },
      awaiting: { bg: "rgba(220,38,38,0.10)", text: "#DC2626", border: "rgba(220,38,38,0.25)", label: t.statusAwaiting },
      paid: { bg: "rgba(42,122,111,0.10)", text: "#2A7A6F", border: "rgba(42,122,111,0.25)", label: t.statusPaid },
    };
    var s = styles[st] || styles.draft;
    return <span style={{ padding: "3px 10px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: s.bg, color: s.text, border: "1px solid " + s.border, boxShadow: SALE_STATUS_BEVEL }}>{s.label}</span>;
  };

  return (
    <DraggableModal title={t.title + " · LOT " + (bill.lot || "—")} onClose={onClose} initialWidth={Math.min(1000, Math.floor(window.innerWidth * 0.8))} initialHeight={Math.min(720, window.innerHeight - 80)}>
      <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: "var(--color-accent)" }}>LOT {bill.lot || "—"}</div>
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{bill.shipperName || "—"} · {bill.proforma || "—"}</div>
          <div style={{ marginTop: 8 }}>{statusBadge(paymentStatus)}</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {/* Open Bill — new browser tab. Preview stays open. */}
          <button onClick={openInNewTab} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>{"↗"} {t.openBtn}</button>
          <button onClick={onClose} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.closeBtn}</button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px", fontSize: 12 }}>
        {/* Bill meta — 4 columns, two rows. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px 18px", marginBottom: 18 }}>
          {[
            [t.lot, bill.lot, true],
            [t.date, bill.billDate ? new Date(bill.billDate + "T00:00:00").toLocaleDateString("en-US") : "—"],
            [t.shipper, bill.shipperName],
            [t.warehouse, whName],
            [t.proforma, bill.proforma, true],
            [t.shipperInvoice, bill.shipperInvoice, true],
            [t.paymentStatus, t["status" + paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1)] || paymentStatus],
          ].map(function (arr, i) {
            return (
              <div key={i}>
                <div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{arr[0]}</div>
                <div style={{ fontWeight: 600, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit", marginTop: 2 }}>{arr[1] || "—"}</div>
              </div>
            );
          })}
        </div>

        {/* Product lines table. */}
        {(bill.lines || []).length === 0 ? (
          <div style={{ padding: "20px 0", textAlign: "center", color: "var(--color-text-muted)", fontStyle: "italic", fontSize: 12 }}>{t.noLines}</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 14 }}>
            <thead><tr>
              <th style={th}>{t.product}</th>
              <th style={th}>{t.description}</th>
              <th style={{ ...th, textAlign: "center" }}>{t.category}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.pallets}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.cases}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.price}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.lineTotal}</th>
              <th style={{ ...th, textAlign: "center" }}>{t.saleStatus}</th>
            </tr></thead>
            <tbody>
              {(bill.lines || []).map(function (l, i) {
                var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
                var sc = SALE_STATUS_COLORS[l.saleStatus] || null;
                return (
                  <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "7px 8px", fontWeight: 600 }}>{l.productName || l.productBase || "—"}</td>
                    <td style={{ padding: "7px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{l.productDescription || "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "center" }}>{l.category ? <span style={{ padding: "2px 7px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: l.category === "OG" ? "rgba(16,185,129,0.10)" : "rgba(107,114,128,0.10)", color: l.category === "OG" ? "#059669" : "#374151" }}>{l.category}</span> : "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{l.pallets || "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{l.cases || "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>{l.reportedPrice ? fmtMoney(l.reportedPrice) : "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{amt > 0 ? fmtMoney(amt) : "—"}</td>
                    <td style={{ padding: "7px 8px", textAlign: "center" }}>{l.saleStatus && sc ? <span style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: sc.bg, color: sc.text, border: "1px solid " + sc.border, boxShadow: SALE_STATUS_BEVEL }}>{l.saleStatus}</span> : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Totals strip — Gross / Commission / Fees / Credits / Subtotal / Paid / Balance. */}
        <div style={{ marginTop: 10, padding: "14px 18px", background: "var(--color-bg)", borderRadius: 10, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, fontSize: 12 }}>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Gross Sales</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>{fmtMoney(grossSales)}</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Commission</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "#DC2626" }}>({fmtMoney(commAmt)})</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Fees</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "#DC2626" }}>({fmtMoney(totalFees)})</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Credits</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "#059669" }}>{fmtMoney(totalCredits)}</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Subtotal</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>{fmtMoney(subtotal)}</div></div>
          <div><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Paid</div><div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "#059669" }}>{fmtMoney(paid)}</div></div>
          <div style={{ gridColumn: "span 2" }}><div style={{ fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Balance</div><div style={{ fontSize: 16, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", marginTop: 2, color: "var(--color-accent)" }}>{fmtMoney(balance)}</div></div>
        </div>
      </div>
    </DraggableModal>
  );
}

/* ======================== PRINT PREVIEW ======================== */
function PrintPreview({ invoice, t, onClose }) {
  var client = resolveClientFull(invoice.clientId);
  var html = buildInvoicePrintHTML(invoice, client);
  var [driveBusy, setDriveBusy] = useState(false);
  var [driveMsg, setDriveMsg] = useState("");
  var printIt = function() {
    var w = window.open("", "_blank"); if (!w) return;
    w.document.open(); w.document.write(html); w.document.close();
    setTimeout(function() { try { w.focus(); w.print(); } catch(e) {} }, 600);
    /* Mirror the print to Drive when configured + signed in. Same
       client subfolder the loading docs land in so Arturo has every
       artefact of the deal in one place. */
    if (driveConfigured() && driveSignedIn() && !driveBusy) {
      setDriveBusy(true); setDriveMsg("");
      (async function () {
        try {
          var pdfBlob = await htmlToPdfBlob(html);
          var shipments = loadLS("gfi-shipments", []);
          var suppliers = loadLS("gfi-suppliers", []);
          var seasons = loadLS("gfi-seasons", []);
          var clients = loadLS("gfi-clients", []);
          var company = loadCompanyProfile();
          var shipment = findShipmentForInvoice(invoice, shipments);
          if (!shipment) throw new Error("No linked shipment");
          var sup = suppliers.find(function (s) { return idMatches(s, shipment.supplierId); });
          var shipperShort = (sup && (sup.shortName || sup.fullName)) || shipment.shipperName || "Unknown";
          var season = seasons.find(function (s) { return String(s.id) === String(shipment.seasonId); });
          var cli = clients.find(function (c) { return idMatches(c, invoice.clientId); });
          var clientShort = (cli && (cli.shortName || cli.fullName)) || invoice.clientName || "Unknown";
          var segments = rootPathSegments(company.driveRootPath).concat([
            seasonFolderName(season),
            embarqueFolderName(shipment, shipperShort),
            invoiceFolderName(invoice, clientShort),
          ]);
          var folderId = await driveEnsurePath(segments);
          var filename = safeFilename("Invoice_PU_" + (invoice.puNumber || "") + "_PO_" + (invoice.clientRef || "")) + ".pdf";
          var file = await driveUploadFile(folderId, filename, pdfBlob, { contentType: "application/pdf" });
          setDriveMsg("✓ " + file.name);
        } catch (e) {
          console.error("[Drive upload] invoice PDF failed", e);
          setDriveMsg("⚠ " + (e.message || String(e)));
        } finally { setDriveBusy(false); }
      })();
    }
  };

  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 20, overflowY: "auto" }} onClick={onClose}>
      <div onClick={function(e) { e.stopPropagation(); }} style={{ width: 780, maxWidth: "97vw", background: "#fff", borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden", marginBottom: 40 }}>
        <div style={{ padding: "10px 20px", background: "var(--color-bg-card-solid)", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>Invoice \u2014 PU# {invoice.puNumber || "\u2014"}</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {driveBusy && <span style={{ fontSize: 11, color: "#1A73E8", fontStyle: "italic" }}>↑ Drive…</span>}
            {!driveBusy && driveMsg && <span style={{ fontSize: 11, color: driveMsg.indexOf("⚠") === 0 ? "#B91C1C" : "#047857" }}>{driveMsg}</span>}
            <button onClick={printIt} style={{ padding: "6px 16px", borderRadius: 6, border: "none", background: "#1B3A4B", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.printPreview}</button>
            <button onClick={onClose} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 12 }}>{t.close}</button>
          </div>
        </div>
        <iframe title="invoice-preview" srcDoc={html} style={{ width: "100%", height: "78vh", border: "none", background: "#fff" }} />
      </div>
    </div>,
    document.body
  );
}

/* ======================== INVOICE FORM ======================== */
/* Shared payments-history block. Each row navigates to PaymentRegistry. */
function goToPayment(paymentId) {
  if (window.__gfiNavigate) window.__gfiNavigate("payments");
  setTimeout(function() {
    if (typeof window.__gfiPaymentOpen === "function") window.__gfiPaymentOpen(paymentId);
  }, 220);
}

/* 2026-10-20, Arturo: "los formatos de payments e invoice summary
   copialos exactamente igual de bill, fonts, espaciado, colores etc"
   — this is now a byte-for-byte match of BillRegistry.js's own
   PaymentsSection "bare" content (same fontSize 11/var(--color-text)
   everywhere, same hover tint, same 2px var(--color-accent) border on
   Current Balance, no custom orange) — always called FROM INSIDE a
   <SectionBox id="payments">, which already supplies the plate
   header, so this renders only the body content. */
function PaymentsSection({ payments, total, t }) {
  var paidSum = (payments || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
  var balance = total - paidSum;
  var hasPayments = (payments || []).length > 0;
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 11 }}>
        <span style={{ color: "var(--color-text)" }}>{t.originalTotal}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text)" }}>{fmtMoney(total)}</span>
      </div>
      {!hasPayments ? (
        <div style={{ padding: "10px 0", fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic", textAlign: "center" }}>{t.noPayments}</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginTop: 6 }}>
          <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{"PAY#"}</th>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{"Date"}</th>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.paymentMethodCol}</th>
            <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.paymentRef}</th>
            <th style={{ padding: "5px 6px", textAlign: "right", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{"Amount"}</th>
          </tr></thead>
          <tbody>{(payments || []).map(function(p, i) {
            return (
              <tr key={i}
                  onClick={function() { goToPayment(p.paymentId); }}
                  title={"Open " + (p.paymentId || "")}
                  style={{ borderBottom: "1px solid rgba(0,0,0,0.04)", cursor: "pointer", transition: "background 0.12s" }}
                  onMouseEnter={function(e) { e.currentTarget.style.background = "rgba(42,122,111,0.06)"; }}
                  onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}>
                <td style={{ padding: "6px", fontSize: 11, color: "var(--color-text)" }}>{p.paymentId || "—"}</td>
                <td style={{ padding: "6px", fontSize: 11, color: "var(--color-text)" }}>{fmtDate(p.date)}</td>
                <td style={{ padding: "6px", fontSize: 11, color: "var(--color-text)", textTransform: "capitalize" }}>{p.method || "—"}</td>
                <td style={{ padding: "6px", fontSize: 11, color: "var(--color-text)" }}>{p.reference || "—"}</td>
                <td style={{ padding: "6px", textAlign: "right", fontSize: 11, color: "var(--color-text)" }}>{fmtMoney(p.amount)}</td>
              </tr>
            );
          })}</tbody>
        </table>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 8, borderTop: "2px solid var(--color-accent)", fontSize: 11 }}>
        <span style={{ color: "var(--color-text)" }}>{t.currentBalance}</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text)" }}>{fmtMoney(balance)}</span>
      </div>
    </>
  );
}

/* Locks edit when an invoice has deposits applied. */
function EditLockDialog({ record, t, onClose }) {
  var payments = record.payments || [];
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div onClick={function(e) { e.stopPropagation(); }} style={{ background: "#fff", borderRadius: 10, maxWidth: 520, width: "100%", padding: 24, boxShadow: "0 16px 48px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 22 }}>{"\u{1F512}"}</span>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#B45309" }}>{t.editLockedTitle}</h3>
        </div>
        <p style={{ margin: "0 0 6px", fontSize: 13, color: "var(--color-text)", lineHeight: 1.5 }}>{t.editLockedMsg}</p>
        <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.editLockedHint}</p>
        <div style={{ borderRadius: 8, border: "1px solid var(--color-border)", overflow: "hidden", marginBottom: 16 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ background: "var(--color-bg)", borderBottom: "1px solid var(--color-border)" }}>
              <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>PAY#</th>
              <th style={{ padding: "6px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>Date</th>
              <th style={{ padding: "6px 10px", textAlign: "right", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" }}>Amount</th>
            </tr></thead>
            <tbody>{payments.map(function(p, i) {
              return (
                <tr key={i}
                    onClick={function() { goToPayment(p.paymentId); onClose(); }}
                    title={"Open " + (p.paymentId || "")}
                    style={{ borderBottom: "1px solid rgba(0,0,0,0.04)", cursor: "pointer", transition: "background 0.12s" }}
                    onMouseEnter={function(e) { e.currentTarget.style.background = "rgba(198,93,0,0.06)"; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}>
                  <td style={{ padding: "8px 10px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#c65d00" }}>{p.paymentId || "—"} →</td>
                  <td style={{ padding: "8px 10px", color: "var(--color-text-secondary)" }}>{fmtDate(p.date)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#059669" }}>{fmtMoney(p.amount)}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{t.close}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* 2026-10-20, Arturo: "todas las tarjetas excepto la placa deben ser
   expandibles" — same shared collapsible-section box BillRegistry.js
   already uses (duplicated here, not imported, per this file's own
   "don't share across registries" convention): one bordered
   rectangle, a clickable plate header (chevron rotates open/closed)
   and its content. Invoice Info is "la placa" — it stays a plain
   unified plate, never wrapped in this. Every other card (Fees &
   Deductions, Invoice Summary, Products, Pallet Allocations,
   Payments, and edit-mode's Fees/Credits/Transfers/Products) uses it.
   `open`/`toggle` are lifted to DetailPanel and passed down to
   InvoiceForm so the SAME sections, in the SAME open/closed state,
   survive the view↔edit toggle — "modo edit y modo no edit son
   copias". */
function SectionBox({ id, title, open, toggle, headerExtra, children, style }) {
  var isOpen = !!(open && open[id]);
  return (
    <div style={{ marginBottom: 0, ...(style && style.outerMargin ? style.outerMargin : {}) }}>
      <div
        role="button"
        tabIndex={0}
        onClick={function () { toggle(id); }}
        onKeyDown={function (e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(id); } }}
        style={{ ...sectionHeaderStyle, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
      >
        <span style={{ fontSize: 10, color: "#000000", textShadow: sectionHeaderStyle.textShadow, transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s", display: "inline-block", width: 10 }}>{"▶"}</span>
        <span style={{ flex: 1 }}>{title}</span>
        {headerExtra}
      </div>
      {/* 2026-10-21, Arturo: "los fondos de las tarjetas... deberian
          tener un color gris claro como bills" — same literal #f7f8fa
          BillRegistry.js's own SectionBox uses, not var(--color-bg-card)
          (which resolves to plain white, not the light gray look). */}
      {isOpen && <div style={{ padding: "0 14px 14px", background: "#f7f8fa", border: "1px solid var(--color-border)", borderTop: "none", ...(style || {}) }}>{children}</div>}
    </div>
  );
}

/* 2026-10-20, Arturo: "client rejections y client responses se agregan
   a una sección llamada invoice activity que vive en el mismo lugar
   que lot activity en bills... debe marcar con un dot rojo" — same
   dropdown-button shell as BillRegistry.js's LotActivityButton (a
   plate button that opens a portaled panel anchored under it, red
   dot when there's something to show), but this is the INVOICE's own
   editable source for this data (Bill's version is read-only, since
   a Bill never owns rejections/protectionResponses — it only mirrors
   them). Takes children instead of being hardcoded to a report
   component so the SAME shell can host the full editable cards in
   DetailPanel (view mode) and a read-only mirror in InvoiceForm (edit
   mode) — see each call site below. */
function InvoiceActivityButton({ hasActivity, lang, t, style, children }) {
  var [open, setOpen] = useState(false);
  var [pos, setPos] = useState(null);
  var btnRef = useRef(null);

  useEffect(function () {
    if (!open) return;
    var onDown = function (e) {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest("[data-invoice-activity-panel]")) return;
      setOpen(false);
    };
    var onKey = function (e) { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return function () {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  var toggleOpen = function () {
    if (!open && btnRef.current) {
      var r = btnRef.current.getBoundingClientRect();
      var panelW = 380;
      setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.right - panelW, window.innerWidth - panelW - 8)) });
    }
    setOpen(function (v) { return !v; });
  };

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        onClick={toggleOpen}
        title={t.invoiceActivity}
        style={{ position: "relative", padding: "5px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: open ? "var(--color-accent)" : "#fff", color: open ? "#fff" : "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap", ...(style || {}) }}
      >
        {"📋"} {t.invoiceActivity}
        {hasActivity && <span style={{ position: "absolute", top: -3, right: -3, width: 8, height: 8, borderRadius: "50%", background: "#DC2626", border: "1.5px solid #fff" }} />}
      </button>
      {open && pos && createPortal(
        /* 2026-10-22, Arturo: (1) "no me aparece nada dentro de invoyce
           activity para agregar, creo que no se agrego la seccion" —
           real root cause: this panel used to show `children` (which
           has the "+ Agregar rechazo" button) ONLY when hasActivity was
           already true — a brand-new invoice with zero rejections/
           responses could never reach the button to add its FIRST one,
           always landing on "Sin actividad registrada" instead. Always
           rendering `children` now — each sub-section already handles
           its own empty state (an "Add" affordance, not a dead end).
           (2) "esa ventana que se abre no tiene el formato placa
           metalica" — same brushed-steel recipe as the other popovers
           in this file (ClientReportedDataButton/SupportingDocsButton). */
        <div data-invoice-activity-panel style={{ position: "fixed", top: pos.top, left: pos.left, width: 380, maxHeight: "70vh", overflowY: "auto", background: "linear-gradient(180deg, #f2f3f4 0%, #dcdfe2 100%)", borderRadius: 0, border: "1px solid #7d838c", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 12px 32px rgba(0,0,0,0.3), 0 2px 6px rgba(0,0,0,0.15)", zIndex: 10060, padding: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #9aa0a8" }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{t.invoiceActivity}</span>
            <button type="button" onClick={function () { setOpen(false); }} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#1a2636", fontSize: 14, lineHeight: 1, fontWeight: 700 }}>{"×"}</button>
          </div>
          {children}
        </div>,
        document.body
      )}
    </>
  );
}

/* 2026-10-21, Arturo: "client report data agregalo a la parte alta
   al lado de open linked bill, pero sin placa mismo nombre al hacer
   click se abre una ventana que muestra la informacion de la
   solicitud de proteccion con boton editar" — same portaled-popover
   shell as InvoiceActivityButton, generalized with a `label` prop
   (that one hardcodes t.invoiceActivity) since this trigger needs
   its own text. "sin placa" — panel background is plain white, no
   colored plate box like the old inline version had. Read-only by
   default with an Edit toggle inside (`editable`/`onToggleEdit`),
   same read-vs-edit split DetailPanel uses elsewhere in this file.
*/
function ClientReportedDataButton({ label, style, children }) {
  var [open, setOpen] = useState(false);
  var [pos, setPos] = useState(null);
  var btnRef = useRef(null);

  useEffect(function () {
    if (!open) return;
    var onDown = function (e) {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest("[data-client-reported-panel]")) return;
      // A nested SearchSelect renders its own option list in a separate
      // document.body portal ([data-searchselect]), outside this panel's
      // DOM subtree — without this check, picking a defect option was
      // treated as an outside click and closed the whole panel before
      // onChange could register (2026-10-21, Arturo: reported live).
      if (e.target.closest && e.target.closest("[data-searchselect]")) return;
      setOpen(false);
    };
    var onKey = function (e) { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return function () {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  var toggleOpen = function () {
    if (!open && btnRef.current) {
      var r = btnRef.current.getBoundingClientRect();
      var panelW = 420;
      setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.right - panelW, window.innerWidth - panelW - 8)) });
    }
    setOpen(function (v) { return !v; });
  };

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        onClick={toggleOpen}
        title={label}
        style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: open ? "var(--color-accent)" : "#fff", color: open ? "#fff" : "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", ...(style || {}) }}
      >
        {label}
      </button>
      {open && pos && createPortal(
        /* 2026-10-21, Arturo: "la ventana que se abre debe ser en
           formato placa metalica" — same brushed-steel plate recipe as
           this file's own card/section panels (plateActionBtnStyle's
           silver gradient + square corners), not a plain white card. */
        <div data-client-reported-panel style={{ position: "fixed", top: pos.top, left: pos.left, width: 420, maxHeight: "70vh", overflowY: "auto", background: "linear-gradient(180deg, #f2f3f4 0%, #dcdfe2 100%)", borderRadius: 0, border: "1px solid #7d838c", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 12px 32px rgba(0,0,0,0.3), 0 2px 6px rgba(0,0,0,0.15)", zIndex: 10060, padding: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #9aa0a8" }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{label}</span>
            <button type="button" onClick={function () { setOpen(false); }} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#1a2636", fontSize: 14, lineHeight: 1, fontWeight: 700 }}>{"×"}</button>
          </div>
          {children}
        </div>,
        document.body
      )}
    </>
  );
}

/* 2026-10-21, Arturo: "remueve toda esta leyenda, no es necesaria,
   encapsulala con la palabra more info y al dar click se muestra en
   una ventana independiente" — the inline pickup-hint paragraph (ROW
   2.5, next to Pickup Date/Time/Temp Recorder) used to sit in the
   grid taking up its own column at all times; now it's a small
   "More info" link that only reveals the text in a popover on click,
   same portaled-popover shell as ClientReportedDataButton but
   lighter (plain text content, no header/children prop needed). */
function InfoHintButton({ lang, text }) {
  var [open, setOpen] = useState(false);
  var [pos, setPos] = useState(null);
  var btnRef = useRef(null);

  useEffect(function () {
    if (!open) return;
    var onDown = function (e) {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest("[data-info-hint-panel]")) return;
      setOpen(false);
    };
    var onKey = function (e) { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return function () {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  var toggleOpen = function () {
    if (!open && btnRef.current) {
      var r = btnRef.current.getBoundingClientRect();
      var panelW = 320;
      setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - panelW - 8)) });
    }
    setOpen(function (v) { return !v; });
  };

  return (
    <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 8 }}>
      <button
        type="button"
        ref={btnRef}
        onClick={toggleOpen}
        style={{ padding: 0, border: "none", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600, textDecoration: "underline", whiteSpace: "nowrap" }}
      >
        {"ⓘ "}{lang === "es" ? "Más info" : "More info"}
      </button>
      {open && pos && createPortal(
        <div data-info-hint-panel style={{ position: "fixed", top: pos.top, left: pos.left, width: 320, background: "#fff", borderRadius: 10, border: "1px solid var(--color-border)", boxShadow: "0 12px 32px rgba(0,0,0,0.22), 0 2px 6px rgba(0,0,0,0.08)", zIndex: 10060, padding: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid var(--color-border)" }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)" }}>{lang === "es" ? "Información" : "Info"}</span>
            <button type="button" onClick={function () { setOpen(false); }} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-secondary)", fontSize: 14, lineHeight: 1 }}>{"×"}</button>
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--color-text-secondary)" }}>{text}</div>
        </div>,
        document.body
      )}
    </div>
  );
}

/* 2026-10-21, Arturo: "aqui aplica la misma logica de fees de bills...
   los demas se agregan mediante boton add fee pero se abre en una
   ventana de captura y popula en siguiente renglon" — same capture-
   window pattern as BillRegistry.js's FeeEditDialog, duplicated per
   this file (never a shared import, same convention as every other
   Registry pair). Invoice's own transfer concept is "transfer to
   Bill" (transferToBill/transferTargetBillId), not Bill's "transfer
   to Invoice" — the one real field-level difference from the source
   dialog; there's no PT/passThrough concept on an Invoice's own fees
   either (that's a Bill-only concern), so that half of Bill's dialog
   is dropped here. */
function InvoiceFeeEditDialog({ fee, feeTypes, availableBills, lot, lang, t, onSave, onClose }) {
  var isNew = !fee;
  var [draft, setDraft] = useState(function () { return fee ? { ...fee } : { feeType: "", feeCustom: "", qty: 1, rate: "", amount: 0 }; });
  var setField = function (field, value) {
    setDraft(function (prev) {
      var updated = { ...prev, [field]: value };
      if (field === "qty" || field === "rate") {
        updated.amount = ((parseFloat(updated.qty) || 1) * (parseFloat(updated.rate) || 0)).toFixed(2);
      }
      if (field === "transferToBill" && value) {
        var match = availableBills.find(function (b) { return b.lot === lot; });
        if (match) updated.transferTargetBillId = match.id;
      }
      return updated;
    });
  };
  var canSave = draft.feeType && draft.feeType !== "" && (draft.feeType !== "__custom__" || String(draft.feeCustom || "").trim());
  return (
    <DraggableModal title={isNew ? (lang === "es" ? "Agregar Fee" : "Add Fee") : (lang === "es" ? "Editar Fee" : "Edit Fee")} onClose={onClose} compact compactWidth={460} compactHeight={draft.transferToBill ? 440 : 360} zIndex={10006} titleBarVariant="groundfresh">
      <div style={{ overflow: "auto", flex: 1, ...readOnlyPlateBodyStyle, borderTop: "none" }}>
        <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <div style={plateFieldLabel}>{t.feeType}</div>
            <SearchSelect value={draft.feeType || ""} onChange={function (v) { setField("feeType", v); }} placeholder={t.selectFee} options={[].concat(feeTypes.filter(function (ft) { return ft !== "Advance Financing Fee"; }).map(function (ft) { return { value: ft, label: ft }; }), [{ value: "__custom__", label: t.customFee }])} style={filterFieldPlate} />
            {draft.feeType === "__custom__" && <input value={draft.feeCustom || ""} onChange={function (e) { setField("feeCustom", e.target.value); }} placeholder={lang === "es" ? "Nombre del gasto" : "Fee name"} style={{ ...inp, ...filterFieldPlate, marginTop: 5 }} />}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <div style={plateFieldLabel}>{t.feeQty}</div>
              <input type="number" min="1" value={draft.qty} onChange={function (e) { setField("qty", e.target.value); }} style={{ ...inp, ...filterFieldPlate, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }} />
            </div>
            <div>
              <div style={plateFieldLabel}>{t.feeRate}</div>
              <input type="number" min="0" step="0.01" value={draft.rate} onChange={function (e) { setField("rate", e.target.value); }} style={{ ...inp, ...filterFieldPlate, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <div style={plateFieldLabel}>{t.feeAmount}</div>
              <input type="number" min="0" step="0.01" value={draft.amount} onChange={function (e) { setField("amount", e.target.value); }} style={{ ...inp, ...filterFieldPlate, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
            </div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#1a2636", cursor: "pointer" }}>
            <input type="checkbox" checked={!!draft.transferToBill} onChange={function (e) { setField("transferToBill", e.target.checked); }} style={{ accentColor: "#c65d00" }} />
            {lang === "es" ? "Traspasar al Bill del embarque" : "Transfer to shipment Bill"}
          </label>
          {draft.transferToBill && (
            <div>
              <div style={plateFieldLabel}>Bill</div>
              <SearchSelect value={draft.transferTargetBillId || ""} onChange={function (v) { setField("transferTargetBillId", v); }} placeholder="Bill" style={filterFieldPlate} options={[].concat(availableBills.filter(function (b) { return b.lot === lot; }).map(function (b) { return { value: b.id, label: "LOT " + b.lot + " " + (b.shipperName || "") }; }), availableBills.filter(function (b) { return b.lot !== lot; }).map(function (b) { return { value: b.id, label: "LOT " + (b.lot || "?") + " " + (b.shipperName || "") }; }))} />
            </div>
          )}
        </div>
        <div style={{ padding: "8px 12px 10px", borderTop: "1px solid #7d838c", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0 }}>
          <button onClick={onClose} style={plateActionBtnStyle}>{t.cancel}</button>
          <button disabled={!canSave} onClick={function () { onSave(draft); }} style={canSave ? plateActionBtnStyle : { ...plateActionBtnStyle, opacity: 0.5, cursor: "not-allowed" }}>{isNew ? t.addFee : t.save}</button>
        </div>
      </div>
    </DraggableModal>
  );
}

/* 2026-10-21, Arturo: "tambien para agregar credit o transfer debe
   ser mediante ventanas emergentes donde se captura la info y popula
   el renglon correspondiente" — same capture-window pattern as
   InvoiceFeeEditDialog, duplicated per this file's own convention,
   for Credits. Fields mirror BillRegistry.js's CreditEditDialog
   wherever the concept overlaps (Fee Type/Amount), but an Invoice
   credit's own reference target is another client INVOICE
   (refInvoiceId/refPuNumber), not another bill/lot like Bill's
   credits — kept as the existing free-text-OR-picker shape already
   used inline before this change. */
function InvoiceCreditEditDialog({ credit, clientInvoices, lang, t, onSave, onClose }) {
  var isNew = !credit;
  var [draft, setDraft] = useState(function () { return credit ? { ...credit } : { type: "free", concept: "", refInvoiceId: "", refPuNumber: "", amount: "" }; });
  var setField = function (field, value) { setDraft(function (prev) { return { ...prev, [field]: value }; }); };
  var canSave = parseFloat(draft.amount) > 0 && (draft.type === "fromInvoice" ? (draft.refInvoiceId || String(draft.refPuNumber || "").trim()) : String(draft.concept || "").trim());
  return (
    <DraggableModal title={isNew ? t.addCredit : (lang === "es" ? "Editar Crédito" : "Edit Credit")} onClose={onClose} compact compactWidth={460} compactHeight={draft.type === "fromInvoice" ? 360 : 300} zIndex={10006} titleBarVariant="groundfresh">
      <div style={{ overflow: "auto", flex: 1, ...readOnlyPlateBodyStyle, borderTop: "none" }}>
        <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <div style={plateFieldLabel}>{t.creditType}</div>
            <SearchSelect value={draft.type || "free"} onChange={function (v) { setField("type", v); }} options={[{ value: "free", label: t.creditFree }, { value: "fromInvoice", label: t.creditFromInvoice }]} style={filterFieldPlate} />
          </div>
          {draft.type === "fromInvoice" ? (
            <div>
              <div style={plateFieldLabel}>{t.creditRefInvoice}</div>
              <SearchSelect value={draft.refInvoiceId || ""} onChange={function (v) {
                var pickedInv = clientInvoices.find(function (inv) { return String(inv.id) === String(v); });
                setDraft(function (prev) { return { ...prev, refInvoiceId: v, refPuNumber: pickedInv ? (pickedInv.puNumber || "") : prev.refPuNumber }; });
              }} placeholder={lang === "es" ? "Selecciona factura del cliente…" : "Select client invoice…"} options={clientInvoices.map(function (inv) { return { value: inv.id, label: "PU# " + (inv.puNumber || inv.id) + " · " + (inv.lot ? "LOT " + inv.lot : "—") }; })} style={filterFieldPlate} />
              <input value={draft.refPuNumber || ""} onChange={function (e) { setField("refPuNumber", e.target.value); }} placeholder={lang === "es" ? "o teclea PU#" : "or type PU#"} style={{ ...inp, ...filterFieldPlate, marginTop: 5, fontFamily: "'JetBrains Mono', monospace" }} />
            </div>
          ) : (
            <div>
              <div style={plateFieldLabel}>{t.creditConcept}</div>
              <input value={draft.concept || ""} onChange={function (e) { setField("concept", e.target.value); }} placeholder={lang === "es" ? "Concepto del crédito" : "Credit concept"} style={{ ...inp, ...filterFieldPlate }} />
            </div>
          )}
          <div>
            <div style={plateFieldLabel}>{t.creditAmount}</div>
            <input type="number" min="0" step="0.01" value={draft.amount || ""} onChange={function (e) { setField("amount", e.target.value); }} style={{ ...inp, ...filterFieldPlate, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
          </div>
        </div>
        <div style={{ padding: "8px 12px 10px", borderTop: "1px solid #7d838c", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0 }}>
          <button onClick={onClose} style={plateActionBtnStyle}>{t.cancel}</button>
          <button disabled={!canSave} onClick={function () { onSave(draft); }} style={canSave ? plateActionBtnStyle : { ...plateActionBtnStyle, opacity: 0.5, cursor: "not-allowed" }}>{isNew ? t.addCredit : t.save}</button>
        </div>
      </div>
    </DraggableModal>
  );
}

/* 2026-10-21, Arturo: same capture-window ask, for Balance Transfers —
   replaces the inline-expanding BalanceTransferCreatorInv panel with a
   DraggableModal popup. No "edit" concept here (same as before this
   change — a transfer is bidirectional/mirrored on the other invoice
   on create, see createBalanceTransfer, so only create/remove ever
   existed, never edit). */
function InvoiceTransferCreateDialog({ sameClientInvoices, suggestedAmount, lang, t, onCreate, onClose }) {
  var [direction, setDirection] = useState("send");
  var [otherInvoiceId, setOtherInvoiceId] = useState("");
  var [amount, setAmount] = useState(suggestedAmount || "");
  var canSave = otherInvoiceId && parseFloat(amount) > 0;
  return (
    <DraggableModal title={t.btCreate} onClose={onClose} compact compactWidth={460} compactHeight={300} zIndex={10006} titleBarVariant="groundfresh">
      <div style={{ overflow: "auto", flex: 1, ...readOnlyPlateBodyStyle, borderTop: "none" }}>
        <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <div style={plateFieldLabel}>{t.btType}</div>
            <SearchSelect value={direction} onChange={setDirection} options={[{ value: "send", label: t.btSend }, { value: "receive", label: t.btReceive }]} style={filterFieldPlate} />
          </div>
          <div>
            <div style={plateFieldLabel}>{t.btOtherInvoice}</div>
            <SearchSelect value={otherInvoiceId} onChange={setOtherInvoiceId} placeholder={t.btOtherInvoice} options={sameClientInvoices.map(function (inv) { return { value: inv.id, label: "PU# " + (inv.puNumber || inv.id) + " · " + (inv.lot ? "LOT " + inv.lot : "—") }; })} style={filterFieldPlate} />
          </div>
          <div>
            <div style={plateFieldLabel}>{t.btAmount}</div>
            <input type="number" min="0" step="0.01" value={amount} onChange={function (e) { setAmount(e.target.value); }} style={{ ...inp, ...filterFieldPlate, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
          </div>
        </div>
        <div style={{ padding: "8px 12px 10px", borderTop: "1px solid #7d838c", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0 }}>
          <button onClick={onClose} style={plateActionBtnStyle}>{t.btCancel}</button>
          <button disabled={!canSave} onClick={function () { onCreate(direction, otherInvoiceId, amount); onClose(); }} style={canSave ? plateActionBtnStyle : { ...plateActionBtnStyle, opacity: 0.5, cursor: "not-allowed" }}>{t.btCreate}</button>
        </div>
      </div>
    </DraggableModal>
  );
}

/* 2026-10-21, Arturo: "los botones add de products y pallet
   allocations quiero remover su placa dejar solo la palabra y
   meterla en los headers de su tarjeta, al clickear abrir la fuente
   en una ventana alterna para escoger, no un drag... en products
   debe llamarse Add from lot" — picks ONE whole shipment lot (same
   addFromShipment behavior as before: every line + every pallet from
   that lot's manifest gets added at once, nothing new there) — this
   is just that same picker moved from an always-visible inline row
   into a popup, triggered from the card header instead. */
function AddFromLotModal({ availableLots, t, lang, onAdd, onClose }) {
  var [lotId, setLotId] = useState("");
  return (
    <DraggableModal title={lang === "es" ? "Agregar de Lote" : "Add from Lot"} onClose={onClose} compact compactWidth={460} compactHeight={260} zIndex={10006} titleBarVariant="groundfresh">
      <div style={{ overflow: "auto", flex: 1, ...readOnlyPlateBodyStyle, borderTop: "none" }}>
        <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <div style={plateFieldLabel}>{t.selectLot}</div>
            <SearchSelect
              value={lotId}
              onChange={setLotId}
              options={availableLots.map(function (s) { return { value: String(s.id), label: "LOT " + s.lot + " — " + (s.shipperName || "?") + " · " + (s.dateArrival || s.dateDeparture || "") }; })}
              placeholder={t.selectLot}
              style={filterFieldPlate}
              limit={10}
            />
            {/* 2026-10-21, Arturo: "la ventana que abra add from lot
                debe mostrar esta leyenda" — same hint the inline
                picker always showed, carried over verbatim. */}
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 5 }}>{lang === "es" ? "Solo embarques con fecha ≤ fecha de factura. Escribe el lot si no aparece en los primeros 10." : "Only shipments dated on/before invoice date. Type the lot if older than the top 10."}</div>
          </div>
        </div>
        <div style={{ padding: "8px 12px 10px", borderTop: "1px solid #7d838c", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0 }}>
          <button onClick={onClose} style={plateActionBtnStyle}>{t.cancel}</button>
          <button disabled={!lotId} onClick={function () { onAdd(lotId); onClose(); }} style={lotId ? plateActionBtnStyle : { ...plateActionBtnStyle, opacity: 0.5, cursor: "not-allowed" }}>{lang === "es" ? "Agregar" : "Add"}</button>
        </div>
      </div>
    </DraggableModal>
  );
}

/* "en pallet allotations debe ser Add Pallet, en la ventana debe
   mostrarme todos los pallets disponibles de los lotes que pre-
   existan en products con la opcion de seleccionar uno por uno o
   agregar todos, siempre con la opcion de buscar manualmente algun
   lote" — SearchSelect's own `multi` mode already gives both pieces
   at once: checkbox-style one-by-one selection AND substring search
   across every option's "LOT # · Plt # · product · cases" label
   (typing a lot number IS the manual lot search), so no separate
   search field was needed — "Add All" is the one addition on top of
   that for the "agregar todos" half. */
function AddPalletModal({ options, t, lang, onAddMany, onClose }) {
  var [selected, setSelected] = useState([]);
  return (
    <DraggableModal title={lang === "es" ? "Agregar Pallets" : "Add Pallets"} onClose={onClose} compact compactWidth={460} compactHeight={340} zIndex={10006} titleBarVariant="groundfresh">
      <div style={{ overflow: "auto", flex: 1, ...readOnlyPlateBodyStyle, borderTop: "none" }}>
        <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={plateFieldLabel}>{lang === "es" ? "Pallets disponibles" : "Available pallets"}</div>
            <button type="button" onClick={function () { setSelected(options.map(function (o) { return o.value; })); }} style={{ padding: 0, border: "none", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 10, fontWeight: 600, textDecoration: "underline" }}>{lang === "es" ? "Seleccionar todos" : "Select all"}</button>
          </div>
          {options.length === 0 ? (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.noPalletsToAdd}</div>
          ) : (
            <SearchSelect multi value={selected} onChange={setSelected} options={options} placeholder={t.addFromOtherLot} style={filterFieldPlate} />
          )}
        </div>
        <div style={{ padding: "8px 12px 10px", borderTop: "1px solid #7d838c", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0 }}>
          <button onClick={onClose} style={plateActionBtnStyle}>{t.cancel}</button>
          <button disabled={selected.length === 0} onClick={function () { onAddMany(selected); onClose(); }} style={selected.length > 0 ? plateActionBtnStyle : { ...plateActionBtnStyle, opacity: 0.5, cursor: "not-allowed" }}>{lang === "es" ? "Agregar" : "Add"}{selected.length > 0 ? " (" + selected.length + ")" : ""}</button>
        </div>
      </div>
    </DraggableModal>
  );
}

/* 2026-10-21, Arturo: "loading info agregale dos botones, add y edit
   para que se capture esa info a traves de una ventana y popule en
   modo plano sin campos editables" — Pickup Date/Pickup Time/Temp
   Recorder move from always-editable inline inputs to this capture
   window, same pattern as Fees/Credits/Transfers above; the plate
   itself goes back to a flat read-only display (readOnlyPlateValueStyle,
   same recipe as view mode) once this has data. */
function LoadingInfoEditDialog({ pickupDate, pickupTime, thermograph, lang, t, onSave, onClose }) {
  var [draft, setDraft] = useState({ pickupDate: pickupDate || "", pickupTime: pickupTime || "", thermograph: thermograph || "" });
  var setField = function (field, value) { setDraft(function (prev) { return { ...prev, [field]: value }; }); };
  return (
    <DraggableModal title={lang === "es" ? "Información de Carga" : "Loading Info"} onClose={onClose} compact compactWidth={460} compactHeight={320} zIndex={10006} titleBarVariant="groundfresh">
      <div style={{ overflow: "auto", flex: 1, ...readOnlyPlateBodyStyle, borderTop: "none" }}>
        <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <div style={plateFieldLabel}>{t.pickupDate}</div>
            <input type="date" value={draft.pickupDate} onChange={function (e) { setField("pickupDate", e.target.value); }} style={{ ...inp, ...filterFieldPlate }} />
          </div>
          <div>
            <div style={plateFieldLabel}>{t.pickupTime}</div>
            <input type="time" value={draft.pickupTime} onChange={function (e) { setField("pickupTime", e.target.value); }} style={{ ...inp, ...filterFieldPlate }} />
          </div>
          <div>
            <div style={plateFieldLabel}>{t.thermograph}</div>
            <input value={draft.thermograph} onChange={function (e) { setField("thermograph", e.target.value.toUpperCase()); }} style={{ ...inp, ...filterFieldPlate, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }} />
          </div>
          <div style={{ fontSize: 10, color: "#1a2636", opacity: 0.75 }}>{t.pickupHint}</div>
        </div>
        <div style={{ padding: "8px 12px 10px", borderTop: "1px solid #7d838c", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0 }}>
          <button onClick={onClose} style={plateActionBtnStyle}>{t.cancel}</button>
          <button onClick={function () { onSave(draft); onClose(); }} style={plateActionBtnStyle}>{t.save}</button>
        </div>
      </div>
    </DraggableModal>
  );
}

/* Unified view+edit modal — DraggableModal at center per CLAUDE.md "REGLA DE
   VENTANAS". View mode shows the read-only summary; clicking Edit toggles to
   edit mode inside the SAME modal (renders InvoiceForm with embedded=true).
   Save returns to view mode without closing. */
function DetailPanel({ invoice, invoices, t, lang, onSave, onDelete, onEditLock, onClose, onPrint, onPrintDoc }) {
  var [mode, setMode] = useState("view");
  /* 2026-11-02, Arturo: "tambien el bill y las facturas debe cambiarse
     el status desde el modo no edicion" — same clickable status pill
     + dropdown as Shipment's/Bill's view-mode DetailPanel, so
     draft/sent/paid can be flipped without opening the full edit form
     (InvoiceForm). */
  var [statusMenuOpen, setStatusMenuOpen] = useState(false);
  var [driveBusy, setDriveBusy] = useState(false);
  var [driveResult, setDriveResult] = useState(null);
  var [emailDialogOpen, setEmailDialogOpen] = useState(false);
  /* Which EmailDialog kindOptions radio to preselect when it opens —
     "inbound" for the normal Email Customer action, "pendingPickupReminder"
     when opened from the dedicated reminder action below (2026-09-12,
     Arturo: nudge the client when a PU is still Pending Pick Up). */
  var [emailDialogDefaultKind, setEmailDialogDefaultKind] = useState("inbound");
  /* For the Pending Pick Up / Pending Delivery fallback used to gate
     the reminder action below (same pattern as InvoiceForm's own
     slPassingIndex a bit further down this file — DetailPanel didn't
     need this before, it only reads/displays already-computed status,
     never had to resolve the pending fallback itself). */
  var [slPassingIndex, setSlPassingIndex] = useState({});
  useEffect(function () {
    var cancelled = false;
    loadFromSupabase("sweetleaf_invoices").then(function (sl) {
      if (!cancelled) setSlPassingIndex(buildSlPassingIndex(sl));
    }).catch(function () {});
    return function () { cancelled = true; };
  }, []);
  /* Fee name → category, for the Summary box's per-category breakdown
     (see FeeBreakdownLinesInv) — "invoice summary debe verse asi"
     (Bill Summary screenshot). Same fetch as BillRegistry.js's own
     DetailPanel, passed down to InvoiceForm when embedded here. */
  var [feeCategoryMap, setFeeCategoryMap] = useState({});
  useEffect(function () {
    var cancelled = false;
    loadFromSupabase("fee_catalog").then(function (rows) {
      if (cancelled) return;
      var map = {};
      (rows || []).forEach(function (r) { if (r && r.name) map[String(r.name).trim().toLowerCase()] = r.category || "other"; });
      setFeeCategoryMap(map);
    }).catch(function () {});
    return function () { cancelled = true; };
  }, []);
  /* 2026-10-21, Arturo: "porque no agregamos un add payment y que me
     mande al modulo de pagos con esa factura en ventana abierta para
     aplicar un pago por si algun dia utilizo esa funcion" — confirmed
     "option a" after being shown the real tradeoff: PaymentRegistry.js
     has zero "create new payment" UI (it only views/edits payments
     that already exist), so rather than build that from scratch,
     reuses the SAME BatchDepositModal already proven by "Record
     Deposit" in the Invoices list — opened right here, pre-selected
     with just this one invoice, same real write-to-Supabase/update-
     invoice effect. View mode only (not edit mode): applying a
     payment writes the payment record immediately, same as every
     other "Record Deposit" entry point in the app — doing that from
     inside an in-progress, unsaved edit would leave the new payment
     record orphaned from this invoice's own payments/amountPaid/
     status if the edit were then cancelled instead of saved. */
  var [addPaymentModalOpen, setAddPaymentModalOpen] = useState(false);
  var applyPaymentToInvoice = async function (payload) {
    var allPayments = loadLS("gfi-payments", []);
    var newPayId = nextPaymentId(allPayments);
    var alloc = (payload.allocations || [])[0];
    if (!alloc) return;
    var clientName = resolveClientName(fresh.clientId) || fresh.clientName || "";
    var paymentRecord = {
      id: newPayId,
      paymentDate: payload.paymentDate,
      type: "inbound",
      paymentMethod: payload.paymentMethod,
      reference: payload.reference,
      notes: payload.notes,
      allocations: [{ invoiceId: fresh.id, puNumber: fresh.puNumber, lot: fresh.lot, clientName: clientName, amount: alloc.amount, concept: alloc.concept || "" }],
      shipperName: clientName,
      createdAt: new Date().toISOString(),
    };
    var existingPays = (fresh.payments || []).filter(function (p) { return p.paymentId !== newPayId; });
    var newPayments = [...existingPays, { paymentId: newPayId, date: payload.paymentDate, amount: alloc.amount, method: payload.paymentMethod, reference: payload.reference, concept: alloc.concept || "" }];
    var totalPaid = newPayments.reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
    var totalDue = calcInvoiceTotal(fresh);
    var due = totalDue - totalPaid;
    var newStatus = fresh.status;
    if (fresh.status !== "draft" && due <= 0.01 && totalPaid > 0) newStatus = "paid";
    try { await saveToSupabase("payments", paymentRecord); } catch (e) { /* swallow, same as handleBatchDepositConfirm */ }
    saveLS("gfi-payments", [...allPayments, paymentRecord]);
    onSave(Object.assign({}, fresh, { payments: newPayments, amountPaid: +totalPaid.toFixed(2), status: newStatus }));
    setAddPaymentModalOpen(false);
  };
  var [emailWarehouseOpen, setEmailWarehouseOpen] = useState(false);
  /* Email Shipper — Protection (2026-09-14, Arturo): lets a user attach
     whatever quality docs/photos the client sent (PDF/JPG/PNG/Word/
     Excel, any number of files) directly inside the send window.
     ORIGINALLY kept in memory only for one send — Arturo hit this the
     same day: "los archivos que se adjuntan al email deben quedarse
     almacenados porque si intento enviarlo nuevamente desaparece"
     (every dialog-open reset the list to [], so reopening after a
     failed send — or just to double check something — silently threw
     away whatever was already picked from disk). Now persisted to
     IndexedDB (gfiDB.js, same store the rest of the app uses for
     document blobs) under key `protectionFilesKeyFor(invoice.id)`,
     loaded back whenever the dialog opens instead of reset to []. */
  var [emailShipperProtectionOpen, setEmailShipperProtectionOpen] = useState(false);
  var [emailShipperUpdateOpen, setEmailShipperUpdateOpen] = useState(false);
  var [rejectionPreviewIdx, setRejectionPreviewIdx] = useState(null);
  var [rejectionSendIdx, setRejectionSendIdx] = useState(null);
  /* 2026-10-22, Arturo: "no me aparece nada dentro de invoice activity
     para agregar, creo que no se agrego la seccion" — the section WAS
     there, but it starts collapsed and the "+ Add rejection" button
     only renders once expanded, with no visual hint that the "Client
     Rejection — 0 cases ▼" row itself is the toggle. Starts OPEN when
     there are no rejections captured yet (nothing to hide, and the
     Add button needs to be reachable on first use); still starts
     collapsed once real rejections exist, preserving the original
     "don't clutter with old entries" intent. */
  var [rejectionDetailOpen, setRejectionDetailOpen] = useState(function () {
    return !(invoice && invoice.rejections && invoice.rejections.length > 0);
  });
  var [rejectionEditIdx, setRejectionEditIdx] = useState(null);
  var [rejectionDraftCases, setRejectionDraftCases] = useState("");
  var [rejectionDraftNotes, setRejectionDraftNotes] = useState("");
  var [rejectionDraftClientAt, setRejectionDraftClientAt] = useState("");
  var [rejectionFiles, setRejectionFiles] = useState([]);
  /* "Client Responses — Protection" (2026-10-07, Arturo: "puede tener
     varias respuestas asi que colapsalas... algo similar a como
     reporto los rechazos" + "el boton de enviar al shipper en cada
     respuesta justo como los rechazos" + "colapsa la respuesta que
     sea solo visible si la abro"). Same shape as the rejection card:
     outer list collapsed, each response ALSO collapsed to a one-line
     summary (respOpenIdx tracks which single row, if any, is
     expanded), edit state separate from the open/closed state, and
     a per-response send dialog (responseSendIdx). */
  /* 2026-10-19, Arturo: "las facturas, a la hora de asignar defectos a
     la fruta, queda una tarjeta mostrando los defectos y el mensaje
     del cliente siempre visible, necesito se colapsable" — same
     collapsed-by-default-with-toggle pattern as the Rejection/Response
     cards right above; starts OPEN since it's the first thing worth
     seeing on a Protection/PAS invoice, but can be collapsed. */
  /* 2026-10-20, Arturo: "los defects found deben verse colapsados por
     default, yo los abro manualmente" — starts closed, same chevron
     toggle as before. */
  var [defectsFoundOpen, setDefectsFoundOpen] = useState(false);
  var [respListOpen, setRespListOpen] = useState(false);
  var [respOpenIdx, setRespOpenIdx] = useState(null);
  var [respEditIdx, setRespEditIdx] = useState(null);
  var [respDraftStatus, setRespDraftStatus] = useState("");
  var [respDraftNote, setRespDraftNote] = useState("");
  var [respDraftDate, setRespDraftDate] = useState("");
  var [respDraftTime, setRespDraftTime] = useState("");
  var [responseSendIdx, setResponseSendIdx] = useState(null);
  var [responseQualityPreviewOpen, setResponseQualityPreviewOpen] = useState(false);
  var REJECTION_ALLOWED_EXT = ["pdf", "jpg", "jpeg", "png", "gif", "webp", "doc", "docx", "xls", "xlsx"];
  var handleAddRejectionFiles = function (fileList) {
    var added = Array.prototype.slice.call(fileList || []).filter(function (f) {
      var ext = (f.name.split(".").pop() || "").toLowerCase();
      return REJECTION_ALLOWED_EXT.indexOf(ext) >= 0;
    });
    if (added.length) setRejectionFiles(function (prev) { return prev.concat(added); });
  };
  var [protectionFiles, setProtectionFiles] = useState([]);
  /* Customer Remarks (2026-09-14, Arturo): the client's own email
     message pasted verbatim — "puede incluir la imagen de su firma
     etc, debe estar habilitado para que se pegue tal cual" — so this
     is a contentEditable div, not a plain <textarea>, since a
     textarea can only ever hold plain text and would silently drop
     any pasted image/formatting. Stored as raw HTML (whatever the
     browser's native paste produces) and persisted to IndexedDB the
     same way protectionFiles is, same key-per-invoice pattern, so it
     survives a dialog reopen. */
  /* Customer Remarks is split into two pieces of state on purpose:
     `...Initial` feeds the contentEditable's dangerouslySetInnerHTML
     and is set ONLY ONCE per dialog-open (right when loaded from
     storage, before the user can have typed anything) — never touched
     again while the div is mounted. `...` (live) is updated on every
     input/paste and is what actually gets sent/persisted. Feeding the
     SAME state back into dangerouslySetInnerHTML on every keystroke is
     a well-known contentEditable+React bug (React reconciles the DOM
     node's children on each render, which resets the caret to
     position 0) — keeping them separate avoids it entirely. */
  var [protectionCustomerRemarksInitial, setProtectionCustomerRemarksInitial] = useState("");
  var [protectionCustomerRemarks, setProtectionCustomerRemarks] = useState("");
  var customerRemarksRef = useRef(null);
  /* Timestamp for the Protection Notice Report PDF's "Notificado:"
     line — set once when the compose dialog opens (not recomputed on
     every render) so the preview and the actual attached copy the
     shipper receives show the exact same moment, and reopening the
     dialog later (e.g. after a failed send) reflects that later time
     instead of freezing on the first attempt. */
  var [protectionNoticeAt, setProtectionNoticeAt] = useState(null);
  var [protectionReportPreviewOpen, setProtectionReportPreviewOpen] = useState(false);
  var [previewHtml, setPreviewHtml] = useState(null);
  /* Pending Pick Ticket remarks prompt for the EMAIL flow. When the
     user clicks Send inside EmailDialog and the attachment list
     includes a "*-pick" kind, we open the same PickTicketRemarksModal
     the print flow uses and Promise-wait for the resolved
     { remarks, loadingDate } before building the PDF attachments. */
  var [pendingRemarks, setPendingRemarks] = useState(null);
  var promptPickRemarks = function (brandKey, defaultLD) {
    return new Promise(function (resolve, reject) {
      setPendingRemarks({ brand: brandKey || "gfi", defaultLD: defaultLD || "", resolve: resolve, reject: reject });
    });
  };
  /* Copy-to-SweetLeaf modal — opens before the actual copy so the
     user can edit INV# (historical imports may need a custom one)
     and capture the SL salePrice per product line. */
  var [slCopyModal, setSlCopyModal] = useState(null);
  var fresh = useMemo(function() {
    return (invoices || []).find(function(x) { return x.id === invoice.id; }) || invoice;
  }, [invoice, invoices]);

  useDocumentTitle(fresh.puNumber ? ("Invoice PU " + fresh.puNumber + (function () { var n = resolveClientName(fresh.clientId) || fresh.clientName; return n ? " | " + n : ""; })()) : null);

  var tryEditMode = function() {
    /* Always enter edit mode — even with payments / settled status.
       The form itself locks every non-client field when payments
       exist (see InvoiceForm `locked` flag below) so the operator
       can still rebind a paid invoice to a different client without
       being able to touch amounts. The old redirect to onEditLock
       has been removed for this reason. */
    setMode("edit");
  };
  var handleSaveInline = function(updated) { onSave(updated); setMode("view"); };
  var handleDeleteInline = function(id) { onDelete(id); onClose(); };

  /* Open the invoice's client folder locally via the Drive desktop
     client mount. Path: <localDrivePath>/<season>/<embarque>/<PU#
     Client>. The browser usually blocks file:// from http origins, so
     the helper also copies the path to the clipboard. */
  var handleOpenLocalFolder = async function () {
    var shipments = loadLS("gfi-shipments", []);
    var suppliers = loadLS("gfi-suppliers", []);
    var clients = loadLS("gfi-clients", []);
    var seasons = loadLS("gfi-seasons", []);
    var company = loadCompanyProfile();
    var shipment = null;
    if (fresh.shipmentId) shipment = shipments.find(function (s) { return idMatches(s, fresh.shipmentId); });
    if (!shipment && fresh.lot) shipment = shipments.find(function (s) { return String(s.lot) === String(fresh.lot); });
    if (!shipment) { alert(t.driveNoShipment); return; }
    var sup = suppliers.find(function (s) { return idMatches(s, shipment.supplierId); });
    var shipperShort = (sup && (sup.shortName || sup.fullName)) || shipment.shipperName || "Unknown";
    var season = seasons.find(function (s) { return String(s.id) === String(shipment.seasonId); });
    var cli = clients.find(function (c) { return idMatches(c, fresh.clientId); });
    var clientShort = (cli && (cli.shortName || cli.fullName)) || fresh.clientName || "Unknown";
    var folder = buildLocalPath(company.localDrivePath, [
      seasonFolderName(season),
      embarqueFolderName(shipment, shipperShort),
      invoiceFolderName(fresh, clientShort),
    ]);
    await openLocalFolder(folder);
    alert(t.openLocalFolderHint + "\n\n" + folder);
  };

  /* Drive: create the per-invoice subfolder inside the embarque folder.
     Walks <root>/<season>/<embarque>/<PU# Client>, ensuring each level.
     Requires the linked shipment to exist so we can build the embarque
     folder name from the same arrival date + shipper short name the
     Shipments button uses. Idempotent — re-running just returns the
     existing folder. */
  var handleCreateDriveFolder = async function() {
    if (driveBusy) return;
    if (!driveConfigured()) { alert(t.driveNotConfigured); return; }
    setDriveBusy(true); setDriveResult(null);
    try {
      if (!driveSignedIn()) {
        try { await driveSignIn(); }
        catch (e) { alert(t.driveSignInNeeded + " (" + (e.message || String(e)) + ")"); setDriveBusy(false); return; }
      }
      var shipments = loadLS("gfi-shipments", []);
      var suppliers = loadLS("gfi-suppliers", []);
      var clients = loadLS("gfi-clients", []);
      var seasons = loadLS("gfi-seasons", []);
      var company = loadCompanyProfile();
      /* Find the linked shipment. Prefer shipmentId; fall back to lot
         when a manually-typed invoice doesn't carry the FK. */
      var shipment = null;
      if (fresh.shipmentId) shipment = shipments.find(function(s) { return idMatches(s, fresh.shipmentId); });
      if (!shipment && fresh.lot) shipment = shipments.find(function(s) { return String(s.lot) === String(fresh.lot); });
      if (!shipment) { alert(t.driveNoShipment); setDriveBusy(false); return; }
      var sup = suppliers.find(function(s) { return idMatches(s, shipment.supplierId); });
      var shipperShort = (sup && (sup.shortName || sup.fullName)) || shipment.shipperName || "Unknown";
      var season = seasons.find(function(s) { return String(s.id) === String(shipment.seasonId); });
      var cli = clients.find(function(c) { return idMatches(c, fresh.clientId); });
      var clientShort = (cli && (cli.shortName || cli.fullName)) || fresh.clientName || "Unknown";
      /* <root>/<season>/<embarque>/<PU# Client>. */
      var segments = rootPathSegments(company.driveRootPath).concat([
        seasonFolderName(season),
        embarqueFolderName(shipment, shipperShort),
        invoiceFolderName(fresh, clientShort),
      ]);
      var leafId = await driveEnsurePath(segments);
      setDriveResult({ url: driveFolderViewUrl(leafId) });
    } catch (e) {
      alert(t.driveError + ": " + (e.message || String(e)));
    } finally {
      setDriveBusy(false);
    }
  };

  /* 2026-10-20, Arturo: "aqui estos deben tener linked bills o linked
     lots en lugar de linked invoices porque estas son las facturas" —
     unlike Bill's own header strip (which links to OTHER invoices on
     the same lot, since a Bill never owns that data), this screen IS
     an invoice — what it links out to is the Lot's Bill. Same
     read-only "copied from the shipment/bill" spirit as Bill's own
     strip, just pointed the other direction.
     2026-10-20, follow-up: "la factura se genera a partir del bill no
     del lote... open lot no es necesario, debemos seguir la secuencia
     lot>bill>invoice>sweetleaf invoice" — the Links strip itself only
     shows Open Linked Bill (one hop up the chain).
     2026-10-20, further follow-up: "el lot dentro de invoice info
     tambien agregale un link para abrir ese lote" — a SEPARATE, inline
     link on the Lot field inside the Invoice Info card (not the header
     strip) opens the Shipment directly. "El PO del cliente debe ser
     link clickeable que abra directamente ese po del modulo de PO's" —
     same idea for Client Ref/PO#, opening the Fixed Pricing PO (new
     window.__gfiFixedPoOpen hook, FixedPricingRegistry.js).
     "Todo lo que se abre desde aqui, al cerrarlo debe regresar a la
     factura abierta" — every one of these stamps
     window.__gfiInvoiceReopenId = fresh.id right before navigating
     away, consumed by the effect near this component's own
     __gfiInvoiceOpen hook (reopens this exact invoice, not just the
     Invoices list). */
  var linkedBillForBar = useMemo(function () {
    if (!fresh.lot) return null;
    return getBills().find(function (b) { return String(b.lot) === String(fresh.lot); }) || null;
  }, [fresh.lot]);
  var linkedShipmentForInfo = useMemo(function () {
    return findShipmentForInvoice(fresh, loadLS("gfi-shipments", [])) || null;
  }, [fresh.id, fresh.lot]); // eslint-disable-line react-hooks/exhaustive-deps
  var openLinkedBill = function () {
    if (!linkedBillForBar) return;
    var billId = linkedBillForBar.id;
    window.__gfiInvoiceReopenId = fresh.id;
    if (window.__gfiNavigate) window.__gfiNavigate("bills");
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiBillOpen) window.__gfiBillOpen(billId); }, 250);
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiBillOpen) window.__gfiBillOpen(billId); }, 900);
  };
  var openLinkedLotFromInfo = function () {
    if (!linkedShipmentForInfo) return;
    var shipmentId = linkedShipmentForInfo.id;
    window.__gfiInvoiceReopenId = fresh.id;
    if (window.__gfiNavigate) window.__gfiNavigate("shipments");
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiShipmentOpen) window.__gfiShipmentOpen(shipmentId); }, 250);
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiShipmentOpen) window.__gfiShipmentOpen(shipmentId); }, 900);
  };
  /* "me voy a dormir" — Arturo's deferred request to make client/
     supplier names clickable app-wide, now authorized to build
     overnight. Same open-and-return convention as every other link in
     this plate (Lot/PO/Bill/SweetLeaf Invoice above/below). Supplier
     is resolved via the linked shipment's own supplierId (an invoice
     has no supplierId of its own) — never guessed from the shipperName
     text. */
  var openLinkedClientFromInfo = function () {
    if (!fresh.clientId) return;
    var clientId = fresh.clientId;
    window.__gfiInvoiceReopenId = fresh.id;
    if (window.__gfiNavigate) window.__gfiNavigate("clients");
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiClientOpen) window.__gfiClientOpen(clientId); }, 250);
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiClientOpen) window.__gfiClientOpen(clientId); }, 900);
  };
  var openLinkedSupplierFromInfo = function () {
    var supplierId = linkedShipmentForInfo && linkedShipmentForInfo.supplierId;
    if (!supplierId) return;
    window.__gfiInvoiceReopenId = fresh.id;
    if (window.__gfiNavigate) window.__gfiNavigate("suppliers");
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiSupplierOpen) window.__gfiSupplierOpen(supplierId); }, 250);
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiSupplierOpen) window.__gfiSupplierOpen(supplierId); }, 900);
  };
  var openLinkedPoFromInfo = function () {
    if (!fresh.linkedPoLineId) return;
    window.__gfiInvoiceReopenId = fresh.id;
    var poCompositeId = fresh.linkedPoLineId;
    if (window.__gfiNavigate) window.__gfiNavigate("fixed_pricing");
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiFixedPoOpen) window.__gfiFixedPoOpen(poCompositeId); }, 250);
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiFixedPoOpen) window.__gfiFixedPoOpen(poCompositeId); }, 900);
  };
  /* 2026-10-20, Arturo: "agregar a las facturas de four seasons
     solamente el numero de invoice generado en sweetleaf ligada a
     esta factura con un link para que se abra el modulo sweetleaf de
     dicha factura y regrese aqui mismo, despues de temp recorder... y
     en un color distinto" — Four Seasons only (slPassingIndex is keyed
     by gfiInvoiceId, built from sweetleaf_invoices, so a non-FS
     invoice simply never has an entry there). Same open-and-return
     dance as Bill/Lot/PO above, pointed at the SweetLeaf module and
     its own window.__gfiInvoiceOpen hook. */
  var linkedSlInvoiceForInfo = isFourSeasonsClientName(resolveClientName(fresh.clientId) || fresh.clientName || "") ? (slPassingIndex[String(fresh.id)] || null) : null;
  var openLinkedSlInvoiceFromInfo = function () {
    if (!linkedSlInvoiceForInfo) return;
    var slId = linkedSlInvoiceForInfo.id;
    window.__gfiInvoiceReopenId = fresh.id;
    if (window.__gfiNavigate) window.__gfiNavigate("sl_invoices");
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiInvoiceOpen) window.__gfiInvoiceOpen(slId); }, 250);
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiInvoiceOpen) window.__gfiInvoiceOpen(slId); }, 900);
  };

  /* 2026-10-20, Arturo: "client rejections y client responses se
     agregan a una sección llamada invoice activity que vive en el
     mismo lugar que lot activity en bills... debe marcar con un dot
     rojo" — these two IIFEs used to render inline in the body;
     computed once here (same closure — fresh/onSave/etc from this
     component) and handed to <InvoiceActivityButton> in the header
     strip below instead, so the trigger lives where Lot Activity
     lives on the Bill. hasInvoiceActivity (the red-dot condition)
     reuses the exact same helpers Bill's LotActivityButton already
     uses, just scoped to this one invoice instead of a whole lot. */
  var hasInvoiceActivity = rejectedCasesForInvoice(fresh) > 0 || hasProtectionResponses(fresh.lot, [fresh]);

  /* 2026-10-20 — precomputed once (same closure as the body below) so
     the Fees/Credits/Balance Transfers cards can gate on "is there
     anything real to show" without re-filtering per render pass.
     "no has agregado las secciones credits y balance transfers en
     fees & deductions" — Arturo changed his mind back: both get
     their own read-only itemized SectionBox cards in view mode too,
     right next to Fees, not just rolled up inside Invoice Summary. */
  var visibleFees = (fresh.fees || []).filter(function(f) { return f && (f.feeType || f.feeCustom || Math.abs(parseFloat(f.amount) || 0) > 0); });
  var visibleCredits = (fresh.credits || []).filter(function(cr) { return cr && Math.abs(parseFloat(cr.amount) || 0) > 0; });
  var visibleTransfers = (fresh.balanceTransfers || []).filter(function(tr) { return tr && Math.abs(parseFloat(tr.amount) || 0) > 0; });
  /* Every card except the Invoice Info plate is collapsible — same
     {id: bool} + toggle(id) shape BillRegistry.js's DetailPanel uses,
     starting fully expanded ("todas las tarjetas deben estar
     abiertas al entrar" — same rule Bill already follows). Lifted
     here and handed down to InvoiceForm so the same open/closed
     state survives the view↔edit toggle. */
  var [sectionsOpen, setSectionsOpen] = useState({ fees: true, credits: true, transfers: true, summary: true, products: true, pallets: true, payments: true });
  var toggleSection = function (k) { setSectionsOpen(function (p) { return { ...p, [k]: !p[k] }; }); };
  var invoiceActivityContent = (
    <>
          {(function () {
            var rejList = fresh.rejections || [];
            var rejTotal = rejList.reduce(function (s, rj) { return s + (parseInt(rj.cases, 10) || 0); }, 0);
            var saveRejections = function (next) { onSave(Object.assign({}, fresh, { rejections: next })); };
            var startSend = function (idx) {
              if (isFourSeasonsClientName(resolveClientName(fresh.clientId) || fresh.clientName || "")) { alert(fourSeasonsRoutingBlockedMessage(lang)); return; }
              var shpS = findShipmentForInvoice(fresh, loadLS("gfi-shipments", []));
              var supS = shpS ? loadLS("gfi-suppliers", []).find(function (s) { return idMatches(s, shpS.supplierId); }) : null;
              if (!supS || !Array.isArray(supS.contacts) || supS.contacts.filter(function (c) { return c && c.email; }).length === 0) {
                alert(t.emailNoShipperContacts); return;
              }
              loadFromSupabase("company_profile").catch(function () {}).then(function () { setRejectionSendIdx(idx); });
            };
            return (
              <div style={{ marginBottom: 18, borderRadius: 8, background: "rgba(220,38,38,0.05)", border: "1px solid rgba(220,38,38,0.3)" }}>
                <button type="button" onClick={function () { setRejectionDetailOpen(function (v) { return !v; }); }} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "transparent", border: "none", cursor: "pointer", color: "#B91C1C", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  <span>{"Client Rejection - " + rejTotal + " cases"}</span>
                  <span>{rejectionDetailOpen ? "▲" : "▼"}</span>
                </button>
                {rejectionDetailOpen && (
                  <div style={{ padding: "0 14px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
                    {rejList.map(function (rj, idx) {
                      if (rejectionEditIdx === idx) {
                        return (
                          <div key={idx} style={{ padding: 10, borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-card)", display: "flex", flexDirection: "column", gap: 8 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "#B91C1C" }}>{"Rejection " + (idx + 1)}</div>
                            <div>
                              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Cajas rechazadas" : "Cases rejected"}</div>
                              <input type="number" min="0" value={rejectionDraftCases} onChange={function (e) { setRejectionDraftCases(e.target.value); }} style={{ ...inp, maxWidth: 200 }} />
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Fecha y hora en que el cliente me notificó" : "Date and time the client notified me"}</div>
                              <input type="datetime-local" value={rejectionDraftClientAt} onChange={function (e) { setRejectionDraftClientAt(e.target.value); }} style={{ ...inp, maxWidth: 260 }} />
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Notas (lo que indicó el cliente)" : "Notes (client's remarks)"}</div>
                              <textarea value={rejectionDraftNotes} onChange={function (e) { setRejectionDraftNotes(e.target.value); }} rows={3} style={{ ...inp, resize: "vertical", fontSize: 12, lineHeight: 1.45 }} />
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button type="button" onClick={function () {
                                var next = rejList.slice();
                                next[idx] = Object.assign({}, rj, { cases: rejectionDraftCases, notes: rejectionDraftNotes, clientNotifiedAt: rejectionDraftClientAt });
                                saveRejections(next);
                                setRejectionEditIdx(null);
                              }} style={{ padding: "5px 14px", borderRadius: 6, border: "none", background: "#B91C1C", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>Save</button>
                              <button type="button" onClick={function () { setRejectionEditIdx(null); }} style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 11 }}>Cancel</button>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={idx} style={{ padding: 10, borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-card)" }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#B91C1C", marginBottom: 4 }}>{"Rejection " + (idx + 1)}</div>
                          <div style={{ fontSize: 12, fontWeight: 700 }}>{(lang === "es" ? "Cajas rechazadas: " : "Cases rejected: ") + (rj.cases || 0)}</div>
                          {rj.notes && <div style={{ fontSize: 12, marginTop: 4, whiteSpace: "pre-line" }}>{rj.notes}</div>}
                          {rj.clientNotifiedAt && <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 4 }}>{(lang === "es" ? "Cliente me notificó: " : "Client notified me: ") + new Date(rj.clientNotifiedAt).toLocaleString()}</div>}
                          {rj.sentAt && <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>{(lang === "es" ? "Nota enviada al shipper: " : "Note sent to shipper: ") + new Date(rj.sentAt).toLocaleString()}</div>}
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                            <button type="button" onClick={function () { setRejectionDraftCases(rj.cases || ""); setRejectionDraftNotes(rj.notes || ""); setRejectionDraftClientAt(rj.clientNotifiedAt || ""); setRejectionEditIdx(idx); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #B91C1C", background: "transparent", color: "#B91C1C", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Edit</button>
                            <button type="button" onClick={function () { setRejectionPreviewIdx(idx); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Vista previa del PDF" : "Preview PDF"}</button>
                            <button type="button" onClick={function () { startSend(idx); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{lang === "es" ? "Enviar nota al shipper" : "Send note to shipper"}</button>
                            <button type="button" onClick={function () { if (window.confirm(lang === "es" ? "¿Quitar este rechazo?" : "Remove this rejection?")) saveRejections(rejList.filter(function (_, i) { return i !== idx; })); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 11 }}>✕</button>
                          </div>
                        </div>
                      );
                    })}
                    <button type="button" onClick={function () {
                      var next = rejList.concat([{ cases: "", notes: "" }]);
                      saveRejections(next);
                      setRejectionDraftCases(""); setRejectionDraftNotes(""); setRejectionEditIdx(next.length - 1);
                    }} style={{ alignSelf: "flex-start", padding: "5px 12px", borderRadius: 6, border: "1px dashed #B91C1C", background: "transparent", color: "#B91C1C", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>+ {lang === "es" ? "Agregar rechazo" : "Add rejection"}</button>
                  </div>
                )}
              </div>
            );
          })()}
          {(function() {
            var clientForLabel = getClients().find(function (c) { return idMatches(c, fresh.clientId); });
            var clientNameForLabel = (clientForLabel && (clientForLabel.shortName || clientForLabel.fullName)) || fresh.clientName || "";
            /* 2026-10-08, Arturo: "quiero aplicar exactamente la misma
               logica de las respuestas a protected files en sweetleaf...
               esta respuesta se esparce a su factura gemela de ground
               fresh, ahi no es editable pero es el mismo modelo que las
               de twin river... desde aqui si se envia al shipper ya que
               el shipper desconoce que existe sweetleaf". Four Seasons
               now gets the SAME collapsible protectionResponses[] list
               as every other client (SweetLeafPortal.js pushes the full
               array into this same field, see its own handleSave) —
               `readOnly` just hides Edit/Add/Remove so the list can only
               ever be changed from the SweetLeaf side, while "Send to
               shipper" per response stays fully active here, since the
               shipper only ever talks to Ground-Fresh. */
            var readOnly = isFourSeasonsClientName(clientNameForLabel);
            /* Collapsible list — each entry ALSO collapsed to a one-line
               summary until opened (Arturo: "colapsa la respuesta que
               sea solo visible si la abro"), with its own Edit/Send/
               Remove (Edit/Remove hidden when readOnly). */
            var pad2 = function (n) { return String(n).padStart(2, "0"); };
            var respList = (fresh.protectionResponses && fresh.protectionResponses.length > 0)
              ? fresh.protectionResponses
              : (fresh.protectionResponseStatus ? [{ status: fresh.protectionResponseStatus, note: fresh.protectionResponseNote || "", at: fresh.protectionResponseAt || "" }] : []);
            var saveResponses = function (next) {
              var last = next.length > 0 ? next[next.length - 1] : null;
              onSave(Object.assign({}, fresh, {
                protectionResponses: next,
                protectionResponseStatus: last ? last.status : "",
                protectionResponseNote: last ? last.note : "",
                protectionResponseAt: last ? last.at : "",
              }));
            };
            var startSendResponse = function (idx) {
              var shpS = findShipmentForInvoice(fresh, loadLS("gfi-shipments", []));
              var supS = shpS ? loadLS("gfi-suppliers", []).find(function (s) { return idMatches(s, shpS.supplierId); }) : null;
              if (!supS || !Array.isArray(supS.contacts) || supS.contacts.filter(function (c) { return c && c.email; }).length === 0) {
                alert(t.emailNoShipperContacts); return;
              }
              loadFromSupabase("company_profile").catch(function () {}).then(function () { setResponseSendIdx(idx); });
            };
            return (
              <div style={{ marginBottom: 18, borderRadius: 8, background: "rgba(220,38,38,0.05)", border: "1px solid rgba(220,38,38,0.3)" }}>
                <button type="button" onClick={function () { setRespListOpen(function (v) { return !v; }); }} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "transparent", border: "none", cursor: "pointer", color: "#DC2626", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  <span>{t.protectionResponseEditableLabel + " - " + respList.length}</span>
                  <span>{respListOpen ? "▲" : "▼"}</span>
                </button>
                {respListOpen && readOnly && (
                  <div style={{ padding: "0 14px 8px", fontSize: 10, fontStyle: "italic", color: "var(--color-text-muted)" }}>
                    {lang === "es" ? "Registradas desde SweetLeaf — solo lectura aquí." : "Registered from SweetLeaf — read-only here."}
                  </div>
                )}
                {respListOpen && (
                  <div style={{ padding: "0 14px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                    {respList.map(function (r, idx) {
                      var atD = r.at ? new Date(r.at) : null;
                      var dStr = atD && !isNaN(atD.getTime()) ? (atD.getFullYear() + "-" + pad2(atD.getMonth() + 1) + "-" + pad2(atD.getDate())) : "";
                      var tStr = atD && !isNaN(atD.getTime()) ? (pad2(atD.getHours()) + ":" + pad2(atD.getMinutes())) : "";
                      var isRowOpen = respOpenIdx === idx;
                      var isEditing = respEditIdx === idx;
                      var statusLabel = t[r.status] || r.status || "—";
                      return (
                        <div key={idx} style={{ borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-card)", overflow: "hidden" }}>
                          <button type="button" onClick={function () { setRespOpenIdx(isRowOpen ? null : idx); if (isRowOpen) setRespEditIdx(null); }} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", background: "transparent", border: "none", cursor: "pointer", fontSize: 12, textAlign: "left" }}>
                            <span><strong>{"Response " + (idx + 1)}</strong>{" — " + statusLabel + (r.at ? " — " + new Date(r.at).toLocaleDateString() : "")}</span>
                            <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{isRowOpen ? "▲" : "▼"}</span>
                          </button>
                          {isRowOpen && (
                            <div style={{ padding: "0 10px 10px" }}>
                              {isEditing ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                  <SearchSelect
                                    value={respDraftStatus}
                                    onChange={function (v) { setRespDraftStatus(v); }}
                                    placeholder={t.selectStatus}
                                    options={PROTECTION_RESPONSE_FINAL_STATUSES.concat(["noResultYet"]).map(function (s) { return { value: s, label: t[s] }; })}
                                  />
                                  <textarea value={respDraftNote} onChange={function (e) { setRespDraftNote(e.target.value); }} placeholder={t.protectionResponseNotePlaceholder} rows={3} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} />
                                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                    <div>
                                      <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Fecha de la respuesta" : "Response date"}</div>
                                      <input type="date" value={respDraftDate} onChange={function (e) { setRespDraftDate(e.target.value); }} style={inp} />
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Hora de la respuesta" : "Response time"}</div>
                                      <input type="time" value={respDraftTime} onChange={function (e) { setRespDraftTime(e.target.value); }} style={inp} />
                                    </div>
                                  </div>
                                  <div style={{ display: "flex", gap: 8 }}>
                                    <button type="button" onClick={function () {
                                      var d = respDraftDate ? new Date(respDraftDate + "T" + (respDraftTime || "00:00") + ":00") : null;
                                      var next = respList.slice();
                                      next[idx] = { status: respDraftStatus, note: respDraftNote, at: d && !isNaN(d.getTime()) ? d.toISOString() : "" };
                                      saveResponses(next);
                                      setRespEditIdx(null);
                                    }} style={{ padding: "5px 14px", borderRadius: 6, border: "none", background: "#DC2626", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>Save</button>
                                    <button type="button" onClick={function () { setRespEditIdx(null); }} style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 11 }}>Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 700 }}>{statusLabel}</div>
                                  {r.note && <div style={{ fontSize: 12, marginTop: 3, whiteSpace: "pre-line" }}>{r.note}</div>}
                                  <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3 }}>{r.at ? new Date(r.at).toLocaleString() : "—"}</div>
                                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                                    {!readOnly && <button type="button" onClick={function () { setRespDraftStatus(r.status || ""); setRespDraftNote(r.note || ""); setRespDraftDate(dStr); setRespDraftTime(tStr); setRespEditIdx(idx); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #DC2626", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Edit</button>}
                                    <button type="button" onClick={function () { startSendResponse(idx); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>{lang === "es" ? "Enviar al shipper" : "Send to shipper"}</button>
                                    {!readOnly && <button type="button" onClick={function () { saveResponses(respList.filter(function (_, i) { return i !== idx; })); setRespOpenIdx(null); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 11 }}>✕</button>}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {!readOnly && <button type="button" onClick={function () {
                      var now = new Date();
                      var next = respList.concat([{ status: "", note: "", at: now.toISOString() }]);
                      saveResponses(next);
                      setRespDraftStatus(""); setRespDraftNote("");
                      setRespDraftDate(now.getFullYear() + "-" + pad2(now.getMonth() + 1) + "-" + pad2(now.getDate()));
                      setRespDraftTime(pad2(now.getHours()) + ":" + pad2(now.getMinutes()));
                      setRespOpenIdx(next.length - 1);
                      setRespEditIdx(next.length - 1);
                    }} style={{ alignSelf: "flex-start", padding: "5px 12px", borderRadius: 6, border: "1px dashed #DC2626", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>+ {lang === "es" ? "Agregar respuesta" : "Add response"}</button>}
                  </div>
                )}
              </div>
            );
          })()}
    </>
  );

  /* 2026-10-20, Arturo: "preview, print, 3 puntos no van ahi, edit
     esta repetido" — Preview/Print PDF/kebab lived in their own
     right-hand column inside the body header (duplicating Edit,
     which also now lives in the Links strip). Computed once here
     (same closure as before) and rendered from the Links strip
     instead; Edit removed from inside it — the strip's own Edit
     is now the only one. */
  /* Print/preview/kebab action buttons.
       Top    : standalone "Print Invoice" button (invoice PDF)
       Middle : org-chart with shipment icon branching into
                the two brand-specific docs dropdowns
       Bottom : Drive-folder link, when one has been resolved
     Primary action buttons grouped at the top (Print Invoice + the
     client-specific Email HTML button), then the loading-doc org
     chart. Email button is gated by client identity: Twin River →
     Inbound Five Brothers (cream + gold), Four Seasons → Outbound
     ACS (teal + mint). Resolves the client name from both
     `clientName` and the catalog lookup so legacy invoices missing
     clientName still hit the right branch. */
  var invoiceActionButtons = (function () {
            var onPreviewInvoice = function () {
              var client = resolveClientFull(fresh.clientId);
              var html = buildInvoicePrintHTML(fresh, client);
              setPreviewHtml({ html: html, title: t.printPreview });
            };
            var pickDoc = function (kind) { if (onPrintDoc) onPrintDoc(fresh, kind); };
            /* Brand sections in the Print menu — small isotipo logos
               serve as the visual cue (replacing the legacy truck
               org-chart that lived in the header). */
            var gfiIcon = <img src={GFI_ISOTIPO_B64} alt="GFI" style={{ width: 14, height: 14, objectFit: "contain" }} />;
            var slIcon = <img src={SL_ISOTIPO_B64} alt="SweetLeaf" style={{ width: 14, height: 14, objectFit: "contain" }} />;
            var printSections = [
              { items: [
                { key: "invoice-print", label: t.printPreview, icon: "📄", onClick: onPreviewInvoice },
              ]},
              { header: "Ground-Fresh", icon: gfiIcon, items: [
                { key: "gfi-pickorinbound", label: lang === "es" ? "Pick Ticket / Inbound" : "Pick Ticket / Inbound", icon: "🖨", onClick: function () { pickDoc("gfi-pickorinbound"); } },
                { key: "gfi-packing", label: lang === "es" ? "Packing List" : "Packing List", icon: "🖨", onClick: function () { pickDoc("gfi-packing"); } },
                { key: "gfi-master", label: lang === "es" ? "Master Inbound" : "Master Inbound", icon: "🖨", onClick: function () { pickDoc("gfi-master"); } },
              ]},
              { header: "SweetLeaf Produce", icon: slIcon, items: [
                { key: "sl-pickorinbound", label: lang === "es" ? "Pick Ticket / Inbound" : "Pick Ticket / Inbound", icon: "🖨", onClick: function () { pickDoc("sl-pickorinbound"); } },
                { key: "sl-packing", label: lang === "es" ? "Packing List" : "Packing List", icon: "🖨", onClick: function () { pickDoc("sl-packing"); } },
                { key: "sl-master", label: lang === "es" ? "Master Inbound" : "Master Inbound", icon: "🖨", onClick: function () { pickDoc("sl-master"); } },
                { key: "sl-driversignoff", label: lang === "es" ? "Driver Sign-Off" : "Driver Sign-Off", icon: "🖨", onClick: function () { pickDoc("sl-driversignoff"); } },
              ]},
            ];
            /* Overall resolved status for this invoice — same
               deriveDeliveryStatus + resolvePendingStage fallback used
               everywhere else in this file (e.g. the line badge just
               below). Gates the pickup-reminder action: it only makes
               sense while nothing has been picked up yet (Pending
               Pick Up) — once it flips to Pending Delivery (or a real
               status), the load is already moving/confirmed.
               2026-11-03, PU 17484: resolveTwinRiverLotStatus checked
               ONE shipment (the invoice's home lot) for the whole
               invoice — on a multi-lot invoice a second lot's real
               ClariFresh status never factored in here. Resolved
               per-line now, same as liveLineStatusFor below. */
            var shipmentsForReminder = loadLS("gfi-shipments", []);
            var trLineStatusesForReminder = (fresh.lines || []).map(function (l) {
              var shp = resolveTwinRiverShipmentForLot(l.lot || fresh.lot, fresh, shipmentsForReminder);
              return shp !== undefined ? (twinRiverProductStatus(shp, l.productBase || l.productName) || "") : undefined;
            });
            var anyTrLineForReminder = trLineStatusesForReminder.some(function (s) { return s !== undefined; });
            var invoiceStatusForReminder = anyTrLineForReminder
              ? (deriveDeliveryStatus(trLineStatusesForReminder.filter(function (s) { return s !== undefined; }).map(function (s) { return { saleStatus: s }; })) || resolvePendingStage([fresh], slPassingIndex))
              : (deriveDeliveryStatus((fresh.lines || []).map(function (l) { return { saleStatus: l.saleStatus }; })) || resolvePendingStage([fresh], slPassingIndex));
            /* Email routing rule (2026-09-14, Arturo): Four Seasons/AGROCAP
               emails must ONLY go out from the SweetLeaf module, with the
               SweetLeaf masked account — never from here, even though this
               screen's "Email Customer" flow technically CAN brand-switch
               to SweetLeaf styling for a Four Seasons client (see the "sl"
               vs "gfi" brand detection a bit further down in this file).
               Blocked at the kebab action itself, before the dialog opens,
               per "no permitir el envio y enviar mensaje de error". */
            var routingClientName = resolveClientName(fresh.clientId) || fresh.clientName || "";
            var isFourSeasonsInvoice = isFourSeasonsClientName(routingClientName);
            /* Gates "Email Shipper — Protection" (2026-09-14, Arturo) —
               only relevant once at least one pallet on this invoice has
               actually been flagged protection/PAS by the client (see
               REGLA DE STATUS DE PALLET — the pallet is always the
               source of truth, never a frozen line-level value). */
            var protectedAllocs = (fresh.palletAllocations || []).filter(function (a) { return a && (a.saleStatus === "protection" || a.saleStatus === "pas"); });
            var hasProtectionPallets = protectedAllocs.length > 0;
            /* Guards against a stale/never-primed company_profile cache
               (2026-09-14, Arturo: "me quitaste la firma de ground-fresh
               y ademas no pide seleccionarla") — every email dialog on
               this screen reads company.emailAccounts/emailSignatures
               via the SYNCHRONOUS loadCompanyProfile() helper, which
               only reflects whatever primeCatalogs() cached at app
               boot. If that boot fetch ever failed/raced (no retry,
               no subscription to re-render callers once it succeeds
               later), every email dialog opened afterward silently
               falls back to DEFAULT_COMPANY — empty accounts, empty
               signatures, no error, nothing to click. Root-caused live:
               Company Profile's OWN screen forces a fresh
               loadFromSupabase("company_profile") on mount and always
               works; these kebab actions never did. Awaiting a (TTL-
               cached, so normally instant) refetch before opening the
               dialog guarantees the data is there by render time,
               without needing a subscription/re-render mechanism. */
            var ensureCompanyProfileFresh = function () { return loadFromSupabase("company_profile").catch(function () {}); };
            var kebabItems = [
              { key: "email", label: t.emailCustomer, icon: "✉", onClick: function () {
                if (isFourSeasonsInvoice) { alert(fourSeasonsRoutingBlockedMessage(lang)); return; }
                if (!fresh.clientId && !fresh.clientName) { alert(t.emailNoClient); return; }
                var shp = findShipmentForInvoice(fresh, loadLS("gfi-shipments", []));
                if (!shp) { alert(t.emailNoShipment); return; }
                setEmailDialogDefaultKind("inbound");
                ensureCompanyProfileFresh().then(function () { setEmailDialogOpen(true); });
              }},
              { key: "email-warehouse", label: t.emailWarehouse, icon: "✉", onClick: function () {
                var shp2 = findShipmentForInvoice(fresh, loadLS("gfi-shipments", []));
                var wh2 = shp2 ? loadLS("gfi-warehouses", []).find(function (w) { return idMatches(w, shp2.warehouseId); }) : null;
                if (!wh2 || !Array.isArray(wh2.contacts) || wh2.contacts.filter(function (c) { return c && c.email; }).length === 0) {
                  alert(t.emailNoWarehouse); return;
                }
                ensureCompanyProfileFresh().then(function () { setEmailWarehouseOpen(true); });
              }},
              { key: "drive-folder", label: t.driveCreateFolder, icon: "📁", onClick: handleCreateDriveFolder },
              { key: "open-folder", label: t.openLocalFolder, icon: "📂", onClick: handleOpenLocalFolder },
            ];
            if (hasProtectionPallets) {
              kebabItems.splice(1, 0, { key: "email-shipper-protection", label: t.emailShipperProtection, icon: "🛡", onClick: function () {
                var shp4 = findShipmentForInvoice(fresh, loadLS("gfi-shipments", []));
                var sup4 = shp4 ? loadLS("gfi-suppliers", []).find(function (s) { return idMatches(s, shp4.supplierId); }) : null;
                if (!sup4 || !Array.isArray(sup4.contacts) || sup4.contacts.filter(function (c) { return c && c.email; }).length === 0) {
                  alert(t.emailNoShipperContacts); return;
                }
                setProtectionNoticeAt(new Date());
                loadDoc("protection-files-" + fresh.id).then(function (stored) { setProtectionFiles(stored || []); }).catch(function () { setProtectionFiles([]); });
                loadDoc("protection-remarks-" + fresh.id).then(function (stored) {
                  setProtectionCustomerRemarksInitial(stored || "");
                  setProtectionCustomerRemarks(stored || "");
                }).catch(function () { setProtectionCustomerRemarksInitial(""); setProtectionCustomerRemarks(""); });
                ensureCompanyProfileFresh().then(function () { setEmailShipperProtectionOpen(true); });
              }});
            }
            /* 2026-10-07 — Arturo: "sobre las respuestas que reciba de
               los clientes el protection status, necesito poder enviar
               al shipper un update via email con comentarios del
               cliente" — moved out of this kebab menu and onto each
               response's own "Send to shipper" button (see the
               "Client Responses — Protection" card above), same as
               the Client Rejection card's per-entry send — no single
               kebab item for it anymore. */
            if (invoiceStatusForReminder === "pendingPickUp") {
              kebabItems.splice(1, 0, { key: "email-reminder", label: t.emailReminderPickup, icon: "⏰", onClick: function () {
                if (isFourSeasonsInvoice) { alert(fourSeasonsRoutingBlockedMessage(lang)); return; }
                if (!fresh.clientId && !fresh.clientName) { alert(t.emailNoClient); return; }
                var shp3 = findShipmentForInvoice(fresh, loadLS("gfi-shipments", []));
                if (!shp3) { alert(t.emailNoShipment); return; }
                setEmailDialogDefaultKind("pendingPickupReminder");
                ensureCompanyProfileFresh().then(function () { setEmailDialogOpen(true); });
              }});
            }
            /* 2026-10-01 — Arturo: "necesito enviar alertas de proteccion
               desde ground fresh a clientes que no sean four seasons".
               Mirrors "Send Pickup Reminder" above (same shared Email
               Customer dialog, a new `kindOptions` entry) — Four Seasons
               is explicitly excluded here (handled by SweetLeafPortal.js's
               own equivalent feature instead, see REGLA DE ENVÍO DE
               EMAILS). */
            if (hasProtectionPallets && !isFourSeasonsInvoice) {
              kebabItems.splice(1, 0, { key: "email-client-protection-reminder", label: t.emailClientProtectionReminder, icon: "🔔", onClick: function () {
                if (!fresh.clientId && !fresh.clientName) { alert(t.emailNoClient); return; }
                setEmailDialogDefaultKind("clientProtectionReminder");
                ensureCompanyProfileFresh().then(function () { setEmailDialogOpen(true); });
              }});
            }
            /* Copy-to-SweetLeaf — only for Four Seasons invoices.
               Clones the invoice into the sweetleaf_invoices ledger
               with a fresh INV-XXXXX consecutive and bumps the
               sweetleaf_settings counter. Marks the source as
               slImported so the badge + duplicate guard work.
               Resolves cli locally since DetailPanel doesn't hoist
               it into the outer IIFE. */
            var clientsForFS = loadLS("gfi-clients", []);
            var cliFS = clientsForFS.find(function (c) { return idMatches(c, fresh.clientId); });
            var isFourSeasons = ((fresh.clientName || "") + " " + ((cliFS && (cliFS.shortName || cliFS.fullName)) || "")).toLowerCase().indexOf("four seasons") >= 0;
            if (isFourSeasons) {
              kebabItems.push({
                key: "copy-sl",
                label: t.copyToSL,
                icon: <img src={SL_ISOTIPO_B64} alt="SL" style={{ width: 14, height: 14, objectFit: "contain" }} />,
                onClick: async function () {
                  /* slImported is a soft flag — if the SL invoice was
                     deleted, allow re-copying. Check gfi-sl-invoices
                     to verify the matching SL record still exists. */
                  if (fresh.slImported) {
                    var slCheck = loadLS("gfi-sl-invoices", []);
                    var stillExists = slCheck.some(function (x) { return String(x.gfiInvoiceId) === String(fresh.id); });
                    if (stillExists) { alert(t.copyToSLExists); return; }
                  }
                  var slSettings = loadLS("gfi-sl-settings", { nextInvoiceNumber: 16229, invoicePrefix: "INV-" });
                  var nextNum = slSettings.nextInvoiceNumber || 16229;
                  var prefix = slSettings.invoicePrefix || "INV-";
                  var invoiceNumber = prefix + String(nextNum).padStart(5, "0");
                  /* 2026-09-28 (Arturo: "al copiar una factura a
                     sweetleaf, me da el precio de gf y tambien un
                     espacio para anotar el precio de venta de sweetleaf
                     pero en blanco, porque no toma el precio de las
                     ordenes? seria el precio de venta a four seasons
                     correspondiente a esa orden") — SL Price defaulted
                     to the GFI line's own salePrice (a no-op, since the
                     line spread already carries that value under the
                     same key) instead of the real SweetLeaf-charges
                     price already captured on this PU's weekly Fixed
                     Pricing PO (priceSLReal if the sale already
                     settled, else the estimated priceSL) — Fixed
                     Pricing is the actual source of "lo que Four
                     Seasons paga", not the GF invoice line. Fetched
                     fresh (not the localStorage mirror, which can be
                     empty on a device that's never opened Fixed
                     Pricing) and matched by product/category/size,
                     same helper Bill/Invoice already use elsewhere to
                     match a line against another entity's lines. Falls
                     back to the line's own salePrice, then blank, if
                     no matching PO or PU is found at all (e.g. a
                     manually-created invoice with no PO behind it). */
                  var poLinesForPU = [];
                  try {
                    if (fresh.puNumber) {
                      var allFixedPos = await loadFromSupabase("fixed_pos");
                      poLinesForPU = (allFixedPos || []).filter(function (p) { return String(p.puNumber) === String(fresh.puNumber); });
                    }
                  } catch (e) { console.warn("[copy-sl] could not fetch fixed_pos for SL price prefill:", e && e.message); }
                  /* Open the pre-copy modal so the user can override
                     INV# (for historical imports) and capture an SL
                     salePrice per line before the SL record is created. */
                  setSlCopyModal({
                    invoiceNumber: invoiceNumber,
                    nextNum: nextNum,
                    lines: (fresh.lines || []).map(function (l) {
                      var poMatches = matchLineToInvoiceLines(l, poLinesForPU);
                      var poPrice = poMatches.length > 0 ? (poMatches[0].priceSLReal || poMatches[0].priceSL || "") : "";
                      return Object.assign({}, l, { salePrice: poPrice || l.salePrice || "", slCommissionPerCase: "" });
                    }),
                  });
                },
              });
            }
            return (
              <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                {/* 2026-10-20, Arturo: "los botones finalized, preview,
                    print pdf y los 3 botones no tienen el formato
                    correcto, no estan placados como edit" / "las
                    ventanas que abre los tres puntos y print pdf
                    tampoco tienen el formato de placa" — same metal-
                    plate recipe as Edit (plateActionBtnStyle), applied
                    to the trigger AND the dropdown panel itself (menuStyle
                    /itemHoverBg/itemTextColor/itemTextShadow — exact
                    recipe already proven in ShipmentRegistry.js's own
                    Print PDF/kebab menus). */}
                <button
                  type="button"
                  onClick={onPreviewInvoice}
                  title={t.preview}
                  style={plateActionBtnStyle}
                >👁 {t.preview}</button>
                <ButtonMenu
                  trigger={<><span>🖨</span><span>{t.printPdf}</span><span style={{ fontSize: 9, opacity: 0.7 }}>▾</span></>}
                  sections={printSections}
                  title={t.printPdf}
                  style={plateActionBtnStyle}
                  menuStyle={{ background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)" }}
                  itemHoverBg="rgba(255,255,255,0.3)"
                  itemTextColor="#000000"
                  itemTextShadow="0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)"
                />
                <ButtonMenu
                  trigger={<span style={{ fontSize: 16, fontWeight: 700 }}>⋮</span>}
                  items={kebabItems}
                  title={t.more}
                  style={{ ...plateActionBtnStyle, padding: "4px 10px" }}
                  menuStyle={{ background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)" }}
                  itemHoverBg="rgba(255,255,255,0.3)"
                  itemTextColor="#000000"
                  itemTextShadow="0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)"
                />
                {driveResult && driveResult.url && <a href={driveResult.url} target="_blank" rel="noopener noreferrer" style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid #047857", background: "rgba(4,120,87,0.10)", color: "#047857", textDecoration: "none", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>{"↗"} {t.driveOpenFolder}</a>}
              </div>
            );
  })();

  /* 2026-10-20, Arturo: "agrega el status de la factura en su color
     junto al nombre de cliente en el header de la ventana (protection,
     good delivery, etc)" — same live, per-line status derivation the
     Products table already uses, aggregated across every line with
     the same deriveDeliveryStatus() the rest of the app uses to mix
     per-line statuses into one overall value (REGLA DE STATUS DE
     PALLET — never a frozen value). "y si tiene rechazos 'has
     rejections'" — a second, separate red badge when the invoice has
     any rejected cases, independent of the delivery-status color. */
  /* "no veo pending delivery solo good delivery... aplica para todas
     las facturas" — the bug: this used `l.saleStatus` directly when
     set, but that's a value FROZEN at whatever point it was last
     saved (often "goodDelivery", the old default) — never recomputed
     live from the pallets actually allocated to this line, unlike
     Pallet Allocations (which always derives live) or Bill/Shipment's
     own status columns (deriveBillLineLiveStatus/derivePalletStatus).
     Same root cause as PU 17474's missing Protection badge: view mode
     trusted a stale/blank l.saleStatus instead of deriving live from
     matched pallets (allocationMatchesLine + deriveDeliveryStatus,
     the same pattern InvoiceForm's recomputeLineDeliveryStatuses
     already uses in edit mode) — now shared by the Products table AND
     this title-bar aggregate, so both always reflect the CURRENT
     status, whatever it is (REGLA DE STATUS DE PALLET). */
  var detailPalletMetaFor = function (lot, palletNum) {
    var shp = getShipments().find(function (s) { return String(s.lot) === String(lot); });
    return (shp && (shp.palletManifest || []).find(function (p) { return String(p.palletNum) === String(palletNum); })) || null;
  };
  /* 2026-10-30, Arturo (PU 17475/lot 58074): same per-product fix as
     recomputeLineDeliveryStatuses above — resolveTwinRiverLotStatus's
     single lot-wide value was being applied to EVERY line here too,
     blending distinct products' real statuses in view mode.
     2026-11-03, PU 17484: that single shipment was ALSO always the
     invoice's home lot — on a multi-lot invoice (blackberry from
     58083, blueberry from 58084), the blueberry line got checked
     against 58083's manifest (no blueberry pallets there), always
     missing its real status. Resolved per-LINE now (its own l.lot),
     not once for the whole invoice. */
  var liveLineStatusFor = function (l) {
    if (DISPOSITION_STATUSES.indexOf(l.saleStatus) !== -1) return l.saleStatus;
    var trShipmentDetail = resolveTwinRiverShipmentForLot(l.lot || fresh.lot, fresh, getShipments());
    if (trShipmentDetail !== undefined) {
      var trProdStatusDetail = twinRiverProductStatus(trShipmentDetail, l.productBase || l.productName);
      return trProdStatusDetail || resolvePendingStage([fresh], slPassingIndex);
    }
    /* "status pending delivery no se muestra en el header" — found on
       PU 17447: allocationMatchesLine() also compares `size`, but
       older line records never carried that field (confirmed: this
       line has no `.size` at all while its pallet's manifest entry
       does), so the match silently failed and a real, already-known
       "protection" status got thrown away in favor of the pending
       default. Two safety nets: on a single-product-line invoice
       there's no ambiguity — every allocated pallet is that line's,
       match or not; and if matching still comes up empty, prefer the
       line's own already-recorded status over guessing "pending"
       (REGLA DE STATUS DE PALLET is about not showing STALE data when
       the live pallets disagree, not about discarding a real value
       just because the match itself couldn't confirm it). */
    var allocsForLine = (fresh.lines || []).length === 1
      ? (fresh.palletAllocations || [])
      : (fresh.palletAllocations || []).filter(function (a) {
          return allocationMatchesLine(l, a.lot, detailPalletMetaFor(a.lot, a.palletNum));
        });
    return deriveDeliveryStatus(allocsForLine) || l.saleStatus || resolvePendingStage([fresh], slPassingIndex);
  };
  var invoiceOverallStatus = (function () {
    var statuses = (fresh.lines || []).map(liveLineStatusFor).filter(Boolean);
    /* deriveDeliveryStatus() expects {saleStatus} rows, not raw
       strings — passing plain strings silently always returned null
       (every row's `.saleStatus` read undefined, same as unset). */
    return statuses.length > 0 ? deriveDeliveryStatus(statuses.map(function (s) { return { saleStatus: s }; })) : null;
  })();
  var invHasRejections = rejectedCasesForInvoice(fresh) > 0;
  var invProtAllocs = (fresh.palletAllocations || []).filter(function (a) { return a && (a.saleStatus === "protection" || a.saleStatus === "pas"); });
  var invProtectionSent = !!fresh.protectionEmailSentAt;
  /* 2026-10-20, Arturo: "ahora no tiene un rejection notice sent fecha
     y hora, agregalo" — mirrors the Protection Notice badge above,
     but for rejections: each rejection entry carries its own sentAt
     (set when "Email Shipper" is used from the Rejection card, see
     the onSent handler further down) rather than one invoice-level
     field. "Sent" only once every rejection on this invoice has been
     notified; shows the most recent sentAt among them. */
  var invRejectionSentAt = (fresh.rejections || []).reduce(function (latest, r) {
    if (!r || !r.sentAt) return latest;
    return (!latest || r.sentAt > latest) ? r.sentAt : latest;
  }, null);
  var invRejectionAllSent = invHasRejections && (fresh.rejections || []).every(function (r) { return r && r.sentAt; });

  return (
    <DraggableModal
      title={"PU# " + (fresh.puNumber || "—") + " · " + (resolveClientName(fresh.clientId) || fresh.clientName || "—") + (mode === "edit" ? " · " + t.edit : "")}
      titleBarVariant="groundfresh"
      titleLeftActions={mode === "view" ? (
        <>
          {invoiceOverallStatus && SALE_STATUS_COLORS[invoiceOverallStatus] && (
            <span style={metallicBadgeStyle(statusColorKeyFor(invoiceOverallStatus))}>{t[invoiceOverallStatus] || invoiceOverallStatus}</span>
          )}
          {/* 2026-10-20, Arturo: "protection notice sent en el header
             principal después de la placa Protection" — moved here
             from the body header (own badge gate unchanged: only
             shown once this invoice actually has a protected pallet,
             same criterion the kebab "Send Protection Notice" action
             uses). Green "sent" / red "not sent", same metallic
             3D treatment as the status/rejections plates next to it. */}
          {invProtAllocs.length > 0 && (
            <span style={metallicBadgeStyle(invProtectionSent ? "green" : "red")}>
              {invProtectionSent ? ("✓ " + t.protectionEmailSent + " " + fmtDateTime(fresh.protectionEmailSentAt)) : ("⚠ " + t.protectionEmailNotSent)}
            </span>
          )}
          {invHasRejections && (
            <span style={metallicBadgeStyle("red")}><span style={{ color: "#FB923C" }}>{"⚠"}</span>{" " + (lang === "es" ? "Rechazos" : "Has Rejections")}</span>
          )}
          {invHasRejections && (
            <span style={metallicBadgeStyle(invRejectionAllSent ? "green" : "red")}>
              {invRejectionAllSent
                ? ("✓ " + (lang === "es" ? "Aviso de Rechazo enviado " : "Rejection Notice sent ") + fmtDateTime(invRejectionSentAt))
                : ("⚠ " + (lang === "es" ? "Aviso de Rechazo no enviado" : "Rejection Notice not sent"))}
            </span>
          )}
        </>
      ) : null}
      /* 2026-10-21, Arturo: "los botones sent, draft y paid no tienen
         el formato correcto... ese sent... debería convivir en el
         header de la ventana alineado al extremo derecho sin marco y
         grabado como PU# en negritas y del color que le corresponda a
         ese status" — clarified right after: "ya entendí, el status
         se cambia manualmente abajo pero debe verse en el header
         principal" — the Draft/Sent/Paid transition BUTTONS stay
         exactly where they are in the body (that's still how the
         status actually changes); this is just a READ-ONLY display of
         the current status, engraved into the title bar itself (same
         text-shadow/weight/size as the "PU# ..." title text next to
         it — see .dm-titlebar--groundfresh .dm-title in App.css — no
         border/box like the old pill had), colored per status via
         INV_STATUS_STYLES. Shown in both modes (reads fresh.status,
         the real persisted value either way — it only changes once a
         status transition is actually saved, same as every other
         read-only title-bar badge on this modal). */
      titleBarActions={(function () {
        var st = fresh.status || "draft";
        var sty = INV_STATUS_STYLES[st] || INV_STATUS_STYLES.draft;
        var key = st === "draft" ? "statusDraft" : st === "sent" ? "statusSent" : "statusPaid";
        /* 2026-11-02, Arturo: "tambien el bill y las facturas debe
           cambiarse el status desde el modo no edicion" — this
           engraved badge used to be purely read-only text (see the
           2026-10-21 comment above, still accurate for edit mode);
           in view mode it's now clickable with the same dropdown
           Shipment/Bill already have, same transitions the body's
           own Draft/Sent/Paid buttons offer (draft→sent, sent→
           draft/paid, paid→sent), saved immediately via onSave. */
        var options = [];
        if (st === "draft") options = ["sent"];
        else if (st === "sent") options = ["draft", "paid"];
        else if (st === "paid") options = ["sent"];
        var changeStatus = function (next) {
          setStatusMenuOpen(false);
          if (next === st) return;
          onSave({ ...fresh, status: next });
        };
        var statusBadge = mode !== "view" ? (
          <span style={{ fontSize: 14, fontWeight: 700, color: sty.text, textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", whiteSpace: "nowrap" }}>
            {t[key]}
          </span>
        ) : (
          <span style={{ position: "relative", display: "inline-block" }} onClick={function (e) { e.stopPropagation(); }}>
            <button type="button" onClick={function () { setStatusMenuOpen(!statusMenuOpen); }} title={lang === "es" ? "Cambiar estado" : "Change status"} style={{ padding: 0, border: "none", background: "transparent", cursor: options.length > 0 ? "pointer" : "default", fontFamily: "inherit", fontSize: 14, fontWeight: 700, color: sty.text, textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", whiteSpace: "nowrap" }}>
              {t[key]}{options.length > 0 && <span style={{ marginLeft: 4, opacity: 0.6 }}>{"▾"}</span>}
            </button>
            {statusMenuOpen && options.length > 0 && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", zIndex: 50, minWidth: 140, overflow: "hidden" }}>
                {options.map(function (opt) {
                  var optSty = INV_STATUS_STYLES[opt] || INV_STATUS_STYLES.draft;
                  var optKey = opt === "draft" ? "statusDraft" : opt === "sent" ? "statusSent" : "statusPaid";
                  return (
                    <div key={opt} onClick={function () { changeStatus(opt); }} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 11, fontWeight: 600, color: optSty.text, textShadow: "0 1px 0 rgba(255,255,255,0.5)", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid #7d838c" }}
                         onMouseEnter={function (e) { e.currentTarget.style.background = optSty.bg; }}
                         onMouseLeave={function (e) { e.currentTarget.style.background = "transparent"; }}>
                      <span style={{ width: 8, height: 8, borderRadius: 4, background: optSty.text, flexShrink: 0 }} />
                      <span>{"→ "}{t[optKey]}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </span>
        );
        if (mode !== "view") {
          return <span style={{ marginLeft: "auto" }}>{statusBadge}</span>;
        }
        var paidSum = (fresh.payments || []).reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
        var balance = calcInvoiceTotal(fresh) - paidSum;
        return (
          <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 14 }}>
            {statusBadge}
            <span style={{ textAlign: "right", fontSize: 12, fontWeight: 700, color: "#059669", textShadow: "0 1px 0 rgba(255,255,255,0.6)", whiteSpace: "nowrap" }}>
              {(lang === "es" ? "Saldo de la Factura: " : "Invoice Balance: ") + fmtMoney(balance)}
            </span>
          </span>
        );
      })()}
      onClose={onClose} initialWidth={Math.min(1200, Math.floor(window.innerWidth * 0.85))} initialHeight={Math.min(900, window.innerHeight - 60)}
    >
      {mode === "edit" ? (
        <InvoiceForm invoice={fresh} invoices={invoices} t={t} lang={lang} onSave={handleSaveInline} onDelete={handleDeleteInline} onCancel={function() { setMode("view"); }} embedded={true} feeCategoryMapProp={feeCategoryMap} />
      ) : (
        <>
        {/* 2026-10-20, Arturo: "baj0 esta misma logica que acabamos de
            hacer... agrega las placas de datos" — same brushed-metal
            strip recipe as BillRegistry.js's own header strip, same
            position (first thing in the modal body). Linked Lot opens
            the Shipment; Linked Bill opens the Bill for this lot —
            both read-only chips (this screen never edits either),
            same pattern as Bill's "Edit in the Shipment" / "Links:"
            buttons just pointed at different records. */}
        <div style={{ padding: "10px 22px", borderBottom: "1px solid #b9bcc0", background: "repeating-linear-gradient(180deg, rgba(0,0,0,0.02) 0px, rgba(0,0,0,0.02) 1px, transparent 1px, transparent 3px), linear-gradient(180deg, #f2f3f4 0%, #dcdfe2 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, fontSize: 11 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)" }}>{lang === "es" ? "Links:" : "Links:"}</span>
            {/* 2026-10-20, Arturo: "no debe decir Links: BILL-0172 solo
                el recuadro de open linked bill y el numero de lote del
                bill, yo no uso el numero interno de la app" — dropped
                the internal BILL-#### id chip, the button itself now
                carries the lot number (what Arturo actually reads). */}
            {linkedBillForBar && <button type="button" onClick={openLinkedBill} style={plateActionBtnStyle}>{t.linkedBill} · {lang === "es" ? "LOTE" : "LOT"} {linkedBillForBar.lot || "—"}</button>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {/* 2026-10-20, Arturo: "finalized esta del lado opuesto, va a
                la derecha" — status badge moves to the right-hand
                group, same order Bill's own strip uses (status first,
                then its action buttons). */}
            {/* 2026-10-20, Arturo: "finalized no es color solido, para
                estar en sincronia con los demas modulos, es semi
                transparente" — the Bill-status pill is NOT one of the
                metallic action buttons (Preview/Print/kebab/Edit) or
                the alert-style badges (Protection/Rejections) — it
                stays the same flat, semi-transparent tinted pill every
                other module's status column already uses. */}
            {linkedBillForBar && (function () {
              var billSty = LINKED_BILL_STATUS_STYLES[linkedBillForBar.status] || LINKED_BILL_STATUS_STYLES.draft;
              return <span style={{ padding: "6px 12px", borderRadius: 0, fontSize: 11, fontWeight: 700, background: billSty.bg, color: billSty.text, border: "1px solid " + billSty.border, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12)", whiteSpace: "nowrap" }}>{linkedBillStatusLabel(linkedBillForBar.status, lang)}</span>;
            })()}
            {/* 2026-10-20, Arturo: "preview, print, 3 puntos no van ahi" —
                moved here from the body header, same closure
                (invoiceActionButtons, computed above). */}
            {invoiceActionButtons}
            <InvoiceActivityButton hasActivity={hasInvoiceActivity} lang={lang} t={t} style={plateActionBtnStyle}>{invoiceActivityContent}</InvoiceActivityButton>
            <button type="button" onClick={tryEditMode} style={plateActionBtnStyle}>{t.editBtn}</button>
          </div>
        </div>
        {/* 2026-10-20, Arturo: "la barra superior no le diste el
            formato de shipment, repites PU y cliente, el nombre del
            cliente debe ir en la barra superior" — PU#/client now
            live in the DraggableModal title itself (groundfresh
            variant, same as Bill/Shipment); LOT/Client are already in
            the Invoice Info plate card below. The Protection-notice
            sent/not-sent badge that used to sit alone in this spot
            moved into the title bar itself ("protection notice sent
            en el header principal después de la placa protection"),
            so this body-header strip is gone entirely — nothing left
            unique to it. */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px", fontSize: 12 }}>
          {/* 2026-10-01 — Protection Status Reminder "due" banner for
              non-Four-Seasons clients, mirrors SweetLeafPortal.js own
              banner above its SearchSelect. Four Seasons is skipped —
              computeProtectionReminderStatusGf already returns null for
              it, this just avoids resolving the client record twice. */}
          {(function () {
            var clientForBanner = getClients().find(function (c) { return idMatches(c, fresh.clientId); });
            var clientNameForBanner = (clientForBanner && (clientForBanner.shortName || clientForBanner.fullName)) || fresh.clientName || "";
            if (isFourSeasonsClientName(clientNameForBanner)) return null;
            var rem = computeProtectionReminderStatusGf(fresh, clientForBanner, null);
            if (!rem || !rem.isDue) return null;
            return (
              <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.3)", color: "#B45309", fontSize: 12, fontWeight: 600 }}>
                {t.protectionReminderDueBanner(rem.daysSinceArrival)}
              </div>
            );
          })()}
          {(selectedProtectionDefects(fresh).length > 0 || (Array.isArray(fresh.loadDefects) && fresh.loadDefects.length > 0) || fresh.clientRemarksSummary) && (function () {
            var isTwinRiverDF = (resolveClientName(fresh.clientId) || fresh.clientName || "").toLowerCase().indexOf("twin river") >= 0;
            var defectPallets = isTwinRiverDF ? (fresh.palletAllocations || []).filter(function (a) { return a && (a.saleStatus === "protection" || a.saleStatus === "pas") && Array.isArray(a.defects) && a.defects.length > 0; }) : [];
            /* 2026-10-23, Arturo: Four Seasons never breaks defects down
               by pallet ("solo general de la carga") — everyone except
               Twin River reads from fresh.loadDefects, one flat list for
               the whole invoice, same as the capture UI above. */
            var loadDefectsDF = isTwinRiverDF ? [] : selectedProtectionDefects(fresh);
            return (
              <div style={{ marginBottom: 18, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.05)", border: "1px solid rgba(220,38,38,0.3)" }}>
                <button type="button" onClick={function () { setDefectsFoundOpen(!defectsFoundOpen); }} style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", padding: 0, marginBottom: defectsFoundOpen ? 8 : 0, background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
                  <span style={{ fontSize: 10, color: "#B91C1C", transform: defectsFoundOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s", display: "inline-block" }}>{"▶"}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#B91C1C" }}>{lang === "es" ? "Defectos encontrados" : "Defects found"}</span>
                </button>
                {defectsFoundOpen && (<>
                {isTwinRiverDF ? defectPallets.map(function (a) {
                  return (
                    <div key={a.palletNum} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{lang === "es" ? "Pallet" : "Pallet"} {a.palletNum || "—"}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {selectedPalletDefects(a).map(function (d) {
                          var r = PROTECTION_DEFECT_SEVERITY[d.severity];
                          return (
                            <span key={d.key} style={{ padding: "4px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700, background: r.bg, color: r.color, border: "1px solid " + r.border }}>
                              {d.labelEn}{(d.pct === 0 || d.pct) ? " (" + d.pct + "%)" : ""} · {lang === "es" ? r.labelEs : r.labelEn}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                }) : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {loadDefectsDF.map(function (d) {
                      var r = PROTECTION_DEFECT_SEVERITY[d.severity];
                      return (
                        <span key={d.key} style={{ padding: "4px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700, background: r.bg, color: r.color, border: "1px solid " + r.border }}>
                          {d.labelEn}{(d.pct === 0 || d.pct) ? " (" + d.pct + "%)" : ""} · {lang === "es" ? r.labelEs : r.labelEn}
                        </span>
                      );
                    })}
                  </div>
                )}
                {fresh.clientRemarksSummary && <div style={{ marginTop: 10, fontSize: 12, whiteSpace: "pre-line" }}><span style={{ fontWeight: 700 }}>Client Quality Inspector Remarks:</span>{fresh.clientRemarksSummary}</div>}
                </>)}
              </div>
            );
          })()}
          {/* 2026-10-20, Arturo: "agrega las placas de datos, headers,
              fonts, tarjetas en 2 columnas, replica de acuerdo a lo que
              ya hemos hecho" — same fixed two-column layout as
              BillRegistry.js (left = Invoice Info/Fees/Credits/
              Transfers/Summary, right = Products/Payments), same plate
              title bars (sectionHeaderStyle), same lighter/smaller
              fonts and tighter row padding already applied there.
              Nothing removed — Credits/Transfers keep their own
              itemized cards (unlike Bill, which only rolls them up
              into the Summary) since nothing here asked for that
              detail to disappear, just to be restyled. */}
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          <div style={{ flex: "0 1 38%", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={readOnlyPlateBodyStyle}>
              <div style={{ ...sectionHeaderStyle, background: "none", border: "none", boxShadow: "none" }}>{lang === "es" ? "Información de la Factura" : "Invoice Info"}</div>
              <div style={{ padding: "0 14px 14px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 18px", fontSize: 12 }}>
                  {[[t.puNum, fresh.puNumber, true], [t.date, fmtDate(fresh.date)], [t.dueDate, fmtDate(fresh.dueDate)]].map(function(arr, i) {
                    return <div key={i}><span style={readOnlyPlateLabelStyle}>{arr[0]}</span><div style={{ ...readOnlyPlateValueStyle, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit" }}>{arr[1] || "—"}</div></div>;
                  })}
                  <div>
                    <span style={readOnlyPlateLabelStyle}>{t.client}</span>
                    {fresh.clientId ? (
                      <div onClick={openLinkedClientFromInfo} title={lang === "es" ? "Abrir cliente" : "Open client"} style={{ ...readOnlyPlateValueStyle, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}>{resolveClientName(fresh.clientId) || fresh.clientName || "—"}</div>
                    ) : (
                      <div style={readOnlyPlateValueStyle}>{fresh.clientName || "—"}</div>
                    )}
                  </div>
                  {/* 2026-10-20, Arturo: "el PO del cliente debe ser link
                      cliqueable que abra directamente ese po del modulo
                      de PO's" */}
                  <div>
                    <span style={readOnlyPlateLabelStyle}>{t.clientRef}</span>
                    {fresh.linkedPoLineId ? (
                      <div onClick={openLinkedPoFromInfo} title={t.linkedPO} style={{ ...readOnlyPlateValueStyle, fontFamily: "'JetBrains Mono', monospace", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}>{fresh.clientRef || "—"}</div>
                    ) : (
                      <div style={{ ...readOnlyPlateValueStyle, fontFamily: "'JetBrains Mono', monospace" }}>{fresh.clientRef || "—"}</div>
                    )}
                  </div>
                  {/* 2026-10-20, Arturo: "el lot dentro de invoice info
                      tambien agregale un link para abrir ese lote" */}
                  <div>
                    <span style={readOnlyPlateLabelStyle}>{t.lot}</span>
                    {/* 2026-11-03, PU 17484: "solo muestra el 58083 y
                        debe mostrar todos" — once "+ Agregar de Lote"
                        pulls lines from more than one shipment, this
                        only ever showed fresh.lot (the single home
                        lot). Now lists every distinct lot across the
                        invoice's lines; the click-to-open still opens
                        the home shipment (linkedShipmentForInfo), same
                        as before — it just can't be a link for every
                        lot at once. */}
                    {(function () {
                      var lotsShown = uniqueInvoiceLots(fresh);
                      var lotText = lotsShown.length > 0 ? lotsShown.join(", ") : "—";
                      return linkedShipmentForInfo ? (
                        <div onClick={openLinkedLotFromInfo} title={t.linkedLot} style={{ ...readOnlyPlateValueStyle, fontFamily: "'JetBrains Mono', monospace", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}>{lotText}</div>
                      ) : (
                        <div style={{ ...readOnlyPlateValueStyle, fontFamily: "'JetBrains Mono', monospace" }}>{lotText}</div>
                      );
                    })()}
                  </div>
                </div>
                {/* 2026-10-20, Arturo: "agrega debajo de client una
                    seccion Loading Info... debe mostrar la siguiente
                    info que solo esta visible en modo edicion: Shipper,
                    pick up date (BOL), Pick Up Time (BOL), Temp
                    Recorder # (Logger) Shipper" — "esta seccion
                    dividela con una linea dentro de la placa" (one
                    divider inside the same unified plate, not a
                    separate bordered box — "alarga la placa si es
                    necesario" is just letting it grow taller, no fixed
                    height to fight). */}
                <div style={{ borderTop: "1px solid rgba(0,0,0,0.15)", margin: "10px 0 8px" }} />
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#2a3542", marginBottom: 8 }}>{lang === "es" ? "Información de Carga" : "Loading Info"}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 18px", fontSize: 12 }}>
                  <div>
                    <span style={readOnlyPlateLabelStyle}>{t.shipper}</span>
                    {linkedShipmentForInfo && linkedShipmentForInfo.supplierId ? (
                      <div onClick={openLinkedSupplierFromInfo} title={lang === "es" ? "Abrir proveedor" : "Open supplier"} style={{ ...readOnlyPlateValueStyle, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}>{fresh.shipperName || "—"}</div>
                    ) : (
                      <div style={readOnlyPlateValueStyle}>{fresh.shipperName || "—"}</div>
                    )}
                  </div>
                  {[[t.pickupDate, fmtDate(fresh.pickupDate)], [t.pickupTime, fresh.pickupTime], [t.thermograph, fresh.thermograph, true]].map(function(arr, i) {
                    return <div key={i}><span style={readOnlyPlateLabelStyle}>{arr[0]}</span><div style={{ ...readOnlyPlateValueStyle, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit" }}>{arr[1] || "—"}</div></div>;
                  })}
                  {/* 2026-10-20, Arturo: "agregar a las facturas de four
                      seasons solamente el numero de invoice generado
                      en sweetleaf ligada a esta factura con un link...
                      despues de temp recorder" — lands directly below
                      Pickup Time in this 3-column grid (row 2, same
                      column), right after Temp Recorder in reading
                      order, exactly where asked. Four Seasons only;
                      distinct teal color so it reads as a different
                      kind of field than the plain data around it. */}
                  {linkedSlInvoiceForInfo && (
                    <div>
                      <span style={readOnlyPlateLabelStyle}>{lang === "es" ? "Factura SweetLeaf" : "SweetLeaf Invoice"}</span>
                      <div onClick={openLinkedSlInvoiceFromInfo} title={lang === "es" ? "Abrir en SweetLeaf" : "Open in SweetLeaf"} style={{ ...readOnlyPlateValueStyle, fontFamily: "'JetBrains Mono', monospace", color: "#2A7A6F", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}>
                        {linkedSlInvoiceForInfo.invoiceNumber || linkedSlInvoiceForInfo.id || "—"}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* 2026-10-20, Arturo: "me equivoqué, fees & deductions no
              esta visible, y debe mostrar credits y balance transfer
              dentro de ella" — ONE always-visible "Fees & Deductions"
              card (no more hiding when there are no fees), Credits and
              Balance Transfers nested inside it as their own labeled
              sub-sections (read-only itemized mirrors of InvoiceForm's
              editable tables, same rows minus drag/inputs/add-remove).
              Each sub-section only prints when it actually has rows;
              an invoice with nothing in any of the 3 just shows the
              empty-state line. All three still roll up into Invoice
              Summary below too, same as before. */}
          <div>
            <SectionBox id="fees" title={t.fees} open={sectionsOpen} toggle={toggleSection}>
              {visibleFees.length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: (visibleCredits.length > 0 || visibleTransfers.length > 0) ? 14 : 0 }}>
                  <tbody>{visibleFees.map(function(f, i) {
                    var fAmtAbs = Math.abs(parseFloat(f.amount) || 0);
                    var fname = f.feeType === "__custom__" ? f.feeCustom : f.feeType;
                    return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "5px 6px" }}>{fname || (f.feeCustom || "Fee")}</td>
                      <td style={{ padding: "5px 4px", textAlign: "center", fontSize: 10, color: "var(--color-text-muted)" }}>{f.qty > 1 ? f.qty + " × " + fmtMoney(f.rate) : (f.pctRate ? f.pctRate + "%" : "")}</td>
                      <td style={{ padding: "5px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#DC2626" }}>({fmtMoney(fAmtAbs)})</td>
                    </tr>;
                  })}</tbody>
                </table>
              )}
              {visibleCredits.length > 0 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#2a3542", marginBottom: 6 }}>{t.credits}</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: visibleTransfers.length > 0 ? 14 : 0 }}>
                    <tbody>{visibleCredits.map(function(cr, i) {
                      var label = cr.type === "fromInvoice" ? ("PU# " + (cr.refPuNumber || "?")) : (cr.concept || t.creditFree);
                      return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                        <td style={{ padding: "5px 6px" }}>{label}</td>
                        <td style={{ padding: "5px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#059669" }}>{fmtMoney(Math.abs(parseFloat(cr.amount) || 0))}</td>
                      </tr>;
                    })}</tbody>
                  </table>
                </>
              )}
              {visibleTransfers.length > 0 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#2a3542", marginBottom: 6 }}>{t.balanceTransfers}</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <tbody>{visibleTransfers.map(function(tr, i) {
                      var isCredit = tr.type === "credit";
                      var tone = isCredit ? "#059669" : "#DC2626";
                      return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                        <td style={{ padding: "5px 6px", fontWeight: 700, color: tone }}>{isCredit ? t.btCredit : t.btDebit}</td>
                        <td style={{ padding: "5px 4px", fontSize: 10, color: "var(--color-text-muted)" }}>{"PU# " + (tr.otherPuNumber || "?")}</td>
                        <td style={{ padding: "5px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: tone }}>{isCredit ? "+" : "−"}{fmtMoney(Math.abs(parseFloat(tr.amount) || 0))}</td>
                      </tr>;
                    })}</tbody>
                  </table>
                </>
              )}
              {visibleFees.length === 0 && visibleCredits.length === 0 && visibleTransfers.length === 0 && (
                <div style={{ padding: "6px 0", fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{lang === "es" ? "Sin cargos" : "No charges"}</div>
              )}
            </SectionBox>
          </div>
          <div>
            {/* 2026-10-20, Arturo: "los formatos de payments e invoice
                summary copialos exactamente igual de bill, fonts,
                espaciado, colores etc" — same wrapper fontSize (11,
                var(--color-text)), same neutral var(--color-border)/
                var(--color-accent) palette instead of the custom
                orange this card used before, and the same Subtotal →
                − Paid → Amount Due breakdown Bill Summary always
                shows (not a single jump straight to the final total). */}
            <SectionBox id="summary" title={lang === "es" ? "Resumen de la Factura" : "Invoice Summary"} open={sectionsOpen} toggle={toggleSection}>
              {(function() {
                var gross = 0; (fresh.lines || []).forEach(function(l) { if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") gross += (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0); });
                var commRate = parseFloat(fresh.commissionRate) || 0;
                var commission = gross * (commRate / 100);
                var tFees = 0; (fresh.fees || []).forEach(function(fe) { tFees += Math.abs(parseFloat(fe.amount) || 0); });
                var tCredits = 0; (fresh.credits || []).forEach(function(cr) { tCredits += Math.abs(parseFloat(cr.amount) || 0); });
                var tTransfers = 0; (fresh.balanceTransfers || []).forEach(function(tr) { var amt = parseFloat(tr.amount) || 0; if (tr.type === "credit") tTransfers += amt; else if (tr.type === "debit") tTransfers -= amt; });
                var subtotal = gross - commission - tFees + tCredits + tTransfers;
                var paidSum = (fresh.payments || []).reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
                var net = subtotal - paidSum;
                return (
                  <div style={{ fontSize: 11, color: "var(--color-text)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>{t.grossSales || "Gross Sales"}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(gross)}</span></div>
                    {commission > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#DC2626" }}><span>- {lang === "es" ? "Comisión" : "Commission"} ({commRate}%)</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(commission)})</span></div>}
                    <FeeBreakdownLinesInv fees={fresh.fees} feeCategoryMap={feeCategoryMap} t={t} />
                    {tCredits > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>+ {t.credits}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(tCredits)}</span></div>}
                    {tTransfers !== 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: tTransfers >= 0 ? "var(--color-text)" : "#DC2626" }}><span>{tTransfers >= 0 ? "+" : "-"} {t.balanceTransfers}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{tTransfers >= 0 ? fmtMoney(tTransfers) : "(" + fmtMoney(-tTransfers) + ")"}</span></div>}
                    <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between" }}><span>{t.subtotal}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(subtotal)}</span></div>
                    {paidSum > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, color: "#DC2626" }}><span>{t.lessPaid}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(paidSum)})</span></div>}
                    <div style={{ borderTop: "2px solid var(--color-accent)", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between" }}><span>{t.totalDue}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(net)}</span></div>
                  </div>
                );
              })()}
            </SectionBox>
          </div>
          </div>
          <div style={{ flex: "1 1 62%", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <SectionBox id="products" title={lang === "es" ? "Productos" : "Products"} open={sectionsOpen} toggle={toggleSection}>
            {/* 2026-10-20, Arturo: "reajustemos la tarjeta de productos...
                debemos reordenar las columnas" — PRODUCT>CAT>PALLETS>
                CASES>PRICE>TOTAL>STATUS (nuevo), Comm%/Comm$ removed.
                "todos los headers alineados con su contenido, los
                headers centrados, el contenido de texto hacia la
                izquierda, numerico al centro, moneda hacia la derecha,
                agrega totales debajo como en bills" — headers always
                centered; data cells: text left, plain numbers center,
                money right. "el producto muestra la descripcion,
                remueve el comotidy y reduce el alto de las filas igual
                que en bills" — single productDescription line (no
                separate commodity-name line above it), same tight
                "2px 6px"/"2px 4px" row padding Bill's own Products
                table uses. Status is the same live pallet-derived
                value as everywhere else in this app (REGLA DE STATUS
                DE PALLET) — never a frozen l.saleStatus when pallets
                are actually linked. */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                {/* 2026-10-20, Arturo: no explicit widths/table-layout:
                    fixed here, same as Bill's own Products table — auto
                    layout already gives Product (the longest content)
                    the most room. Product's header IS left-aligned
                    though — "products esta a la altura de [el final
                    del texto]... debe estar alineado con blackberry
                    del inicio": a centered header over left-aligned
                    content visibly floats over the middle/end of the
                    text instead of its start, confirmed as a real
                    problem worth deviating from Bill's own centered
                    header for this one column — every other header
                    stays centered. */}
                <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                  <th style={{ padding: "5px 6px", textAlign: "left", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.product}</th>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.category}</th>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{lang === "es" ? "Pallets" : "Pallets"}</th>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.cases}</th>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.salePrice}</th>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.amount}</th>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.saleStatus}</th>
                </tr></thead>
                <tbody>{(fresh.lines || []).map(function(l, i) {
                  var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
                  /* 2026-10-20, Arturo: "si los pallets estaban pending
                     delivery, sales status es el mismo" / "no veo
                     pending delivery solo good delivery... aplica
                     para todas las facturas" — view mode never needs
                     InvoiceForm's "badge vs editable dropdown"
                     branching (that's an edit-mode UI concern), so
                     this always shows the LIVE status derived from
                     this line's actual matched pallets (same
                     liveLineStatusFor() the title-bar badge above
                     uses — Twin River/ClariFresh aware, never a stale
                     frozen l.saleStatus). A manual disposition
                     override always wins, same as everywhere else. */
                  var lineStatus = liveLineStatusFor(l);
                  var lsc = lineStatus ? SALE_STATUS_COLORS[lineStatus] : null;
                  return <tr key={i} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "2px 6px", fontSize: 11, color: "var(--color-text-secondary)" }}>{l.productDescription || l.productName || l.productBase || l.product || "—"}</td>
                    <td style={{ padding: "2px 4px", textAlign: "center" }}>{l.category ? <span style={{ padding: "2px 6px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: l.category === "OG" ? "rgba(16,185,129,0.10)" : "rgba(107,114,128,0.10)", color: l.category === "OG" ? "#059669" : "#374151" }}>{l.category}</span> : "—"}</td>
                    <td style={{ padding: "2px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{parseInt(l.pallets, 10) || "—"}</td>
                    <td style={{ padding: "2px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{l.cases}</td>
                    <td style={{ padding: "2px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtMoney(l.salePrice)}</td>
                    <td style={{ padding: "2px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtMoney(amt)}</td>
                    <td style={{ padding: "2px 4px", textAlign: "center" }}>{lsc ? <span style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: lsc.bg, color: lsc.text, border: "1px solid " + lsc.border, boxShadow: SALE_STATUS_BEVEL }}>{t[lineStatus] || lineStatus}</span> : "—"}</td>
                  </tr>;
                })}</tbody>
                <tfoot>
                  <tr style={{ background: "var(--color-bg)", borderTop: "2px solid var(--color-accent)" }}>
                    <td colSpan={2} style={{ padding: "6px 4px", textAlign: "right", fontSize: 10, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{lang === "es" ? "Total" : "Total"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{(fresh.lines || []).reduce(function (s, l) { return s + (parseInt(l.pallets, 10) || 0); }, 0)}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{(fresh.lines || []).reduce(function (s, l) { return s + (parseInt(l.cases, 10) || 0); }, 0)}</td>
                    <td></td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtMoney((fresh.lines || []).reduce(function (s, l) { return s + (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0); }, 0))}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            </SectionBox>
          </div>
          {/* 2026-10-20, Arturo: "debajo de products debe verse pallet
              allocations que actualmente solo aparece en modo edicion"
              — read-only mirror of InvoiceForm's own editable table
              (same columns/meta lookup, duplicated per this file's
              existing palletMetaFor-in-InvoiceForm/getShipments-
              everywhere-else split), since view mode never edits
              anything here — editing pallet status stays in Edit, per
              REGLA DE STATUS DE PALLET. */}
          {(fresh.palletAllocations || []).length > 0 && (
            <div>
              <SectionBox id="pallets" title={t.palletAllocations} open={sectionsOpen} toggle={toggleSection}>
              <div style={{ overflowX: "auto" }}>
                {/* 2026-10-20, Arturo: exact alignment spec — "lot,
                    pallet #, tag id: centrado contenido y header.
                    product header y contenido alineado a la izquierda
                    y muestras descripcion no comoddity. variety,
                    grower y status header y contenido centrado. weight
                    header centrado, contenido alineado a la derecha."
                    Cases follows the same numeric-centers-everywhere-
                    else rule as the Products table above (not money,
                    so center rather than right). Product now builds
                    the same auto-description (commodity + units/size +
                    category + label) the rest of the app shows instead
                    of the bare commodity name. "muestrame como se
                    verian los headers con una linea divisoria entre
                    cada columna" — a 1px vertical rule (borderLeft) on
                    every header except the first. */}
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                    <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.lot}</th>
                    <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", borderLeft: "1px solid var(--color-border)" }}>{t.palletNumCol}</th>
                    <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", borderLeft: "1px solid var(--color-border)" }}>{t.tagIdCol}</th>
                    <th style={{ padding: "5px 4px", textAlign: "left", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", borderLeft: "1px solid var(--color-border)" }}>{t.product}</th>
                    <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", borderLeft: "1px solid var(--color-border)" }}>{t.varietyCol}</th>
                    <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", borderLeft: "1px solid var(--color-border)" }}>{t.allocCases}</th>
                    <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", borderLeft: "1px solid var(--color-border)" }}>{t.growerCol}</th>
                    <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", borderLeft: "1px solid var(--color-border)" }}>{t.weightCol}</th>
                    <th style={{ padding: "5px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", borderLeft: "1px solid var(--color-border)" }}>{t.palletStatusCol}</th>
                  </tr></thead>
                  <tbody>{fresh.palletAllocations.map(function(a, idx) {
                    var shp = getShipments().find(function (s) { return String(s.lot) === String(a.lot); });
                    var meta = (shp && (shp.palletManifest || []).find(function (p) { return String(p.palletNum) === String(a.palletNum); })) || {};
                    var w = docPalletWeight({ size: meta.size, unitsCase: meta.unitsCase, casesPallet: a.cases });
                    /* 2026-10-30, Arturo (PU 17475/lote 58074): "en el
                       manifiesto, los pallets en modo no edicion dicen
                       pending pick up y en modo edicion cambian a su
                       status real" — this view-mode table never checked
                       twinRiverPalletStatus, only the raw (often
                       unset/manual) a.saleStatus; the edit-mode table
                       got this fix already. Same precedence as there:
                       live from the shipment first for ClariFresh. */
                    var shownStatus = twinRiverPalletStatus(shp, a.palletNum) || a.saleStatus || resolvePendingStage([fresh], slPassingIndex);
                    var sc = SALE_STATUS_COLORS[shownStatus];
                    var metaDesc = [meta.productBase, (meta.unitsCase && meta.size) ? (meta.unitsCase + "/" + meta.size) : "", meta.category, meta.labelInitials ? (meta.labelInitials + " Label") : ""].filter(Boolean).join(" ") || meta.productBase || "—";
                    return <tr key={idx} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-accent)" }}>{a.lot || "—"}</td>
                      <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{a.palletNum || "—"}</td>
                      <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{meta.tagId || "—"}</td>
                      <td style={{ padding: "6px 4px", textAlign: "left", fontSize: 11 }}>{metaDesc}</td>
                      <td style={{ padding: "6px 4px", textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>{meta.variety || "—"}</td>
                      <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{a.cases || "—"}</td>
                      <td style={{ padding: "6px 4px", textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>{meta.growerCode || "—"}</td>
                      <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{w.lb > 0 ? w.lb.toFixed(1) + " lb" : "—"}</td>
                      <td style={{ padding: "6px 4px", textAlign: "center" }}>{sc ? <span style={{ padding: "2px 8px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: sc.bg, color: sc.text, border: "1px solid " + sc.border, boxShadow: SALE_STATUS_BEVEL, whiteSpace: "nowrap" }}>{t[shownStatus] || shownStatus}</span> : (t[shownStatus] || "—")}</td>
                    </tr>;
                  })}</tbody>
                </table>
              </div>
              </SectionBox>
            </div>
          )}
          <div>
            <SectionBox id="payments" title={<span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <span>{t.paymentsSection}</span>
              <button type="button" onClick={function (e) { e.stopPropagation(); setAddPaymentModalOpen(true); }} style={{ padding: 0, border: "none", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600, textDecoration: "underline" }}>+ {lang === "es" ? "Agregar Pago" : "Add Payment"}</button>
            </span>} open={sectionsOpen} toggle={toggleSection}>
              <PaymentsSection payments={fresh.payments} total={calcInvoiceTotal(fresh)} t={t} />
            </SectionBox>
          </div>
          {addPaymentModalOpen && (
            <BatchDepositModal
              invoices={[fresh]}
              t={t}
              lang={lang}
              onConfirm={applyPaymentToInvoice}
              onCancel={function () { setAddPaymentModalOpen(false); }}
            />
          )}
          </div>
        </div>
        </div>
        </>
      )}
      {emailDialogOpen && (function () {
        var clients = loadLS("gfi-clients", []);
        var cli = clients.find(function (c) { return idMatches(c, fresh.clientId); });
        var company = loadCompanyProfile();
        var distList = (cli && cli.distributionList && cli.distributionList.length > 0)
          ? cli.distributionList
          : (cli && cli.contacts || []).filter(function (c) { return c && c.email; }).map(function (c) {
              var name = [c.firstName, c.lastName].filter(Boolean).join(" ") || c.nombre || c.name || "";
              return { name: name, email: c.email, docs: { inbound: true, packing: true, invoice: true, master: true } };
            });
        /* Detect brand by client name (TR → GFI; FS → SweetLeaf).
           Falls back to GFI for any other client. */
        var nameLower = (fresh.clientName || (cli && cli.shortName) || "").toLowerCase();
        var brand = nameLower.indexOf("four seasons") >= 0 ? "sl" : "gfi";
        var pu = fresh.puNumber || "—";
        var po = fresh.clientRef || "—";
        var lot = fresh.lot || "—";
        /* Recipients start checked only if they're actually opted
           into at least one of the doc kinds this dialog sends
           (inbound/pick/packing/master/invoice) — a contact flagged
           ONLY for "compliance" (or with every relevant flag off)
           must not appear pre-selected for a shipment/invoice email
           (Arturo: "me aparecen seleccionados... aunque a ellos solo
           los tengo marcados para compliance"). A contact with no
           `docs` object at all (legacy data) still defaults to
           checked, same as before. They stay listed either way so
           the user can still tick them by hand for a one-off case. */
        /* 2026-10-01 — Arturo: "fui para enviar uno manual y me da
           toda la lista de contactos seleccionados, eso no es
           correcto, porque estas solo van a personas especificas...
           por ejemplo ahi estas la aduana, la bodega, logistica, y
           otros que no deben recibir eso". Protection Status
           Reminders must pre-check ONLY contacts explicitly flagged
           "protection" in the client's Distribution List (new
           CLIENT_DOC_KINDS entry, ClientRegistry.js) — never the
           broad inbound/packing/invoice/master set the normal
           shipment-document emails use, which is exactly why
           customs/warehouse/logistics contacts were showing up
           checked by default (they're flagged for those doc kinds,
           not for Protection). Contacts stay listed either way so
           Arturo can still tick one by hand for a one-off case. */
        var isProtectionReminderDialog = emailDialogDefaultKind === "clientProtectionReminder";
        var recipientDocKinds = isProtectionReminderDialog ? ["protection"] : ["inbound", "packing", "invoice", "master"];
        var recipients = distList
          .filter(function (c) { return c && c.email; })
          .map(function (c) {
            /* 2026-10-07 — Arturo: "la alerta de proteccion de twin
               river se fue a todos los contactos... solo deberia
               haberse ido a algunos... a los que tengo seleccionados".
               The "!c.docs → receives everything" fallback below is
               correct for the regular document kinds (inbound/pick/
               packing/master/invoice — missing config really does mean
               "never configured, so don't silently exclude them"), but
               Protection is the one category this app has always kept
               narrow and explicit (same principle as the Protection
               Notice to the shipper) — a contact with no docs object
               at all must NOT be swept in by that broad fallback here,
               only one actually opted into docs.protection. */
            var hasAnyRelevantDoc = isProtectionReminderDialog
              ? !!(c.docs && c.docs.protection)
              : (!c.docs || recipientDocKinds.some(function (k) { return !!c.docs[k]; }));
            return { name: c.name || "", email: c.email, checked: hasAnyRelevantDoc };
          });
        /* Doc options. Initial checked state mirrors the client's
           distribution-list config: a doc is pre-checked iff at
           least one contact in the list has its corresponding doc
           flag enabled in Client Profile. Pick stays off — it's
           only flipped on via the Inbound↔Pick radio. If the client
           has no distribution list at all, every doc starts
           unchecked and the user picks manually. */
        var anyContactHas = function (k) {
          return distList.some(function (c) { return c && c.docs && c.docs[k]; });
        };
        /* 2026-10-07 — Arturo: "los que envio a twin river desde
           ground-fresh solo deben mostrar preseleccionado el master
           inbound" — scoped to Twin River specifically (confirmed:
           "el cambio... solo aplicaba para twin river"), not every
           Ground-Fresh client. Every other client keeps the normal
           distribution-list-driven defaults (anyContactHas) below. */
        var isTwinRiverEmail = nameLower.indexOf("twin river") >= 0;
        var docOptions = isTwinRiverEmail ? [
          { key: brand + "-inbound", label: t.emailCustomerDocInbound, checked: false },
          { key: brand + "-pick",    label: t.emailCustomerKindPick, checked: false },
          { key: brand + "-packing", label: t.emailCustomerDocPacking, checked: false },
          { key: brand + "-master",  label: t.emailCustomerDocMaster, checked: true },
          { key: "invoice",          label: t.emailCustomerDocInvoice, checked: false },
        ] : [
          { key: brand + "-inbound", label: t.emailCustomerDocInbound, checked: anyContactHas("inbound") },
          { key: brand + "-pick",    label: t.emailCustomerKindPick, checked: false },
          { key: brand + "-packing", label: t.emailCustomerDocPacking, checked: anyContactHas("packing") },
          { key: brand + "-master",  label: t.emailCustomerDocMaster, checked: anyContactHas("master") },
          { key: "invoice",          label: t.emailCustomerDocInvoice, checked: anyContactHas("invoice") },
        ];
        var tpl = function (s) {
          return String(s || "")
            .replace("{pu}", pu)
            .replace("{po}", po)
            .replace("{lot}", lot);
        };
        var ctx = {
          clients: clients,
          shipments: loadLS("gfi-shipments", []),
          warehouses: loadLS("gfi-warehouses", []),
          company: company,
        };
        var buildAttachments = async function (kinds) {
          /* If any selected attachment is a Pick Ticket we surface
             the same PickTicketRemarksModal the print path uses so
             the emailed PDF carries the locked temperature line +
             whatever extra notes the operator picked. The brand
             passed to the modal mirrors the email brand so the
             label says "SweetLeaf Produce" vs "Ground-Fresh". */
          var needsPick = kinds.some(function (k) { return /(^|[-:])pick$/.test(k); });
          var pickExtras = null;
          if (needsPick) {
            var shpForLD = findShipmentForInvoice(fresh, ctx.shipments);
            var etaLD = shpForLD ? (shpForLD.dateArrival || "") : "";
            var defLD = "";
            if (etaLD) {
              var dLD = new Date(etaLD + "T00:00:00");
              if (!isNaN(dLD.getTime())) {
                dLD.setDate(dLD.getDate() + 1);
                defLD = dLD.getFullYear() + "-" + String(dLD.getMonth() + 1).padStart(2, "0") + "-" + String(dLD.getDate()).padStart(2, "0");
              }
            }
            pickExtras = await promptPickRemarks(brand, defLD);
          }
          var out = [];
          for (var i = 0; i < kinds.length; i++) {
            var isPick = /(^|[-:])pick$/.test(kinds[i]);
            var att = await buildInvoiceDocPdf(fresh, kinds[i], ctx, isPick ? pickExtras : null);
            out.push(att);
          }
          return out;
        };
        /* Inline doc embed for email body. Full PDF docs don't render
           well inside Gmail/Outlook (their CSS sanitisers strip
           <style> tags and complex layouts) — and the user prefers
           a short summary anyway. We render JUST the product table
           (Product / Description / Pallets / Fill / UOM + totals)
           using inline styles so every email client renders it
           identically. */
        var buildEmbedHtml = async function (/* kinds */) {
          var lines = (fresh.lines || []).filter(function (l) { return l && (l.productName || l.productBase); });
          if (lines.length === 0) return "";
          var esc = function (s) { return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); };
          var totals = { pallets: 0, cases: 0 };
          var rows = lines.map(function (l) {
            var p = parseInt(l.pallets, 10) || 0;
            var c = parseInt(l.cases, 10) || 0;
            totals.pallets += p; totals.cases += c;
            return {
              product: l.productBase || l.productName || "",
              desc: l.productDescription || "",
              pallets: p,
              cases: c,
              uom: l.uom || "Flats",
            };
          });
          var th = 'style="text-align:left;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          var thNum = 'style="text-align:right;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          var thCtr = 'style="text-align:center;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          var html = '<div style="font-family:Arial,sans-serif;font-size:13px;color:#1a1a1a;line-height:1.4">';
          html += '<table cellspacing="0" cellpadding="0" style="border-collapse:collapse;width:100%;border:1px solid #d0d5dd;background:#ffffff">';
          html += '<thead><tr style="background:#f7f8fa">';
          html += '<th ' + th + '>Product</th>';
          html += '<th ' + th + '>Description</th>';
          html += '<th ' + thNum + '>Pallets</th>';
          html += '<th ' + thNum + '>Fill (Cases)</th>';
          html += '<th ' + thCtr + '>UOM</th>';
          html += '</tr></thead><tbody>';
          rows.forEach(function (r) {
            html += '<tr style="border-bottom:1px solid #eef0f3">';
            html += '<td style="padding:7px 10px;font-weight:600">' + esc(r.product) + '</td>';
            html += '<td style="padding:7px 10px;color:#444">' + esc(r.desc) + '</td>';
            html += '<td style="padding:7px 10px;text-align:right;font-family:Courier New,monospace">' + r.pallets + '</td>';
            html += '<td style="padding:7px 10px;text-align:right;font-family:Courier New,monospace;font-weight:600">' + r.cases + '</td>';
            html += '<td style="padding:7px 10px;text-align:center">' + esc(r.uom) + '</td>';
            html += '</tr>';
          });
          html += '<tr style="background:#f7f8fa;font-weight:700">';
          html += '<td colspan="2" style="padding:9px 10px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:0.04em">Totals</td>';
          html += '<td style="padding:9px 10px;text-align:right;font-family:Courier New,monospace">' + totals.pallets + '</td>';
          html += '<td style="padding:9px 10px;text-align:right;font-family:Courier New,monospace">' + totals.cases + '</td>';
          html += '<td></td>';
          html += '</tr></tbody></table></div>';
          return html;
        };
        /* Email body composed from the user's "invoice" template
           (defaults to "inbound" when no doc-kind override given). */
        var tplCfgInbound = pickEmailTemplate(company, "inbound");
        var tplCfgPick    = pickEmailTemplate(company, "pickTicket");
        var sup = (loadLS("gfi-suppliers", []) || []).find(function (s) { var ship = findShipmentForInvoice(fresh, ctx.shipments); return ship && idMatches(s, ship.supplierId); });
        var wh = (ctx.warehouses || []).find(function (w) { var ship = findShipmentForInvoice(fresh, ctx.shipments); return ship && idMatches(w, ship.warehouseId); });
        var shp = findShipmentForInvoice(fresh, ctx.shipments);
        var tplValues = {
          pu: pu, po: po, lot: lot,
          arrival: (shp && (shp.dateArrival || shp.dateDeparture)) || "",
          shipper: (sup && sup.shortName) || (shp && shp.shipperName) || "",
          client: (cli && (cli.shortName || cli.fullName)) || fresh.clientName || "",
          warehouse: (wh && (wh.shortName || wh.fullName)) || "",
        };
        /* Each kind uses its OWN template from Company Profile so
           Arturo can customize body + fields independently. */
        var tplBodyInbound = composeEmailBody(tplCfgInbound, tplValues, { labels: { pu: "Inbound" } });
        var tplBodyPick    = composeEmailBody(tplCfgPick, tplValues, { labels: { pu: "PU#" } });
        /* 2026-09-30 — Arturo: for Twin River shipments specifically,
           he already adds the customs broker as a manual recipient on
           this same "Email Customer" send, and wants the DEFAULT body
           to ask them (in the same email) to keep GFI posted on the
           border crossing — not a separate email, not a global change
           to every client's template. Scoped to Twin River only via
           the same nameLower brand-detection check already computed
           above. Inserted before the closing "Thanks,"/"Saludos" line,
           same injection point composeEmailBody itself uses for the
           fact rows, so it reads as the last paragraph before sign-off. */
        var appendBrokerCrossingRequest = function (body) {
          var para = "Customs Broker: please keep us informed on the crossing status of this shipment.";
          var m = body.match(/\n\nThanks,[\s\S]*$/i) || body.match(/\n\nSaludos[\s\S]*$/i);
          if (m) return body.substring(0, m.index) + "\n\n" + para + body.substring(m.index);
          return body + "\n\n" + para;
        };
        if (nameLower.indexOf("twin river") >= 0) {
          tplBodyInbound = appendBrokerCrossingRequest(tplBodyInbound);
          tplBodyPick = appendBrokerCrossingRequest(tplBodyPick);
        }
        /* Subject + body selector: Inbound (default) ↔ Pick Ticket.
           Picking one swaps the subject + body AND flips the
           corresponding doc checkbox. */
        var kindOptions = [
          { value: "inbound", label: t.emailCustomerKindInbound, subject: tpl(t.emailCustomerSubjectInbound), body: tplBodyInbound, docKeyOn: brand + "-inbound", docKeyOff: brand + "-pick" },
          { value: "pick",    label: t.emailCustomerKindPick,    subject: tpl(t.emailCustomerSubjectPick),    body: tplBodyPick,    docKeyOn: brand + "-pick",    docKeyOff: brand + "-inbound" },
        ];
        /* Reminder kind only ever gets preselected (via emailDialogDefaultKind,
           set by the kebab menu's "Send Pickup Reminder" action) — never
           shown as a normal radio choice alongside Inbound/Pick Ticket,
           since it only makes sense while the PU is still Pending Pick
           Up. Appended after the fact rather than folded into the array
           literal above so the normal Email Customer flow's radio list
           stays exactly Inbound/Pick Ticket, unchanged. */
        /* 2026-10-01 — "clientProtectionReminder" kind: Protection
           Status Reminder to a non-Four-Seasons client. Always from
           alerts@gfinexus.com ("GFI Automated"), no personal signature
           by default (Arturo: "sin firma mia aunque las respuestas
           viajen a mi cuenta") — same noSignatureByDefault flag already
           used by SweetLeafPortal.js's own Protection Reminder. */
        var isClientProtectionReminderKind = emailDialogDefaultKind === "clientProtectionReminder";
        var kindOptionsWithReminder = kindOptions.concat([
          { value: "pendingPickupReminder", label: t.emailReminderKindPickup, subject: tpl(t.emailReminderSubjectPickup), body: tpl(t.emailReminderDefaultBody), noAttachments: true },
          { value: "clientProtectionReminder", label: t.emailClientProtectionReminderKind, subject: tpl(t.emailClientProtectionReminderSubject), body: tpl(t.emailClientProtectionReminderBody), noAttachments: true, noSignatureByDefault: true },
        ]);
        var gfiAutomatedAcct = (company.emailAccounts || []).find(function (a) { return (a.name || "").toLowerCase().indexOf("automat") >= 0; });
        return (
          <EmailDialog
            lang={lang}
            title={(emailDialogDefaultKind === "pendingPickupReminder" ? t.emailReminderPickup : isClientProtectionReminderKind ? t.emailClientProtectionReminder : t.emailCustomerTitle) + " — " + (fresh.clientName || (cli && (cli.shortName || cli.fullName)) || "—")}
            recipients={recipients}
            docOptions={docOptions}
            kindOptions={kindOptionsWithReminder}
            defaultKind={emailDialogDefaultKind}
            defaultSubject={emailDialogDefaultKind === "pendingPickupReminder" ? tpl(t.emailReminderSubjectPickup) : isClientProtectionReminderKind ? tpl(t.emailClientProtectionReminderSubject) : tpl(t.emailCustomerSubjectInbound)}
            defaultBody={emailDialogDefaultKind === "pendingPickupReminder" ? tpl(t.emailReminderDefaultBody) : isClientProtectionReminderKind ? tpl(t.emailClientProtectionReminderBody) : (tplBodyInbound || tpl(t.emailCustomerDefaultBody))}
            fromEmail={isClientProtectionReminderKind ? ((gfiAutomatedAcct || {}).maskedFrom || company.outboundEmail || "") : (brand === "sl" ? ((company.emailAccounts || []).find(function (a) { return (a.name || "").toLowerCase().indexOf("sweetleaf") >= 0; }) || {}).maskedFrom || company.outboundEmail || "" : company.outboundEmail || "")}
            /* 2026-09-30 (Arturo: "ME APARECEN NUEVAMENTE 4 CUENTAS DE
               CORREOS DE SALIDA") — this flow legitimately needs both
               Ground-Fresh and Sweetleaf accounts available (brand
               decides the default), so it can't exclude "sweetleaf"
               like the personal-only flows — but the automated
               accounts (GFI Automated / SL Automated) were never
               meant to be manually selectable here either.
               2026-10-01 — EXCEPT for clientProtectionReminder, which
               must ALWAYS come from the automated account, manual or
               not (Arturo: "aunque las haga manuales salen de ahi") —
               that one kind gets ONLY the automated account offered. */
            /* 2026-10-07 — Arturo: "remueve el email de sweetleaf de la
               parte superior, de hecho ese no debe salir en nada de
               ground-fresh" — SweetLeaf stays offered only when this
               invoice's own brand IS SweetLeaf (Four Seasons); any
               Ground-Fresh client (Twin River, etc.) never sees it as
               a selectable From account here. */
            emailAccounts={isClientProtectionReminderKind ? (gfiAutomatedAcct ? [gfiAutomatedAcct] : []) : (company.emailAccounts || []).filter(function (a) { var n = (a.name || "").toLowerCase(); if (n.indexOf("automat") >= 0) return false; if (brand !== "sl" && n.indexOf("sweetleaf") >= 0) return false; return true; })}
            signatures={isClientProtectionReminderKind ? [] : (company.emailSignatures || [])}
            postProcessHtmlBody={function (html, resolvedFrom) { return appendDisclosureByAccount(html, resolvedFrom, company, readSlSettingsForDisclosure()); }}
            buildAttachments={buildAttachments}
            buildEmbedHtml={buildEmbedHtml}
            onClose={function () { setEmailDialogOpen(false); }}
            onSent={function () {
              /* Stamp emailSentAt on successful send so the list view
                 can render the "✉ sent" badge. Routes through the
                 parent's `onSave` (same path the rest of DetailPanel
                 uses for status flips, sale-status edits, etc.) —
                 that's what persists to Supabase + LS in lockstep. */
              var stamp = { emailSentAt: new Date().toISOString() };
              if (isClientProtectionReminderKind) {
                stamp.protectionReminderSentAt = new Date().toISOString();
                stamp.protectionReminderAutoCount = (parseInt(fresh.protectionReminderAutoCount, 10) || 0) + 1;
              }
              try { onSave(Object.assign({}, fresh, stamp)); }
              catch (e) { console.warn("[invoices] could not stamp emailSentAt:", e && e.message); }
            }}
          />
        );
      })()}
      {emailWarehouseOpen && (function () {
        var company = loadCompanyProfile();
        var shipments = loadLS("gfi-shipments", []);
        var warehouses = loadLS("gfi-warehouses", []);
        var clients = loadLS("gfi-clients", []);
        var cli = clients.find(function (c) { return idMatches(c, fresh.clientId); });
        var shp = findShipmentForInvoice(fresh, shipments);
        var wh = shp ? warehouses.find(function (w) { return idMatches(w, shp.warehouseId); }) : null;
        var pu = fresh.puNumber || "—";
        var po = fresh.clientRef || "—";
        var lot = fresh.lot || "—";
        var whName = wh ? (wh.shortName || wh.fullName || "Warehouse") : "Warehouse";
        /* Brand detection mirrors the customer dialog so attachments
           pick up the right SweetLeaf / GFI templates. */
        var nameLower = (fresh.clientName || (cli && cli.shortName) || "").toLowerCase();
        var brand = nameLower.indexOf("four seasons") >= 0 ? "sl" : "gfi";
        var recipients = (wh && Array.isArray(wh.contacts) ? wh.contacts : [])
          .filter(function (c) { return c && c.email; })
          .map(function (c) {
            return { name: (c.name || "") + " · " + whName, email: c.email, checked: true };
          });
        var tplCfgInbound = pickEmailTemplate(company, "inbound");
        var tplCfgPick    = pickEmailTemplate(company, "pickTicket");
        var sup = (loadLS("gfi-suppliers", []) || []).find(function (s) { return shp && idMatches(s, shp.supplierId); });
        var tplValues = {
          pu: pu, po: po, lot: lot,
          arrival: (shp && (shp.dateArrival || shp.dateDeparture)) || "",
          shipper: (sup && sup.shortName) || (shp && shp.shipperName) || "",
          client: (cli && (cli.shortName || cli.fullName)) || fresh.clientName || "",
          warehouse: whName,
        };
        var bodyInbound = composeEmailBody(tplCfgInbound, tplValues, { labels: { pu: "Inbound" } });
        var bodyPick    = composeEmailBody(tplCfgPick, tplValues, { labels: { pu: "PU#" } });
        var tpl = function (s) { return String(s || "").replace("{pu}", pu).replace("{po}", po).replace("{lot}", lot); };
        var subjectInbound = tpl(t.emailWarehouseDefaultSubject);
        var subjectPick    = tpl(t.emailCustomerSubjectPick);
        /* 2026-10-07 — Arturo: "cuando envio un email a la bodega desde
           la factura para enviar el Pick Up unicamente los que
           pertenezcan a four seasons por default debe preseleccionar
           que es Pick Ticket, y los documentos preseleccionados a
           enviar son Pick Ticket y Driver Sign-Off". Scoped to Four
           Seasons (brand "sl") only — Ground-Fresh clients keep the
           existing Inbound+Packing List default untouched. */
        var isFsWarehouseEmail = brand === "sl";
        var docOptions = isFsWarehouseEmail ? [
          { key: brand + "-inbound", label: t.emailCustomerDocInbound, checked: false },
          { key: brand + "-pick",    label: t.emailCustomerKindPick, checked: true },
          { key: brand + "-packing", label: t.emailCustomerDocPacking, checked: false },
          { key: brand + "-master",  label: t.emailCustomerDocMaster, checked: false },
          { key: brand + "-driversignoff", label: "Driver Sign-Off", checked: true },
        ] : [
          { key: brand + "-inbound", label: t.emailCustomerDocInbound, checked: true },
          { key: brand + "-pick",    label: t.emailCustomerKindPick, checked: false },
          { key: brand + "-packing", label: t.emailCustomerDocPacking, checked: true },
          { key: brand + "-master",  label: t.emailCustomerDocMaster, checked: false },
          { key: brand + "-driversignoff", label: "Driver Sign-Off", checked: false },
        ];
        var ctx = { clients: clients, shipments: shipments, warehouses: warehouses, company: company };
        var buildAttachments = async function (kinds) {
          /* Warehouse-email Pick Ticket runs through the same remarks
             prompt the customer-email path uses — both PDFs must be
             identical so warehouse staff + the customer see the same
             temperature line, loading date, and any extra notes. */
          var needsPick = kinds.some(function (k) { return /(^|[-:])pick$/.test(k); });
          var pickExtras = null;
          if (needsPick) {
            var shpForLD = findShipmentForInvoice(fresh, shipments);
            var etaLD = shpForLD ? (shpForLD.dateArrival || "") : "";
            var defLD = "";
            if (etaLD) {
              var dLD = new Date(etaLD + "T00:00:00");
              if (!isNaN(dLD.getTime())) {
                dLD.setDate(dLD.getDate() + 1);
                defLD = dLD.getFullYear() + "-" + String(dLD.getMonth() + 1).padStart(2, "0") + "-" + String(dLD.getDate()).padStart(2, "0");
              }
            }
            pickExtras = await promptPickRemarks(brand, defLD);
          }
          var out = [];
          for (var i = 0; i < kinds.length; i++) {
            var isPick = /(^|[-:])pick$/.test(kinds[i]);
            var att = await buildInvoiceDocPdf(fresh, kinds[i], ctx, isPick ? pickExtras : null);
            out.push(att);
          }
          return out;
        };
        var buildEmbedHtml = async function () {
          var lines = (fresh.lines || []).filter(function (l) { return l && (l.productName || l.productBase); });
          if (lines.length === 0) return "";
          var esc = function (s) { return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); };
          var totals = { pallets: 0, cases: 0 };
          var rows = lines.map(function (l) {
            var p = parseInt(l.pallets, 10) || 0;
            var c = parseInt(l.cases, 10) || 0;
            totals.pallets += p; totals.cases += c;
            return { product: l.productBase || l.productName || "", desc: l.productDescription || "", pallets: p, cases: c, uom: l.uom || "Flats" };
          });
          var th = 'style="text-align:left;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          var thNum = 'style="text-align:right;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          var thCtr = 'style="text-align:center;padding:8px 10px;border-bottom:1px solid #d0d5dd;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#1a1a1a"';
          var html = '<div style="font-family:Arial,sans-serif;font-size:13px;color:#1a1a1a;line-height:1.4">';
          html += '<table cellspacing="0" cellpadding="0" style="border-collapse:collapse;width:100%;border:1px solid #d0d5dd;background:#ffffff">';
          html += '<thead><tr style="background:#f7f8fa"><th ' + th + '>Product</th><th ' + th + '>Description</th><th ' + thNum + '>Pallets</th><th ' + thNum + '>Fill (Cases)</th><th ' + thCtr + '>UOM</th></tr></thead><tbody>';
          rows.forEach(function (r) {
            html += '<tr style="border-bottom:1px solid #eef0f3"><td style="padding:7px 10px;font-weight:600">' + esc(r.product) + '</td><td style="padding:7px 10px;color:#444">' + esc(r.desc) + '</td><td style="padding:7px 10px;text-align:right;font-family:Courier New,monospace">' + r.pallets + '</td><td style="padding:7px 10px;text-align:right;font-family:Courier New,monospace;font-weight:600">' + r.cases + '</td><td style="padding:7px 10px;text-align:center">' + esc(r.uom) + '</td></tr>';
          });
          html += '<tr style="background:#f7f8fa;font-weight:700"><td colspan="2" style="padding:9px 10px;text-align:right;font-size:11px;text-transform:uppercase;letter-spacing:0.04em">Totals</td><td style="padding:9px 10px;text-align:right;font-family:Courier New,monospace">' + totals.pallets + '</td><td style="padding:9px 10px;text-align:right;font-family:Courier New,monospace">' + totals.cases + '</td><td></td></tr>';
          html += '</tbody></table></div>';
          return html;
        };
        return (
          <EmailDialog
            lang={lang}
            title={t.emailWarehouseTitle + " — " + whName}
            recipients={recipients}
            docOptions={docOptions}
            kindOptions={[
              { value: "inbound", label: t.emailCustomerKindInbound, subject: subjectInbound, body: bodyInbound, docKeyOn: brand + "-inbound", docKeyOff: brand + "-pick" },
              { value: "pick",    label: t.emailCustomerKindPick,    subject: subjectPick,    body: bodyPick,    docKeyOn: brand + "-pick",    docKeyOff: brand + "-inbound" },
            ]}
            defaultKind={isFsWarehouseEmail ? "pick" : "inbound"}
            defaultSubject={isFsWarehouseEmail ? subjectPick : subjectInbound}
            defaultBody={isFsWarehouseEmail ? bodyPick : bodyInbound}
            fromEmail={((company.emailAccounts || []).find(function (a) { return (a.name || "").toLowerCase().indexOf("sweetleaf") < 0 && a.maskedFrom; }) || {}).maskedFrom || company.outboundEmail || ""}
            /* 2026-09-30 — same "automat" exclusion as the Email
               Customer flow above; Warehouse recipients should never
               see the automated accounts as selectable options.
               2026-10-07 — Arturo: also exclude SweetLeaf — warehouse
               emails must always go from Ground-Fresh, never SweetLeaf
               (same REGLA DE ENVÍO DE EMAILS boundary used everywhere
               else: shippers/warehouses/customs are Ground-Fresh-only). */
            emailAccounts={(company.emailAccounts || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("automat") < 0 && n.indexOf("sweetleaf") < 0; })}
            signatures={company.emailSignatures || []}
            postProcessHtmlBody={function (html, resolvedFrom) { return appendDisclosureByAccount(html, resolvedFrom, company, readSlSettingsForDisclosure()); }}
            buildAttachments={buildAttachments}
            buildEmbedHtml={buildEmbedHtml}
            onClose={function () { setEmailWarehouseOpen(false); }}
            onSent={function () {
              try { onSave(Object.assign({}, fresh, { emailWarehouseSentAt: new Date().toISOString() })); }
              catch (e) { console.warn("[invoices] could not stamp emailWarehouseSentAt:", e && e.message); }
            }}
          />
        );
      })()}
      {/* Email Shipper — Protection (2026-09-14, Arturo): "necesito
          enviar a los shippers un email desde dentro de un invoice
          cuando ese invoice paso a proteccion... debe dejarme elejir a
          los destinatarios que tiene ese shipper, y debe permitirme en
          la ventana de envio, agregar los archivos que me envio el
          cliente". Always Ground-Fresh masked account (shippers are
          never Four Seasons/AGROCAP, see REGLA DE ENVÍO DE EMAILS) and
          always a Spanish body regardless of the app's own lang toggle
          — shippers are Mexican, per CLAUDE.md's own communication
          rules. Recipients come from the linked shipment's supplier,
          filtered by the new "protection" SHIPPER_EMAIL_CATEGORIES
          entry (emailHelpers.js) — same opt-in/opt-out pattern already
          used for shipment/sales_report/payment_confirmation. */}
      {emailShipperProtectionOpen && (function () {
        var company = loadCompanyProfile();
        var shipments = loadLS("gfi-shipments", []);
        var suppliers = loadLS("gfi-suppliers", []);
        var shp = findShipmentForInvoice(fresh, shipments);
        var sup = shp ? suppliers.find(function (s) { return idMatches(s, shp.supplierId); }) : null;
        var supName = sup ? (sup.shortName || sup.fullName || "Shipper") : "Shipper";
        var contactDisplayName = function (c) {
          return c.name || [c.firstName, c.lastName].filter(Boolean).join(" ") || [c.nombre, c.apPaterno].filter(Boolean).join(" ") || "";
        };
        /* 2026-10-08, Arturo: "quisiera que al enviar... avisos de
           proteccion al shipper... como en reportes de calidad al
           shipper... siempre se anexara la hoja de tolerancias" — used
           only to decide whether to attach the fixed reference PDF
           below, never shown anywhere the shipper can see (this is
           purely internal — the shipper must never learn the client's
           identity, see the confidentiality rule right below this
           block: "el shipper no debe ver el nombre del cliente en
           ningun lado"). */
        /* 2026-10-08 — "ese anexo solo se debe agregar si la factura o
           embarque contiene blackberry en sus productos porque esa
           hoja de calidad es para blackberry, si fuera solo arandano
           o frambuesa, no se anexa" — Twin River alone isn't enough,
           the invoice's own lines must include Blackberry. */
        /* 2026-10-22, Arturo (same fix as ShipmentRegistry.js's Quality
           Report send): "la hoja de defectos que se agrega al email
           debe ser de acuerdo al producto que se esta reportando" —
           was Blackberry-only; generalized so Blueberry invoices
           attach their own spec sheet too, same Twin River gate. */
        var SPEC_SHEETS_BY_PRODUCT = [
          { match: "blackberry", filename: BLACKBERRY_SPEC_FILENAME, build: buildBlackberrySpecAtOriginBlob },
          { match: "blueberry", filename: BLUEBERRY_SPEC_FILENAME, build: buildBlueberrySpecAtOriginBlob },
        ];
        var isTwinRiverInvoice = String(fresh.clientName || "").toLowerCase().indexOf("twin river") >= 0;
        var specSheetsToAttach = isTwinRiverInvoice ? SPEC_SHEETS_BY_PRODUCT.filter(function (spec) {
          return (fresh.lines || []).some(function (l) {
            return String(l.productBase || l.productName || "").toLowerCase().indexOf(spec.match) >= 0;
          });
        }) : [];
        /* filterContactsByCategory treats a contact with no
           emailPreferences configured yet as "receives everything" —
           but every contact created BEFORE "protection" existed as a
           category already has an explicit preferences array that
           simply never mentions it, so the filtered list comes back
           empty for every supplier that existed before today (caught
           live testing this feature: Terey Avocados' 3 contacts all
           had explicit shipment/sales_report/payment_confirmation
           arrays, none included "protection", DESTINATARIOS rendered
           blank). Arturo's actual ask was just "let me pick from that
           shipper's contacts" — not "respect this brand-new opt-in
           category that nobody has configured yet" — so fall back to
           every contact with an email when the category filter yields
           nothing, same "never show zero recipients" principle
           filterContactsByCategory's own docs already call for. */
        var protectionRecipientSource = filterContactsByCategory(sup && sup.contacts, "protection");
        /* Fallback re-adding opted-out contacts removed 2026-09-25 (it
           overrode explicit Email Preferences) — see ShipmentRegistry.js. */
        var recipients = protectionRecipientSource
          .map(function (c) { return { name: (contactDisplayName(c) ? contactDisplayName(c) + " · " : "") + supName, email: c.email, checked: true }; });

        /* Recomputed here rather than reused from the kebab-menu IIFE
           above — each dialog render block in this component is its
           own isolated closure (same pattern as `nameLower`/`brand`
           being recomputed per block instead of hoisted/shared). */
        var protectedAllocsForEmail = (fresh.palletAllocations || []).filter(function (a) { return a && (a.saleStatus === "protection" || a.saleStatus === "pas"); });
        var protectedCases = protectedAllocsForEmail.reduce(function (s, a) { return s + (parseInt(a.cases, 10) || 0); }, 0);
        var protectedPalletKeys = new Set(protectedAllocsForEmail.map(function (a) { return String(a.lot) + "::" + String(a.palletNum); }));
        var protectedPalletCount = protectedPalletKeys.size;
        /* 2026-10-02 — Arturo, first scoped to Twin River ("en los
           avisos de proteccion al shipper de twin river, quita el
           nombre del cliente en el cuerpo del mensaje"), then
           generalized: "el shipper no debe ver el nombre del cliente
           en ningun lado" — applies to every client now, not just
           Twin River. Same severity family as "SALES REPORT (to
           Shipper): shipper never sees actual client sale price"
           (CLAUDE.md) — the shipper doesn't get to know WHO bought
           the fruit either, same confidentiality boundary. */
        var subject = "Aviso de Protección — PU# " + (fresh.puNumber || "—") + " / Lote " + (fresh.lot || "—");
        var body = "Estimados,\n\n"
          + "Hemos recibido una solicitud de protección relacionada con el siguiente embarque:\n\n"
          + "PU#: " + (fresh.puNumber || "—") + "\n"
          + "PO#: " + (fresh.clientRef || "—") + "\n"
          + "Factura Shipper #: " + ((shp && shp.shipperInvoice) || "—") + "\n"
          + "Proforma #: " + ((shp && shp.proforma) || "—") + "\n"
          + "Lote: " + (fresh.lot || "—") + "\n"
          + "Pallets protegidos: " + protectedPalletCount + "\n"
          + "Cajas protegidas: " + protectedCases + "\n\n"
          + protectionDefectsEmailText(fresh)
          + "Favor de revisar los reportes de calidad, fotografías y demás documentación adjunta.\n\n"
          + "Los mantendremos informados sobre el progreso del presente lote.\n\n"
          + "Saludos,";

        /* Files stay in memory for this one send only — not uploaded to
           Storage or saved onto the invoice's own documents (Arturo
           didn't ask for a permanent record, just the ability to
           attach and send; can be revisited later if he wants an
           audit trail of exactly what was sent to the shipper). */
        var ALLOWED_PROTECTION_EXT = ["pdf", "jpg", "jpeg", "png", "gif", "webp", "doc", "docx", "xls", "xlsx"];
        var protectionFilesKey = "protection-files-" + fresh.id;
        var handleAddProtectionFiles = function (fileList) {
          var accepted = [];
          var rejected = [];
          Array.from(fileList || []).forEach(function (f) {
            var ext = (f.name.split(".").pop() || "").toLowerCase();
            if (ALLOWED_PROTECTION_EXT.indexOf(ext) >= 0) accepted.push(f); else rejected.push(f.name);
          });
          if (accepted.length > 0) {
            setProtectionFiles(function (prev) {
              var next = prev.concat(accepted);
              saveDoc(protectionFilesKey, next).catch(function (e) { console.warn("[protection-files] save failed", e); });
              return next;
            });
          }
          if (rejected.length > 0) alert((lang === "es" ? "Tipo de archivo no permitido: " : "File type not allowed: ") + rejected.join(", "));
        };
        var removeProtectionFile = function (idx) {
          setProtectionFiles(function (prev) {
            var next = prev.filter(function (_, i) { return i !== idx; });
            saveDoc(protectionFilesKey, next).catch(function (e) { console.warn("[protection-files] save failed", e); });
            return next;
          });
        };
        /* Protection Notice Report — auto-generated PDF, ALWAYS
           attached alongside whatever proof files the user added
           (2026-09-14, Arturo: "generar una especie de reporte de
           proteccion que se anexe automatico en PDF"). Built fresh
           from the exact same data the preview button below shows, so
           what the shipper receives always matches what Arturo last
           saw on screen — never a stale/cached copy. */
        var buildAttachments = async function () {
          var attachments = protectionFiles.map(function (file) {
            return { filename: file.name, blob: file, mimeType: file.type || "application/octet-stream" };
          });
          try {
            var reportBlob = await buildProtectionReportBlob(fresh, shp, sup, company, protectionNoticeAt || new Date(), fresh.clientArrivalDate, fresh.clientArrivalTime);
            attachments.push({ filename: protectionReportFilename(fresh, shp, sup), blob: reportBlob, mimeType: "application/pdf" });
          } catch (e) { console.error("[protection-report] build failed", e); }
          for (var si = 0; si < specSheetsToAttach.length; si++) {
            var spec = specSheetsToAttach[si];
            try {
              var specBlob = await spec.build();
              attachments.push({ filename: spec.filename, blob: specBlob, mimeType: "application/pdf" });
            } catch (e) { console.error("[protection-report] tolerance sheet attach failed", spec.filename, e); }
          }
          return attachments;
        };
        /* Signature (2026-09-14, Arturo: "default firma es la de
           ground-fresh") — emailSignatures is one flat, unbranded list
           (Company Profile has no GF/SL split for signatures the way
           it does for email accounts), so the global `isDefault` flag
           could in principle point at a SweetLeaf-named one if Arturo
           ever set it that way for some other flow. Look up a
           signature named "Ground" first (mirrors how `fromEmail`
           above excludes "sweetleaf" by name), falling back to
           whichever is flagged default, then none. */
        var gfSignature = (company.emailSignatures || []).find(function (s) { return s && (s.name || "").toLowerCase().indexOf("ground") >= 0; })
          || (company.emailSignatures || []).find(function (s) { return s && s.isDefault; });
        /* "From" account switcher (2026-09-14, Arturo: "no debe
           aceptar envios de sweetleaf, de hecho eliminar la opcion de
           la parte superior") — EmailDialog only renders its own
           account dropdown when it's handed more than one account, so
           excluding SweetLeaf-named ones here removes the option
           entirely rather than merely defaulting away from it (the
           previous fix only changed the *default* selection, not
           whether SweetLeaf could still be picked). */
        /* 2026-09-30 (Arturo: "AHORA QUE AGREGAMOS UN NUEVO EMAIL PARA
           ENVIO DE CORREOS AUTOMATIZADOS... ME APARECEN NUEVAMENTE 4
           CUENTAS") — this filter only ever excluded "sweetleaf" in
           the name, which never matched the new automated accounts
           ("GFI Automated" / "SL Automated") either — same latent gap
           now excludes "automat" too, so this personal-send flow only
           ever offers the real named Ground-Fresh account. */
        var gfOnlyAccounts = (company.emailAccounts || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("sweetleaf") < 0 && n.indexOf("automat") < 0; });
        /* Mandatory attachment (2026-09-14, Arturo: "no debe dejar
           enviar si no se anexan documentos de prueba") — the whole
           point of this email is to forward the client's quality
           proof, so unlike every other EmailDialog caller in this app
           (where attachments are optional), Send stays disabled until
           at least one file is added. */
        var sendDisabledReason = protectionFiles.length === 0
          ? (lang === "es" ? "Adjunta al menos un documento de prueba antes de enviar" : "Attach at least one proof document before sending")
          : "";
        /* Customer Remarks block — built fresh from the LIVE state
           (protectionCustomerRemarks, updated on every input/paste),
           not the *Initial one used only to seed the contentEditable.
           Empty when Arturo hasn't pasted anything, so a send with no
           remarks doesn't add a stray heading/divider to the email. */
        /* 2026-09-14 — Arturo: the rule above the heading only marked
           where the client's own text STARTS — nothing marked where it
           ENDS, so it read as attached to whatever came after it
           instead of clearly bounded on both sides. Same closing rule
           added below the pasted content. */
        var customerRemarksBlock = (protectionCustomerRemarks && protectionCustomerRemarks.trim())
          ? '<div style="margin-top:20px;padding-top:14px;border-top:1px solid #d0d5dd;"><div style="font-weight:700;font-size:12px;color:#333;margin-bottom:8px;">Customer Remarks</div><div style="font-size:13px;color:#1a1a1a;line-height:1.5;">' + protectionCustomerRemarks + '</div><div style="margin-top:14px;padding-top:14px;border-top:1px solid #d0d5dd;"></div></div>'
          : "";

        return (
          <EmailDialog
            lang={lang}
            title={t.emailShipperProtection + " — " + supName}
            recipients={recipients}
            docOptions={[]}
            defaultSubject={subject}
            defaultBody={body}
            fromEmail={(gfOnlyAccounts.find(function (a) { return a.maskedFrom; }) || {}).maskedFrom || company.outboundEmail || ""}
            emailAccounts={gfOnlyAccounts}
            signatures={company.emailSignatures || []}
            defaultSignatureId={gfSignature ? gfSignature.id : undefined}
            sendDisabledReason={sendDisabledReason}
            alwaysBuildAttachments={true}
            buildAttachments={buildAttachments}
            insertBeforeSignatureHtml={customerRemarksBlock}
            postProcessHtmlBody={function (html, resolvedFrom) {
              /* Protection Notice always in Spanish here regardless of
                 the app's own lang toggle — same rule as the body text
                 itself (shippers are Mexican). Uses the SAME
                 protectionNoticeEs field Company Profile already
                 exposes and PaymentRegistry.js's Payment Confirmation
                 PDF already reads (Arturo: "el protection notice esta
                 en payment registry"). */
              var withNotice = appendProtectionNotice(html, company, "es");
              return appendDisclosureByAccount(withNotice, resolvedFrom, company, readSlSettingsForDisclosure());
            }}
            topContent={
              <>
              {/* Protection Notice Report — the auto-attached per-pallet
                 PDF (2026-09-14, Arturo: "poder previsualizar este pdf
                 antes de enviar"). Preview button only — the PDF itself
                 is always built and attached at send time regardless of
                 whether Arturo opens this preview. */}
              <div style={{ marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                <span style={{ fontSize: 11, fontWeight: 700 }}>📄 {lang === "es" ? "Reporte de Protección (PDF, se anexa automáticamente)" : "Protection Report (PDF, auto-attached)"}</span>
                <button type="button" onClick={function () { setProtectionReportPreviewOpen(true); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                  {lang === "es" ? "Vista previa" : "Preview"}
                </button>
              </div>
              {specSheetsToAttach.map(function (spec) {
                return (
                  <div key={spec.filename} style={{ marginBottom: 10, padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                    <span style={{ fontSize: 11, fontWeight: 700 }}>📄 {spec.filename} {lang === "es" ? "(hoja de tolerancias, se anexa automáticamente — Twin River)" : "(tolerance sheet, auto-attached — Twin River)"}</span>
                  </div>
                );
              })}
              {/* Client-reported data (clientReportedDate/Time,
                 clientArrivalDate/Time) — no longer captured HERE
                 (2026-09-16, Arturo: "lo ideal es capturarlo aqui en la
                 seccion de edit del invoice y no al entrar a enviar el
                 email"): the editable inputs now live directly on the
                 invoice's own Edit form (ROW 2.6, only shown when the
                 invoice has a Protection/PAS line), so they're visible
                 just by opening the invoice instead of only appearing
                 here. This is now a read-only recap so Arturo can still
                 confirm what the Protection Report PDF/reminder will
                 use without having to close this dialog to check. */}
              <div style={{ marginBottom: 12, padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{lang === "es" ? "Datos reportados por el cliente" : "Client-reported data"} <span style={{ fontWeight: 400, fontStyle: "italic", color: "var(--color-text-muted)" }}>({lang === "es" ? "capturado en el invoice" : "captured on the invoice"})</span></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
                  <div><div style={{ fontSize: 9.5, color: "var(--color-text-muted)" }}>{lang === "es" ? "Fecha reportada" : "Date reported"}</div><div style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{fresh.clientReportedDate || "—"}</div></div>
                  <div><div style={{ fontSize: 9.5, color: "var(--color-text-muted)" }}>{lang === "es" ? "Hora reportada" : "Time reported"}</div><div style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{fresh.clientReportedTime || "—"}</div></div>
                  <div><div style={{ fontSize: 9.5, color: "var(--color-text-muted)" }}>{lang === "es" ? "Llegada a su bodega" : "Arrival at their warehouse"}</div><div style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{fresh.clientArrivalDate || "—"}</div></div>
                  <div><div style={{ fontSize: 9.5, color: "var(--color-text-muted)" }}>{lang === "es" ? "Hora de llegada" : "Arrival time"}</div><div style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{fresh.clientArrivalTime || "—"}</div></div>
                </div>
              </div>
              <div style={{ marginBottom: 12, padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{lang === "es" ? "Archivos del cliente (reportes de calidad, fotos, etc.)" : "Client files (quality reports, photos, etc.)"}</div>
                <FileDropZone lang={lang} multiple={true} onFiles={handleAddProtectionFiles} style={{ width: "100%" }}>
                  <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600, width: "100%", boxSizing: "border-box" }}>
                    <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> {lang === "es" ? "Agregar archivos" : "Add files"}
                    <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx" style={{ display: "none" }} onChange={function (e) { handleAddProtectionFiles(e.target.files); e.target.value = ""; }} />
                  </label>
                </FileDropZone>
                {protectionFiles.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
                    {protectionFiles.map(function (file, idx) {
                      return (
                        <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "var(--color-bg-card)", borderRadius: 6, border: "1px solid var(--color-border)" }}>
                          <span style={{ fontSize: 13 }}>📎</span>
                          <span style={{ flex: 1, fontSize: 11, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                          <span style={{ fontSize: 10, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>{(file.size / 1024).toFixed(0)} KB</span>
                          <button type="button" onClick={function () { removeProtectionFile(idx); }} style={{ fontSize: 11, color: "#ef4444", fontWeight: 600, cursor: "pointer", background: "none", border: "none", padding: 0 }}>✕</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Customer Remarks (2026-09-14, Arturo): "quiero agregar
                 un campo de customer remarks donde yo pueda pegar el
                 mensaje del cliente que me envio... puede incluir la
                 imagen de su firma etc, debe estar habilitado para que
                 se pegue tal cual" — a contentEditable, not a plain
                 textarea, so a rich paste (formatting, an inline image)
                 survives intact. Inserted into the outgoing email
                 between the message and Arturo's own signature, headed
                 "Customer Remarks" and separated by a rule — see
                 customerRemarksBlock below and EmailDialog's new
                 insertBeforeSignatureHtml prop. */}
              <div style={{ marginBottom: 12, padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{lang === "es" ? "Customer Remarks (pega aquí el mensaje del cliente)" : "Customer Remarks (paste the client's message here)"}</div>
                <div
                  contentEditable
                  suppressContentEditableWarning
                  ref={function (el) {
                    customerRemarksRef.current = el;
                    /* Seed the node's content exactly ONCE per real DOM
                       mount, via a dataset flag on the node itself —
                       NOT dangerouslySetInnerHTML, which React re-syncs
                       on every re-render this div's containing IIFE
                       produces (it rebuilds fresh JSX every time
                       protectionCustomerRemarks changes, i.e. on every
                       keystroke), wiping whatever the user just typed
                       right back to the stale "Initial" snapshot. An
                       inline ref callback like this one also re-fires
                       on every render (React treats a new function
                       identity as a new ref), so the dataset flag is
                       what actually makes this a one-time seed rather
                       than a repeat/no-op guard against React itself. */
                    if (el && !el.dataset.seeded) {
                      el.innerHTML = protectionCustomerRemarksInitial;
                      el.dataset.seeded = "1";
                    }
                  }}
                  onInput={function () { setProtectionCustomerRemarks(customerRemarksRef.current ? customerRemarksRef.current.innerHTML : ""); }}
                  onPaste={function () { setTimeout(function () { setProtectionCustomerRemarks(customerRemarksRef.current ? customerRemarksRef.current.innerHTML : ""); }, 0); }}
                  onBlur={function () {
                    saveDoc("protection-remarks-" + fresh.id, customerRemarksRef.current ? customerRemarksRef.current.innerHTML : "").catch(function (e) { console.warn("[protection-remarks] save failed", e); });
                  }}
                  style={{ minHeight: 70, maxHeight: 220, overflowY: "auto", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-input)", fontSize: 12, lineHeight: 1.5, outline: "none" }}
                />
              </div>
              </>
            }
            onClose={function () { setEmailShipperProtectionOpen(false); }}
            onSent={function () {
              try { onSave(Object.assign({}, fresh, { protectionEmailSentAt: new Date().toISOString() })); }
              catch (e) { console.warn("[invoices] could not stamp protectionEmailSentAt:", e && e.message); }
            }}
          />
        );
      })()}
      {responseSendIdx !== null && (function () {
        var respListU = (fresh.protectionResponses && fresh.protectionResponses.length > 0)
          ? fresh.protectionResponses
          : (fresh.protectionResponseStatus ? [{ status: fresh.protectionResponseStatus, note: fresh.protectionResponseNote || "", at: fresh.protectionResponseAt || "" }] : []);
        var respSend = respListU[responseSendIdx] || { status: "", note: "", at: "" };
        var companyU = loadCompanyProfile();
        var shipmentsU = loadLS("gfi-shipments", []);
        var suppliersU = loadLS("gfi-suppliers", []);
        var shpU = findShipmentForInvoice(fresh, shipmentsU);
        var supU = shpU ? suppliersU.find(function (s) { return idMatches(s, shpU.supplierId); }) : null;
        var supNameU = supU ? (supU.shortName || supU.fullName || "Shipper") : "Shipper";
        var contactDisplayNameU = function (c) {
          return c.name || [c.firstName, c.lastName].filter(Boolean).join(" ") || [c.nombre, c.apPaterno].filter(Boolean).join(" ") || "";
        };
        var updateRecipientSource = filterContactsByCategory(supU && supU.contacts, "protection");
        var recipientsU = updateRecipientSource
          .map(function (c) { return { name: (contactDisplayNameU(c) ? contactDisplayNameU(c) + " · " : "") + supNameU, email: c.email, checked: true }; });
        /* Shipper never sees the client's identity, same confidentiality
           boundary as the original Protection Notice — only the
           response status/note/timestamp. Labels fixed in Spanish
           regardless of app lang (shippers are Mexican, see
           CLAUDE.md COMMUNICATION rules), independent from the
           in-app PROTECTION_RESPONSE labels which follow `lang`. */
        var RESPONSE_LABELS_ES = { willPayFull: "Pago Completo", settled: "Precio Ajustado", noResultYet: "Aún sin Resultado" };
        var responseLabel = RESPONSE_LABELS_ES[respSend.status] || respSend.status || "—";
        var responseAtText = respSend.at ? new Date(respSend.at).toLocaleString("es-MX") : "—";
        var subjectU = "Actualización de Protección — PU# " + (fresh.puNumber || "—") + " / Lote " + (fresh.lot || "—");
        var bodyU = "Estimados,\n\n"
          + "Les compartimos una actualización sobre la solicitud de protección del siguiente embarque:\n\n"
          + "PU#: " + (fresh.puNumber || "—") + "\n"
          + "PO#: " + (fresh.clientRef || "—") + "\n"
          + "Factura Shipper #: " + ((shpU && shpU.shipperInvoice) || "—") + "\n"
          + "Proforma #: " + ((shpU && shpU.proforma) || "—") + "\n"
          + "Lote: " + (fresh.lot || "—") + "\n\n"
          + "Respuesta: " + responseLabel + "\n"
          + "Fecha: " + responseAtText + "\n\n"
          + "Los mantendremos informados sobre el progreso del presente lote.\n\n"
          + "Saludos,";
        var customerRemarksU = (respSend.note && String(respSend.note).trim())
          ? '<div style="margin-top:20px;"><div style="font-weight:700;font-size:12px;color:#333;margin-bottom:8px;">Customer Remarks</div><div style="padding:12px 14px;border:1px solid #d0d5dd;border-radius:6px;background:#fafbfc;font-size:13px;color:#1a1a1a;line-height:1.5;white-space:pre-line;">' + String(respSend.note).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") + '</div></div>'
          : "";
        var gfSignatureU = (companyU.emailSignatures || []).find(function (s) { return s && (s.name || "").toLowerCase().indexOf("ground") >= 0; })
          || (companyU.emailSignatures || []).find(function (s) { return s && s.isDefault; });
        var gfOnlyAccountsU = (companyU.emailAccounts || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("sweetleaf") < 0 && n.indexOf("automat") < 0; });
        return (
          <>
          <EmailDialog
            lang="es"
            title={t.emailShipperProtectionUpdate + " — " + supNameU}
            recipients={recipientsU}
            docOptions={[]}
            hideDocs={true}
            defaultSubject={subjectU}
            defaultBody={bodyU}
            fromEmail={(gfOnlyAccountsU.find(function (a) { return a.maskedFrom; }) || {}).maskedFrom || companyU.outboundEmail || ""}
            emailAccounts={gfOnlyAccountsU}
            signatures={companyU.emailSignatures || []}
            defaultSignatureId={gfSignatureU ? gfSignatureU.id : undefined}
            insertBeforeSignatureHtml={customerRemarksU}
            topContent={shpU && (
              <div style={{ marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                <span style={{ fontSize: 11, fontWeight: 700 }}>📄 {lang === "es" ? "Reporte de Calidad (PDF, se anexa automáticamente)" : "Quality Report (PDF, auto-attached)"}</span>
                <button type="button" onClick={function () { setResponseQualityPreviewOpen(true); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                  {lang === "es" ? "Vista previa" : "Preview"}
                </button>
              </div>
            )}
            alwaysBuildAttachments={true}
            buildAttachments={async function () {
              /* Attach the lot's own Texas QC Quality Report (lives on
                 the shipment, not the invoice — Arturo: "se podria
                 anexar el reporte de calidad que ya existe en ese lote
                 pero vive en embarques... con los defectos encontrados
                 etc") alongside the defects list already in the body
                 above. Best-effort: if the shipment can't be resolved
                 (deleted, never linked) the update still sends without
                 it rather than blocking the send. */
              if (!shpU) return [];
              try {
                var qBlob = await buildQualityReportBlob(shpU, supU, companyU);
                return [{ filename: qualityReportFilename(shpU, supU), blob: qBlob, mimeType: "application/pdf" }];
              } catch (e) {
                console.warn("[protection-update] could not build Quality Report attachment:", e && e.message);
                return [];
              }
            }}
            postProcessHtmlBody={function (html, resolvedFrom) {
              return appendDisclosureByAccount(html, resolvedFrom, companyU, readSlSettingsForDisclosure());
            }}
            onClose={function () { setResponseSendIdx(null); }}
            onSent={function () {
              var next = respListU.slice();
              next[responseSendIdx] = Object.assign({}, respSend, { sentAt: new Date().toISOString() });
              var last = next.length > 0 ? next[next.length - 1] : null;
              onSave(Object.assign({}, fresh, {
                protectionResponses: next,
                protectionResponseStatus: last ? last.status : "",
                protectionResponseNote: last ? last.note : "",
                protectionResponseAt: last ? last.at : "",
              }));
              setResponseSendIdx(null);
            }}
          />
          {responseQualityPreviewOpen && shpU && (
            <QualityReportPreview
              shipment={shpU}
              supplier={supU}
              company={companyU}
              onClose={function () { setResponseQualityPreviewOpen(false); }}
            />
          )}
          </>
        );
      })()}
      {rejectionSendIdx !== null && (function () {
        var rejSend = (fresh.rejections || [])[rejectionSendIdx] || { cases: 0, notes: "" };
        var companyR = loadCompanyProfile();
        var shipmentsR = loadLS("gfi-shipments", []);
        var suppliersR = loadLS("gfi-suppliers", []);
        var shpR2 = findShipmentForInvoice(fresh, shipmentsR);
        var supR2 = shpR2 ? suppliersR.find(function (s) { return idMatches(s, shpR2.supplierId); }) : null;
        var supNameR = supR2 ? (supR2.shortName || supR2.fullName || "Shipper") : "Shipper";
        var recipientsR = (supR2 && supR2.contacts ? supR2.contacts : []).filter(function (c) { return c && c.email; })
          .map(function (c) { return { name: ((c.name || [c.firstName, c.lastName].filter(Boolean).join(" ")) ? (c.name || [c.firstName, c.lastName].filter(Boolean).join(" ")) + " · " : "") + supNameR, email: c.email, checked: true }; });
        var rejectedTotal = parseInt(rejSend.cases, 10) || 0;
        var customerRemarksR = (rejSend.notes && String(rejSend.notes).trim())
          ? '<div style="margin-top:20px;"><div style="font-weight:700;font-size:12px;color:#333;margin-bottom:8px;">Customer Remarks</div><div style="padding:12px 14px;border:1px solid #d0d5dd;border-radius:6px;background:#fafbfc;font-size:13px;color:#1a1a1a;line-height:1.5;white-space:pre-line;">' + String(rejSend.notes).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") + '</div></div>'
          : "";
        var bodyR = "Estimados,\n\n"
          + rejectionNoticeSentence(rejSend) + "\n\n"
          + "PU#: " + (fresh.puNumber || "—") + "\n"
          + "PO#: " + (fresh.clientRef || "—") + "\n"
          + "Factura Shipper #: " + ((shpR2 && shpR2.shipperInvoice) || "—") + "\n"
          + "Proforma #: " + ((shpR2 && shpR2.proforma) || "—") + "\n"
          + "Lote: " + (fresh.lot || "—") + "\n"
          + "Producto: " + ((fresh.lines || []).map(function (l) { return l.productDescription || [l.productBase || l.productName, l.size, l.category].filter(Boolean).join(" "); }).filter(Boolean).join(", ") || "—") + "\n"
          + "Cajas rechazadas: " + rejectedTotal + "\n"
          + "Notificado por el cliente: " + (rejSend.clientNotifiedAt ? new Date(rejSend.clientNotifiedAt).toLocaleString() : "—") + "\n"
          + "Notificado al shipper: " + new Date().toLocaleString() + "\n\n"
          + "Saludos,";
        var gfSigR = (companyR.emailSignatures || []).find(function (s) { return /ground/i.test(s.name || ""); }) || (companyR.emailSignatures || []).find(function (s) { return s.isDefault; });
        var gfOnlyR = (companyR.emailAccounts || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("sweetleaf") < 0 && n.indexOf("automat") < 0; });
        return (
          <EmailDialog
            lang="es"
            title={(lang === "es" ? "Nota de Rechazo — " : "Rejection Note — ") + supNameR}
            recipients={recipientsR}
            docOptions={[]}
            defaultSubject={"Nota de Rechazo — PU# " + (fresh.puNumber || "—") + " / Lote " + (fresh.lot || "—")}
            defaultBody={bodyR}
            fromEmail={(gfOnlyR.find(function (a) { return a.maskedFrom; }) || {}).maskedFrom || companyR.outboundEmail || ""}
            emailAccounts={gfOnlyR}
            signatures={companyR.emailSignatures || []}
            defaultSignatureId={gfSigR ? gfSigR.id : undefined}
            alwaysBuildAttachments={true}
            insertBeforeSignatureHtml={customerRemarksR}
            topContent={
              <>
              <div style={{ marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                <span style={{ fontSize: 11, fontWeight: 700 }}>📄 {lang === "es" ? "Nota de Rechazo (PDF, se anexa automáticamente)" : "Rejection Note (PDF, auto-attached)"}</span>
                <button type="button" onClick={function () { setRejectionPreviewIdx(rejectionSendIdx); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                  {lang === "es" ? "Vista previa del PDF" : "Preview PDF"}
                </button>
              </div>
              <div style={{ marginBottom: 12, padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{lang === "es" ? "Archivos (fotos, reportes del cliente)" : "Files (photos, client reports)"}</div>
                <FileDropZone lang={lang} multiple={true} onFiles={handleAddRejectionFiles} style={{ width: "100%" }}>
                  <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600, width: "100%", boxSizing: "border-box" }}>
                    <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> {lang === "es" ? "Agregar archivos" : "Add files"}
                    <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx" style={{ display: "none" }} onChange={function (e) { handleAddRejectionFiles(e.target.files); e.target.value = ""; }} />
                  </label>
                </FileDropZone>
                {rejectionFiles.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
                    {rejectionFiles.map(function (file, idx) {
                      return (
                        <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "var(--color-bg-card)", borderRadius: 6, border: "1px solid var(--color-border)" }}>
                          <span style={{ fontSize: 13 }}>📎</span>
                          <span style={{ flex: 1, fontSize: 11, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                          <button type="button" onClick={function () { setRejectionFiles(function (prev) { return prev.filter(function (_, i) { return i !== idx; }); }); }} style={{ fontSize: 11, color: "#ef4444", fontWeight: 600, cursor: "pointer", background: "none", border: "none", padding: 0 }}>✕</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              </>
            }
            buildAttachments={function () {
              return buildRejectionReportBlob(fresh, shpR2, supR2, companyR, new Date(), rejSend).then(function (blob) {
                var out = [{ filename: rejectionReportFilename(fresh, shpR2, supR2), blob: blob, mimeType: "application/pdf" }];
                rejectionFiles.forEach(function (file) { out.push({ filename: file.name, blob: file, mimeType: file.type || "application/octet-stream" }); });
                return out;
              });
            }}
            postProcessHtmlBody={function (html, resolvedFrom) {
              return appendDisclosureByAccount(html, resolvedFrom, companyR, readSlSettingsForDisclosure());
            }}
            onClose={function () { setRejectionSendIdx(null); }}
            onSent={function () {
              var next = (fresh.rejections || []).slice();
              next[rejectionSendIdx] = Object.assign({}, rejSend, { sentAt: new Date().toISOString() });
              onSave(Object.assign({}, fresh, { rejections: next }));
              setRejectionSendIdx(null);
            }}
          />
        );
      })()}
      {rejectionPreviewIdx !== null && (function () {
        var rejP = (fresh.rejections || [])[rejectionPreviewIdx] || { cases: 0, notes: "" };
        var companyP = loadCompanyProfile();
        var shpP = findShipmentForInvoice(fresh, loadLS("gfi-shipments", []));
        var supP = shpP ? loadLS("gfi-suppliers", []).find(function (s) { return idMatches(s, shpP.supplierId); }) : null;
        return (
          <RejectionReportPreview invoice={fresh} shipment={shpP} supplier={supP} company={companyP} notifiedAt={new Date()} rejection={rejP} onClose={function () { setRejectionPreviewIdx(null); }} />
        );
      })()}
      {protectionReportPreviewOpen && (function () {
        var company2 = loadCompanyProfile();
        var shipments2 = loadLS("gfi-shipments", []);
        var suppliers2 = loadLS("gfi-suppliers", []);
        var shp2 = findShipmentForInvoice(fresh, shipments2);
        var sup2 = shp2 ? suppliers2.find(function (s) { return idMatches(s, shp2.supplierId); }) : null;
        return (
          <ProtectionReportPreview
            invoice={fresh}
            shipment={shp2}
            supplier={sup2}
            company={company2}
            notifiedAt={protectionNoticeAt || new Date()}
            clientArrivalDate={fresh.clientArrivalDate}
            clientArrivalTime={fresh.clientArrivalTime}
            onClose={function () { setProtectionReportPreviewOpen(false); }}
          />
        );
      })()}
      {previewHtml && (
        <DocPreviewModal
          title={previewHtml.title}
          html={previewHtml.html}
          lang={lang}
          onClose={function () { setPreviewHtml(null); }}
        />
      )}
      {pendingRemarks && (function () {
        /* Pre-send Pick Ticket remarks prompt. brandLabel mirrors the
           email's resolved brand so the user sees "SweetLeaf Produce"
           when emailing FS or "Ground-Fresh" otherwise. */
        return (
          <PickTicketRemarksModal
            brandLabel={pendingRemarks.brand === "sl" ? "SweetLeaf Produce" : "Ground-Fresh"}
            lang={lang}
            defaultLoadingDate={pendingRemarks.defaultLD}
            onCancel={function () {
              var rj = pendingRemarks.reject;
              setPendingRemarks(null);
              if (rj) rj(new Error("pick-remarks-cancelled"));
            }}
            onPrint={function (lines, chosenLD) {
              var rs = pendingRemarks.resolve;
              setPendingRemarks(null);
              if (rs) rs({ remarks: lines, loadingDate: chosenLD });
            }}
          />
        );
      })()}
      {/* Copy-to-SweetLeaf prep modal — INV# override + per-line SL
          price. Opened from the kebab Copy-to-SL action; commits to
          sweetleaf_invoices on Confirm. */}
      {slCopyModal && (
        <DraggableModal
          title={lang === "es" ? "Copiar a SweetLeaf" : "Copy to SweetLeaf"}
          onClose={function () { setSlCopyModal(null); }}
          initialWidth={Math.min(720, Math.floor(window.innerWidth * 0.7))}
          initialHeight={Math.min(640, window.innerHeight - 60)}
          zIndex={10004}
        >
          <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", marginBottom: 4 }}>{lang === "es" ? "Número INV# (SweetLeaf)" : "INV# (SweetLeaf)"}</div>
              <input
                type="text"
                value={slCopyModal.invoiceNumber}
                onChange={function (e) {
                  var v = e.target.value;
                  setSlCopyModal(function (prev) { return Object.assign({}, prev, { invoiceNumber: v }); });
                }}
                style={{ padding: "8px 10px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 14, color: "#2A7A6F", border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", outline: "none", width: "100%", boxSizing: "border-box" }}
              />
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 4, fontStyle: "italic" }}>{lang === "es" ? "Editable. El consecutivo se incrementa solo si dejas el número auto-generado." : "Editable. The counter only advances if you keep the auto-generated number."}</div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", marginBottom: 6 }}>{lang === "es" ? "Líneas de Producto" : "Product Lines"}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 16 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                  <th style={{ textAlign: "left", padding: "6px 8px", fontSize: 10, textTransform: "uppercase", color: "var(--color-text-muted)" }}>{lang === "es" ? "Producto" : "Product"}</th>
                  <th style={{ textAlign: "center", padding: "6px 8px", fontSize: 10, textTransform: "uppercase", color: "var(--color-text-muted)" }}>Cases</th>
                  <th style={{ textAlign: "right", padding: "6px 8px", fontSize: 10, textTransform: "uppercase", color: "var(--color-text-muted)" }}>{lang === "es" ? "Precio GFI" : "GFI Price"}</th>
                  <th style={{ textAlign: "right", padding: "6px 8px", fontSize: 10, textTransform: "uppercase", color: "var(--color-text-muted)" }}>{lang === "es" ? "Precio SL" : "SL Price"}</th>
                </tr>
              </thead>
              <tbody>
                {slCopyModal.lines.map(function (l, idx) {
                  var gfiSrc = (fresh.lines || [])[idx] || {};
                  return (
                    <tr key={idx} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "6px 8px", fontWeight: 600 }}>{l.productName || l.productBase || "—"}<div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{l.productDescription || ""}</div></td>
                      <td style={{ padding: "6px 8px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{l.cases || "—"}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-muted)" }}>{gfiSrc.salePrice ? fmtMoney(gfiSrc.salePrice) : "—"}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={l.salePrice || ""}
                          onChange={function (e) {
                            var v = e.target.value;
                            setSlCopyModal(function (prev) {
                              var newLines = prev.lines.map(function (ln, i) { return i === idx ? Object.assign({}, ln, { salePrice: v }) : ln; });
                              return Object.assign({}, prev, { lines: newLines });
                            });
                          }}
                          style={{ width: 90, padding: "5px 8px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, textAlign: "right", border: "1px solid var(--color-border)", borderRadius: 4, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none" }}
                          placeholder="0.00"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={function () { setSlCopyModal(null); }} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{lang === "es" ? "Cancelar" : "Cancel"}</button>
            <button
              onClick={async function () {
                var mod = slCopyModal;
                var newSl = {
                  id: String(Date.now()),
                  invoiceNumber: mod.invoiceNumber,
                  puNumber: mod.invoiceNumber,
                  gfiInvoiceId: fresh.id,
                  gfiPuNumber: fresh.puNumber || "",
                  gfiLot: fresh.lot || "",
                  date: fresh.date || new Date().toISOString().substring(0, 10),
                  dueDate: fresh.dueDate || "",
                  clientId: fresh.clientId,
                  clientName: "Four Seasons Produce Inc.",
                  clientRef: fresh.clientRef || "",
                  shipmentId: fresh.shipmentId || "",
                  status: "draft",
                  pickupDate: fresh.pickupDate || "",
                  pickupTime: fresh.pickupTime || "",
                  thermograph: fresh.thermograph || "",
                  lines: mod.lines.map(function (l) { return Object.assign({}, l, { slCommissionPerCase: "" }); }),
                  fees: [],
                  credits: [],
                  balanceTransfers: [],
                  payments: [],
                  advanceFinancingFee: "",
                };
                try {
                  await saveToSupabase("sweetleaf_invoices", newSl);
                  /* Counter advances only when the user kept the
                     auto-generated INV# — manual overrides for
                     historical imports leave nextInvoiceNumber alone. */
                  var slSettings = loadLS("gfi-sl-settings", { nextInvoiceNumber: 16229, invoicePrefix: "INV-" });
                  var prefix = slSettings.invoicePrefix || "INV-";
                  var usedConsec = mod.invoiceNumber === prefix + String(mod.nextNum).padStart(5, "0");
                  if (usedConsec) {
                    slSettings.nextInvoiceNumber = mod.nextNum + 1;
                    try { localStorage.setItem("gfi-sl-settings", JSON.stringify(slSettings)); } catch (e) {}
                    try { await saveToSupabase("sweetleaf_settings", Object.assign({ id: "sl" }, slSettings)); } catch (e) {}
                  }
                  onSave(Object.assign({}, fresh, { slImported: true }));
                  setSlCopyModal(null);
                  alert(t.copyToSLSuccess);
                } catch (e) {
                  alert("Error: " + (e.message || String(e)));
                }
              }}
              style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: "#2A7A6F", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}
            >{lang === "es" ? "Copiar a SweetLeaf" : "Copy to SweetLeaf"}</button>
          </div>
        </DraggableModal>
      )}
    </DraggableModal>
  );
}

/* Inline picker for the Balance Transfers section. Mirrors the
   BillRegistry version. Holds direction/other-invoice/amount state until
   the user clicks Create. onCreate(direction, otherInvoiceId, amount)
   writes both invoices atomically via the InvoiceForm's
   createBalanceTransfer handler. */
function InvoiceForm({ invoice, invoices, t, lang, onSave, onDelete, onCancel, embedded, feeCategoryMapProp }) {
  var isNew = !invoice || !invoice.id;
  var clients = useMemo(function() { return getClients(); }, []);
  var availableBills = useMemo(function() { return loadLS("gfi-bills", []); }, []);
  /* Fee name → category, for the Summary box's per-category breakdown
     (see FeeBreakdownLinesInv). DetailPanel already loads this and
     passes it down when this form is embedded there; a directly-
     opened InvoiceForm (new invoice, or opened outside DetailPanel)
     loads its own copy — same fallback pattern as BillRegistry.js's
     BillForm/feeCategoryMapProp. */
  var [feeCategoryMapOwn, setFeeCategoryMapOwn] = useState({});
  useEffect(function () {
    if (feeCategoryMapProp) return;
    var cancelled = false;
    loadFromSupabase("fee_catalog").then(function (rows) {
      if (cancelled) return;
      var map = {};
      (rows || []).forEach(function (r) { if (r && r.name) map[String(r.name).trim().toLowerCase()] = r.category || "other"; });
      setFeeCategoryMapOwn(map);
    }).catch(function () {});
    return function () { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var feeCategoryMap = feeCategoryMapProp || feeCategoryMapOwn;

  /* For the Pending Pick Up / Pending Delivery display default (see
     saleStatusCalc.js resolvePendingStage) — only Four Seasons invoices
     ever reach Pending Delivery, once their SweetLeaf mirror's Passing/
     BOL has been sent. Loaded once per form open; a stale read here just
     means the badge catches up on next open, not incorrect forever. */
  var [slPassingIndex, setSlPassingIndex] = useState({});
  /* 2026-10-20, Arturo: "sigues sin agregar credits y balance
     transfers a la tarjeta invoice summary... debe ser siempre
     visible" — the totals block used to be a bare borderless div,
     not its own titled card like view mode's "Invoice Summary"
     SectionBox. Wrapped the same way here (own open/close state,
     starts expanded, same "always a real card" treatment). */
  var [summaryOpen, setSummaryOpen] = useState(true);
  /* "me equivoqué, fees & deductions no esta visible, y debe mostrar
     credits y balance transfer dentro de ella" — same merge as view
     mode: one always-visible "Fees & Deductions" card housing Credits
     and Balance Transfers as labeled sub-sections underneath (own
     collapse state, starts expanded). */
  var [feesOpen, setFeesOpen] = useState(true);
  var [paymentsOpen, setPaymentsOpen] = useState(true);
  var [productsOpenEdit, setProductsOpenEdit] = useState(true);
  var [palletsOpenEdit, setPalletsOpenEdit] = useState(true);
  /* Client-Reported Data popover (see ClientReportedDataButton) — read
     by default, flips to the real editable fields on "Edit". */
  var [crDataEditOpen, setCrDataEditOpen] = useState(false);
  useEffect(function() {
    var cancelled = false;
    loadFromSupabase("sweetleaf_invoices").then(function(sl) {
      if (!cancelled) setSlPassingIndex(buildSlPassingIndex(sl));
    }).catch(function() {});
    return function() { cancelled = true; };
  }, []);

  /* 2026-10-08, Arturo: "el dropdown list me muestra PO de mayo que
     ya fueron utilizadas" — availablePOs (below) used to filter
     getFixedPOs()/getConsignmentPOs(), which only ever read the
     gfi-fixed-pos/gfi-consignment-pos localStorage mirror — a cache
     that's only (re)written when FixedPricingRegistry.js itself gets
     opened in that browser. A PO linked to an invoice on another
     device/session, or just a while ago, can sit there forever still
     looking "available" to this screen. Same class of bug already
     documented and fixed for syncPoLinkage in this same file ("never
     trust localStorage to find 'existing', only ever as a display
     cache") — force a fresh Supabase read on mount so the dropdown
     reflects real linkedInvoiceId/saleStatus, not a stale snapshot.
     Seeded from the local cache so the dropdown isn't empty for the
     instant before the fetch resolves. */
  var [freshFixedPOs, setFreshFixedPOs] = useState(function() { return getFixedPOs(); });
  var [freshConsPOs, setFreshConsPOs] = useState(function() { return getConsignmentPOs(); });
  useEffect(function() {
    var cancelled = false;
    Promise.all([
      loadFromSupabase("fixed_pos", { force: true }),
      loadFromSupabase("consignment_pos", { force: true }),
    ]).then(function(res) {
      if (cancelled) return;
      setFreshFixedPOs(res[0] || []);
      setFreshConsPOs(res[1] || []);
    }).catch(function() {});
    return function() { cancelled = true; };
  }, []);

  /* 2026-10-10, Arturo: "al asignar la hora y fecha de llegada a mi
     bodega o a la bodega de twin river, necesito se replique en vivo
     tambien a la o las facturas que se generaron de ese embarque...
     ahorita tengo que ingresarla en el embarque y nuevamente al
     reportar una proteccion" — only Twin River: their load never
     stops at GFI's own warehouse first, it goes straight to their
     dock (Five Brothers), so the shipment's own Received date/time
     (shipment.receivedDate/Time, "Received date (GFI)" in
     ShipmentRegistry.js) IS the same event as the client's arrival —
     unlike Four Seasons, where GFI's own receiving and FS's Ephrata,
     PA arrival are two separate events on two separate timelines.
     Same staleness class already fixed for availablePOs above — force
     a fresh Supabase read rather than trusting the localStorage
     mirror, which only updates when Shipments happens to be opened in
     that browser. */
  var [freshShipmentsForTR, setFreshShipmentsForTR] = useState(function() { return getShipments(); });
  useEffect(function() {
    var cancelled = false;
    loadFromSupabase("shipments", { force: true }).then(function(rows) {
      if (!cancelled) setFreshShipmentsForTR(rows || []);
    }).catch(function() {});
    return function() { cancelled = true; };
  }, []);

  var [f, setF] = useState(function() {
    if (invoice) return {
      ...invoice,
      /* Surface the inv-level commissionRate as the per-line default
         when the line doesn't carry one yet — so the form's Comm %
         column never shows blank for legacy lines. */
      lines: (invoice.lines || []).map(function(l) {
        return (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
          ? { ...l, commissionRate: invoice.commissionRate || "" }
          : { ...l };
      }),
      fees: (invoice.fees || []).map(function(fe) { return { ...fe }; }),
      credits: (invoice.credits || []).map(function(cr) { return { ...cr }; }),
      balanceTransfers: (invoice.balanceTransfers || []).map(function(t) { return { ...t }; }),
      palletAllocations: (invoice.palletAllocations || []).map(function(a) { return { ...a }; }),
    };
    return {
      id: "", puNumber: nextPU(invoices), date: new Date().toISOString().substring(0, 10),
      dueDate: "", clientId: "", clientInitials: "", clientRef: "", linkedPoLineId: "",
      lot: "", shipmentId: "", billId: "", shipperName: "",
      status: "draft", lessAdvance: "", otherFees: "", lessCredits: "",
      remarks: "", lines: [], saleStatus: "", pickupDate: "", pickupTime: "", thermograph: "",
      fees: [],
      credits: [],
      balanceTransfers: [],
      palletAllocations: [],
      commissionRate: "",
    };
  });

  /* Keeps f.pickupDate/pickupTime (ROW 2.5, relabeled "Arrival
     Date/Time" for Twin River) mirrored live from the linked
     shipment's own Received date/time, so Arturo never types it twice
     — it also keeps handleSave's existing pickupDate->clientArrivalDate
     mirror (below) working unchanged, since that code still just
     reads f.pickupDate/pickupTime. Non-Twin-River invoices are
     untouched — those fields stay directly editable there, same as
     always. */
  useEffect(function() {
    var isTR = (resolveClientName(f.clientId) || f.clientName || "").toLowerCase().indexOf("twin river") >= 0;
    if (!isTR) return;
    var shp = findShipmentForInvoice(f, freshShipmentsForTR);
    if (!shp) return;
    var rd = shp.receivedDate || "";
    var rt = shp.receivedTime || "";
    if (f.pickupDate !== rd || f.pickupTime !== rt) {
      setF(function(prev) { return { ...prev, pickupDate: rd, pickupTime: rt }; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.clientId, f.clientName, f.lot, f.shipmentId, freshShipmentsForTR]);

  /* Editing enabled always — even with payments applied. Previously
     this locked all fields except the client picker; removed so the
     operator can correct any field. Balance recalculates from the
     updated line totals minus existing payments. Flag kept as `false`
     so the ~15 `disabled={locked}` references still compile. */
  var locked = false;

  /* Filtered list of shipments selectable as line sources. Only those
     dated on/before the invoice date, sorted desc. SearchSelect with
     limit=10 surfaces the most recent; typed query widens the search. */
  var availableLots = useMemo(function() {
    var all = getAvailableLots();
    var invDate = f.date || new Date().toISOString().substring(0, 10);
    return all
      .filter(function(s) {
        var sd = s.dateArrival || s.dateDeparture || "";
        return !sd || sd <= invDate;
      })
      .sort(function(a, b) {
        var ad = a.dateArrival || a.dateDeparture || "";
        var bd = b.dateArrival || b.dateDeparture || "";
        return bd.localeCompare(ad);
      });
  }, [f.date]);
  /* 2026-10-13 — pending "+ Add" selection for the per-pallet defect
     picker, keyed by pallet allocation index so each protected pallet
     has its own independent pending pick (same purpose as
     otherLotPickerValue above, just one per row instead of one for
     the whole form). */
  var [pendingDefectKey, setPendingDefectKey] = useState({});
  /* 2026-10-27, Arturo: "necesito agregar... la opcion de agregar
     defectos que no esten listados ahi, y su severidad" — when
     pendingDefectKey.__load__ === "__custom__" this holds the typed
     name + chosen severity until "+ Agregar" commits them. */
  var [pendingCustomDefect, setPendingCustomDefect] = useState({ name: "", severity: "medium" });

  var [errors, setErrors] = useState({});
  var [showDel, setShowDel] = useState(false);
  var [dirty, setDirty] = useState(false);
  var [showExitConfirm, setShowExitConfirm] = useState(false);
  var [trVer, setTrVer] = useState(0);

  var FEE_TYPES_KEY = "gfi-fee-types";
  var DEFAULT_FEE_TYPES = ["Custom Broker Fee", "Warehouse Fees", "QC Inspection", "Temp Recorders", "Advance Financing Fee", "Wire Transfer Fee", "Shipping", "Repack", "Fumigation", "USDA Inspection", "Cold Treatment", "Palletizing", "Labels/Stickers"];
  var EMPTY_FEE = { feeType: "", feeCustom: "", qty: 1, rate: "", amount: 0 };
  var getFeeTypes = function() { try { var d = localStorage.getItem(FEE_TYPES_KEY); return d ? JSON.parse(d) : DEFAULT_FEE_TYPES; } catch { return DEFAULT_FEE_TYPES; } };
  var [feeTypes, setFeeTypes] = useState(getFeeTypes);
  var saveFeeTypes = function(types) { try { localStorage.setItem(FEE_TYPES_KEY, JSON.stringify(types)); } catch {} };

  var set = function(k, v) { setF(function(prev) { return { ...prev, [k]: v }; }); setDirty(true); };

  var removeFee = function(idx) { setF(function(prev) { return { ...prev, fees: (prev.fees || []).filter(function(_, i) { return i !== idx; }) }; }); setDirty(true); };
  /* 2026-10-21, Arturo: "aqui aplica la misma logica de fees de bills,
     son datos no de lectura se muestran en columnas, segmentas
     financing fee al inicio fijo y los demas se agregan mediante
     boton add fee pero se abre en una ventana de captura y popula en
     siguiente renglon" — same pattern as BillRegistry.js's own Fees
     & Deductions: a locked list (edit via pencil button, not inline
     inputs), Advance Financing Fee pulled OUT into its own always-
     visible %-row, "+ Add Fee" opens InvoiceFeeEditDialog instead of
     inserting a blank editable row. editingFeeIdx: null = closed,
     "new" = Add dialog, a number = Edit dialog for that f.fees index. */
  var [editingFeeIdx, setEditingFeeIdx] = useState(null);
  var saveFeeDialog = function(draft) {
    if (editingFeeIdx === "new") {
      setF(function(prev) { return { ...prev, fees: [...(prev.fees || []), draft] }; });
    } else {
      setF(function(prev) { return { ...prev, fees: prev.fees.map(function(fe, i) { return i === editingFeeIdx ? draft : fe; }) }; });
    }
    setDirty(true);
    setEditingFeeIdx(null);
  };
  /* Advance Financing Fee — same upsert-by-feeType pattern as Bill's
     updateFinancingFee, keyed off totalAmount (this file's equivalent
     of Bill's totals.grossSales) instead of being re-typed through
     the dialog like any other fee. An empty % removes the entry. */
  var updateFinancingFee = function(pctValue) {
    setF(function(prev) {
      var fees = prev.fees || [];
      var idx = fees.findIndex(function(fe) { return fe.feeType === "Advance Financing Fee"; });
      if (pctValue === "") {
        if (idx === -1) return prev;
        return { ...prev, fees: fees.filter(function(_, i) { return i !== idx; }) };
      }
      var amt = (totalAmount * ((parseFloat(pctValue) || 0) / 100)).toFixed(2);
      var newFees = idx === -1
        ? fees.concat([{ ...EMPTY_FEE, feeType: "Advance Financing Fee", pctRate: pctValue, amount: amt }])
        : fees.map(function(fe, i) { return i === idx ? { ...fe, pctRate: pctValue, amount: amt } : fe; });
      return { ...prev, fees: newFees };
    });
    setDirty(true);
  };
  /* 2026-10-21, Arturo: "ese 154.80 es informativo y debe estar junto
     al signo de %, al final un campo para ingresar esa cantidad, por
     default la copia pero la puedo editar porque manualmente puedo
     cobrar mas o menos de lo que el 2% marca" — the %-computed amount
     stays purely informational next to "%"; this is the REAL charged
     amount, a separate editable field. updateFinancingFee (above)
     still overwrites it with the fresh %-computed default every time
     the % itself changes ("por default la copia") — this handler only
     fires on a direct edit of the amount field itself, leaving
     pctRate untouched so the % and its reference total keep showing
     what 2% actually computes to, even once the real amount diverges
     from it. */
  var updateFinancingFeeAmount = function(amtValue) {
    setF(function(prev) {
      var fees = prev.fees || [];
      var idx = fees.findIndex(function(fe) { return fe.feeType === "Advance Financing Fee"; });
      if (idx === -1) {
        if (amtValue === "") return prev;
        return { ...prev, fees: fees.concat([{ ...EMPTY_FEE, feeType: "Advance Financing Fee", pctRate: "", amount: amtValue }]) };
      }
      return { ...prev, fees: fees.map(function(fe, i) { return i === idx ? { ...fe, amount: amtValue } : fe; }) };
    });
    setDirty(true);
  };

  /* Credits — reduce the invoice total. Each credit has:
       - type: "free" → user-defined concept, OR
                "fromInvoice" → reference (visual only) to another
                invoice from the same client. The link does NOT modify
                the referenced invoice; it just tells the reader where
                this credit came from.
       - concept: free-text label (used when type==="free")
       - refInvoiceId / refPuNumber: identify the source invoice (only
         refPuNumber is shown to the user / on print; id is for record). */
  var removeCredit = function(idx) { setF(function(prev) { return { ...prev, credits: (prev.credits || []).filter(function(_, i) { return i !== idx; }) }; }); setDirty(true); };
  /* 2026-10-21, Arturo: "tambien para agregar credit o transfer debe
     ser mediante ventanas emergentes donde se captura la info y
     popula el renglon correspondiente" — same capture-window pattern
     as Fees: a locked list, edit via pencil (InvoiceCreditEditDialog),
     "+ Add Credit" opens the same dialog instead of inserting a blank
     editable row. editingCreditIdx: null = closed, "new" = Add
     dialog, a number = Edit dialog for that f.credits index. */
  var [editingCreditIdx, setEditingCreditIdx] = useState(null);
  var saveCreditDialog = function(draft) {
    if (editingCreditIdx === "new") {
      setF(function(prev) { return { ...prev, credits: [...(prev.credits || []), draft] }; });
    } else {
      setF(function(prev) { return { ...prev, credits: prev.credits.map(function(cr, i) { return i === editingCreditIdx ? draft : cr; }) }; });
    }
    setDirty(true);
    setEditingCreditIdx(null);
  };
  /* Balance Transfer creation popup state — "+ Create Transfer" opens
     InvoiceTransferCreateDialog instead of the old inline-expanding
     BalanceTransferCreatorInv panel. No edit concept (unchanged from
     before — a transfer is mirrored on the other invoice at create
     time, see createBalanceTransfer, only create/remove ever existed). */
  var [transferDialogOpen, setTransferDialogOpen] = useState(false);
  /* 2026-10-21, Arturo: "los botones add de products y pallet
     allocations... al clickear abrir la fuente en una ventana
     alterna para escoger" — same popup-window pattern as Fees/
     Credits/Transfers above, for the 2 remaining inline pickers. */
  var [addFromLotModalOpen, setAddFromLotModalOpen] = useState(false);
  var [addPalletModalOpen, setAddPalletModalOpen] = useState(false);
  /* "loading info agregale dos botones, add y edit" — same popup
     pattern for Pickup Date/Pickup Time/Temp Recorder. */
  var [loadingInfoModalOpen, setLoadingInfoModalOpen] = useState(false);
  var saveLoadingInfo = function (draft) {
    setF(function (prev) { return { ...prev, pickupDate: draft.pickupDate, pickupTime: draft.pickupTime, thermograph: draft.thermograph }; });
    setDirty(true);
  };

  /* Other invoices for THIS client — used by the "From Invoice" credit
     SearchSelect. Excludes the current invoice itself. */
  var clientInvoices = useMemo(function() {
    if (!f.clientId) return [];
    return (invoices || []).filter(function(inv) { return String(inv.clientId) === String(f.clientId) && String(inv.id) !== String(f.id); });
  }, [invoices, f.clientId, f.id]);

  /* Available POs from Fixed + Consignment Pricing. Filter rules:
       1. Client must match (id, fallback to name for old records)
       2. linkedInvoiceId must be empty
       3. saleStatus NOT in {invoiced, sent, paid}
     No date filtering — the user picks the right PO manually from
     the list. Exception: this invoice's own linked PO always
     passes (so edit mode keeps the chip + banner). Console logs
     each candidate so data drift is diagnosable from the browser
     console. Sort is chronological by PO departure date so the
     newest PO appears at the bottom. */
  var availablePOs = useMemo(function() {
    if (!f.clientId) return [];
    var clientObj = (clients || []).find(function (c) { return idMatches(c, f.clientId); });
    var clientName = clientObj ? (clientObj.shortName || clientObj.fullName || clientObj.name || "") : "";
    var fixedRows = freshFixedPOs.map(function (p) { return { ...p, _src: "F" }; });
    var consRows  = freshConsPOs.map(function (p) { return { ...p, _src: "C" }; });
    var all = fixedRows.concat(consRows);
    var currentInvoiceId = String(f.id || "");
    var INVOICED_STATUSES = ["invoiced", "sent", "paid"];
    /* eslint-disable no-console */
    console.log("[Invoice PO Picker] Evaluating " + all.length + " POs for invoice id=" + (currentInvoiceId || "(new)") + ", client=" + (clientName || "?"));
    var result = all.filter(function (p) {
      var compositeId = p._src + ":" + p.id;
      var poLink = (p.linkedInvoiceId == null || p.linkedInvoiceId === "") ? "" : String(p.linkedInvoiceId);
      var poStatus = String(p.saleStatus || "").toLowerCase();
      /* Exception — current invoice's own linked PO bypasses every
         other filter so the edit flow doesn't drop its chip + banner. */
      var isOwnLink = currentInvoiceId && poLink && poLink === currentInvoiceId;
      if (isOwnLink) {
        console.log("[PO] " + compositeId + " · " + (p.poNumber || "?") + " · linkedInvoiceId=" + poLink + " · KEEP (own link)");
        return true;
      }
      /* Filter 1 — client mismatch. */
      var idMatch   = p.clientId && String(p.clientId) === String(f.clientId);
      var nameMatch = p.clientName && clientName && p.clientName === clientName;
      if (!(idMatch || nameMatch)) {
        console.log("[PO] " + compositeId + " · " + (p.poNumber || "?") + " · client=" + (p.clientName || "?") + " · DROP (client mismatch)");
        return false;
      }
      /* Filter 2 — already linked to ANY invoice. */
      if (poLink) {
        console.log("[PO] " + compositeId + " · " + (p.poNumber || "?") + " · linkedInvoiceId=" + poLink + " · DROP (already invoiced)");
        return false;
      }
      /* Filter 3 — sale status indicates the PO is already gone. */
      if (INVOICED_STATUSES.indexOf(poStatus) >= 0) {
        console.log("[PO] " + compositeId + " · " + (p.poNumber || "?") + " · saleStatus=" + poStatus + " · DROP (status indicates invoiced)");
        return false;
      }
      console.log("[PO] " + compositeId + " · " + (p.poNumber || "?") + " · saleStatus='" + poStatus + "' · KEEP (available)");
      return true;
    });
    console.log("[Invoice PO Picker] Result: " + result.length + " of " + all.length + " POs available");
    /* eslint-enable no-console */
    /* Sort by Loading/Delivery Date ascending — oldest at the top,
       newest at the bottom. Falls back to Departure Date when
       deliveryDate isn't set, and POs missing both dates sink to
       the bottom so they don't crowd the relevant options. */
    return result.sort(function (a, b) {
      var da = a.deliveryDate || a.date || "";
      var db = b.deliveryDate || b.date || "";
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da < db ? -1 : (da > db ? 1 : 0);
    });
  }, [f.clientId, f.id, invoices, clients, freshFixedPOs, freshConsPOs]);

  /* Resolve the currently-linked PO directly from storage so the
     informational banner works even if the user changed clients
     after linking (which would filter the PO out of availablePOs)
     or if the PO is no longer in availablePOs for any other reason. */
  var linkedPo = useMemo(function () {
    if (!f.linkedPoLineId) return null;
    var key = poStorageKeyFor(f.linkedPoLineId);
    var poId = poIdFor(f.linkedPoLineId);
    if (!key || !poId) return null;
    var src = String(f.linkedPoLineId).split(":")[0];
    var found = loadLS(key, []).find(function (p) { return String(p.id) === String(poId); });
    return found ? { ...found, _src: src } : null;
  }, [f.linkedPoLineId]);
  /* One-time backfill (2026-09-12, Arturo — PU 17445 case): before the
     Invoice had its own thermograph field, Temp Recorder/Logger # was
     only ever entered on the Pricing PO's loggerNumber. Invoices that
     already have a linked PO with a loggerNumber but no thermograph of
     their own (never re-saved since this field existed) get it filled
     in automatically here so Arturo doesn't have to retype a value he
     already captured — same effect as if he'd typed it into the new
     field himself, then a normal save propagates it onward (PO +
     SweetLeaf) same as any manual edit. */
  useEffect(function () {
    if (!f.thermograph && linkedPo && linkedPo.loggerNumber) {
      setF(function (prev) { return prev.thermograph ? prev : { ...prev, thermograph: String(linkedPo.loggerNumber).toUpperCase() }; });
    }
  }, [linkedPo]); // eslint-disable-line react-hooks/exhaustive-deps
  /* The reference-price banner only nags the user UNTIL they've
     started entering prices in the line items. Once any line has a
     non-zero salePrice, the banner hides — the user has internalized
     the reference and made their pricing call. */
  var hasPricedLines = useMemo(function () {
    return (f.lines || []).some(function (l) { return parseFloat(l.salePrice) > 0; });
  }, [f.lines]);

  /* Drag-reorder hooks for each line list. Each callback writes the
     reordered array back to f and marks the form dirty. */
  var dndLines = useDragReorder(f.lines, function(next) { setF(function(p) { return { ...p, lines: next }; }); setDirty(true); });
  var dndFees = useDragReorder(f.fees, function(next) { setF(function(p) { return { ...p, fees: next }; }); setDirty(true); });
  var dndCredits = useDragReorder(f.credits, function(next) { setF(function(p) { return { ...p, credits: next }; }); setDirty(true); });
  var dndTransfers = useDragReorder(f.balanceTransfers, function(next) { setF(function(p) { return { ...p, balanceTransfers: next }; }); setDirty(true); });

  /* ─── Balance Transfers (cross-invoice, atomic) ───
     Mirrors BillRegistry's pattern. Each entry stamped on BOTH invoices
     with cross-references. Filtered by SAME CLIENT (vs same shipper for
     bills). Persisted to gfi-invoices immediately so the OTHER invoice
     is updated even if the user cancels editing this one. */
  function nextTransferId() { return "BT-" + Date.now() + "-" + Math.random().toString(36).slice(2, 6); }
  /* direction: "send" → this invoice gets credit, other gets debit
                "receive" → this invoice gets debit, other gets credit */
  var createBalanceTransfer = function(direction, otherInvoiceId, amount) {
    var amt = parseFloat(amount) || 0;
    if (!otherInvoiceId || amt <= 0) return;
    var other = (invoices || []).find(function(inv) { return String(inv.id) === String(otherInvoiceId); });
    if (!other) return;
    var tid = nextTransferId();
    var today = new Date().toISOString().substring(0, 10);
    var thisType = direction === "send" ? "credit" : "debit";
    var otherType = direction === "send" ? "debit" : "credit";
    var thisEntry = { id: tid, type: thisType, otherInvoiceId: other.id, otherPuNumber: other.puNumber || "", otherClientName: other.clientName || "", amount: amt, date: today };
    var otherEntry = { id: tid, type: otherType, otherInvoiceId: f.id || "(this invoice)", otherPuNumber: f.puNumber || "", otherClientName: f.clientName || "", amount: amt, date: today };
    /* Persist the OTHER invoice immediately. */
    try {
      var current = loadLS(STORAGE_KEY, []);
      var nextStore = current.map(function(inv) {
        if (String(inv.id) !== String(other.id)) return inv;
        return { ...inv, balanceTransfers: [...(inv.balanceTransfers || []), otherEntry] };
      });
      saveLS(STORAGE_KEY, nextStore);
    } catch (e) { console.error("[InvoiceBalanceTransfer] failed to persist other invoice", e); return; }
    /* Update this invoice's in-memory state (will persist when user saves). */
    setF(function(prev) { return { ...prev, balanceTransfers: [...(prev.balanceTransfers || []), thisEntry] }; });
    setDirty(true);
  };
  var removeBalanceTransfer = function(idx) {
    var entry = (f.balanceTransfers || [])[idx];
    if (!entry) return;
    if (entry.otherInvoiceId && entry.id) {
      try {
        var current = loadLS(STORAGE_KEY, []);
        var nextStore = current.map(function(inv) {
          if (String(inv.id) !== String(entry.otherInvoiceId)) return inv;
          return { ...inv, balanceTransfers: (inv.balanceTransfers || []).filter(function(t) { return t.id !== entry.id; }) };
        });
        saveLS(STORAGE_KEY, nextStore);
      } catch (e) { console.error("[InvoiceBalanceTransfer] failed to update other invoice on remove", e); }
    }
    setF(function(prev) { return { ...prev, balanceTransfers: (prev.balanceTransfers || []).filter(function(_, i) { return i !== idx; }) }; });
    setDirty(true);
  };
  var addCustomFeeType = function(name) { if (!name || feeTypes.includes(name)) return; var updated = [...feeTypes, name]; setFeeTypes(updated); saveFeeTypes(updated); };

  var handleClientChange = function(clientId) {
    var cl = clients.find(function(c) { return String(c.id) === String(clientId); });
    var initials = cl ? (cl.initials || (cl.shortName || "").substring(0, 2).toUpperCase()) : "";
    var clientName = cl ? (cl.shortName || cl.fullName || cl.name || cl.companyName || "") : "";
    /* Auto-calculate due date from payment term */
    var dueDateCalc = "";
    if (cl && cl.paymentTerm) {
      var pt = String(cl.paymentTerm); var nums = pt.match(/\d+/);
      var days = nums ? parseInt(nums[0], 10) : NaN;
      if (!isNaN(days) && days > 0) {
        var baseDate = new Date((f.date || new Date().toISOString().substring(0, 10)) + "T00:00:00");
        baseDate.setDate(baseDate.getDate() + days);
        dueDateCalc = baseDate.toISOString().substring(0, 10);
      }
    }
    /* Pull client default commission rate. Only override the invoice's
       current rate when one isn't already set on this invoice (so the
       user's manual edit survives a client re-pick). */
    var clientComm = cl && cl.commissionRate != null ? parseFloat(cl.commissionRate) || 0 : 0;
    setF(function(prev) {
      var nextRate = (prev.commissionRate === undefined || prev.commissionRate === null || prev.commissionRate === "") ? clientComm : prev.commissionRate;
      /* When this is a brand-new invoice with no manual commission yet,
         use the client's default; otherwise keep what the user typed. */
      var rateToUse = (prev.commissionRate === undefined || prev.commissionRate === null || prev.commissionRate === "") ? clientComm : prev.commissionRate;
      return { ...prev, clientId: clientId, clientInitials: initials, clientName: clientName, dueDate: dueDateCalc || prev.dueDate, commissionRate: rateToUse };
    });
    setDirty(true);
  };

  var addFromShipment = function(shipmentId) {
    var shipment = availableLots.find(function(s) { return String(s.id) === String(shipmentId); });
    if (!shipment) return;
    var supName = shipment.shipperName || "";
    if (!supName && shipment.supplierId) {
      var sup = getSuppliers().find(function(s) { return idMatches(s, shipment.supplierId); });
      if (sup) supName = sup.shortName || sup.fullName || sup.name || "";
    }
    var prods = getProducts();
    var newLines = (shipment.lines || []).map(function(l) {
      var desc = l.productDescription || "";
      if (!desc && l.productId) {
        var prod = prods.find(function(p) { return String(p.id) === String(l.productId); });
        if (prod) {
          var cat = (prod.category === "Organic" || prod.category === "OG") ? "OG" : "CV";
          desc = (prod.description || (prod.size || "") + " " + (prod.unitsCase || "") + "u " + cat + " " + (prod.labelInitials || "")).trim();
        }
      }
      return { ...l, productDescription: desc, salePrice: l.price || "", lot: shipment.lot, shipmentId: shipment.id, shipperName: supName };
    });
    var lotStr = shipment.lot != null ? String(shipment.lot) : "";
    var alreadyHasLot = lotStr && (f.palletAllocations || []).some(function(a) { return String(a.lot) === lotStr; });
    var applyAdd = function() {
      var newAllocRows = populateAllocationsForLot(shipment);
      setF(function(prev) {
        var shipperName = prev.shipperName || supName;
        var lot = prev.lot || shipment.lot;
        var keptAllocs = (prev.palletAllocations || []).filter(function(a) { return String(a.lot) !== lotStr; });
        return { ...prev, lot: lot, shipperName: shipperName, lines: [...(prev.lines || []), ...newLines], palletAllocations: [...keptAllocs, ...newAllocRows] };
      });
      setDirty(true);
    };
    /* Re-picking a lot that already has captured allocations regenerates
       that lot's rows from scratch (100% cases again) — warn first since
       it discards any manual edits the user made to those rows. A brand
       new lot just appends, nothing to lose. */
    if (alreadyHasLot) {
      if (window.confirm(t.confirmRelot)) applyAdd();
    } else {
      applyAdd();
    }
  };
  /* Pallet rows for a shipment's manifest, used both to seed
     palletAllocations when a lot is added and to regenerate it if the
     same lot is re-picked. 2026-09-28 (Arturo: lote 5400 con pallets
     1-5, PU 101 ya se quedó con 1,2,3 — al generar PU 102 y "Traer del
     Lote" volvía a traer TODOS los pallets del manifiesto, incluidos
     1,2,3 ya asignados a la otra factura, en sus cajas completas —
     "solo debe mostrar los pallets disponibles"). Now excludes any
     pallet with zero cases left after subtracting what OTHER GF
     invoices already claim (same casesAllocatedElsewhere pool the
     "+ Add pallet from another lot" picker already respects — see
     otherLotPalletOptions above) and caps a partially-claimed pallet's
     row to just its remaining cases, never the full manifest amount. */
  var populateAllocationsForLot = function(shipment) {
    var lotStr = shipment.lot != null ? String(shipment.lot) : "";
    return (shipment.palletManifest || [])
      .filter(function(p) { return p && p.palletNum && (parseInt(p.casesPallet, 10) || 0) > 0; })
      .map(function(p) {
        var total = parseInt(p.casesPallet, 10) || 0;
        var available = total - casesAllocatedElsewhere(lotStr, p.palletNum);
        return { lot: lotStr, palletNum: p.palletNum, cases: available, __total: total };
      })
      .filter(function(row) { return row.cases > 0; })
      .map(function(row) { return { lot: row.lot, palletNum: row.palletNum, cases: row.cases }; });
  };
  var removeProductLine = function(idx) {
    setF(function(prev) { return { ...prev, lines: prev.lines.filter(function(_, i) { return i !== idx; }) }; });
    setDirty(true);
  };

  /* ─── Pallet Allocations (per-invoice, per-physical-pallet case tracking) ───
     Reads the shipment's pallet manifest live (not duplicated into the
     allocation row) so Product/Variety/Grower/Weight always reflect the
     current manifest. Availability is computed against GF invoices only
     (the fresh `invoices` prop) — SweetLeaf invoices are never an
     independent claim on the pool. 2026-09-18, Arturo (still blocked
     after the gfiPuNumber-exclusion fix below): "porque insiste en
     decir que ya no tengo cajas? si esta leyendo las cajas asignadas a
     los invoices de sweetleaf las esta duplicando, esas son solo
     informativas no para conteo" — every sweetleaf_invoices row is a
     one-way mirror of some GF invoice (created via "Copy to SL", never
     independently), so its palletAllocations are always a COPY of
     cases the mirrored GF invoice already claims, never a second real
     consumer of the pallet — counting them at all (even after
     correctly excluding just this invoice's own mirror) double-counts
     every OTHER GF invoice's cases too, since each of those has its
     own SL mirror. SL rows are dropped from the pool entirely now. */
  var shipmentByLot = function(lot) {
    return getShipments().find(function(s) { return String(s.lot) === String(lot); });
  };
  var palletMetaFor = function(lot, palletNum) {
    var shp = shipmentByLot(lot);
    if (!shp) return null;
    return (shp.palletManifest || []).find(function(p) { return String(p.palletNum) === String(palletNum); }) || null;
  };
  var otherInvoicesForAlloc = useMemo(function() {
    /* `invoices` (the GF list, passed in as a prop) is already loaded
       fresh from Supabase by the parent Registry — no need for its
       own localStorage read. */
    var gf = invoices || [];
    /* Dedupe by id, keeping the LAST occurrence — 2026-09-19, Arturo
       (PU 17449, pallet 026 split 29/151 with PU 17450, math that
       should never error): "dice que tiene -122 disponibles y no es
       correcto". 180 (real capacity) minus a DOUBLE-counted 151 = -122
       exactly, so some invoice — almost certainly PU 17450, itself
       previously deleted/regenerated earlier this session — is
       appearing twice in the `invoices` state array (a stale entry
       from before a save/refresh sitting alongside the current one,
       most likely). Deduping here is a cheap, always-safe guard
       against whatever upstream state-update path let that happen,
       regardless of root cause. */
    var seen = {}; var deduped = [];
    for (var i = gf.length - 1; i >= 0; i--) {
      var key = String(gf[i].id);
      if (seen[key]) continue;
      seen[key] = true;
      deduped.unshift(gf[i]);
    }
    /* String() both sides — f.id (from the Supabase-shaped `invoice`
       prop, legacy_id is a text column) and the id on records read
       fresh from Supabase can end up as different JS types (number vs
       string) even for the SAME invoice, since they come from two
       different load paths. A raw !== then never excludes the invoice
       being edited from its own "used elsewhere" pool, so re-saving
       an unchanged allocation reads as 100% over-allocated (available
       = total - itself = 0). */
    return deduped.filter(function(i) { return String(i.id) !== String(f.id); });
  }, [f.id, invoices]);
  var casesAllocatedElsewhere = function(lot, palletNum) {
    var sum = 0;
    otherInvoicesForAlloc.forEach(function(inv) {
      (inv.palletAllocations || []).forEach(function(a) {
        if (String(a.lot) === String(lot) && String(a.palletNum) === String(palletNum)) sum += parseInt(a.cases, 10) || 0;
      });
    });
    return sum;
  };
  var updateAllocCases = function(idx, value) {
    setF(function(prev) { return { ...prev, palletAllocations: (prev.palletAllocations || []).map(function(a, i) { return i === idx ? { ...a, cases: value } : a; }) }; });
    setDirty(true);
  };
  /* 2026-10-13, Arturo: "los defectos de calidad deben ser a nivel
     pallet, uno podria tener cierto tipo de defectos y otro pallet
     distintos" — defects used to live on each pallet allocation
     (a.defects, entries of {key, pct}). 2026-10-23: removed the
     manual per-pallet add/remove/set-pct mutators that wrote there
     (addAllocDefect/removeAllocDefect/setAllocDefectPct) — Four
     Seasons never actually reports defects per pallet, only one
     aggregate list for the whole load, so the manual capture UI moved
     to f.loadDefects (see addLoadDefect/removeLoadDefect/
     setLoadDefectPct below). a.defects itself is left untouched on any
     older invoice that already has it (still read by
     selectedPalletDefects/buildProtectionReportRows for Twin River's
     own read-only per-pallet mirror), just no longer writable here. */
  /* 2026-10-23, Arturo: "corrigeme algo en el client reported data para
     facturas de four seasons, ellos no me detallan los defectos por
     pallet, solo general de la carga, asi que para agregarlos no los
     agrega individualmente... o sea no se debe desglosar defects by
     pallet si no total defects, solo four seasons" — Four Seasons' own
     QC report (see QC Results PO 2236319.pdf) is ONE aggregate test
     for the whole load/PO (DECAY/LEAKY/SOFT with a single Damage %
     each), never broken down per pallet — so unlike Twin River (which
     has real per-pallet data, mirrored read-only from the Shipment),
     Four Seasons' defects live on the invoice itself (f.loadDefects,
     same {key,pct} shape as a pallet's `defects`), not on any single
     pallet allocation. Same add/remove/set-pct pattern as
     addAllocDefect/removeAllocDefect/setAllocDefectPct above, just
     without a per-pallet index. */
  var addLoadDefect = function(key) {
    setF(function(prev) {
      var cur = Array.isArray(prev.loadDefects) ? prev.loadDefects : [];
      if (cur.some(function(e) { return e.key === key; })) return prev;
      return { ...prev, loadDefects: cur.concat([{ key: key, pct: undefined }]) };
    });
    setDirty(true);
  };
  /* 2026-10-27, Arturo: "necesito agregar... la opcion de agregar
     defectos que no esten listados ahi, y su severidad" — a defect
     outside the fixed PROTECTION_DEFECTS catalog. Unlike a catalog
     pick (whose severity always comes from PROTECTION_DEFECTS), this
     one carries its own typed name + chosen severity right on the
     entry — protectionReport.js's defectsFromEntries() reads those
     two fields back out when the catalog lookup misses. Each custom
     entry gets its own unique key (not a shared "__custom__", since
     more than one custom defect can be added to the same load). */
  var addCustomLoadDefect = function(name, severity) {
    var trimmed = String(name || "").trim();
    if (!trimmed) return;
    var key = "__custom__" + Date.now() + "_" + Math.floor(Math.random() * 1000);
    setF(function(prev) {
      var cur = Array.isArray(prev.loadDefects) ? prev.loadDefects : [];
      return { ...prev, loadDefects: cur.concat([{ key: key, custom: trimmed, severity: severity || "medium", pct: undefined }]) };
    });
    setDirty(true);
  };
  var removeLoadDefect = function(key) {
    setF(function(prev) {
      return { ...prev, loadDefects: (Array.isArray(prev.loadDefects) ? prev.loadDefects : []).filter(function(e) { return e.key !== key; }) };
    });
    setDirty(true);
  };
  var setLoadDefectPct = function(key, pct) {
    setF(function(prev) {
      return { ...prev, loadDefects: (Array.isArray(prev.loadDefects) ? prev.loadDefects : []).map(function(e) { return e.key === key ? { ...e, pct: pct } : e; }) };
    });
    setDirty(true);
  };
  /* Per-pallet delivery status (2026-09-12) — the actual source of
     truth for goodDelivery/protection/pas. Line-level status is derived
     from these, never set manually anymore for those 3 values (see
     recomputeLineDeliveryStatuses()). */
  var updateAllocStatus = function(idx, value) {
    setF(function(prev) { return { ...prev, palletAllocations: (prev.palletAllocations || []).map(function(a, i) { return i === idx ? { ...a, saleStatus: value } : a; }) }; });
    setDirty(true);
  };
  /* Recomputes every line's delivery-condition status (goodDelivery /
     protection / pas / partialProtection) from its matched pallets —
     called after any allocation status edit. Lines already on a manual
     disposition (dumped/donated/samples/transfer) are left untouched;
     that choice always overrides the pallet-derived value. Lines with
     no matched pallets yet also keep whatever they already had (usually
     blank) rather than being forced to a guess. */
  var recomputeLineDeliveryStatuses = function(lines, palletAllocations, invoiceLike, shipments) {
    var changed = false;
    var next = (lines || []).map(function(l) {
      if (DISPOSITION_STATUSES.indexOf(l.saleStatus) !== -1) return l;
      var derived;
      /* 2026-11-03, PU 17484: this used to receive ONE pre-resolved
         trShipment (the invoice's home lot) applied to every line —
         on a multi-lot invoice (blackberry from lot 58083, blueberry
         from 58084), the blueberry line got checked against 58083's
         manifest (no blueberry pallets there), always missing its
         real status and getting SAVED with the wrong fallback. Now
         resolved per-line, against THAT line's own lot. */
      var trShipment = resolveTwinRiverShipmentForLot(l.lot || invoiceLike.lot, invoiceLike, shipments);
      if (trShipment !== undefined) {
        /* 2026-10-20, Arturo: "la [PU 17474] ya le agregue hora de
           llegada a bodega, defectos etc aun se muestra como pending
           pick up [aunque] su lote el 58073 ya esta en status
           protection" (Twin River). Twin River never sets a per-pallet
           saleStatus on palletAllocations — its real status lives on
           the Shipment (texasQcMode "clarifresh", twinRiverLotStatus)
           per the 2026-10-15 exception to REGLA DE STATUS DE PALLET —
           so the normal matched-pallet derivation below always came
           back null for these invoices, leaving the line blank
           forever (→ always fell to the Pending Pick Up default).
           trShipment short-circuits that for a Twin River/ClariFresh
           invoice: null here still means "nothing confirmed yet",
           same as before.
           2026-10-30, Arturo (PU 17475/lot 58074): "las lineas de
           producto... las protecciones se definen por producto...
           no debe mezclar productos" — this used to apply ONE lot-wide
           status (twinRiverLotStatus) to every line, so zarzamora
           (fully protected) and arándano (fully good) both got
           blended into the same "partialProtection". Now resolved
           PER PRODUCT (twinRiverProductStatus, scoped to only this
           line's own productBase), so each product keeps its own real
           status and a mix only happens when THAT product's own
           pallets are themselves mixed. */
        derived = twinRiverProductStatus(trShipment, l.productBase || l.productName);
      } else {
        var matched = (palletAllocations || []).filter(function(a) {
          return allocationMatchesLine(l, a.lot, palletMetaFor(a.lot, a.palletNum));
        });
        derived = deriveDeliveryStatus(matched);
      }
      if (derived === null || derived === l.saleStatus) return l;
      changed = true;
      return { ...l, saleStatus: derived };
    });
    return changed ? next : (lines || []);
  };
  /* Keeps every line's delivery status in sync with its pallets any time
     palletAllocations changes (edit a pallet's status, add/remove a
     pallet, re-populate a lot). setF's functional update bails out to
     the same object when recomputeLineDeliveryStatuses finds nothing to
     change, so this doesn't loop or re-render needlessly. */
  useEffect(function() {
    setF(function(prev) {
      var next = recomputeLineDeliveryStatuses(prev.lines, prev.palletAllocations, prev, getShipments());
      return next === prev.lines ? prev : { ...prev, lines: next };
    });
  }, [f.palletAllocations, f.clientId, f.clientName, f.lot]); // eslint-disable-line react-hooks/exhaustive-deps
  var removeAlloc = function(idx) {
    setF(function(prev) { return { ...prev, palletAllocations: (prev.palletAllocations || []).filter(function(_, i) { return i !== idx; }) }; });
    setDirty(true);
  };
  /* Picker for "+ Add pallet from another lot" — restricted to lots
     already pulled in via "Add from shipment" (f.lines), excludes
     pallets already represented as a row here, and excludes pallets
     with zero cases left after subtracting other invoices' claims. */
  var addedLots = useMemo(function() {
    return [...new Set((f.lines || []).map(function(l) { return l.lot; }).filter(Boolean).map(String))];
  }, [f.lines]);
  var otherLotPalletOptions = useMemo(function() {
    var existing = new Set((f.palletAllocations || []).map(function(a) { return String(a.lot) + "::" + String(a.palletNum); }));
    var opts = [];
    addedLots.forEach(function(lot) {
      var shp = shipmentByLot(lot);
      if (!shp) return;
      (shp.palletManifest || []).forEach(function(p) {
        if (!p || !p.palletNum) return;
        var key = String(lot) + "::" + String(p.palletNum);
        if (existing.has(key)) return;
        var total = parseInt(p.casesPallet, 10) || 0;
        var available = total - casesAllocatedElsewhere(lot, p.palletNum);
        if (available <= 0) return;
        opts.push({ value: key, label: "LOT " + lot + " · Plt " + p.palletNum + " · " + (p.productBase || "") + " · " + available + " " + t.allocCases, available: available, lot: lot, palletNum: p.palletNum });
      });
    });
    return opts;
  }, [addedLots, f.palletAllocations, otherInvoicesForAlloc]); // eslint-disable-line react-hooks/exhaustive-deps
  /* "la opcion de seleccionar uno por uno o agregar todos" — AddPalletModal's
     multi-select confirm, adds every checked pallet in one update. */
  var addFromOtherLotPalletMany = function(keys) {
    var newRows = keys.map(function(key) { return otherLotPalletOptions.find(function(o) { return o.value === key; }); }).filter(Boolean)
      .map(function(opt) { return { lot: opt.lot, palletNum: opt.palletNum, cases: opt.available }; });
    if (newRows.length === 0) return;
    setF(function(prev) { return { ...prev, palletAllocations: [...(prev.palletAllocations || []), ...newRows] }; });
    setDirty(true);
  };
  /* FASE 7 — opt-in migration for historical invoices with no
     palletAllocations. Only surfaces when there's actually something
     to populate from: allocations empty, a header lot resolved, and
     that lot's shipment has a non-empty manifest. Never runs
     automatically — the button + confirm are the only trigger. */
  var populateFromLotShipment = useMemo(function() {
    if ((f.palletAllocations || []).length > 0) return null;
    if (!f.lot) return null;
    var shp = findShipmentForInvoice(f, getShipments());
    if (!shp || !Array.isArray(shp.palletManifest) || shp.palletManifest.length === 0) return null;
    return shp;
  }, [f.palletAllocations, f.lot]); // eslint-disable-line react-hooks/exhaustive-deps
  var populateFromLot = function() {
    if (!populateFromLotShipment) return;
    var msg = lang === "es"
      ? "Esto poblará las asignaciones de pallets desde el manifiesto del embarque. ¿Continuar?"
      : "This will populate pallet allocations from the shipment's pallet manifest. Continue?";
    if (!window.confirm(msg)) return;
    var rows = populateAllocationsForLot(populateFromLotShipment);
    setF(function(prev) { return { ...prev, palletAllocations: rows }; });
    setDirty(true);
  };

  /* Editable Qty (cases) per line — was read-only before. The user
     needs to split a single bill line into two invoice lines with
     different cases + sale status (e.g. 240 Good Delivery + 240
     Protection on the same product). Empty input is allowed and
     stored as "" so calcInvoiceTotal treats it as 0. */
  var updateLineCases = function(idx, cases) {
    setF(function(prev) { var nl = prev.lines.map(function(l, i) { return i === idx ? { ...l, cases: cases } : l; }); return { ...prev, lines: nl }; });
    setDirty(true);
  };
  /* Editable Pallets per line — symmetric with Cases. Lets the user
     reapportion both quantities when splitting a line (e.g. 4 pallets
     / 480 cases → 2/240 + 2/240). The invoice totals reduce over
     .lines so they recompute automatically. */
  var updateLinePallets = function(idx, pallets) {
    setF(function(prev) { var nl = prev.lines.map(function(l, i) { return i === idx ? { ...l, pallets: pallets } : l; }); return { ...prev, lines: nl }; });
    setDirty(true);
  };

  /* Split a product line in halves — mirrors BillRegistry.splitLine.
     Cases AND pallets are halved (round-down on first half). Both
     halves keep the full product metadata so descriptions and certs
     survive. User then tweaks either half. */
  var splitProductLine = function(idx) {
    setF(function(prev) {
      var newLines = prev.lines.slice();
      var orig = newLines[idx];
      if (!orig) return prev;
      var total = parseInt(orig.cases, 10) || 0;
      var halfA = total >= 2 ? Math.floor(total / 2) : (total > 0 ? 1 : 0);
      var halfB = total - halfA;
      var totalP = parseInt(orig.pallets, 10) || 0;
      var halfPA = totalP >= 2 ? Math.floor(totalP / 2) : (totalP > 0 ? 1 : 0);
      var halfPB = totalP - halfPA;
      newLines[idx] = { ...orig, cases: String(halfA), pallets: totalP > 0 ? String(halfPA) : orig.pallets };
      newLines.splice(idx + 1, 0, { ...orig, cases: String(halfB), pallets: totalP > 0 ? String(halfPB) : orig.pallets });
      return { ...prev, lines: newLines };
    });
    setDirty(true);
  };

  /* Append a clone of an existing product line directly below it.
     Preserves all product metadata (productBase, productName,
     productDescription, category, lot, shipmentId, productId, etc.)
     so the same SKU is referenced, but clears the per-instance
     fields (cases + pallets + saleStatus) so the user fills them in.
     Price is kept since the spread is usually the same. */
  var addProductLineCopy = function(idx) {
    setF(function(prev) {
      var newLines = prev.lines.slice();
      var orig = newLines[idx];
      if (!orig) return prev;
      newLines.splice(idx + 1, 0, { ...orig, cases: "", pallets: "", saleStatus: "" });
      return { ...prev, lines: newLines };
    });
    setDirty(true);
  };

  var updateLinePrice = function(idx, price) {
    setF(function(prev) { var nl = prev.lines.map(function(l, i) { return i === idx ? { ...l, salePrice: price } : l; }); return { ...prev, lines: nl }; });
    setDirty(true);
  };

  var updateLineSaleStatus = function(idx, status) {
    setF(function(prev) { var nl = prev.lines.map(function(l, i) { return i === idx ? { ...l, saleStatus: status } : l; }); return { ...prev, lines: nl }; });
    setDirty(true);
  };

  var totalAmount = useMemo(function() {
    var total = 0;
    (f.lines || []).forEach(function(l) {
      if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
        total += (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
      }
    });
    return total;
  }, [f.lines]);
  var totalFees = useMemo(function() { var t = 0; (f.fees || []).forEach(function(fe) { t += Math.abs(parseFloat(fe.amount) || 0); }); return t; }, [f.fees]);
  var totalCredits = useMemo(function() { var t = 0; (f.credits || []).forEach(function(cr) { t += Math.abs(parseFloat(cr.amount) || 0); }); return t; }, [f.credits]);
  /* Signed sum of balance transfers — type "credit" adds, "debit" subtracts.
     See calcInvoiceTotal for the same formula in the persisted-record path. */
  var totalTransfers = useMemo(function() {
    var t = 0;
    (f.balanceTransfers || []).forEach(function(tr) {
      var amt = parseFloat(tr.amount) || 0;
      if (tr.type === "credit") t += amt;
      else if (tr.type === "debit") t -= amt;
    });
    return t;
  }, [f.balanceTransfers]);
  /* Commission is now PER-LINE: each line carries its own
     commissionRate (% of that line's value). The displayed total at
     the bottom is the SUM of every line's computed commission $.
     Legacy lines without a per-line rate fall back to
     invoice.commissionRate so old records still calc the same.
     Excluded sale statuses (dumped/donated/samples) contribute $0. */
  var commissionAmount = useMemo(function () {
    var invDefault = parseFloat(f.commissionRate) || 0;
    var sum = 0;
    (f.lines || []).forEach(function (l) {
      if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
      var lineAmt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
      var rate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
        ? invDefault
        : (parseFloat(l.commissionRate) || 0);
      sum += lineAmt * (rate / 100);
    });
    return sum;
  }, [f.lines, f.commissionRate]);
  /* Fees SUBTRACT (deductions GFI passes to client: PTI, QC, warehouse,
     advance financing, etc.), Credits ADD, Transfers ± per type,
     Commission SUBTRACTS.
       TOTAL DUE = Gross − Commission − Fees + Credits + Transfers */
  var due = totalAmount - commissionAmount - totalFees + totalCredits + totalTransfers;

  var validate = function() {
    var errs = {};
    if (!f.puNumber) errs.puNumber = true;
    if (!f.clientId) errs.clientId = true;
    if ((f.lines || []).length === 0) errs.lot = true;
    if (!(f.lines || []).length) errs.lines = true;
    setErrors(errs); return Object.keys(errs).length === 0;
  };

  /* Live reconciliation banner + save guard — lines[].cases (what the
     invoice document shows) vs palletAllocations[].cases (the physical
     source of truth calcLotProfit reads for shipment/bill profit).
     Recomputed on every render off current form state so it updates as
     the user edits either side, no debounce needed (cheap sums). */
  var lotBalances = useMemo(function() { return computeLotBalances(f.lines, f.palletAllocations); }, [f.lines, f.palletAllocations]);
  var hasLotMismatch = lotBalances.some(function(b) { return !b.balanced; });

  /* Pallet-allocation save guard. Groups this invoice's own rows by
     (lot, palletNum) — in case the same pallet ended up on two rows —
     then checks elsewhere-usage + this invoice's total against the
     pallet's real case count. Returns one message per over-allocated
     pallet so a single alert can list all of them at once. */
  var validatePalletAllocations = function(allocs) {
    var perPallet = {};
    allocs.forEach(function(a) {
      var key = String(a.lot) + "::" + String(a.palletNum);
      perPallet[key] = (perPallet[key] || 0) + (parseInt(a.cases, 10) || 0);
    });
    var errors = [];
    Object.keys(perPallet).forEach(function(key) {
      var idx = key.indexOf("::");
      var lot = key.slice(0, idx), palletNum = key.slice(idx + 2);
      var meta = palletMetaFor(lot, palletNum);
      var total = meta ? (parseInt(meta.casesPallet, 10) || 0) : 0;
      var usedElsewhere = casesAllocatedElsewhere(lot, palletNum);
      var thisInvoiceCases = perPallet[key];
      var available = total - usedElsewhere;
      if (usedElsewhere + thisInvoiceCases > total) {
        errors.push(lang === "es"
          ? "Pallet " + palletNum + " del lote " + lot + ": intentas asignar " + thisInvoiceCases + " pero solo hay " + available + " disponibles"
          : "Pallet " + palletNum + " of lot " + lot + ": you're trying to assign " + thisInvoiceCases + " but only " + available + " are available");
      }
    });
    return errors;
  };

  var handleSave = function() {
    if (!validate()) return;
    /* Drop rows that can't mean anything (0/blank cases, or missing
       lot/palletNum from a corrupted/legacy row) before validating —
       these are silently cleaned up, not reported as errors, since
       there's nothing actionable for the user to fix on them. */
    var cleanAllocs = (f.palletAllocations || []).filter(function(a) {
      return a && a.lot && a.palletNum && (parseInt(a.cases, 10) || 0) > 0;
    });
    var allocErrors = validatePalletAllocations(cleanAllocs);
    if (allocErrors.length > 0) {
      alert((lang === "es" ? "No se puede guardar — excede lo disponible:\n\n" : "Cannot save — exceeds available cases:\n\n") + allocErrors.join("\n"));
      return;
    }
    /* Lines[].cases vs palletAllocations reconciliation — same rule the
       live banner above the Pallet Allocations table shows, enforced
       again here so it can't be bypassed by saving before the banner
       re-renders. Uses cleanAllocs (post-cleanup) so a stray 0-case row
       doesn't falsely count toward a lot's allocation total. */
    var lotMismatches = lotBalanceMismatches(f.lines, cleanAllocs);
    if (lotMismatches.length > 0) {
      var mismatchMsg = lotMismatches.map(function(b) {
        return lang === "es"
          ? "El lote " + b.lot + ": la línea de producto muestra " + b.linesCases + " cajas pero los pallets asignados suman " + b.allocCases + " cajas. Corrige antes de guardar."
          : "Lot " + b.lot + ": the product line shows " + b.linesCases + " cases but the assigned pallets add up to " + b.allocCases + " cases. Fix before saving.";
      }).join("\n\n");
      alert(mismatchMsg);
      return;
    }
    (f.fees || []).forEach(function(fe) { if (fe.feeType === "__custom__" && fe.feeCustom) addCustomFeeType(fe.feeCustom); });
    var invoiceToSave = { ...f, palletAllocations: cleanAllocs, id: f.id || nextId(invoices) };
    /* 2026-10-02 — Arturo, for Twin River: "este campo Pickup Date
       (BOL) cambiarlo a Arrival Date y este Pickup Time (BOL)
       cambiarlo a Arrival Time y estos mismos se usan al momento de
       pasarlo a protection agregando solamente Date protection was
       reported pero solo para twin river" — Twin River never has a
       separate "client-reported arrival" event to ask about (ROW 2.6)
       because it's the SAME event already captured as Arrival Date/
       Time in ROW 2.5 (pickupDate/pickupTime, relabeled for TR — see
       that row's own comment). Mirrored here, once, at save time, so
       every existing downstream reader of clientArrivalDate/Time
       (computeProtectionReminderStatusGf, the due banner, the
       Protection Report PDF) keeps working unchanged for Twin River
       without needing its own special case — they just always see the
       Arrival Date/Time TR already entered. */
    if ((resolveClientName(invoiceToSave.clientId) || invoiceToSave.clientName || "").toLowerCase().indexOf("twin river") >= 0) {
      invoiceToSave.clientArrivalDate = invoiceToSave.pickupDate || "";
      invoiceToSave.clientArrivalTime = invoiceToSave.pickupTime || "";
    }
    /* Protection Notice reminder (2026-09-14, Arturo: "alertas de que
       no se ha enviado cuando cambie a ese status un file") — fires
       only on the actual transition into protection (none of the
       ORIGINAL invoice's pallets were protected, but at least one is
       now) and only when the notice hasn't already gone out. Compares
       the pre-save `invoice` prop (not `f`/`invoiceToSave`, which
       already reflect this save's edits) against `cleanAllocs` so a
       save that merely keeps an already-protected invoice protected —
       or edits an unrelated field — doesn't re-alert every time. */
    var isProtectedAlloc = function(a) { return a && (a.saleStatus === "protection" || a.saleStatus === "pas"); };
    var hadProtectionBefore = !!(invoice && Array.isArray(invoice.palletAllocations) && invoice.palletAllocations.some(isProtectedAlloc));
    var hasProtectionNow = cleanAllocs.some(isProtectedAlloc);
    if (!hadProtectionBefore && hasProtectionNow && !invoiceToSave.protectionEmailSentAt) {
      alert(t.protectionEmailReminderPrefix + t.emailShipperProtection + t.protectionEmailReminderSuffix);
    }
    /* Create fee transfers for fees marked transferToBill */
    var transferFees = (f.fees || []).filter(function(fe) { return fe.transferToBill && parseFloat(fe.amount) > 0 && fe.transferTargetBillId; });
    if (transferFees.length > 0) {
      try {
        var transfers = loadLS("gfi-fee-transfers", []);
        transfers = transfers.filter(function(t) { return t.sourceInvoice !== invoiceToSave.id; });
        transferFees.forEach(function(fe) {
          var targetBill = availableBills.find(function(b) { return b.id === fe.transferTargetBillId; });
          transfers.push({
            id: "TRF-" + Date.now() + "-" + Math.random().toString(36).substring(2, 6),
            sourceType: "invoice", sourceInvoice: invoiceToSave.id, sourcePU: invoiceToSave.puNumber,
            targetType: "bill", targetLot: targetBill ? targetBill.lot : f.lot, targetBillId: fe.transferTargetBillId,
            feeType: fe.feeType, feeCustom: fe.feeCustom || "",
            qty: fe.qty, rate: fe.rate, amount: fe.amount, pctRate: fe.pctRate || "",
            status: "pending", dateCreated: new Date().toISOString().substring(0, 10),
          });
        });
        localStorage.setItem("gfi-fee-transfers", JSON.stringify(transfers));
      } catch(e) { console.error("Fee transfer error", e); }
    }
    /* Sync PO linkage with the Client Pricing modules. The previous
       linkage (if any) is read from the persisted version of the
       invoice so a "Save & New"-style flow doesn't end up with two
       invoices claiming the same PO. Done before onSave so the
       persisted state on disk is consistent with the back-link.
       Passes full invoice context (id + puNumber + status) so the
       PO row mirrors the invoice's PU# and status, not just the id. */
    try {
      var existing = invoice && invoice.id ? (loadLS(STORAGE_KEY, []).find(function (x) { return x.id === invoice.id; }) || null) : null;
      var prevLink = existing && existing.linkedPoLineId ? existing.linkedPoLineId : "";
      var newLink  = invoiceToSave.linkedPoLineId || "";
      if (prevLink || newLink) {
        syncPoLinkage(prevLink, newLink, {
          id: invoiceToSave.id,
          puNumber: invoiceToSave.puNumber || "",
          status: invoiceToSave.status || "draft",
          thermograph: invoiceToSave.thermograph || "",
        });
      }
    } catch (e) { /* linkage best-effort — don't block the save */ }
    onSave(invoiceToSave);
  };

  var tryCancel = function() { if (dirty) setShowExitConfirm(true); else onCancel(); };
  var errS = { borderColor: "#ef4444" };

  /* 2026-10-20, Arturo: "modo edit y modo no edit son copias... recuerda
     modo edit y modo no edit son copias, es decir al pasar de modo no
     edit a modo edit la interfase debe verse igual" — same Links strip
     as DetailPanel's view mode (Linked Bill only — "open lot no es
     necesario, debemos seguir la secuencia lot>bill>invoice>sweetleaf
     invoice", the Lot is one hop further up, reachable from the Bill).
     Invoice Activity here shows the SAME read-only mirror Bill/
     Shipment already use for this exact data (RejectionDetailsReadOnly/
     ProtectionResponsesReadOnly) rather than duplicating the full
     editable Rejection/Response cards into a second place — editing
     those stays in view mode (DetailPanel), same "some things can
     only be corrected from one spot" precedent already set elsewhere
     in this file (REGLA DE STATUS DE PALLET). */
  var linkedBillForBarEdit = useMemo(function () {
    if (!f.lot) return null;
    return getBills().find(function (b) { return String(b.lot) === String(f.lot); }) || null;
  }, [f.lot]);
  var openLinkedBillEdit = function () {
    if (!linkedBillForBarEdit) return;
    var billId = linkedBillForBarEdit.id;
    window.__gfiInvoiceReopenId = f.id;
    if (window.__gfiNavigate) window.__gfiNavigate("bills");
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiBillOpen) window.__gfiBillOpen(billId); }, 250);
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiBillOpen) window.__gfiBillOpen(billId); }, 900);
  };
  /* 2026-10-21, Arturo: "tampoco veo el sweetleaf linked invoice" —
     edit mode's equivalent of DetailPanel's linkedSlInvoiceForInfo/
     openLinkedSlInvoiceFromInfo, reading f/f.id instead of fresh. */
  var linkedSlInvoiceForInfoEdit = isFourSeasonsClientName(resolveClientName(f.clientId) || f.clientName || "") ? (slPassingIndex[String(f.id)] || null) : null;
  var openLinkedSlInvoiceFromInfoEdit = function () {
    if (!linkedSlInvoiceForInfoEdit) return;
    var slId = linkedSlInvoiceForInfoEdit.id;
    window.__gfiInvoiceReopenId = f.id;
    if (window.__gfiNavigate) window.__gfiNavigate("sl_invoices");
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiInvoiceOpen) window.__gfiInvoiceOpen(slId); }, 250);
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiInvoiceOpen) window.__gfiInvoiceOpen(slId); }, 900);
  };
  /* "me voy a dormir" — edit mode's equivalent of DetailPanel's
     openLinkedSupplierFromInfo. Supplier resolved via the linked
     shipment's own supplierId (an invoice has no supplierId of its
     own), same lookup already used elsewhere in this form (e.g. the
     Twin River check right above). */
  var linkedShipmentForInfoEdit = findShipmentForInvoice(f, getShipments());
  var openLinkedSupplierFromInfoEdit = function () {
    var supplierId = linkedShipmentForInfoEdit && linkedShipmentForInfoEdit.supplierId;
    if (!supplierId) return;
    window.__gfiInvoiceReopenId = f.id;
    if (window.__gfiNavigate) window.__gfiNavigate("suppliers");
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiSupplierOpen) window.__gfiSupplierOpen(supplierId); }, 250);
    setTimeout(function () { window.__gfiReturnModule = "invoices"; if (window.__gfiSupplierOpen) window.__gfiSupplierOpen(supplierId); }, 900);
  };
  var hasInvoiceActivityEdit = rejectedCasesForInvoice(f) > 0 || hasProtectionResponses(f.lot, [f]);

  var body = (
    <>
        {/* 2026-10-20, Arturo: "modo edit y modo no edit son copias" \u2014
            same Links strip as DetailPanel's view mode, same position
            (first thing in the body), same Linked Lot/Linked Bill
            buttons + an Invoice Activity button \u2014 read-only mirror
            here (see note above hasInvoiceActivityEdit). */}
        <div style={{ padding: "10px 22px", borderBottom: "1px solid #b9bcc0", background: "repeating-linear-gradient(180deg, rgba(0,0,0,0.02) 0px, rgba(0,0,0,0.02) 1px, transparent 1px, transparent 3px), linear-gradient(180deg, #f2f3f4 0%, #dcdfe2 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, fontSize: 11 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)" }}>{lang === "es" ? "Links:" : "Links:"}</span>
            {/* 2026-10-21, Arturo: "ese bill finalized es del bill, no
                tiene nada que hacer aqui, solo debe existir open
                linked bill y el numero de lote de ese bill" — same
                simplified single-button format view mode's own Links
                strip already uses (no raw BILL-#### id, no Bill-status
                badge — that status belongs to the Bill record, not
                this Invoice, and was confusing to show side by side
                with the Invoice's own Draft/Sent/Paid). */}
            {linkedBillForBarEdit && <button type="button" onClick={openLinkedBillEdit} style={plateActionBtnStyle}>{t.linkedBill} · {lang === "es" ? "LOTE" : "LOT"} {linkedBillForBarEdit.lot || "—"}</button>}
            {/* 2026-10-21, Arturo: "client report data agregalo a la
                parte alta al lado de open linked bill, pero sin
                placa mismo nombre al hacer click se abre una ventana
                que muestra la informacion de la solicitud de
                proteccion con boton editar, esta seccion se agrega
                al momento de cambiar el status a proteccion si es
                good delivery no se agrega" — moved out of the main
                form body (used to always take up vertical space
                whenever a line was Protection/PAS) into a small
                title-bar button + popover, right next to Open
                Linked Bill. The gate below decides whether the
                TRIGGER itself renders at all (so a Good Delivery
                invoice shows nothing here, not just an empty
                popover) — same hasProtectionLine check the content
                itself used to gate on internally. */}
            {(function () {
              var hasProtectionLine = (f.palletAllocations || []).some(function (a) { return a && (a.saleStatus === "protection" || a.saleStatus === "pas"); })
                || (f.lines || []).some(function (l) { return l && (l.saleStatus === "protection" || l.saleStatus === "pas"); });
              if (!hasProtectionLine) return null;
              return (
                <ClientReportedDataButton label={lang === "es" ? "Datos reportados por el cliente" : "Client-reported data"} style={statusPlateBtnStyle("orange")}>
            {(function () {
              /* 2026-10-02 — Arturo: "al pasar a proteccion solamente que
                 me pida Date protection was reported" (Twin River only),
                 then corrected: "tambien agrega Date protection was
                 reporte[d] despues de Date protection was reported" — he
                 meant Time protection was reported, right after Date —
                 so TR gets Date+Time reported, same as everyone else.
                 What stays skipped for TR is only Arrival date/time:
                 the SAME event already captured in ROW 2.5 (Arrival
                 Date/Arrival Time, i.e. pickupDate/pickupTime) — mirrored
                 into clientArrivalDate/Time at save time (see
                 handleSave), so there's no reason to ask for it again. */
              var isTwinRiver = (resolveClientName(f.clientId) || f.clientName || "").toLowerCase().indexOf("twin river") >= 0;
              /* 2026-10-19, Arturo: Twin River's defects + total load
                 regression % are captured ONCE already, at the Shipment
                 (ShipmentRegistry.js's Pallet Manifest qcDefects /
                 lotRegressionPct) — never re-typed here. Looked up live
                 (same findShipmentForInvoice(f, getShipments()) pattern
                 already used elsewhere in this same form, e.g. for the
                 Arrival Date/Time box right above) so a later correction
                 on the Shipment shows up here without resaving the
                 invoice. */
              var shpForTR = isTwinRiver ? findShipmentForInvoice(f, getShipments()) : null;
              var palletsByNumFromShpForTR = {};
              ((shpForTR && shpForTR.palletManifest) || []).forEach(function (pm) {
                if (pm && pm.palletNum) palletsByNumFromShpForTR[String(pm.palletNum)] = pm;
              });
              var lotRegressionPctForTR = shpForTR && shpForTR.texasQcMode === "clarifresh" ? shpForTR.lotRegressionPct : undefined;
              /* 2026-10-21, Arturo: "sin placa... con boton editar" — read-
                 only summary by default (plain list, no colored box), an
                 Edit button flips to the exact same interactive fields
                 that already existed here (unchanged below). */
              if (!crDataEditOpen) {
                var protPalletsForSummary = (f.palletAllocations || []).filter(function (a) { return a && (a.saleStatus === "protection" || a.saleStatus === "pas"); });
                return (
                  <div style={{ fontSize: 12 }}>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                      <button type="button" onClick={function () { setCrDataEditOpen(true); }} style={{ padding: "3px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Editar" : "Edit"}</button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 14px", marginBottom: 10 }}>
                      <div><span style={readOnlyPlateLabelStyle}>{lang === "es" ? "Fecha reportada" : "Date reported"}</span><div style={readOnlyPlateValueStyle}>{fmtDate(f.clientReportedDate) || "—"}</div></div>
                      <div><span style={readOnlyPlateLabelStyle}>{lang === "es" ? "Hora reportada" : "Time reported"}</span><div style={readOnlyPlateValueStyle}>{f.clientReportedTime || "—"}</div></div>
                      {!isTwinRiver && <div><span style={readOnlyPlateLabelStyle}>{lang === "es" ? "Fecha de llegada" : "Arrival date"}</span><div style={readOnlyPlateValueStyle}>{fmtDate(f.clientArrivalDate) || "—"}</div></div>}
                      {!isTwinRiver && <div><span style={readOnlyPlateLabelStyle}>{lang === "es" ? "Hora de llegada" : "Arrival time"}</span><div style={readOnlyPlateValueStyle}>{f.clientArrivalTime || "—"}</div></div>}
                    </div>
                    {isTwinRiver && lotRegressionPctForTR !== undefined && lotRegressionPctForTR !== null && lotRegressionPctForTR !== "" && (
                      <div style={{ marginBottom: 10, fontSize: 11, color: "#B45309", fontWeight: 600 }}>{(lang === "es" ? "Regresión total: " : "Total Regression: ") + lotRegressionPctForTR + "%"}</div>
                    )}
                    {isTwinRiver ? (<>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 6 }}>{lang === "es" ? "Defectos por pallet" : "Defects by pallet"}</div>
                    {protPalletsForSummary.length === 0 ? (
                      <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{lang === "es" ? "Sin pallets protegidos." : "No protected pallets."}</div>
                    ) : protPalletsForSummary.map(function (a) {
                      var n = Array.isArray(a.defects) ? a.defects.length : 0;
                      return <div key={a.palletNum} style={{ fontSize: 11, marginBottom: 3 }}>{(lang === "es" ? "Pallet " : "Pallet ") + (a.palletNum || "—") + ": " + (n > 0 ? n + " " + (lang === "es" ? "defecto(s)" : "defect(s)") : (lang === "es" ? "sin defectos capturados" : "no defects captured"))}</div>;
                    })}
                    </>) : (<>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 6 }}>{lang === "es" ? "Total de defectos" : "Total defects"}</div>
                    {(Array.isArray(f.loadDefects) ? f.loadDefects : []).length === 0 ? (
                      <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{lang === "es" ? "Sin defectos capturados." : "No defects captured."}</div>
                    ) : f.loadDefects.map(function (entry) {
                      /* 2026-10-27: entry.key may not be in the fixed
                         catalog — a custom defect carries its own typed
                         name on entry.custom instead. */
                      var d = PROTECTION_DEFECTS.find(function (dd) { return dd.key === entry.key; });
                      var label = d ? d.labelEn : entry.custom;
                      if (!label) return null;
                      var pctTxt = (entry.pct === 0 || entry.pct) ? " — " + entry.pct + "%" : "";
                      return <div key={entry.key} style={{ fontSize: 11, marginBottom: 3 }}>{label + pctTxt}</div>;
                    })}
                    </>)}
                    {f.clientRemarksSummary && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 4 }}>{lang === "es" ? "Comentarios del inspector" : "Inspector remarks"}</div>
                        <div style={{ fontSize: 11, color: "var(--color-text-secondary)", whiteSpace: "pre-line" }}>{f.clientRemarksSummary}</div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div style={{ fontSize: 12 }}>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
                    <button type="button" onClick={function () { setCrDataEditOpen(false); }} style={{ padding: "3px 10px", borderRadius: 5, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Listo" : "Done"}</button>
                  </div>
                  {isTwinRiver ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 8 }}>
                      <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Fecha en que reportaron protección" : "Date protection was reported"}</div><input type="date" value={f.clientReportedDate || ""} onChange={function (e) { set("clientReportedDate", e.target.value); }} style={inp} /></div>
                      <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Hora en que reportaron" : "Time protection was reported"}</div><input type="time" value={f.clientReportedTime || ""} onChange={function (e) { set("clientReportedTime", e.target.value); }} style={inp} /></div>
                    </div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
                      <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Fecha en que reportaron protección" : "Date protection was reported"}</div><input type="date" value={f.clientReportedDate || ""} onChange={function (e) { set("clientReportedDate", e.target.value); }} style={inp} /></div>
                      <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Hora en que reportaron" : "Time protection was reported"}</div><input type="time" value={f.clientReportedTime || ""} onChange={function (e) { set("clientReportedTime", e.target.value); }} style={inp} /></div>
                      <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Fecha de llegada a su bodega" : "Arrival date at their warehouse"}</div><input type="date" value={f.clientArrivalDate || ""} onChange={function (e) { set("clientArrivalDate", e.target.value); }} style={inp} /></div>
                      <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{lang === "es" ? "Hora de llegada" : "Arrival time"}</div><input type="time" value={f.clientArrivalTime || ""} onChange={function (e) { set("clientArrivalTime", e.target.value); }} style={inp} /></div>
                    </div>
                  )}
                  {isTwinRiver && lotRegressionPctForTR !== undefined && lotRegressionPctForTR !== null && lotRegressionPctForTR !== "" && (
                    <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 6, background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.25)", fontSize: 11, color: "#B45309", fontWeight: 600 }}>
                      {(lang === "es" ? "Regresión total reportada en esta carga: " : "Total Regression shown in this load was: ") + lotRegressionPctForTR + "%"}
                    </div>
                  )}
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#DC2626", marginBottom: 6 }}>{lang === "es" ? "Defectos encontrados (se incluyen en el aviso)" : "Defects found (included in the notice)"}</div>
                    {isTwinRiver ? (
                      /* 2026-10-19, Arturo: "si quiero reportar algo de
                         twin river al shipper desde la factura, utiliza
                         la logica de four seasons y no copia lo ya
                         previamente capturado en el pallet que populo
                         esa factura" — read-only mirror of the Shipment's
                         own qcDefects per pallet, never the manual
                         Four-Seasons-style picker below (Twin River's
                         defects were already captured once, at the
                         Shipment, when the quality report came in). */
                      (f.palletAllocations || []).map(function (a, idx) { return { a: a, idx: idx }; })
                        .filter(function (x) { return x.a && (x.a.saleStatus === "protection" || x.a.saleStatus === "pas"); })
                        .map(function (x) {
                          var a = x.a, idx = x.idx;
                          var livePalletTR = palletsByNumFromShpForTR[String(a.palletNum)] || {};
                          var qcTR = livePalletTR.qcDefects || {};
                          var qcCatsPresentTR = ["severeCondition", "condition", "quality", "findings"].filter(function (cat) {
                            return Array.isArray(qcTR[cat]) && qcTR[cat].length > 0;
                          });
                          return (
                            <div key={idx} style={{ marginBottom: 14, padding: "8px 10px", borderRadius: 8, border: "1px solid var(--color-border)" }}>
                              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{"Pallet "}{a.palletNum || "—"}</div>
                              {qcCatsPresentTR.length === 0 ? (
                                <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{lang === "es" ? "Sin defectos capturados en el embarque para este pallet." : "No defects captured on the Shipment for this pallet."}</div>
                              ) : (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                  {qcCatsPresentTR.map(function (cat) {
                                    var entries = qcTR[cat];
                                    var c = QC_CAT_COLORS[cat];
                                    var summaryText = entries.map(function (e) {
                                      var lbl = e.key === "__custom__" ? (e.custom || (lang === "es" ? "Otro" : "Other")) : qcTolLabel(e.key, lang);
                                      var pct = (e.pct === 0 || e.pct) ? " " + e.pct + "%" : "";
                                      return lbl + pct;
                                    }).join(", ");
                                    return (
                                      <span key={cat} style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: c.bg, color: c.text, border: "1px solid " + c.border }}>
                                        <b style={{ textTransform: "uppercase", letterSpacing: "0.03em" }}>{qcCatLabel(cat, lang)}</b>: {summaryText}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })
                    ) : (<>
                    {/* 2026-10-23, Arturo: "ellos no me detallan los
                       defectos por pallet, solo general de la carga... o
                       sea no se debe desglosar defects by pallet si no
                       total defects, solo four seasons" — Four Seasons'
                       own QC report (QC Results PO 2236319.pdf) is ONE
                       aggregate test for the whole load/PO, never broken
                       down per pallet, so this is now a single picker for
                       the whole invoice (f.loadDefects), not one box per
                       protected pallet. Same add-one-at-a-time
                       SearchSelect + chip pattern as the per-pallet
                       picker it replaces (and as Twin River's own
                       picker above it, before that one moved to a
                       read-only Shipment mirror) — "podrias darmelos con
                       un dropdown list y que se vayan agregando". Each
                       chip keeps its own optional % input (blank by
                       default, never required). pendingDefectKey is
                       reused here with the special key "__load__" since
                       there's no pallet index to key it by. */}
                    {(function () {
                      var currentKeys = (Array.isArray(f.loadDefects) ? f.loadDefects : []).map(function (e) { return e.key; });
                      var defectOptions = PROTECTION_DEFECTS.filter(function (d) { return currentKeys.indexOf(d.key) < 0; })
                        .map(function (d) { return { value: d.key, label: d.labelEn + " · " + (lang === "es" ? PROTECTION_DEFECT_SEVERITY[d.severity].labelEs : PROTECTION_DEFECT_SEVERITY[d.severity].labelEn) }; });
                      /* 2026-10-27, Arturo: "necesito agregar... la
                         opcion de agregar defectos que no esten
                         listados ahi, y su severidad" — always
                         available (never filtered out), since more
                         than one custom defect can be added. */
                      defectOptions = defectOptions.concat([{ value: "__custom__", label: lang === "es" ? "+ Otro (escribir)" : "+ Other (type it in)" }]);
                      var isPendingCustom = pendingDefectKey.__load__ === "__custom__";
                      var severityOptions = Object.keys(PROTECTION_DEFECT_SEVERITY).map(function (k) {
                        return { value: k, label: lang === "es" ? PROTECTION_DEFECT_SEVERITY[k].labelEs : PROTECTION_DEFECT_SEVERITY[k].labelEn };
                      });
                      return (
                        <div style={{ marginBottom: 14, padding: "8px 10px", borderRadius: 8, border: "1px solid var(--color-border)" }}>
                          {(Array.isArray(f.loadDefects) ? f.loadDefects : []).length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                              {f.loadDefects.map(function (entry) {
                                /* entry.key may not be in the fixed
                                   catalog — a custom defect carries its
                                   own typed name/severity on the entry
                                   itself instead. */
                                var d = PROTECTION_DEFECTS.find(function (dd) { return dd.key === entry.key; });
                                var label = d ? d.labelEn : entry.custom;
                                if (!label) return null;
                                var sevKey = d ? d.severity : (PROTECTION_DEFECT_SEVERITY[entry.severity] ? entry.severity : "medium");
                                var r = PROTECTION_DEFECT_SEVERITY[sevKey];
                                return (
                                  <div key={entry.key} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 8, fontSize: 12, fontWeight: 600, border: "1px solid " + r.border, background: r.bg, color: r.color }}>
                                    <span>{label}</span>
                                    <span style={{ fontSize: 9, fontWeight: 700, opacity: 0.85 }}>· {lang === "es" ? r.labelEs : r.labelEn}</span>
                                    <input
                                      type="number" min="0" max="100" step="1" placeholder="%"
                                      value={entry.pct === undefined || entry.pct === null ? "" : entry.pct}
                                      onChange={function (e) { setLoadDefectPct(entry.key, e.target.value === "" ? undefined : Number(e.target.value)); }}
                                      style={{ width: 44, padding: "2px 4px", borderRadius: 4, border: "1px solid " + r.border, background: "rgba(255,255,255,0.6)", color: r.color, fontSize: 11, fontWeight: 700, textAlign: "center" }}
                                    />
                                    <button type="button" onClick={function () { removeLoadDefect(entry.key); }} style={{ padding: "0 4px", border: "none", background: "transparent", color: r.color, cursor: "pointer", fontSize: 12, fontWeight: 700, lineHeight: 1 }}>✕</button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          <div style={{ display: "flex", gap: 4 }}>
                            <SearchSelect
                              value={pendingDefectKey.__load__ || ""}
                              onChange={function (v) { setPendingDefectKey(function (prev) { return { ...prev, __load__: v }; }); setPendingCustomDefect({ name: "", severity: "medium" }); }}
                              options={defectOptions}
                              placeholder={lang === "es" ? "+ Agregar defecto" : "+ Add defect"}
                              style={{ flex: 1, fontSize: 11 }}
                            />
                            {!isPendingCustom && (
                              <button type="button" onClick={function () {
                                var key = pendingDefectKey.__load__;
                                if (!key) return;
                                addLoadDefect(key);
                                setPendingDefectKey(function (prev) { return { ...prev, __load__: "" }; });
                              }} disabled={!pendingDefectKey.__load__} style={{ padding: "6px 12px", borderRadius: 5, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: pendingDefectKey.__load__ ? "pointer" : "not-allowed", opacity: pendingDefectKey.__load__ ? 1 : 0.5, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>+ {lang === "es" ? "Agregar" : "Add"}</button>
                            )}
                          </div>
                          {isPendingCustom && (
                            <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                              <input
                                type="text"
                                value={pendingCustomDefect.name}
                                onChange={function (e) { setPendingCustomDefect(function (prev) { return { ...prev, name: e.target.value }; }); }}
                                placeholder={lang === "es" ? "Nombre del defecto" : "Defect name"}
                                style={{ ...inp, flex: 1, fontSize: 11 }}
                              />
                              <SearchSelect
                                value={pendingCustomDefect.severity}
                                onChange={function (v) { setPendingCustomDefect(function (prev) { return { ...prev, severity: v }; }); }}
                                options={severityOptions}
                                placeholder={lang === "es" ? "Severidad" : "Severity"}
                                style={{ width: 140, fontSize: 11 }}
                              />
                              <button type="button" onClick={function () {
                                addCustomLoadDefect(pendingCustomDefect.name, pendingCustomDefect.severity);
                                setPendingDefectKey(function (prev) { return { ...prev, __load__: "" }; });
                                setPendingCustomDefect({ name: "", severity: "medium" });
                              }} disabled={!pendingCustomDefect.name.trim()} style={{ padding: "6px 12px", borderRadius: 5, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: pendingCustomDefect.name.trim() ? "pointer" : "not-allowed", opacity: pendingCustomDefect.name.trim() ? 1 : 0.5, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>+ {lang === "es" ? "Agregar" : "Add"}</button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    </>)}
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#DC2626", marginBottom: 6 }}>Client Quality Inspector Remarks (shown on the PDF)</div>
                      <textarea value={f.clientRemarksSummary || ""} onChange={function (e) { set("clientRemarksSummary", e.target.value); }} rows={3} style={{ ...inp, resize: "vertical", fontSize: 12, lineHeight: 1.45 }} />
                    </div>
                  </div>
                </div>
              );
            })()}
                </ClientReportedDataButton>
              );
            })()}
          </div>
          {/* 2026-10-21, Arturo: "invoice activity lo empujaste
              demasiado a la izquierda, debe estar situado justo al
              lado izquierdo de sent" \u2014 Invoice Activity and the
              status pill/transition buttons are now ONE right-side
              flex group (not two separate top-level items), so
              justify-content:space-between on the outer row only ever
              splits it into exactly two clusters (left links, right
              status) instead of spreading three items apart and
              stranding Invoice Activity in the middle. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InvoiceActivityButton hasActivity={hasInvoiceActivityEdit} lang={lang} t={t} style={plateActionBtnStyle}>
            <RejectionDetailsReadOnly invoices={[f]} lot={f.lot} lang={lang} />
            <ProtectionResponsesReadOnly invoices={[f]} lot={f.lot} lang={lang} />
          </InvoiceActivityButton>
          {/* 2026-10-21, Arturo: "ya entend\u00ed, el status se cambia
              manualmente abajo pero debe verse en el header
              principal" \u2014 the read-only status display moved to the
              modal's own title bar (see DetailPanel's titleBarActions
              above); only the transition buttons stay here, where the
              status actually gets changed. */}
          <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
            {f.status === "draft" && <button onClick={function() { set("status", "sent"); }} style={statusPlateBtnStyle("teal", 6)}>{"\u2192"} {t.statusSent}</button>}
            {f.status === "sent" && <>
              <button onClick={function() { set("status", "draft"); }} style={statusPlateBtnStyle("gray", 6)}>{"\u2190"} {t.statusDraft}</button>
              <button onClick={function() { set("status", "paid"); }} style={statusPlateBtnStyle("green", 3)}>{"\u2192"} {t.statusPaid}</button>
            </>}
            {f.status === "paid" && <button onClick={function() { set("status", "sent"); }} style={statusPlateBtnStyle("teal", 6)}>{"\u2190"} {t.statusSent}</button>}
          </div>
          </div>
        </div>

        {/* BODY */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {/* PENDING FEE TRANSFERS from Bills */}
          {(function() {
            void trVer;
            var allTransfers = loadLS("gfi-fee-transfers", []);
            var transfers = allTransfers.filter(function(tr) {
              if (tr.status !== "pending") return false;
              if (tr.targetType !== "invoice") return false;
              if (tr.targetPU !== f.puNumber && tr.targetLot !== f.lot) return false;
              /* Also skip if fee already exists in current form (already accepted in a prior session) */
              var fname = tr.feeType === "__custom__" ? tr.feeCustom : tr.feeType;
              var alreadyIn = (f.fees || []).some(function(ef) { return ef.fromBill && (ef.feeType === "__custom__" ? ef.feeCustom : ef.feeType) === fname; });
              return !alreadyIn;
            });
            if (transfers.length === 0) return null;
            return <div style={{ margin: "10px 20px 0", padding: "10px 14px", borderRadius: 8, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#D97706" }}>{lang === "es" ? "Costos transferidos del Bill (LOT " + f.lot + ")" : "Transferred costs from Bill (LOT " + f.lot + ")"}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={function() {
                    var existingFees = f.fees || [];
                    var newFees = []; var conflicts = [];
                    transfers.forEach(function(tr) {
                      var fname = tr.feeType === "__custom__" ? tr.feeCustom : tr.feeType;
                      var existing = existingFees.find(function(ef) { var efName = ef.feeType === "__custom__" ? ef.feeCustom : ef.feeType; return efName === fname; });
                      if (existing) { conflicts.push({ transfer: tr, existing: existing }); }
                      else { newFees.push({ feeType: tr.feeType, feeCustom: tr.feeCustom || "", qty: tr.qty || 1, rate: tr.rate || "", amount: tr.amount, pctRate: tr.pctRate || "", fromBill: true }); }
                    });
                    if (conflicts.length > 0) {
                      var msg = conflicts.map(function(c) { var fname = c.transfer.feeType === "__custom__" ? c.transfer.feeCustom : c.transfer.feeType; return fname + ": " + (lang === "es" ? "existe con " : "exists at ") + fmtMoney(c.existing.amount) + (lang === "es" ? ", viene " : ", incoming ") + fmtMoney(c.transfer.amount); }).join("\n");
                      var action = prompt((lang === "es" ? "Conflictos encontrados:\n" : "Conflicts found:\n") + msg + "\n\n" + (lang === "es" ? "Escriba: agregar / sustituir / rechazar" : "Type: add / replace / reject"));
                      if (action && action.toLowerCase().startsWith("a")) { conflicts.forEach(function(c) { newFees.push({ feeType: c.transfer.feeType, feeCustom: c.transfer.feeCustom || "", qty: c.transfer.qty || 1, rate: c.transfer.rate || "", amount: c.transfer.amount, pctRate: c.transfer.pctRate || "", fromBill: true }); }); }
                      else if (action && (action.toLowerCase().startsWith("s") || action.toLowerCase().startsWith("r") && action.toLowerCase().includes("epl"))) {
                        setF(function(prev) { var updated = prev.fees.map(function(ef) { var efName = ef.feeType === "__custom__" ? ef.feeCustom : ef.feeType; var match = conflicts.find(function(c) { var fn = c.transfer.feeType === "__custom__" ? c.transfer.feeCustom : c.transfer.feeType; return fn === efName; }); return match ? { ...ef, amount: match.transfer.amount, rate: match.transfer.rate || ef.rate } : ef; }); return { ...prev, fees: updated }; });
                      }
                    }
                    if (newFees.length > 0) setF(function(prev) { return { ...prev, fees: [...(prev.fees || []), ...newFees] }; });
                    try { var allTr = loadLS("gfi-fee-transfers", []); localStorage.setItem("gfi-fee-transfers", JSON.stringify(allTr.map(function(t) { return transfers.some(function(tr) { return tr.id === t.id; }) ? { ...t, status: "accepted" } : t; }))); } catch(e) {}
                    setDirty(true); setTrVer(function(v) { return v + 1; });
                  }} style={{ padding: "4px 12px", borderRadius: 5, border: "none", background: "#059669", color: "#fff", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{lang === "es" ? "Aceptar" : "Accept"}</button>
                  <button onClick={function() { try { var allTr = loadLS("gfi-fee-transfers", []); localStorage.setItem("gfi-fee-transfers", JSON.stringify(allTr.map(function(t) { return transfers.some(function(tr) { return tr.id === t.id; }) ? { ...t, status: "rejected" } : t; }))); } catch(e) {} setTrVer(function(v) { return v + 1; }); }} style={{ padding: "4px 12px", borderRadius: 5, border: "1px solid rgba(239,68,68,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{lang === "es" ? "Rechazar" : "Reject"}</button>
                </div>
              </div>
              <div style={{ fontSize: 11 }}>{transfers.map(function(tr, i) { var fname = tr.feeType === "__custom__" ? tr.feeCustom : tr.feeType; return <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", color: "#92400E" }}><span>{fname}</span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{fmtMoney(tr.amount)}</span></div>; })}</div>
            </div>;
          })()}
          {locked && (
            <div style={{ margin: "10px 20px 0", padding: "8px 12px", borderRadius: 6, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.25)", fontSize: 11, color: "#92400E", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14 }}>🔒</span>
              <span>{lang === "es"
                ? "Esta factura tiene pagos registrados. Solo el cliente es editable; los demás campos están bloqueados."
                : "This invoice has recorded payments. Only the client field is editable; all other fields are locked."}</span>
            </div>
          )}

          {/* Client-only lock — every field after this point sits inside
              this fieldset. Native <fieldset disabled> cascades to every
              descendant form control, so no per-control gating needed
              below. Borderless styling so the fieldset is invisible.
              2026-10-21 — moved here (used to open right before the old
              ROW 2 position) since ROW1/ROW2/ROW2.5 relocated bodily
              into the Invoice Info plate above, each now carrying its
              own explicit disabled={locked} instead of relying on this
              cascade. Everything from here down (Client-reported data,
              Defects, the two-column Fees/Products/etc layout) still
              needs the cascade exactly as before. */}
          <fieldset disabled={locked} style={{ border: "none", padding: 0, margin: 0, minWidth: 0, opacity: locked ? 0.65 : 1 }}>

          {/* "Client Responses — Protection" moved to DetailPanel's view
              mode (2026-10-07, Arturo: "agrega el boton de enviar al
              shipper en cada respuesta justo como los rechazos") — same
              reasoning as Client Rejections: a per-entry "send to
              shipper" action needs a persisted record to send about, so
              editing+sending both live in the view, saved straight
              through onSave, not staged in this form's draft state. */}

          {/* 2026-10-20, Arturo: "esta misma estructura en modo no
              edicion (layout) ahora replicalo en modo edicion" —
              same fixed two-column layout as DetailPanel's view mode
              (left = Fees & Deductions/Credits/Transfers/Invoice
              Summary, right = Products/Pallet Allocations/Payments),
              matching BillForm's own identical edit-mode split.
              2026-10-21, Arturo: "eliminaste el margen izquierdo de la
              ventana entera... las tarjetas de la derecha tampoco
              tienen el gap del extremo derecho" — this BODY wrapper
              (unlike view mode's, which has padding:"18px 24px") has
              no padding of its own; every row above (ROW1/ROW2/etc)
              always carried its own "0 20px" margin individually, and
              that's exactly what every section here lost once it
              became a SectionBox card (only 14px of INTERNAL padding,
              nothing pushing the card itself away from the window
              edges) — restored with the same "0 20px" on this
              container, same convention as the rows above it.
              2026-10-21, Arturo: "tambien quitaste el gap superior" —
              the old ROW 1 (now moved inside the Invoice Info plate)
              used "12px 20px 0", i.e. it ALSO supplied the top gap
              between this area and the locked-banner/links strip
              above it — restoring that same 12px top here too, not
              just the left/right 20px. */}
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "12px 20px 0" }}>
          <div style={{ flex: "0 1 38%", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          {/* 2026-10-21, Arturo: "aqui la placa es editable" / "la
              placa la hicistes repitiendo los datos de arriba, no es
              repeticion, esos datos de arriba editables son los que
              deben estar en la placa" — this card now CONTAINS the
              real editable Invoice Info fields (moved here bodily
              from the top of the form, not duplicated) — PU#/Client/
              Date/Due Date, Client Ref (PO picker), Shipper, Pickup/
              Temp Recorder (or Twin River's read-only Arrival Date/
              Time). disabled={locked} added explicitly to each
              control that used to rely on the <fieldset disabled>
              cascade, since they no longer sit inside it here. */}
          <div>
            <div style={readOnlyPlateBodyStyle}>
              <div style={{ ...sectionHeaderStyle, background: "none", border: "none", boxShadow: "none" }}>{lang === "es" ? "Información de la Factura" : "Invoice Info"}</div>
              <div style={{ padding: "0 14px 14px" }}>
          {/* ROW 1 — 2026-10-21, Arturo: "los campos de la primer linea
              tampoco son del mismo tamaño de alto, todos los campos en
              esa tarjeta deben ser del mismo alto y estar alineados no
              deben verse uno mas arriba de otro" / "los headers deben
              ser color negro" — same two fixes as ROW 2.5 below,
              applied card-wide: every control gets the same explicit
              height:32 (a plain text input, a native date input, and
              the SearchSelect trigger each have a different natural
              height in Chrome — SearchSelect's own `style.minHeight`
              prop switches it to border-box + a real fixed height,
              see SearchSelect.js), and every field label switches from
              the muted gray to black. */}
          {/* 2026-10-21, Arturo: pasted a screenshot of the Invoice Info
              plate ("y la parte de arriba debe verse asi pero en modo
              captura, el lot no se captura solo debe aparecer") — PU#/
              Date/Due Date on one row, Client/Customer Ref (PO)/Lot on
              the next, same 3-column grouping as the reference; Lot is
              never an editable field anywhere in this app (it's always
              derived from the linked shipment/lines), so it's a plain
              read-only value here, not an input. */}
          <div style={{ padding: "12px 20px 0", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <div><div style={{ fontSize: 10, color: "#000", marginBottom: 2 }}>{t.puNum}*</div><input value={f.puNumber} onChange={function(e) { set("puNumber", e.target.value); }} disabled={locked} style={{ ...inp, height: 32, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, ...(errors.puNumber ? errS : {}) }} /></div>
            <div><div style={{ fontSize: 10, color: "#000", marginBottom: 2 }}>{t.date}</div><input type="date" value={f.date || ""} onChange={function(e) { set("date", e.target.value); }} disabled={locked} style={{ ...inp, height: 32 }} /></div>
            <div>
              <div style={{ fontSize: 10, color: "#000", marginBottom: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>{t.dueDate}</span>
                <button type="button" disabled={locked} onClick={function() {
                  var cli = loadLS("gfi-clients", []).find(function(c) { return idMatches(c, f.clientId); });
                  var days = parseTermDays(cli && cli.paymentTerm);
                  if (days === null) days = 21;
                  set("dueDate", addDaysToISO(f.date, days));
                }} style={{ fontSize: 9, padding: "1px 6px", border: "1px solid var(--color-border)", borderRadius: 4, background: "transparent", color: locked ? "var(--color-text-muted)" : "#c65d00", cursor: locked ? "not-allowed" : "pointer", fontWeight: 600 }} title="Calculate from Invoice Date + Client Term">↻ Default</button>
              </div>
              <input type="date" value={f.dueDate || ""} onChange={function(e) { set("dueDate", e.target.value); }} disabled={locked} style={{ ...inp, height: 32 }} />
            </div>
          </div>

          {/* ROW 2 — Client / Customer Ref (PO) / Lot. */}
          <div style={{ padding: "6px 20px 12px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, borderBottom: "1px solid var(--color-border)" }}>
            <div><div style={{ fontSize: 10, color: "#000", marginBottom: 2 }}>{t.client}*</div><SearchSelect value={f.clientId || ""} onChange={handleClientChange} placeholder={t.selectClient} error={!!errors.clientId} options={clients.map(function(c) { return { value: c.id, label: c.shortName || c.fullName || c.name || c.id }; })} style={{ minHeight: 32 }} /></div>
            <div>
              <div style={{ fontSize: 10, color: "#000", marginBottom: 2 }}>{t.clientRef}</div>
              {(function () {
                /* PO picker — pulls from Fixed + Consignment Pricing
                   filtered by client + unlinked, sorted by date
                   proximity to the invoice date. Picking a real PO
                   stamps both linkedPoLineId (for back-link on save)
                   and clientRef (the PO# itself).
                   2026-10-21, Arturo: "al abrir el drop debe mostrarme
                   como se ve, pero al seleccionar solo debe mostrar el
                   numero de PO... no necesita validar en un segundo
                   renglon, la validacion del po que la haga ahi mismo
                   si es captura manual pues no la valida pero sin
                   necesidad de agregar un segundo renglon" — ONE
                   SearchSelect now does everything: triggerLabel shows
                   just the PO# once a real PO is picked (dropdown rows
                   still show the full "PO# · date · product · cases ·
                   type" detail), and allowCustom lets free-typed text
                   stand in directly as clientRef when there's no
                   matching PO to pick (same as the old "manual" input,
                   just not a second field/row anymore — unvalidated
                   either way, exactly as before). */
                var currentLink = f.linkedPoLineId || "";
                var linkExists = currentLink && availablePOs.some(function (p) { return p._src + ":" + p.id === currentLink; });
                var poOpts = availablePOs.map(function (p) {
                  var compositeId = p._src + ":" + p.id;
                  var ddate = p.deliveryDate || p.date || "—";
                  var prod  = p.productDescription || p.productBase || p.productName || "";
                  var cs = parseInt(p._src === "F" ? (p.casesReceived || p.casesSent) : p.cases, 10) || 0;
                  /* 2026-11-02, Arturo (PO 72296, Twin River, real
                     term="semifixed"): "porque estoy generando un
                     invoice y asignandole el PO de twin river 72296
                     que es semi fixed pero en el drop de seleccion de
                     po's lo muestra fixed?" — Fixed AND Semi-fixed
                     both live in the SAME fixed_pos table (_src==="F",
                     see FixedPricingRegistry.js's "Fase 1" merge), so
                     this label only ever checked which TABLE the row
                     came from, never the row's own `term` field —
                     every semifixed PO showed "Fixed" here. */
                  var typeLabel = p._src !== "F" ? t.poTypeConsignment : (p.term === "semifixed" ? t.poTypeSemifixed : t.poTypeFixed);
                  return {
                    value: compositeId,
                    label: (p.poNumber || "?") + " · " + ddate + " · " + prod + " · " + cs + "c · " + typeLabel,
                    triggerLabel: p.poNumber || "?",
                    search: (p.poNumber || "") + " " + (p.puNumber || "") + " " + prod,
                  };
                });
                var pickerValue = linkExists ? currentLink : (f.clientRef || "");
                var onPickerChange = function (v) {
                  var po = availablePOs.find(function (p) { return p._src + ":" + p.id === v; });
                  if (po) {
                    setF(function (prev) { return { ...prev, linkedPoLineId: v, clientRef: po.poNumber || "" }; });
                  } else {
                    /* Free-typed text (allowCustom) or cleared — no
                       longer a real linked PO. */
                    setF(function (prev) { return { ...prev, linkedPoLineId: "", clientRef: v || "" }; });
                  }
                  setDirty(true);
                };
                /* Reference-price banner — shows the PO's source
                   prices so the user can eyeball them while keying
                   the invoice. Hides once any line carries a price
                   (user has internalized the reference). Distinct
                   format per source: Fixed shows the GF price; TR
                   Consignment shows the raw range BEFORE TR's
                   commission deduction (per spec — user does the
                   commission math themselves). */
                var banner = null;
                if (linkedPo && !hasPricedLines) {
                  if (linkedPo._src === "F") {
                    var gf = parseFloat(linkedPo.priceGF) || 0;
                    banner = (
                      <div style={{ marginTop: 6, padding: "8px 12px", background: "#FFF3E0", border: "1px solid #FF9800", borderRadius: 6, fontSize: 12, color: "#7c4a03", display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 14 }}>📋</span>
                        <span>
                          <strong>PO {linkedPo.poNumber || "?"}</strong>
                          {" — "}{lang === "es" ? "Precio GF" : "GF Price"}{": "}
                          <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>{gf > 0 ? fmtMoney(gf) : "—"}</strong>
                        </span>
                      </div>
                    );
                  } else if (linkedPo._src === "C") {
                    var minTR = parseFloat(linkedPo.minTR) || 0;
                    var maxTR = parseFloat(linkedPo.maxTR) || 0;
                    banner = (
                      <div style={{ marginTop: 6, padding: "8px 12px", background: "#FFF3E0", border: "1px solid #FF9800", borderRadius: 6, fontSize: 12, color: "#7c4a03", display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 14 }}>📋</span>
                        <span>
                          <strong>PO {linkedPo.poNumber || "?"}</strong>
                          {" — "}{lang === "es" ? "Rango TR" : "TR Range"}{": "}
                          <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>{minTR > 0 ? fmtMoney(minTR) : "—"}</strong>
                          {" - "}
                          <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>{maxTR > 0 ? fmtMoney(maxTR) : "—"}</strong>
                        </span>
                      </div>
                    );
                  }
                }
                return (
                  <>
                    <SearchSelect
                      value={pickerValue}
                      onChange={onPickerChange}
                      options={poOpts}
                      placeholder={availablePOs.length === 0 ? t.noAvailablePOs : t.selectPO}
                      disabled={!f.clientId || locked}
                      allowCustom
                      nowrap
                      panelMinWidth={420}
                      style={{ minHeight: 32 }}
                    />
                    {banner}
                  </>
                );
              })()}
            </div>
            {/* "el lot no se captura solo debe aparecer" — Lot is always
                derived (from the linked shipment/lines), never a
                manual field anywhere in this app; plain read-only text,
                no input. 2026-11-03, PU 17484: same "only shows the
                home lot" gap as Invoice Info's view-mode Lot field —
                lists every distinct lot across f.lines now. */}
            <div><div style={{ fontSize: 10, color: "#000", marginBottom: 2 }}>{t.lot}</div><div style={{ ...inp, height: 32, boxSizing: "border-box", display: "flex", alignItems: "center", background: "var(--color-bg)", color: "var(--color-text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>{uniqueInvoiceLots(f).join(", ") || "—"}</div></div>
          </div>

          {/* 2026-10-21, Arturo: "a la placa le faltó agregar loading
              info como en modo no editable" — same divider + section
              label view mode's plate already has right before its own
              Shipper/Pickup/Temp Recorder block; edit mode never got
              one when ROW1/ROW2/ROW2.5 were moved into this plate.
              "agregale dos botones, add y edit para que se capture esa
              info a traves de una ventana" — Add shows when nothing's
              captured yet, Edit once it has, same convention as every
              other capture-window trigger this session (never both at
              once — there's only one Loading Info "entry" to add or
              edit, not a list). Twin River's own branch below is
              already live-synced read-only (no manual capture exists
              for it), so the button is skipped there entirely. */}
          {(function () {
            var isTwinRiverLI = (resolveClientName(f.clientId) || f.clientName || "").toLowerCase().indexOf("twin river") >= 0;
            var hasLoadingInfo = !!(f.pickupDate || f.pickupTime || f.thermograph);
            return (
              <div style={{ padding: "8px 20px 0" }}>
                {/* 2026-10-21, Arturo: "veo dos lineas de division entre
                    customer ref y loading info, solo debe existir una"
                    — ROW 2 right above already closes with its own
                    borderBottom; this section's own borderTop divider
                    was a second, redundant line immediately after it. */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#000" }}>{lang === "es" ? "Información de Carga" : "Loading Info"}</div>
                  {!isTwinRiverLI && (
                    <button type="button" disabled={locked} onClick={function () { setLoadingInfoModalOpen(true); }} style={{ padding: 0, border: "none", background: "transparent", color: locked ? "var(--color-text-muted)" : "var(--color-accent)", cursor: locked ? "not-allowed" : "pointer", fontSize: 10, fontWeight: 600, textDecoration: "underline" }}>
                      {hasLoadingInfo ? (lang === "es" ? "Editar" : "Edit") : "+ " + (lang === "es" ? "Agregar" : "Add")}
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ROW 2.5 — Pickup Date/Time + Temp Recorder (2026-09-12, Arturo):
              pickup date/time recorded by hand from the driver's BOL once
              the client's truck actually picks up the load — separate
              from the Loading Date used for the Pick Ticket print (that
              one's a plan chosen before pickup; this one's the real event
              after it happened). Feeds the ETA estimate on the Active
              Shipments dashboard card (see etaCalc.js).
              Temp Recorder / Logger # moved here as the SOURCE OF TRUTH
              (Arturo: "es conveniente agregar el temp recorder directo en
              la invoice de ground fresh y de ahi que se propague a SL y a
              fixed pricing o consignment pricing" — was previously only
              ever entered on the Pricing PO's loggerNumber field, which
              the Packing List print had to look up via a fragile PO match
              that silently failed whenever that lookup missed; now it's
              captured once here and pushed outward — see syncPoLinkage
              (-> PO's loggerNumber) and the GFI->SL push in handleSave
              (-> SL invoice's thermograph) below. "Temp Recorder" and
              "Logger" are the same physical device, per Arturo.
              2026-10-02 — Arturo: "las facturas de twin river que se
              entregan en su bodega no necesitan que ingrese Pickup Date
              (BOL) ni Pickup Time (BOL) ni Temp Recorder # (Logger)" —
              this whole row only makes sense when the CLIENT's own truck
              physically picks the load up (per the comment above), which
              never happens for Twin River: GFI's driver delivers TO their
              warehouse instead, there's no pickup BOL on their end to
              transcribe, and no logger device to read either. Arturo then
              clarified the replacement — "deberia decir solamente arrival
              time" then "and date" — so Twin River gets its own 2-field
              version (Arrival Date / Arrival Time, no Temp Recorder),
              reusing the SAME pickupDate/pickupTime fields under a
              different label rather than adding new ones (same physical
              slot: "the one real logistics event's date/time", whether
              that event is the client picking up or GFI delivering).
              Twin River detection: same inline "twin river" name check
              DetailPanel's email dialog already uses (that one lives in
              a sibling closure, not in scope here, so recomputed from
              the live form state `f`). */}
          {(function () {
            var isTwinRiver = (resolveClientName(f.clientId) || f.clientName || "").toLowerCase().indexOf("twin river") >= 0;
            if (isTwinRiver) {
              /* 2026-10-10, Arturo: "necesito se replique en vivo...
                 esto solo aplica a twin river ya que ellos se
                 encuentran en texas y la llegada del embarque es la
                 misma que la llegada al cliente" — read-only here, kept
                 in sync live from the shipment's own Received date/time
                 by the useEffect above; no reason to let Arturo
                 re-type the same event a second time. */
              var roBox = { ...inp, background: "var(--color-bg)", color: "var(--color-text-secondary)", cursor: "default" };
              return (
                <div style={{ padding: "0 20px 12px", display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, borderBottom: "1px solid var(--color-border)" }}>
                  <div><div style={{ fontSize: 10, color: "#000", marginBottom: 2 }}>{t.arrivalDateLabel}</div><div style={{ ...roBox, height: 32, boxSizing: "border-box" }}>{f.pickupDate || "—"}</div></div>
                  <div><div style={{ fontSize: 10, color: "#000", marginBottom: 2 }}>{t.arrivalTimeLabel}</div><div style={{ ...roBox, height: 32, boxSizing: "border-box" }}>{f.pickupTime || "—"}</div></div>
                  <InfoHintButton lang={lang} text={lang === "es" ? "Se toma en vivo de la fecha/hora de recepción capturada en el Embarque." : "Taken live from the Received date/time captured on the Shipment."} />
                </div>
              );
            }
            /* 2026-10-21, Arturo: "asi debe verse loading info en modo
               edit" / "misma informacion misma ubicacion de los campos
               mismo formato" — rebuilt to be byte-for-byte the SAME
               grid view mode's plate uses: Shipper/Pickup Date/Pickup
               Time on the first row, Temp Recorder (+ SweetLeaf
               Invoice, Four Seasons only) wrapping onto the next row
               of the same 3-column grid — same array-map + conditional
               append, not two separate blocks like before. No longer
               inline inputs either way ("agregale dos botones, add y
               edit... popule en modo plano sin campos editables") —
               LoadingInfoEditDialog (triggered from the header above)
               does the capture, this only displays. */
            /* 2026-10-21, Arturo: "emula el tamaño de los fonts del
               modo no editable a la placa invoice info en modo
               editable" — this grid was missing the explicit
               fontSize:12 view mode's own Loading Info grid sets;
               without it, readOnlyPlateValueStyle (no fontSize of its
               own) fell back to an inherited ~15px instead of matching
               view mode's 12px exactly. */
            return (
              <div style={{ padding: "0 20px 12px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 18px", fontSize: 12, borderBottom: "1px solid var(--color-border)" }}>
                <div>
                  <span style={readOnlyPlateLabelStyle}>{t.shipper}</span>
                  {linkedShipmentForInfoEdit && linkedShipmentForInfoEdit.supplierId ? (
                    <div onClick={openLinkedSupplierFromInfoEdit} title={lang === "es" ? "Abrir proveedor" : "Open supplier"} style={{ ...readOnlyPlateValueStyle, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}>{f.shipperName || "—"}</div>
                  ) : (
                    <div style={readOnlyPlateValueStyle}>{f.shipperName || "—"}</div>
                  )}
                </div>
                {[[t.pickupDate, fmtDate(f.pickupDate)], [t.pickupTime, f.pickupTime], [t.thermograph, f.thermograph, true]].map(function (arr, i) {
                  return <div key={i}><span style={readOnlyPlateLabelStyle}>{arr[0]}</span><div style={{ ...readOnlyPlateValueStyle, fontFamily: arr[2] ? "'JetBrains Mono', monospace" : "inherit" }}>{arr[1] || "—"}</div></div>;
                })}
                {linkedSlInvoiceForInfoEdit && (
                  <div>
                    <span style={readOnlyPlateLabelStyle}>{lang === "es" ? "Factura SweetLeaf" : "SweetLeaf Invoice"}</span>
                    <div onClick={openLinkedSlInvoiceFromInfoEdit} title={lang === "es" ? "Abrir en SweetLeaf" : "Open in SweetLeaf"} style={{ ...readOnlyPlateValueStyle, fontFamily: "'JetBrains Mono', monospace", color: "#2A7A6F", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}>
                      {linkedSlInvoiceForInfoEdit.invoiceNumber || linkedSlInvoiceForInfoEdit.id || "—"}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
              </div>
            </div>
          </div>
          {loadingInfoModalOpen && (
            <LoadingInfoEditDialog
              pickupDate={f.pickupDate}
              pickupTime={f.pickupTime}
              thermograph={f.thermograph}
              lang={lang}
              t={t}
              onSave={saveLoadingInfo}
              onClose={function () { setLoadingInfoModalOpen(false); }}
            />
          )}
          {/* FEES — 2026-10-20, Arturo: "remueve negritas... reduce fonts" —
              same centered/lighter header treatment and plate title as
              the view-mode Fees card, applied here too for Edit<->View
              parity. Interactive cell content (SearchSelect/inputs/drag
              handles) left untouched — only the chrome changed. */}
          <div>
            <SectionBox id="fees" title={t.fees} open={{ fees: feesOpen }} toggle={function () { setFeesOpen(!feesOpen); }}>
            {/* 2026-10-21, Arturo: pasted a screenshot of
                BillRegistry.js's own current Fees & Deductions card
                ("aplica este mismo tema") — this replaces the
                table-with-thead/grid-aligned version from the
                previous 2 rounds with an exact match of Bill's real,
                current markup: Advance Financing Fee as a plain flex
                row (not a column-grid), a borderless "OPERATING FEES"
                section label in accent teal, and fee rows with no
                column headers at all — name+detail stacked in one
                cell, Qty×Rate right-aligned in its own narrow column,
                amount right-aligned, edit/remove icons at the end.
                The total line also matches Bill's own: red,
                parenthesized, no "+" sign — these ARE a deduction
                from Gross Sales per Invoice Summary ("- Fees &
                Deductions"), same as they deduct from what Bill owes
                the shipper, so "+154.80"/"Total: +" from the last
                round was backwards. No Origin/→Bill column (Bill's
                has one because expense-bill transfers always know
                which specific bill they came from; an invoice fee
                transfer only ever carries a boolean fromBill flag, no
                bill id) — folded into the same small gray detail line
                under the name that Bill uses for its PT/→Invoice
                tags, instead of a dedicated column no other row here
                would ever need. */}
            {/* 2026-10-21, Arturo: "ese 154.80 es informativo y debe
                estar junto al signo de %, al final un campo para
                ingresar esa cantidad, por default la copia pero la
                puedo editar porque manualmente puedo cobrar mas o
                menos de lo que el 2% marca" — the %-computed amount
                is just a reference next to "%", never itself editable;
                the REAL charged amount is the separate field at the
                end (updateFinancingFeeAmount), which updateFinancingFee
                still defaults/overwrites to match the % every time the
                % itself changes, but can be hand-overridden afterward. */}
            {(function () {
              var financingFee = (f.fees || []).find(function (fe) { return fe.feeType === "Advance Financing Fee"; });
              var refAmount = financingFee ? (totalAmount * ((parseFloat(financingFee.pctRate) || 0) / 100)) : 0;
              return (
                <div style={{ padding: "8px 0", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>Advance Financing Fee:</span>
                  <input type="number" min="0" max="100" step="0.1" value={financingFee ? (financingFee.pctRate || "") : ""} onChange={function (e) { updateFinancingFee(e.target.value); }} style={{ ...inp, width: 60, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} placeholder="%" />
                  <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>% = {fmtMoney(refAmount)}</span>
                  <span style={{ fontSize: 10, color: "var(--color-text-secondary)", marginLeft: "auto" }}>{lang === "es" ? "Monto a cobrar:" : "Amount to charge:"}</span>
                  <input type="number" min="0" step="0.01" value={financingFee ? financingFee.amount : ""} onChange={function (e) { updateFinancingFeeAmount(e.target.value); }} disabled={!financingFee} style={{ ...inp, width: 90, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600 }} />
                </div>
              );
            })()}
            <div style={{ borderTop: "1px solid var(--color-border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "10px 0 8px", paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)" }}>{lang === "es" ? "Gastos Operativos" : "Operating Fees"}</div>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <tbody>
                  {(f.fees || []).map(function(fee, idx) { return { fee: fee, idx: idx }; }).filter(function(x) { return x.fee.feeType !== "Advance Financing Fee"; }).map(function(x) {
                    var fee = x.fee, idx = x.idx;
                    var rp = dndFees.rowProps(idx);
                    var name = fee.feeType === "__custom__" ? (fee.feeCustom || t.customFee) : fee.feeType;
                    var qtyRateLabel = parseInt(fee.qty, 10) > 1 ? fee.qty + " × " + fmtMoney(fee.rate) : "";
                    var detailBits = [];
                    if (fee.fromBill) detailBits.push(lang === "es" ? "Del Bill" : "From Bill");
                    if (fee.transferToBill) {
                      var targetBill = availableBills.find(function (b) { return String(b.id) === String(fee.transferTargetBillId); });
                      detailBits.push((lang === "es" ? "Bill" : "Bill") + (targetBill ? " LOT " + (targetBill.lot || "?") : ""));
                    }
                    return <tr key={idx} {...rp} style={{ borderBottom: "1px solid var(--color-border)", ...(rp.style || {}) }}>
                      <td style={{ padding: "6px 0", textAlign: "center", width: 20 }}><DragHandle handleProps={dndFees.handleProps()} /></td>
                      <td style={{ padding: "6px 4px" }}>
                        <div style={{ fontSize: 11, color: name ? "var(--color-text)" : "var(--color-text-muted)", fontStyle: name ? "normal" : "italic" }}>{name || t.selectFee}</div>
                        {detailBits.length > 0 && <div style={{ fontSize: 9, color: "#B45309", marginTop: 1 }}>{detailBits.join(" · ")}</div>}
                      </td>
                      <td style={{ padding: "6px 4px", textAlign: "right", fontSize: 9, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap", width: 60 }}>{qtyRateLabel}</td>
                      <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, whiteSpace: "nowrap" }}>{fmtMoney(fee.amount)}</td>
                      <td style={{ padding: "6px 4px", textAlign: "center", whiteSpace: "nowrap", width: 50 }}>
                        <button onClick={function() { setEditingFeeIdx(idx); }} title={t.editBtn} style={{ background: "none", border: "none", color: "var(--color-accent)", cursor: "pointer", fontSize: 12, padding: "2px 4px" }}>{"✎"}</button>
                        <button onClick={function() { removeFee(idx); }} title={t.removeFee} style={{ background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 12, padding: "2px 4px" }}>{"✕"}</button>
                      </td>
                    </tr>;
                  })}
                </tbody>
                <tfoot><tr><td colSpan={2} style={{ padding: "8px 4px" }}><button onClick={function() { setEditingFeeIdx("new"); }} style={{ padding: "4px 12px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>+ {t.addFee}</button></td><td></td><td style={{ padding: "8px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#DC2626" }}>{totalFees > 0 ? "(" + fmtMoney(totalFees) + ")" : ""}</td><td></td></tr></tfoot>
              </table>
            </div>
            {editingFeeIdx !== null && (
              <InvoiceFeeEditDialog
                fee={editingFeeIdx === "new" ? null : f.fees[editingFeeIdx]}
                feeTypes={feeTypes}
                availableBills={availableBills}
                lot={f.lot}
                lang={lang}
                t={t}
                onSave={saveFeeDialog}
                onClose={function() { setEditingFeeIdx(null); }}
              />
            )}

            {/* CREDITS — reduce total. 2026-10-21, Arturo: "tambien
                para agregar credit o transfer debe ser mediante
                ventanas emergentes donde se captura la info y popula
                el renglon correspondiente" — locked list (edit via
                pencil, InvoiceCreditEditDialog), "+ Add Credit" opens
                the same dialog instead of an inline editable row.
                Nested sub-section inside the same Fees & Deductions
                card, not its own card. */}
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#2a3542", margin: "14px 0 6px" }}>{t.credits}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
              <tbody>
                {(f.credits || []).map(function(cr, idx) {
                  var rp = dndCredits.rowProps(idx);
                  var name = cr.type === "fromInvoice" ? ("PU# " + (cr.refPuNumber || "?")) : (cr.concept || t.creditConcept);
                  return <tr key={idx} {...rp} style={{ borderBottom: "1px solid var(--color-border)", ...(rp.style || {}) }}>
                    <td style={{ padding: "6px 0", textAlign: "center", width: 20 }}><DragHandle handleProps={dndCredits.handleProps()} /></td>
                    <td style={{ padding: "6px 4px" }}>
                      <div style={{ fontSize: 11, color: "var(--color-text)" }}>{name}</div>
                    </td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, whiteSpace: "nowrap" }}>{fmtMoney(cr.amount)}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", whiteSpace: "nowrap", width: 50 }}>
                      <button onClick={function() { setEditingCreditIdx(idx); }} title={t.editBtn} style={{ background: "none", border: "none", color: "var(--color-accent)", cursor: "pointer", fontSize: 12, padding: "2px 4px" }}>{"✎"}</button>
                      <button onClick={function() { removeCredit(idx); }} title={t.removeFee} style={{ background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 12, padding: "2px 4px" }}>{"✕"}</button>
                    </td>
                  </tr>;
                })}
              </tbody>
              <tfoot><tr>
                <td colSpan={2} style={{ padding: "8px 4px" }}><button onClick={function() { setEditingCreditIdx("new"); }} style={{ padding: "4px 12px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>+ {t.addCredit}</button></td>
                <td style={{ padding: "8px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#DC2626" }}>{totalCredits > 0 ? "(" + fmtMoney(totalCredits) + ")" : ""}</td>
                <td></td>
              </tr></tfoot>
            </table>
            {editingCreditIdx !== null && (
              <InvoiceCreditEditDialog
                credit={editingCreditIdx === "new" ? null : f.credits[editingCreditIdx]}
                clientInvoices={clientInvoices}
                lang={lang}
                t={t}
                onSave={saveCreditDialog}
                onClose={function() { setEditingCreditIdx(null); }}
              />
            )}

            {/* BALANCE TRANSFERS — bidirectional cross-invoice linkage,
                filtered by SAME CLIENT. Each entry is mirrored on the
                other invoice (atomic write to gfi-invoices on
                create/remove). type "credit" adds; type "debit"
                subtracts. Nested sub-section, same card as above. */}
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#2a3542", margin: "14px 0 6px" }}>{t.balanceTransfers}</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
              <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                <th style={{ width: "3%" }}></th>
                <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "20%" }}>{t.btType}</th>
                <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "57%" }}>{t.btOtherInvoice}</th>
                <th style={{ padding: "5px 3px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "16%" }}>{t.btAmount}</th>
                <th style={{ width: "4%" }}></th>
              </tr></thead>
              <tbody>
                {(f.balanceTransfers || []).map(function(tr, idx) {
                  var rp = dndTransfers.rowProps(idx);
                  var isCredit = tr.type === "credit";
                  var tone = isCredit ? "#059669" : "#DC2626";
                  return <tr key={tr.id || idx} {...rp} style={{ borderBottom: "1px solid var(--color-border)", ...(rp.style || {}) }}>
                    <td style={{ padding: "5px 0", textAlign: "center" }}><DragHandle handleProps={dndTransfers.handleProps()} /></td>
                    <td style={{ padding: "5px 3px", fontSize: 11, fontWeight: 700, color: tone }}>{isCredit ? t.btCredit : t.btDebit}</td>
                    <td style={{ padding: "5px 3px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>PU# {tr.otherPuNumber || "?"} · <span style={{ color: "var(--color-text-muted)" }}>{tr.otherClientName || ""}</span> {tr.date && <span style={{ color: "var(--color-text-muted)", fontSize: 10 }}> · {tr.date}</span>}</td>
                    <td style={{ padding: "5px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: tone }}>{isCredit ? "+" : "−"}{fmtMoney(tr.amount)}</td>
                    <td style={{ padding: "5px 1px", textAlign: "center" }}><button onClick={function() { if (window.confirm(lang === "es" ? "¿Eliminar transferencia? Esto también la quita de la otra factura." : "Remove transfer? This also removes it from the other invoice.")) removeBalanceTransfer(idx); }} style={{ background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 12 }}>{"✕"}</button></td>
                  </tr>;
                })}
              </tbody>
              <tfoot><tr>
                <td colSpan={5} style={{ padding: "8px 4px" }}>
                  <button type="button" onClick={function () { setTransferDialogOpen(true); }} style={{ padding: "5px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                    + {t.btCreate}
                  </button>
                  {transferDialogOpen && (
                    <InvoiceTransferCreateDialog
                      sameClientInvoices={clientInvoices}
                      suggestedAmount={Math.max(0, -due).toFixed(2)}
                      lang={lang}
                      t={t}
                      onCreate={createBalanceTransfer}
                      onClose={function () { setTransferDialogOpen(false); }}
                    />
                  )}
                </td>
              </tr></tfoot>
            </table>
            </SectionBox>
          </div>

          {/* SUMMARY — Total Due = Gross − Commission − Fees + Credits ± Transfers. */}
          {/* 2026-10-20, Arturo: "los formatos de payments e invoice
              summary copialos exactamente igual de bill" — same
              fontSize 11/color(var(--color-text)) wrapper, same
              neutral border/accent palette, same Subtotal → − Paid →
              Amount Due breakdown as Bill Summary (not a single jump
              to the final total), matching the view-mode fix above. */}
          <div>
            <SectionBox id="summary" title={lang === "es" ? "Resumen de la Factura" : "Invoice Summary"} open={{ summary: summaryOpen }} toggle={function () { setSummaryOpen(!summaryOpen); }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{ width: 320, fontSize: 11, color: "var(--color-text)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>{t.grossSales}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(totalAmount)}</span></div>
                  {commissionAmount > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#DC2626" }}><span>- {lang === "es" ? "Comisión (líneas)" : "Commission (lines)"}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(commissionAmount)})</span></div>}
                  <FeeBreakdownLinesInv fees={f.fees} feeCategoryMap={feeCategoryMap} t={t} />
                  {totalCredits > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>+ {t.credits}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(totalCredits)}</span></div>}
                  {totalTransfers !== 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: totalTransfers >= 0 ? "var(--color-text)" : "#DC2626" }}><span>{totalTransfers >= 0 ? "+" : "-"} {t.balanceTransfers}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{totalTransfers >= 0 ? fmtMoney(totalTransfers) : "(" + fmtMoney(-totalTransfers) + ")"}</span></div>}
                  <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between" }}><span>{t.subtotal}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(due)}</span></div>
                  {(function () { var paidSum = (f.payments || []).reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0); return paidSum > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, color: "#DC2626" }}><span>{t.lessPaid}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>({fmtMoney(paidSum)})</span></div>; })()}
                  <div style={{ borderTop: "2px solid var(--color-accent)", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between" }}><span>{t.totalDue}</span><span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(due - (f.payments || []).reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0))}</span></div>
                </div>
              </div>
            </SectionBox>
          </div>
          </div>
          <div style={{ flex: "1 1 62%", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          {/* PRODUCT LINES — 2026-10-20, Arturo: "baj0 esta misma logica...
              remueve negritas... reduce fonts" — same centered/lighter
              header treatment as the view-mode Products card (and as
              Bills), applied here too so switching Edit<->View keeps
              the same look, per "modo edit y modo no edit son copias". */}
          {(f.lines || []).length > 0 && (
            <div>
              {/* 2026-10-21, Arturo: "en products y pallet allocations,
                  los add que agregaste alinealos al extremo derecho de
                  cada head line" — the title span already fills the
                  SectionBox header's full width (SectionBox gives it
                  flex:1), so justify-content:space-between inside it
                  pushes the button to the far right edge of the header
                  row instead of sitting right next to the title text. */}
              <SectionBox id="products" title={<span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                <span>{lang === "es" ? "Productos" : "Products"}</span>
                <button type="button" onClick={function (e) { e.stopPropagation(); setAddFromLotModalOpen(true); }} style={{ padding: 0, border: "none", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600, textDecoration: "underline" }}>+ {lang === "es" ? "Agregar de Lote" : "Add from Lot"}</button>
              </span>} open={{ products: productsOpenEdit }} toggle={function () { setProductsOpenEdit(!productsOpenEdit); }}>
              <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
                {/* 2026-10-21, Arturo: "de hecho asi esta en modo no
                    edicion, y debe ser igual en modo edicion" (view
                    mode's own Product/Cat/Pallets/Qty/Value/Amount/
                    Sale Status + Total footer) + "solo la primer
                    columna si es lot, en product debe mostrar la
                    descripcion, y elimina Type/Cond/Var/Pac etc" —
                    dropped the separate Description/Comm%/Comm $/Net
                    Amount columns entirely (never shown in view mode)
                    and merged Product+Description into the single
                    Product cell view mode already uses. LOT and the
                    drag handle/action icons are the only edit-only
                    additions kept beyond view mode's own 7 columns —
                    both are editing affordances (reorder/split/
                    remove/add-from-shipment), never data content. */}
                <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                  {/* 2026-10-21, Arturo: "good delivery a un renglon,
                      empuja las columnas a la izquierda incluyendo
                      header, que no se desalineen" — Sale Status was
                      too narrow (10%) for "Good Delivery" to render on
                      one line under table-layout:fixed, so the badge
                      wrapped and grew the row; widened to 18% (taken
                      mostly from Product, which has the least need for
                      every last pixel) so it fits in one line, with
                      every header width still matching its column's
                      data cells exactly — never independently. */}
                  <th style={{ width: "3%" }}></th>
                  <th style={{ padding: "6px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "5%" }}>LOT</th>
                  <th style={{ padding: "6px 6px", textAlign: "left", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "20%" }}>{t.product}</th>
                  <th style={{ padding: "6px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "7%" }}>{t.category}</th>
                  <th style={{ padding: "6px 3px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "7%" }}>{t.pallets}</th>
                  <th style={{ padding: "6px 3px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "7%" }}>{t.cases}</th>
                  <th style={{ padding: "6px 3px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "9%" }}>{t.salePrice}</th>
                  <th style={{ padding: "6px 3px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "10%" }}>{t.amount}</th>
                  <th style={{ padding: "6px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "18%" }}>{t.saleStatus}</th>
                  <th style={{ width: "7%" }}></th>
                </tr></thead>
                <tbody>{f.lines.map(function(line, idx) {
                  var isLoss = line.saleStatus === "dumped" || line.saleStatus === "donated" || line.saleStatus === "samples" || line.saleStatus === "transfer";
                  var amt = isLoss ? 0 : (parseInt(line.cases, 10) || 0) * (parseFloat(line.salePrice) || 0);
                  var rp = dndLines.rowProps(idx);
                  return <tr key={idx} {...rp} style={{ borderBottom: "1px solid var(--color-border)", opacity: isLoss ? 0.6 : 1, ...(rp.style || {}) }}>
                    <td style={{ padding: "2px 0", textAlign: "center" }}><DragHandle handleProps={dndLines.handleProps()} /></td>
                    <td style={{ padding: "2px 4px", fontFamily: "'JetBrains Mono', monospace", textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>{line.lot || "—"}</td>
                    <td style={{ padding: "2px 6px", fontSize: 11, color: "var(--color-text-secondary)" }}>{line.productDescription || line.productName || line.productBase || "—"}</td>
                    <td style={{ padding: "2px 4px", textAlign: "center" }}>{line.category ? <span style={{ padding: "2px 6px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: line.category === "OG" ? "rgba(16,185,129,0.10)" : "rgba(107,114,128,0.10)", color: line.category === "OG" ? "#059669" : "#374151" }}>{line.category}</span> : "—"}</td>
                    <td style={{ padding: "2px 3px" }}><input type="number" min="0" step="1" value={line.pallets === undefined || line.pallets === null ? "" : line.pallets} onChange={function(e) { updateLinePallets(idx, e.target.value); }} style={{ ...inp, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} /></td>
                    <td style={{ padding: "2px 3px" }}><input type="number" min="0" step="1" value={line.cases === undefined || line.cases === null ? "" : line.cases} onChange={function(e) { updateLineCases(idx, e.target.value); }} style={{ ...inp, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} /></td>
                    {/* 2026-10-21, Arturo: "price es con 2 decimales" —
                        onBlur reformats to a fixed 2-decimal string
                        (21.5 -> 21.50) once the user leaves the field,
                        same "format on blur, raw while typing" pattern
                        used for money fields elsewhere in this app —
                        reformatting on every keystroke would fight the
                        user mid-edit (e.g. typing "21." collapses back
                        to "21" instantly). "aplica el mismo estilo de
                        fonts... reduce el tamaño de los renglones al
                        aplicado en los modulos anteriores" — row
                        padding dropped from 6px to Bill's own 2px, and
                        the bold weights this row carried (LOT/Pallets/
                        Qty/Amount) removed to match Bill's data-cell
                        recipe (11px/text-secondary/no bold) exactly. */}
                    <td style={{ padding: "2px 3px" }}><input type="number" min="0" step="0.01" value={line.salePrice || ""} onChange={function(e) { updateLinePrice(idx, e.target.value); }} onBlur={function(e) { var v = parseFloat(e.target.value); if (!isNaN(v)) updateLinePrice(idx, v.toFixed(2)); }} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} /></td>
                    <td style={{ padding: "2px 3px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: isLoss ? "#DC2626" : "var(--color-text-secondary)" }}>{fmtMoney(amt)}</td>
                    <td style={{ padding: "2px 3px", textAlign: "center", whiteSpace: "nowrap" }}>{(function() {
                      var hasMatchedPallets = (f.palletAllocations || []).some(function(a) {
                        return allocationMatchesLine(line, a.lot, palletMetaFor(a.lot, a.palletNum));
                      });
                      if (hasMatchedPallets && DISPOSITION_STATUSES.indexOf(line.saleStatus) === -1) {
                        var shownStatus = line.saleStatus || resolvePendingStage([f], slPassingIndex);
                        var sc = SALE_STATUS_COLORS[shownStatus];
                        return sc
                          ? <span title={line.saleStatus ? t.computedFromPallets : ""} style={{ display: "inline-block", padding: "3px 8px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: sc.bg, color: sc.text, border: "1px solid " + sc.border, boxShadow: SALE_STATUS_BEVEL, cursor: line.saleStatus ? "help" : "default", whiteSpace: "nowrap" }}>{t[shownStatus] || shownStatus}</span>
                          : <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>—</span>;
                      }
                      return <SearchSelect value={line.saleStatus || ""} onChange={v => updateLineSaleStatus(idx, v)} placeholder={t.selectStatus} options={SALE_STATUSES.map(function(s) { return { value: s, label: t[s] }; })} />;
                    })()}</td>
                    {/* 2026-10-21, Arturo: "los botones de accion en
                        products uno encima de otro sin cuadro
                        alrededor" — stacked vertically, plain icons
                        (no border/box chrome), instead of the 3
                        horizontal bordered buttons this used to be. */}
                    <td style={{ padding: "4px 2px", textAlign: "center" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                        <button type="button" onClick={function() { splitProductLine(idx); }} title={lang === "es" ? "Dividir línea" : "Split line"} style={{ padding: "1px 4px", border: "none", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>{"⇆"}</button>
                        <button type="button" onClick={function() { addProductLineCopy(idx); }} title={lang === "es" ? "Agregar línea del mismo producto" : "Add row (same product)"} style={{ padding: "1px 4px", border: "none", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>{"+"}</button>
                        <button type="button" onClick={function() { removeProductLine(idx); }} title={lang === "es" ? "Eliminar línea" : "Remove line"} style={{ padding: "1px 4px", border: "none", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>{"×"}</button>
                      </div>
                    </td>
                  </tr>;
                })}</tbody>
                <tfoot>
                  <tr style={{ background: "var(--color-bg)", borderTop: "2px solid var(--color-accent)" }}>
                    <td colSpan={3} style={{ padding: "6px 4px", textAlign: "right", fontSize: 10, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{lang === "es" ? "Total" : "Total"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{(f.lines || []).reduce(function (s, l) { return s + (parseInt(l.pallets, 10) || 0); }, 0)}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{(f.lines || []).reduce(function (s, l) { return s + (parseInt(l.cases, 10) || 0); }, 0)}</td>
                    <td></td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtMoney((f.lines || []).reduce(function (s, l) { var ls = l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer"; return s + (ls ? 0 : (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0)); }, 0))}</td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
              </div>
              </SectionBox>
            </div>
          )}
          {addFromLotModalOpen && (
            <AddFromLotModal
              availableLots={availableLots}
              t={t}
              lang={lang}
              onAdd={function (lotId) { addFromShipment(lotId); }}
              onClose={function () { setAddFromLotModalOpen(false); }}
            />
          )}

          {/* PALLET ALLOCATIONS — per-physical-pallet case tracking,
              separate from the aggregate product Lines above. Seeded
              100%-cases from a lot's manifest via "Add from shipment";
              editable per row; additional pallets from other lots
              already on this invoice come from the picker below. */}
          <div>
            {/* 2026-10-21, Arturo: "iniciando con la leyenda allocations
                balanced, cambialo de posicion, remueve la burbuja, y
                la palabra allocations, y colocalo solo la flecha y
                balance del mismo color en el header despues de pallet
                allocations" — the balanced state no longer gets its
                own bordered/bubbled banner in the card body; it's now
                a plain "✓ Balanced" (same green, no box) appended
                right after the "Pallet Allocations" title text itself
                (title is JSX now, not a bare string, so it sits next
                to the text instead of floating to the header's far
                right like headerExtra would). The mismatch warning
                keeps its own banner below — it carries real per-lot
                detail that doesn't fit in a header line, and was never
                part of this specific ask. */}
            {/* "en products y pallet allocations, los add que agregaste
                alinealos al extremo derecho de cada head line" — same
                space-between fix as Products' title above; "✓
                Balanced" stays grouped with the title on the left,
                only the Add Pallet trigger moves to the far right. */}
            <SectionBox id="pallets" title={<span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>{t.palletAllocations}</span>
                {lotBalances.length > 0 && !hasLotMismatch && <span style={{ fontSize: 11, fontWeight: 600, color: "#059669" }}>{"✓ " + (lang === "es" ? "Balanceado" : "Balanced")}</span>}
              </span>
              <button type="button" onClick={function (e) { e.stopPropagation(); setAddPalletModalOpen(true); }} style={{ padding: 0, border: "none", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600, textDecoration: "underline" }}>+ {lang === "es" ? "Agregar Pallets" : "Add Pallets"}</button>
            </span>} open={{ pallets: palletsOpenEdit }} toggle={function () { setPalletsOpenEdit(!palletsOpenEdit); }}>
            {hasLotMismatch && (
              <div style={{ marginBottom: 8, padding: "7px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: "rgba(220,38,38,0.08)", color: "#DC2626", border: "1px solid rgba(220,38,38,0.25)" }}>
                {lotBalances.filter(function(b) { return !b.balanced; }).map(function(b) {
                  return <div key={b.lot}>{"⚠ " + (lang === "es" ? "Descuadre: Lote " + b.lot + " — Línea: " + b.linesCases + " vs Allocations: " + b.allocCases : "Mismatch: Lot " + b.lot + " — Line: " + b.linesCases + " vs Allocations: " + b.allocCases)}</div>;
                })}
              </div>
            )}
            {/* 2026-10-21, Arturo: "los fonts no negritas, tamaño,
                alineacion igual que modo no editar" + "replica esto"
                (view mode's own Pallet Allocations table) + "alineacion,
                centrado, headers con divisores" — same recipe as
                DetailPanel's own Pallet Allocations table: header 9px/
                weight 600/muted/uppercase/letterSpacing instead of
                10px/weight 700/secondary, 5px padding instead of 7px,
                border-left divider on every header but the first, and
                header alignment matches view mode exactly (Cases/
                Weight centered, not right — the DATA cells stay right-
                aligned for Weight, same "numbers/money align right
                regardless of a centered header" convention as Products
                above). Data cells drop every bold weight this table
                had (LOT/Product/Cases) and Variety/Grower switch from
                left to center to match view mode. */}
            {(f.palletAllocations || []).length > 0 && (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed", marginBottom: 8 }}>
                <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "8%" }}>{t.lot}</th>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "7%", borderLeft: "1px solid var(--color-border)" }}>{t.palletNumCol}</th>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "9%", borderLeft: "1px solid var(--color-border)" }}>{t.tagIdCol}</th>
                  <th style={{ padding: "5px 4px", textAlign: "left", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "14%", borderLeft: "1px solid var(--color-border)" }}>{t.product}</th>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "9%", borderLeft: "1px solid var(--color-border)" }}>{t.varietyCol}</th>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "8%", borderLeft: "1px solid var(--color-border)" }}>{t.allocCases}</th>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "9%", borderLeft: "1px solid var(--color-border)" }}>{t.growerCol}</th>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "9%", borderLeft: "1px solid var(--color-border)" }}>{t.weightCol}</th>
                  <th style={{ padding: "5px 4px", textAlign: "center", fontWeight: 600, fontSize: 9, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", width: "20%", borderLeft: "1px solid var(--color-border)" }}>{t.palletStatusCol}</th>
                  <th style={{ width: "7%" }}></th>
                </tr></thead>
                <tbody>{f.palletAllocations.map(function(a, idx) {
                  var meta = palletMetaFor(a.lot, a.palletNum) || {};
                  var w = docPalletWeight({ size: meta.size, unitsCase: meta.unitsCase, casesPallet: a.cases });
                  /* 2026-10-30, Arturo (lote 58074/PU 17475): "todos los
                     pallets dicen pending pick up" — this cell never
                     checked twinRiverPalletStatus, so a ClariFresh/Twin
                     River pallet's real status (captured on the
                     Shipment's own tolerance sheet, per the 2026-10-15
                     exception to REGLA DE STATUS DE PALLET) was invisible
                     here; the manual dropdown below was the only thing
                     ever shown, and it's not even the value anything
                     downstream uses for a Twin River lot (the line-level
                     status already ignores it, see recomputeLineDeliveryStatuses's
                     trStatus branch). Now read-only + live from the
                     shipment for ClariFresh, same precedence as
                     Bill/Shipment's own pallet tables — the editable
                     dropdown only applies to non-Twin-River lots.
                     2026-11-03, PU 17484: linkedShipmentForInfoEdit is
                     always the invoice's single home-lot shipment —
                     on a multi-lot invoice, a pallet from the SECOND
                     lot got looked up by palletNum inside the WRONG
                     shipment's manifest, occasionally matching a
                     same-numbered pallet from a different lot/product
                     and inheriting ITS status. Resolve by this
                     allocation's own a.lot instead. */
                  var trPalletStatus = twinRiverPalletStatus(findShipmentByLot(a.lot, getShipments()), a.palletNum);
                  var trSc = trPalletStatus ? SALE_STATUS_COLORS[trPalletStatus] : null;
                  return <tr key={idx} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-accent)" }}>{a.lot || "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{a.palletNum || "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--color-text-secondary)" }}>{meta.tagId || "—"}</td>
                    <td style={{ padding: "6px 4px", fontSize: 11 }}>{meta.productBase || "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>{meta.variety || "—"}</td>
                    <td style={{ padding: "6px 3px" }}><input type="number" min="0" step="1" value={a.cases === undefined || a.cases === null ? "" : a.cases} onChange={function(e) { updateAllocCases(idx, e.target.value); }} style={{ ...inp, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} /></td>
                    <td style={{ padding: "6px 4px", textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>{meta.growerCode || "—"}</td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{w.lb > 0 ? w.lb.toFixed(1) + " lb" : "—"}</td>
                    <td style={{ padding: "6px 3px" }}>
                      {trPalletStatus ? (
                        <span style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9, fontWeight: 700, background: trSc.bg, color: trSc.text, border: "1px solid " + trSc.border, boxShadow: SALE_STATUS_BEVEL }}>{t[trPalletStatus] || trPalletStatus}</span>
                      ) : (
                      <>
                      {/* 2026-09-12 — no longer pre-selects "goodDelivery"
                         for an untouched pallet (Arturo: "el cliente
                         informa todos los status" — unset means pending,
                         not confirmed good; see saleStatusCalc.js). The
                         placeholder itself shows Pending Pick Up/Delivery
                         so an unconfirmed pallet never looks blank.
                         SearchSelect already lets you clear a selection
                         back to "" via an italic row at the top of its
                         own dropdown, but that's easy to miss — Arturo
                         (2026-09-14): "podria aquivocarme y poner un
                         pallet como good delivery y no tendria forma de
                         regresarlo". Added a visible ✕ next to the
                         field, shown only once a status is actually
                         set, that does the exact same reset (back to
                         the auto-computed Pending Pick Up/Delivery) —
                         same effect as SearchSelect's own Clear row,
                         just discoverable without opening the dropdown
                         first. */}
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <SearchSelect
                          value={a.saleStatus || ""}
                          onChange={function(v) { updateAllocStatus(idx, v); }}
                          placeholder={t[resolvePendingStage([f], slPassingIndex)]}
                          /* 2026-09-14, Arturo (asked several times):
                             "puedo regresar un pallet a status pending
                             delivery o pending pick up manualmente...
                             agrega esas 2 opciones al dropdown list".
                             PENDING_STATUSES are normally NEVER
                             persisted (see saleStatusCalc.js — always
                             computed live from resolvePendingStage) —
                             but Arturo wants the ability to explicitly
                             LOCK a pallet to one of them by hand (e.g.
                             after mistakenly marking it Good Delivery),
                             overriding the automatic computation until
                             he clears it again via the ✕ button. Listed
                             first since they're the "earlier"/less-final
                             states. */
                          options={PENDING_STATUSES.concat(DELIVERY_STATUSES).map(function(s) { return { value: s, label: t[s] }; })}
                          style={{ fontSize: 11, flex: 1 }}
                        />
                        {a.saleStatus && (
                          <button
                            type="button"
                            onClick={function() { updateAllocStatus(idx, ""); }}
                            title={t.resetToPending}
                            style={{ padding: "3px 6px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: 1 }}
                          >✕</button>
                        )}
                      </div>
                      </>
                      )}
                    </td>
                    <td style={{ padding: "6px 1px", textAlign: "center" }}>
                      <button type="button" onClick={function() { removeAlloc(idx); }} title={lang === "es" ? "Eliminar renglón" : "Remove row"} style={{ padding: "2px 6px", borderRadius: 4, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{"🗑"}</button>
                    </td>
                  </tr>;
                })}</tbody>
              </table>
            )}
            {populateFromLotShipment && (
              <button type="button" onClick={populateFromLot} style={{ padding: "6px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600, marginBottom: 10 }}>+ {t.populateFromLot}</button>
            )}
            </SectionBox>
          </div>
          {addPalletModalOpen && (
            <AddPalletModal
              options={otherLotPalletOptions}
              t={t}
              lang={lang}
              onAddMany={addFromOtherLotPalletMany}
              onClose={function () { setAddPalletModalOpen(false); }}
            />
          )}
          {/* PAYMENTS HISTORY */}
          <div>
            <SectionBox id="payments" title={t.paymentsSection} open={{ payments: paymentsOpen }} toggle={function () { setPaymentsOpen(!paymentsOpen); }}>
              <PaymentsSection payments={f.payments} total={due} t={t} />
            </SectionBox>
          </div>
          </div>
          </div>
          </fieldset>
        </div>

        {/* FOOTER */}
        <div style={{ padding: "10px 20px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 6 }}>
            {!isNew && !showDel && <button onClick={function() { setShowDel(true); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid rgba(239,68,68,0.3)", background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.delete}</button>}
            {showDel && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 11, color: "#ef4444" }}>{t.confirmDel}</span><button onClick={function() { onDelete(invoice.id); }} style={{ padding: "4px 10px", borderRadius: 4, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 11 }}>{t.yes}</button><button onClick={function() { setShowDel(false); }} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 11 }}>{t.no}</button></div>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={tryCancel} style={{ padding: "7px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12 }}>{t.cancel}</button>
            <button onClick={handleSave} disabled={hasLotMismatch} title={hasLotMismatch ? (lang === "es" ? "Corrige el descuadre de pallets antes de guardar" : "Fix the pallet mismatch before saving") : undefined} style={{ padding: "7px 20px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: hasLotMismatch ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600, opacity: hasLotMismatch ? 0.5 : 1 }}>{t.save}</button>
          </div>
        </div>

        {showExitConfirm && <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}><div style={{ background: "var(--color-bg-card-solid)", padding: 24, borderRadius: 12, maxWidth: 300, textAlign: "center" }}><p style={{ fontSize: 13, marginBottom: 14 }}>{t.confirmExit}</p><div style={{ display: "flex", gap: 8, justifyContent: "center" }}><button onClick={onCancel} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.yesExit}</button><button onClick={function() { setShowExitConfirm(false); }} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 12 }}>{t.noExit}</button></div></div></div>}
    </>
  );
  if (embedded) return body;
  return (
    <DraggableModal title={(isNew ? t.add : t.edit) + (f.puNumber ? "  ·  PU# " + f.puNumber : "") + ((resolveClientName(f.clientId) || f.clientName) ? "  ·  " + (resolveClientName(f.clientId) || f.clientName) : "")} titleBarVariant="groundfresh" onClose={tryCancel} initialWidth={820} initialHeight={Math.min(900, window.innerHeight - 60)}>
      {body}
    </DraggableModal>
  );
}

/* ======================== BATCH DEPOSIT MODAL ======================== */
function calcInvoiceTotal(inv) {
  /* Net = gross − commission − fees + credits + Σ(balanceTransfers signed).
       commission = client's commission % of gross — now PER LINE.
                    Each line carries its own commissionRate (since
                    the line-commission migration). Lines without an
                    explicit rate fall back to inv.commissionRate so
                    legacy bills keep producing the same total
                    (rate × Σamts ≡ Σ(rate × amts) by linearity).
       fees       = deductions GFI passes through to client — SUBTRACT.
       credits    = additions / adjustments owed to GFI — ADD.
       balanceTransfers = cross-invoice atomic links. */
  var invDefaultRate = parseFloat(inv.commissionRate) || 0;
  var total = 0, commission = 0;
  (inv.lines || []).forEach(function(l) {
    if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
    var lineAmt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
    var lineRate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
      ? invDefaultRate
      : (parseFloat(l.commissionRate) || 0);
    total += lineAmt;
    commission += lineAmt * (lineRate / 100);
  });
  var tFees = 0;
  (inv.fees || []).forEach(function(fe) { tFees += Math.abs(parseFloat(fe.amount) || 0); });
  var tCredits = 0;
  (inv.credits || []).forEach(function(cr) { tCredits += Math.abs(parseFloat(cr.amount) || 0); });
  var tTransfers = 0;
  (inv.balanceTransfers || []).forEach(function(t) {
    var amt = parseFloat(t.amount) || 0;
    if (t.type === "credit") tTransfers += amt;
    else if (t.type === "debit") tTransfers -= amt;
  });
  return total - commission - tFees + tCredits + tTransfers;
}

/* Per-line commission $ for an invoice line. 0 for excluded statuses
   so it matches the calc above. */
function invLineCommissionAmount(line, invDefaultRate) {
  if (!line) return 0;
  if (line.saleStatus === "dumped" || line.saleStatus === "donated" || line.saleStatus === "samples" || line.saleStatus === "transfer") return 0;
  var amt = (parseInt(line.cases, 10) || 0) * (parseFloat(line.salePrice) || 0);
  var rate = (line.commissionRate === undefined || line.commissionRate === null || line.commissionRate === "")
    ? (parseFloat(invDefaultRate) || 0)
    : (parseFloat(line.commissionRate) || 0);
  return amt * (rate / 100);
}

function buildInvRef(inv) {
  var parts = [];
  if (inv.puNumber) parts.push("PU " + inv.puNumber);
  if (inv.lot) parts.push("LOT " + inv.lot);
  if (inv.clientRef) parts.push(inv.clientRef);
  return parts.join(" · ");
}

function BatchDepositModal({ invoices, t, lang, onConfirm, onCancel }) {
  var initRows = useMemo(function() {
    return invoices.map(function(inv) {
      var totalDue = calcInvoiceTotal(inv);
      var paid = parseFloat(inv.amountPaid) || 0;
      var due = +(totalDue - paid).toFixed(2);
      return { inv: inv, totalDue: totalDue, paid: paid, due: due, amountStr: due > 0 ? due.toFixed(2) : "", concept: "" };
    });
  }, [invoices]);

  var [rows, setRows] = useState(initRows);
  var [depositDate, setDepositDate] = useState(todayISO());
  var [depositMethod, setDepositMethod] = useState("wire");
  var [reference, setReference] = useState("");
  var [notes, setNotes] = useState("");
  var [error, setError] = useState("");
  var [sortCol, setSortCol] = useState("ref"); /* ref | client | dueDate | amount */
  var [sortDir, setSortDir] = useState("asc");

  var openRows = useMemo(function() { return rows.filter(function(r) { return r.due > 0.01; }); }, [rows]);
  var total = useMemo(function() { return openRows.reduce(function(s, r) { return s + (parseFloat(r.amountStr) || 0); }, 0); }, [openRows]);

  /* Sorted-for-display rows, hoisted out of the render IIFE below so
     handleConfirm can also use it — otherwise the saved allocations
     array (and the printed Payment Confirmation PDF, which just walks
     it in array order) never matches whatever order Arturo arranged
     on screen with the sortable headers. 2026-09-18, Arturo: "el pdf
     que se imprime debe mantener el orden que se tiene en la ventana
     de pagos". */
  var sortedOpenRows = useMemo(function () {
    var sortKeyRef = function (r) {
      var n = parseInt(r.inv.puNumber, 10);
      if (!isNaN(n)) return { kind: 0, n: n, s: "" };
      return { kind: 1, n: 0, s: String(buildInvRef(r.inv) || r.inv.id || "") };
    };
    var compare = function (a, b) {
      var cmp;
      if (sortCol === "client") {
        cmp = String(resolveClientName(a.inv.clientId) || a.inv.clientName || "")
          .localeCompare(String(resolveClientName(b.inv.clientId) || b.inv.clientName || ""));
      } else if (sortCol === "dueDate") {
        cmp = String(a.inv.dueDate || "").localeCompare(String(b.inv.dueDate || ""));
      } else if (sortCol === "amount") {
        cmp = (parseFloat(a.amountStr) || 0) - (parseFloat(b.amountStr) || 0);
      } else {
        var ka = sortKeyRef(a), kb = sortKeyRef(b);
        cmp = ka.kind - kb.kind;
        if (cmp === 0) cmp = ka.kind === 0 ? (ka.n - kb.n) : ka.s.localeCompare(kb.s);
      }
      return sortDir === "asc" ? cmp : -cmp;
    };
    return openRows.slice().sort(compare);
  }, [openRows, sortCol, sortDir]);

  /* Amount as RAW STRING during typing — normalize only on blur.
     Same pattern as PaymentRegistry.EditPaymentModal. */
  var setRowAmountStr = function(invId, raw) {
    setRows(function(prev) { return prev.map(function(r) { return r.inv.id === invId ? { ...r, amountStr: raw } : r; }); });
    setError("");
  };
  var setRowConcept = function(invId, val) {
    setRows(function(prev) { return prev.map(function(r) { return r.inv.id === invId ? { ...r, concept: val } : r; }); });
  };
  var normalizeRowAmount = function(invId) {
    setRows(function(prev) { return prev.map(function(r) {
      if (r.inv.id !== invId) return r;
      var raw = r.amountStr;
      if (raw === undefined || raw === null || String(raw).trim() === "") return { ...r, amountStr: "" };
      var n = parseFloat(raw);
      if (!isFinite(n)) return r;
      return { ...r, amountStr: n.toFixed(2) };
    }); });
  };

  var validate = function() {
    if (!depositDate) return lang === "es" ? "Selecciona una fecha" : "Select a date";
    for (var i = 0; i < openRows.length; i++) {
      var r = openRows[i];
      var amt = parseFloat(r.amountStr) || 0;
      if (amt < 0) return t.overApplyInv + " (" + r.inv.id + ")";
      if (amt > r.due + 0.01) return t.overApplyInv + " (" + r.inv.id + ")";
    }
    if (total <= 0) return lang === "es" ? "Captura al menos un monto > 0" : "Enter at least one amount > 0";
    return null;
  };

  var handleConfirm = function() {
    var err = validate();
    if (err) { setError(err); return; }
    var allocations = sortedOpenRows
      .filter(function(r) { return (parseFloat(r.amountStr) || 0) > 0; })
      .map(function(r) { return { invoiceId: r.inv.id, puNumber: r.inv.puNumber, lot: r.inv.lot, clientName: r.inv.clientName || "", amount: +(parseFloat(r.amountStr) || 0).toFixed(2), concept: r.concept || "" }; });
    onConfirm({
      paymentDate: depositDate,
      paymentMethod: depositMethod,
      reference: reference,
      notes: notes,
      allocations: allocations,
    });
  };

  var methodLabel = function(m) { return t["method" + m.charAt(0).toUpperCase() + m.slice(1)] || m; };

  return (
    <DraggableModal title={t.batchDepositTitle} onClose={onCancel} initialWidth={920} initialHeight={680} zIndex={10005}>
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.depositDate} *</label>
              <input type="date" value={depositDate} onChange={function(e) { setDepositDate(e.target.value); }} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.depositMethod}</label>
              <SearchSelect value={depositMethod} onChange={v => setDepositMethod(v)} options={PAYMENT_METHODS.map(function(m) { return { value: m, label: methodLabel(m) }; })} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.depositReference}</label>
              <input type="text" value={reference} onChange={function(e) { setReference(e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} placeholder="e.g. WIRE-2026-0123" />
            </div>
          </div>

          {openRows.length === 0 ? (
            <div style={{ padding: "32px 16px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13, background: "var(--color-bg)", borderRadius: 10, border: "1px dashed var(--color-border)" }}>{t.noOpenInvoices}</div>
          ) : (
            <div style={{ borderRadius: 10, border: "1px solid var(--color-border)", overflow: "hidden" }}>
              {(function () {
                var sortedRows = sortedOpenRows;
                var arrow = function (col) { return sortCol !== col ? " ⇅" : (sortDir === "asc" ? " ▲" : " ▼"); };
                var toggle = function (col) {
                  if (sortCol === col) setSortDir(function (d) { return d === "asc" ? "desc" : "asc"; });
                  else { setSortCol(col); setSortDir("asc"); }
                };
                var thStyle  = { padding: "8px 10px", textAlign: "left",   fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none" };
                var thStyleC = { padding: "8px 10px", textAlign: "center", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", cursor: "pointer", userSelect: "none" };
                var thStyleR = { padding: "8px 10px", textAlign: "right",  fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)" };
                return (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead><tr style={{ background: "var(--color-bg)", borderBottom: "2px solid var(--color-border)" }}>
                      <th style={thStyle}  onClick={function () { toggle("client"); }}  title={lang === "es" ? "Ordenar por cliente" : "Sort by client"}>{t.client}{arrow("client")}</th>
                      <th style={thStyle}  onClick={function () { toggle("ref"); }}     title={lang === "es" ? "Ordenar por referencia" : "Sort by reference"}>{t.refColumn}{arrow("ref")}</th>
                      <th style={thStyleC} onClick={function () { toggle("dueDate"); }} title={lang === "es" ? "Ordenar por fecha de vencimiento" : "Sort by due date"}>{t.dueDate}{arrow("dueDate")}</th>
                      <th style={thStyleR}>{t.dueAmount}</th>
                      <th style={thStyleR}>{t.depositAmount}</th>
                      <th style={{ ...thStyleC, cursor: "default" }}>{t.concept}</th>
                    </tr></thead>
                    <tbody>{sortedRows.map(function(r) {
                      var inv = r.inv;
                      return <tr key={inv.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                        <td style={{ padding: "10px", fontSize: 12, fontWeight: 600 }}>{resolveClientName(inv.clientId) || inv.clientName || "—"}</td>
                        <td style={{ padding: "10px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: "var(--color-text-secondary)" }}>{buildInvRef(inv)}</td>
                        <td style={{ padding: "10px", textAlign: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtDate(inv.dueDate)}</td>
                        <td style={{ padding: "10px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: "#c65d00" }}>{fmtMoney(r.due)}</td>
                        <td style={{ padding: "6px 10px", textAlign: "right" }}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={r.amountStr === undefined ? "" : r.amountStr}
                            onChange={function(e) { setRowAmountStr(inv.id, e.target.value); }}
                            onBlur={function() { normalizeRowAmount(inv.id); }}
                            style={{ ...inp, width: 130, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, marginLeft: "auto" }}
                          />
                        </td>
                        <td style={{ padding: "6px 10px", width: 130 }}>
                          <SearchSelect value={r.concept || ""} onChange={function (v) { setRowConcept(inv.id, v); }} options={[{ value: "", label: t.conceptDeposit }, { value: "advance", label: t.conceptAdvance }]} />
                        </td>
                      </tr>;
                    })}</tbody>
                  </table>
                );
              })()}
            </div>
          )}

          <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "block", marginBottom: 4 }}>{t.notes}</label>
              <textarea value={notes} onChange={function(e) { setNotes(e.target.value); }} rows={2} style={{ ...inp, fontFamily: "inherit", resize: "vertical" }} />
            </div>
            <div style={{ background: "var(--color-bg)", borderRadius: 10, padding: 14, alignSelf: "end" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 4 }}>{t.totalDeposit}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 800, color: "#059669" }}>{fmtMoney(total)}</div>
            </div>
          </div>

          {error && <div style={{ marginTop: 12, padding: "8px 12px", background: "rgba(231,76,60,0.08)", border: "1px solid rgba(231,76,60,0.25)", borderRadius: 6, color: "#DC2626", fontSize: 12, fontWeight: 600 }}>{error}</div>}
        </div>

        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>{t.cancel}</button>
          <button onClick={handleConfirm} disabled={openRows.length === 0 || total <= 0} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: (openRows.length === 0 || total <= 0) ? "var(--color-border)" : "#059669", color: "#fff", cursor: (openRows.length === 0 || total <= 0) ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>{t.recordDeposits}</button>
        </div>
    </DraggableModal>
  );
}

/* ======================== MAIN REGISTRY ======================== */
export default function InvoiceRegistry({ lang: globalLang }) {
  var lang = globalLang || "es"; var t = T[lang];
  var [invoices, setInvoices] = useState([]);
  /* For the Pending Pick Up / Pending Delivery SALE-column display
     default on the list (see saleStatusCalc.js resolvePendingStage). */
  var [slPassingIndexList, setSlPassingIndexList] = useState({});
  useEffect(function () {
    var cancelled = false;
    loadFromSupabase("sweetleaf_invoices").then(function (sl) {
      if (!cancelled) setSlPassingIndexList(buildSlPassingIndex(sl));
    }).catch(function () {});
    return function () { cancelled = true; };
  }, []);
  /* Client/Sale Status/Week are multi-select arrays (2026-09-27,
     Arturo: "poder seleccionar mas de un filtro de un concepto...
     tener casillas de seleccion") — empty array = no restriction. */
  var [search, setSearch] = useState(""); var [filterClient, setFilterClient] = useState([]); var [filterStatus, setFilterStatus] = useState(""); var [filterSaleStatus, setFilterSaleStatus] = useState([]); var [filterWeek, setFilterWeek] = useState([]);
  /* Filter popover state — Xero-style "⋮ Filters" button collapses
     the full filter set into an anchored panel. Mirrors the
     identically-named pattern in BillRegistry. */
  var [showFilters, setShowFilters] = useState(false);
  var filtersRef = useRef(null);
  /* Brushed Steel redesign (ported from ShipmentRegistry.js/
     BillRegistry.js) — consolidates the document/export actions
     (Invoice PDF / Print Documents / Export Excel) into one kebab
     menu, same anchored-popover pattern as showFilters above. */
  var [showMoreMenu, setShowMoreMenu] = useState(false);
  var moreMenuRef = useRef(null);
  /* Season filter — multi-select grouped per CLAUDE.md "REGLA DE FILTRO
     DE SEASON". Pre-selects all Open seasons on mount. */
  var [filterSeasons, setFilterSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  var [dateFrom, setDateFrom] = useState(""); var [dateTo, setDateTo] = useState("");
  var seasons = useMemo(function() { return loadLS("gfi-seasons", []); }, []);
  /* Resolves the season for an invoice. invoice.date is the primary key
     for the lookup; falls back to invoice.seasonId for historical imports. */
  var seasonNameForInv = useCallback(function(invDate, sid) {
    if (sid) { var byId = seasons.find(function(s) { return String(s.id) === String(sid); }); if (byId) return byId.seasonName || byId.startDate.substring(0, 4) + "-" + byId.endEstimate.substring(2, 4); }
    if (!invDate) return "";
    var d = new Date(invDate + "T00:00:00");
    var s = seasons.find(function(s) { return new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00"); });
    return s ? (s.seasonName || s.startDate.substring(0, 4) + "-" + s.endEstimate.substring(2, 4)) : "";
  }, [seasons]);
  var uniqueSeasons = useMemo(function() { return [...new Set(seasons.map(function(s) { return s.seasonName || s.startDate.substring(0, 4) + "-" + s.endEstimate.substring(2, 4); }).filter(Boolean))].sort(); }, [seasons]);
  var [sortCol, setSortCol] = useState(function() { try { var s = JSON.parse(localStorage.getItem("gfi-invoices-sort") || "null"); return (s && s.column) || "puNumber"; } catch { return "puNumber"; } });
  var [sortDir, setSortDir] = useState(function() { try { var s = JSON.parse(localStorage.getItem("gfi-invoices-sort") || "null"); return (s && s.direction) || "desc"; } catch { return "desc"; } });
  useEffect(function() { try { localStorage.setItem("gfi-invoices-sort", JSON.stringify({ column: sortCol, direction: sortDir })); } catch {} }, [sortCol, sortDir]);
  /* Close the filter popover on outside click + Escape. */
  useEffect(function() {
    if (!showFilters) return;
    var onDown = function(e) { if (filtersRef.current && !filtersRef.current.contains(e.target) && !(e.target.closest && e.target.closest('[data-searchselect]'))) setShowFilters(false); };
    var onKey  = function(e) { if (e.key === "Escape") setShowFilters(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return function() {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showFilters]);
  /* Close the "⋮" more-actions menu on outside click + Escape — same
     pattern as the filter popover right above. */
  useEffect(function() {
    if (!showMoreMenu) return;
    var onDown = function(e) { if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setShowMoreMenu(false); };
    var onKey  = function(e) { if (e.key === "Escape") setShowMoreMenu(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return function() {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showMoreMenu]);
  var [viewing, setViewing] = useState(null); var [editing, setEditing] = useState(null); var [showForm, setShowForm] = useState(false);
  /* 2026-09-14 — Arturo: "cualquier cosa que abra desde el dashboard, al
     cerrar que me regrese al dashboard". window.__gfiReturnModule is set
     by the caller (Dashboard.js's navigateAndOpen/navigateAndCall) right
     before it invokes __gfiInvoiceOpen/__gfiInvoiceNew — captured here
     and consumed on the matching Close/Cancel so a record opened via a
     normal in-module click (which never touches this global) behaves
     exactly as before. */
  var returnModuleRef = useRef(null);
  var closeToOrigin = useCallback(function() {
    if (returnModuleRef.current && window.__gfiNavigate) window.__gfiNavigate(returnModuleRef.current);
    returnModuleRef.current = null;
  }, []);
  var [showPrint, setShowPrint] = useState(null);
  /* Bill preview opened from the LOT cell in the invoices list. */
  var [lotPreviewBill, setLotPreviewBill] = useState(null);
  /* Fresh bills snapshot used by the LOT cell to decide whether to
     render a link or plain text. Memo on invoices so an invoice
     edit / status change reflects without a full reload. */
  /* 2026-10-07 — Arturo: in production, the LOT cell only showed a
     link for whichever invoice happened to already have a Bill in
     this device's localStorage cache (gfi-bills) — same "localStorage
     is not the source of truth" bug class documented repeatedly in
     this app's history. A session that never opened Bill Registry has
     an empty/stale local cache, so lookupBillByLot silently fails for
     almost every row. Force a fresh Supabase read once on mount
     (mirrors BillRegistry.js's own fix for the stale-invoices version
     of this same bug) and bump a counter to re-render once it lands. */
  var [, bumpBillsRefresh] = useState(0);
  useEffect(function () {
    var cancelled = false;
    loadFromSupabase("bills").then(function () { if (!cancelled) bumpBillsRefresh(function (n) { return n + 1; }); }).catch(function () {});
    return function () { cancelled = true; };
  }, []);
  var billsByLot = useMemo(function () {
    var cached = getCached("bills");
    var bills = (cached && cached.length > 0) ? cached : loadLS("gfi-bills", []);
    var map = {};
    bills.forEach(function (b) { if (b && b.lot) map[String(b.lot).trim()] = b; });
    return map;
    /* `invoices` is intentionally the trigger — bills and invoices
       often co-update (creating an invoice from a bill, status
       changes, etc.) and the invoice list re-renders on any of
       those, refreshing this map alongside. Reading bills here
       doesn't depend on the invoices value itself, but the dep keeps
       the cache in sync with normal user activity in this module. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);
  /* Helper used by the LOT cell; null when no bill matches. */
  function lookupBillByLot(lot) {
    if (!lot && lot !== 0) return null;
    var hit = billsByLot[String(lot)];
    if (hit) return hit;
    hit = billsByLot[String(lot).trim()];
    if (hit) return hit;
    /* Numeric fallback covers "57916" vs 57916 mismatches. */
    var n = parseInt(lot, 10);
    if (isNaN(n)) return null;
    var keys = Object.keys(billsByLot);
    for (var i = 0; i < keys.length; i++) {
      var bn = parseInt(keys[i], 10);
      if (!isNaN(bn) && bn === n) return billsByLot[keys[i]];
    }
    return null;
  }
  var [toast, setToast] = useState(null); var [loading, setLoading] = useState(true);
  /* Expose a global toast hook so child components (DetailPanel)
     can fire toasts without prop-drilling. Cleaned up on unmount. */
  useEffect(function () {
    window.__gfiToast = function (msg) { setToast(msg); };
    return function () { try { delete window.__gfiToast; } catch (e) {} };
  }, []);
  var [selected, setSelected] = useState({});
  var [showBatchDeposit, setShowBatchDeposit] = useState(false);
  var [lockedRecord, setLockedRecord] = useState(null);
  var [confirmationPayment, setConfirmationPayment] = useState(null);
  var [cardFilter, setCardFilter] = useState(null); /* draft | sent | overdue | paid */

  var [error, setError] = useState(null);
  var [saving, setSaving] = useState(false);
  var aliveRef = useRef(true);
  var invoicesRef = useRef([]);
  useEffect(function () { invoicesRef.current = invoices; }, [invoices]);

  /* Source of truth = Supabase `invoices`. Dual-write to localStorage
     `gfi-invoices` so legacy sync consumers (ProfitTracker,
     Settlements, SalesReports, computeCertUsage's invoice scan, etc.)
     keep working until they migrate. */
  var writeInvoicesCache = function (next) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) {}
  };
  var reload = async function () {
    setError(null); setLoading(true);
    try {
      var rows = await loadFromSupabase("invoices", { force: true });
      if (aliveRef.current) { setInvoices(rows); writeInvoicesCache(rows); }
    } catch (e) {
      if (aliveRef.current) setError("Could not load invoices: " + (e.message || String(e)));
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  };
  useEffect(function () {
    aliveRef.current = true;
    setError(null); setLoading(true);
    loadFromSupabase("invoices")
      .then(function (rows) { if (aliveRef.current) { setInvoices(rows); writeInvoicesCache(rows); } })
      .catch(function (e) { if (aliveRef.current) setError("Could not load invoices: " + (e.message || String(e))); })
      .finally(function () { if (aliveRef.current) setLoading(false); });
    return function () { aliveRef.current = false; };
  }, []);

  /* CRUD helpers — row-targeted Supabase writes that keep local state
     and localStorage cache in sync. */
  var persistSingleInv = async function (inv) {
    var saved = await saveToSupabase("invoices", inv);
    var current = invoicesRef.current || [];
    /* String() the id comparison — a raw === here can miss a real
       match when `inv.id` and the cached `x.id` came from different
       load paths (number vs string, same class of mismatch already
       documented elsewhere in this file for `f.id`/`i.id`). When that
       happens this silently APPENDS a duplicate row instead of
       replacing the existing one, leaving a stale ghost entry with
       the invoice's pre-edit data sitting in `invoices` state forever
       (until a hard reload re-fetches from Supabase) — found 2026-09-19
       via PU 17449's pallet-availability check reporting "-122
       disponibles" on a real 29/151 split that sums exactly to the
       pallet's 180 real cases: 180 - (151 counted twice) = -122,
       meaning PU 17450 (deleted/regenerated earlier this session) had
       become exactly such a ghost duplicate. */
    var ex = current.find(function (x) { return String(x.id) === String(inv.id); });
    var next = ex ? current.map(function (x) { return String(x.id) === String(inv.id) ? saved : x; }) : current.concat([saved]);
    setInvoices(next); writeInvoicesCache(next);
    return saved;
  };
  var persistManyInv = async function (records) {
    if (records.length === 0) return [];
    var saved = await saveManyToSupabase("invoices", records);
    var map = {};
    saved.forEach(function (s) { map[s.id] = s; });
    var next = (invoicesRef.current || []).map(function (x) { return map[x.id] || x; });
    setInvoices(next); writeInvoicesCache(next);
    return saved;
  };
  var persistDeleteInv = async function (id) {
    await deleteFromSupabase("invoices", id);
    /* Same String() safety as persistSingleInv above — a raw !==
       could leave the "deleted" row sitting in local state forever
       if its id came from a different load path/type than `id`. */
    var next = (invoicesRef.current || []).filter(function (x) { return String(x.id) !== String(id); });
    setInvoices(next); writeInvoicesCache(next);
  };
  /* Cross-write to Supabase bills for the line sync (mirror of
     BillRegistry's syncInvoicesToSupabase). Best-effort. */
  var syncBillsToSupabase = function (updatedBills) {
    if (!updatedBills || updatedBills.length === 0) return;
    saveManyToSupabase("bills", updatedBills)
      .catch(function (e) { console.warn("[InvoiceRegistry] bill line-sync to Supabase failed:", e && e.message); });
  };

  /* Legacy localStorage-only migrations kept for new installs that
     bootstrap from a backup JSON. All flag-gated; no-ops once Supabase
     is the primary store. */
  // eslint-disable-next-line no-unused-vars
  var _legacyMigrationsBlockInv = function () {
    var loaded = loadLS(STORAGE_KEY, []);
    /* One-time migration: copy inv.commissionRate → each line.
       commissionRate when not yet set. Math no-op for totals. Same
       LS flag as BillRegistry's so the two migrations move in
       lockstep when both are first loaded. */
    try {
      var migDone = localStorage.getItem("gfi-mig-line-commission-inv-v1");
      if (!migDone) {
        var changed = false;
        loaded = loaded.map(function (inv) {
          if (!inv.lines || inv.lines.length === 0) return inv;
          var rate = inv.commissionRate;
          var newLines = inv.lines.map(function (l) {
            if (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "") {
              changed = true;
              return { ...l, commissionRate: rate };
            }
            return l;
          });
          return changed ? { ...inv, lines: newLines } : inv;
        });
        if (changed) {
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(loaded)); } catch (e) {}
        }
        try { localStorage.setItem("gfi-mig-line-commission-inv-v1", "1"); } catch (e) {}
      }
    } catch (e) {}
    /* "partialProtection" → "protection" one-time migration REMOVED
       2026-09-12 — that status is back, this time computed from
       pallet-level status (see saleStatusCalc.js), never manually
       chosen. Leaving the old migration running would have silently
       stripped every freshly-computed Partial Protection value back
       down to Protection on next load. */
    /* One-time migration: normalize legacy raw-label saleStatus values
       on invoice lines to the canonical keys used by SALE_STATUSES.
       Pre-fix Excel imports stored the human label which the badge can
       render but the edit-mode SearchSelect can't match. Mirror of
       ImportFromExcel.normalizeSaleStatus(). Flag-gated. */
    try {
      var nsDoneInv = localStorage.getItem("gfi-mig-ss-norm-inv-v1");
      if (!nsDoneInv) {
        var SS_CANON_INV = { goodDelivery: 1, protection: 1, pas: 1, partialProtection: 1, samples: 1, dumped: 1, donated: 1, transfer: 1 };
        var SS_MAP_INV = { gooddelivery: "goodDelivery", protection: "protection", pas: "pas", sample: "samples", samples: "samples", dumped: "dumped", donated: "donated", transfer: "transfer" };
        var anyNS = false;
        loaded = loaded.map(function (inv) {
          if (!inv.lines || inv.lines.length === 0) return inv;
          var hit = false;
          var nl = inv.lines.map(function (l) {
            if (!l || !l.saleStatus) return l;
            if (SS_CANON_INV[l.saleStatus]) return l;
            var key = String(l.saleStatus).toLowerCase().replace(/[^a-z0-9]/g, "");
            var mapped = SS_MAP_INV[key];
            if (mapped && mapped !== l.saleStatus) { hit = true; anyNS = true; return { ...l, saleStatus: mapped }; }
            return l;
          });
          return hit ? { ...inv, lines: nl } : inv;
        });
        if (anyNS) {
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(loaded)); } catch (e) {}
        }
        try { localStorage.setItem("gfi-mig-ss-norm-inv-v1", "1"); } catch (e) {}
      }
    } catch (e) {}
    setInvoices(loaded);
    setLoading(false);
  }; /* end _legacyMigrationsBlockInv \u2014 dead code, kept for reference */

  var resolvedInvoices = useMemo(function() {
    return invoices.map(function(inv) {
      var clientName = inv.clientName || resolveClientName(inv.clientId);
      return clientName !== inv.clientName ? { ...inv, clientName: clientName } : inv;
    });
  }, [invoices]);

  var handleSave = async function(inv) {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await persistSingleInv(inv);
    /* 2026-09-12 — REPLACES the old per-index Invoice→Bill saleStatus
       push (it paired "1st Blackberry line" on each side and copied
       whichever invoice saved last, which isn't real aggregation — two
       POs for the same lot/product would just fight over one bill line
       instead of the bill reflecting BOTH). Now: recompute every
       affected bill's line statuses from ALL invoices currently tied to
       that lot (see saleStatusCalc.js's deriveDeliveryStatus and
       BillRegistry.js's matching recompute-on-bill-save, which is the
       defensive half of this same logic). This is what makes a pallet
       status edit on one PO immediately show up as the bill's
       "Partial Protection" even though a sibling PO on the same lot is
       still Good Delivery. */
    try {
      var allInvoicesForPush = loadLS("gfi-invoices", []);
      var allInvoicesFresh = allInvoicesForPush.map(function(x) { return x.id === inv.id ? inv : x; });
      var affectedLots = {};
      (inv.lines || []).forEach(function(il) { var lot = il.lot || inv.lot; if (lot) affectedLots[String(lot)] = true; });
      var allBills = loadLS("gfi-bills", []);
      var changed = false;
      var updated = allBills.map(function(b) {
        if (!b.lot || !affectedLots[String(b.lot)]) return b;
        var invoiceLinesForLot = [];
        allInvoicesFresh.forEach(function(otherInv) {
          (otherInv.lines || []).forEach(function(il) {
            if (String(il.lot || otherInv.lot) === String(b.lot)) invoiceLinesForLot.push(il);
          });
        });
        var billChanged = false;
        var newLines = (b.lines || []).map(function(bl) {
          if (DISPOSITION_STATUSES.indexOf(bl.saleStatus) !== -1) return bl;
          var billKey = String(bl.productName || bl.productBase || "").trim().toLowerCase();
          var billCat = String(bl.category || "").trim().toLowerCase();
          var billSize = String(bl.size || "").trim().toLowerCase();
          var matched = invoiceLinesForLot.filter(function(il) {
            var k = String(il.productName || il.productBase || "").trim().toLowerCase();
            if (!k || k !== billKey) return false;
            if (billCat && String(il.category || "").trim().toLowerCase() !== billCat) return false;
            if (billSize && String(il.size || "").trim().toLowerCase() !== billSize) return false;
            return true;
          });
          var derived = deriveDeliveryStatus(matched);
          if (derived === null || derived === bl.saleStatus) return bl;
          billChanged = true;
          return { ...bl, saleStatus: derived };
        });
        if (billChanged) { changed = true; return { ...b, lines: newLines }; }
        return b;
      });
      if (changed) {
        localStorage.setItem("gfi-bills", JSON.stringify(updated));
        var changedBills = updated.filter(function (b) {
          var orig = allBills.find(function (o) { return o.id === b.id; });
          return orig && orig !== b;
        });
        syncBillsToSupabase(changedBills);
      }
    } catch(e) {}
    /* Push to the SweetLeaf twin (2026-09-12) — Four Seasons invoices
       copied to sweetleaf_invoices via "Copy to SL" (see the kebab-menu
       action above) are a one-time clone with no ongoing sync; Arturo:
       "las afectaciones a los invoices de Four Seasons en Ground-Fresh
       se trasladan a sus dobles en SL". Matched by index — the SL copy
       was built by mapping fresh.lines 1:1 at copy time (see slCopyModal
       above), so line i on one side is line i on the other, no
       product/category/size matching needed like the Bill push above. */
    if (inv.slImported) {
      try {
        /* loadLS("gfi-sl-invoices") isn't reliable here — that local
           cache is only populated once the SweetLeaf module has been
           opened in this browser session, so on a fresh session it's
           silently empty and this push would no-op. Read straight from
           Supabase instead (same "always live" rule as everything else
           in this feature). */
        var allSlInvoices = await loadFromSupabase("sweetleaf_invoices");
        var matchSl = allSlInvoices.find(function(x) { return String(x.gfiInvoiceId) === String(inv.id); });
        if (matchSl) {
          var slChanged = false;
          /* 2026-09-14 bug found live (Arturo: PU 17442/INV-16247 showed
             "Partial Protection" on the SL line while every pallet was
             genuinely Pending Delivery) — this used to only push when
             gfLine.saleStatus was TRUTHY, so a line that legitimately
             reverts to unconfirmed/blank on the GFI side (recomputed to
             "" by recomputeLineDeliveryStatuses, or cleared during a
             data-cleanup) never propagated that clearing to SL — the
             mirror stayed stuck on whatever non-blank value it last
             had, forever, since a blank push was silently skipped.
             Compare normalized ("" for falsy) values on BOTH sides so
             a clear pushes exactly like a real status does. */
          var newSlLines = (matchSl.lines || []).map(function(sl, i) {
            var gfLine = (inv.lines || [])[i];
            if (!gfLine) return sl;
            var gfStatus = gfLine.saleStatus || "";
            var slStatus = sl.saleStatus || "";
            if (gfStatus === slStatus) return sl;
            slChanged = true;
            return { ...sl, saleStatus: gfStatus };
          });
          /* Pickup date/time (2026-09-12) — invoice-level, not per-line,
             so it's not covered by the saleStatus loop above. Arturo:
             "fecha y hora de pick no viajo a sweetleaf" — the initial
             Copy to SL clone now seeds these (see slCopyModal's Copy
             button above), but an invoice can get its BOL pickup info
             filled in AFTER it was already copied, so this ongoing
             push needs to carry it over too, same as saleStatus. */
          var slPatch = {};
          if ((inv.pickupDate || "") !== (matchSl.pickupDate || "")) { slPatch.pickupDate = inv.pickupDate || ""; slChanged = true; }
          if ((inv.pickupTime || "") !== (matchSl.pickupTime || "")) { slPatch.pickupTime = inv.pickupTime || ""; slChanged = true; }
          /* Temp Recorder / Logger # (2026-09-12, Arturo: "es conveniente
             agregar el temp recorder directo en la invoice de ground
             fresh y de ahi que se propague a sl") — same invoice-level
             push as pickup date/time above. SweetLeafPortal.js's own
             Packing List already reads matchSl.thermograph first (with
             a PO-lookup fallback for older invoices), so this alone is
             enough to make it show up there. */
          if ((inv.thermograph || "") !== (matchSl.thermograph || "")) { slPatch.thermograph = inv.thermograph || ""; slChanged = true; }
          /* clientReportedDate/clientArrivalDate/clientArrivalTime
             (2026-09-14) — captured on the GFI invoice's Protection
             Notice dialog, needed on the SL side for the new "Protection
             status reminder" email (SweetLeafPortal.js), same push
             pattern as pickupDate/pickupTime/thermograph above. */
          if ((inv.clientReportedDate || "") !== (matchSl.clientReportedDate || "")) { slPatch.clientReportedDate = inv.clientReportedDate || ""; slChanged = true; }
          if ((inv.clientReportedTime || "") !== (matchSl.clientReportedTime || "")) { slPatch.clientReportedTime = inv.clientReportedTime || ""; slChanged = true; }
          if ((inv.clientArrivalDate || "") !== (matchSl.clientArrivalDate || "")) { slPatch.clientArrivalDate = inv.clientArrivalDate || ""; slChanged = true; }
          if ((inv.clientArrivalTime || "") !== (matchSl.clientArrivalTime || "")) { slPatch.clientArrivalTime = inv.clientArrivalTime || ""; slChanged = true; }
          if (slChanged) {
            var updatedSl = { ...matchSl, ...slPatch, lines: newSlLines };
            await saveToSupabase("sweetleaf_invoices", updatedSl);
            try {
              var slCache = loadLS("gfi-sl-invoices", []);
              var newSlCache = slCache.map(function(x) { return x.id === updatedSl.id ? updatedSl : x; });
              localStorage.setItem("gfi-sl-invoices", JSON.stringify(newSlCache));
            } catch (e2) {}
          }
        }
      } catch (e) {}
    }
      setShowForm(false); setEditing(null); setToast(t.saved);
      /* Only the standalone new/edit form (showForm) is truly closing
         here — DetailPanel's own onSave re-opens viewing right after
         calling handleSave (setViewing(updated)), so a save made there
         must never redirect away. */
      if (showForm) closeToOrigin();
    } catch (e) {
      setError("Could not save invoice: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  var handleDelete = async function(id) {
    if (saving) return;
    setSaving(true); setError(null);
    /* If the invoice was linked to a Client-Pricing PO, release the
       link so the PO reverts to "pending" + clears the PU# and shows
       up in other invoices' PO picker again. */
    try {
      var existing = invoices.find(function (x) { return x.id === id; });
      if (existing && existing.linkedPoLineId) {
        await syncPoLinkage(existing.linkedPoLineId, "", { id: id, puNumber: "", status: "draft" });
      }
    } catch (e) {}
    try {
      await persistDeleteInv(id);
      setShowForm(false); setEditing(null); setViewing(null); setToast(t.deleted);
      closeToOrigin();
    } catch (e) {
      setError("Could not delete invoice: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  /* Confirm batch deposit: append to inv.payments, update amountPaid + status, and log to gfi-payments */
  var handleBatchDepositConfirm = async function(payload) {
    var allPayments = loadLS("gfi-payments", []);
    var newPayId = nextPaymentId(allPayments);
    var clientNames = [...new Set(payload.allocations.map(function(a) { return a.clientName; }).filter(Boolean))];
    var paymentRecord = {
      id: newPayId,
      paymentDate: payload.paymentDate,
      type: "inbound",
      paymentMethod: payload.paymentMethod,
      reference: payload.reference,
      notes: payload.notes,
      allocations: payload.allocations.map(function(a) { return { invoiceId: a.invoiceId, puNumber: a.puNumber, lot: a.lot, clientName: a.clientName, amount: a.amount, concept: a.concept || "" }; }),
      shipperName: clientNames.join(", "),
      createdAt: new Date().toISOString(),
    };
    /* concept is per-allocation (per invoice row), not per whole
       payment — each invoice's payments[] entry carries its own
       concept for the confirmation PDF to read back later. */
    var allocByInvId = {};
    payload.allocations.forEach(function(a) { allocByInvId[a.invoiceId] = a; });
    var toUpdate = invoices.filter(function(inv) { return allocByInvId[inv.id]; }).map(function(inv) {
      var alloc = allocByInvId[inv.id];
      var amt = alloc.amount;
      /* Dedupe by paymentId — defensive against retries / double-
         clicks. If a row with this paymentId already exists, drop
         it before appending the fresh one. */
      var existingPays = (inv.payments || []).filter(function (p) { return p.paymentId !== newPayId; });
      var newPayments = [...existingPays, {
        paymentId: newPayId,
        date: payload.paymentDate,
        amount: amt,
        method: payload.paymentMethod,
        reference: payload.reference,
        concept: alloc.concept || "",
      }];
      var totalPaid = newPayments.reduce(function(s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var totalDue = calcInvoiceTotal(inv);
      var due = totalDue - totalPaid;
      var newStatus = inv.status;
      if (inv.status !== "draft" && due <= 0.01 && totalPaid > 0) newStatus = "paid";
      return { ...inv, payments: newPayments, amountPaid: +totalPaid.toFixed(2), status: newStatus };
    });
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await persistManyInv(toUpdate);
      try { await saveToSupabase("payments", paymentRecord); } catch (e) { /* swallow */ }
      saveLS("gfi-payments", [...allPayments, paymentRecord]);
      setSelected({});
      setShowBatchDeposit(false);
      setToast(t.depositSaved + " (" + newPayId + ")");
      setConfirmationPayment(paymentRecord);
    } catch (e) {
      setError("Could not record deposit: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  /* Expose for cross-module use. createFromBill force-reloads invoices
     from Supabase first so nextPU(...) is computed against the latest
     committed state — the closure-captured `invoices` array can be
     stale right after a save (state hasn't propagated yet) or on
     fresh mount before the initial Supabase fetch settled. Without
     this re-fetch, a second invoice created right after the first
     was reusing the prior PU number. */
  var createFromBill = useCallback(async function(bill) {
    var latestInvoices = invoicesRef.current || invoices;
    try {
      var fresh = await loadFromSupabase("invoices", { force: true });
      if (Array.isArray(fresh) && fresh.length > 0) latestInvoices = fresh;
    } catch (e) { /* offline / Supabase down — fall back to the cached state */ }
    var newInv = {
      id: "", puNumber: nextPU(latestInvoices), date: bill.billDate || new Date().toISOString().substring(0, 10),
      dueDate: "", clientId: "", clientInitials: "", clientRef: "", linkedPoLineId: "",
      lot: bill.lot, shipmentId: bill.shipmentId, billId: bill.id,
      shipperName: bill.shipperName, status: "draft",
      lessAdvance: "", otherFees: "", lessCredits: "", remarks: "",
      pickupDate: "", pickupTime: "", thermograph: "",
      /* Stamp lot / shipmentId / shipperName onto every inherited line.
         Bills carry the lot at the top level only — their lines spread
         straight from shipment.lines (BillRegistry line ~1144) without
         per-line lot. The LOT column on the invoice form reads from
         line.lot, so without this stamping every line on a bill→
         invoice handoff renders with an empty LOT cell and the user
         has to delete + re-add the line via the lot dropdown.
         commissionRate is intentionally CLEARED — the bill's rate is
         the shipper's commission (% GFI keeps from the supplier);
         the invoice's rate (if any) is what GFI charges the CLIENT.
         Carrying the bill's rate over would prefill the client invoice
         with the wrong number. */
      lines: (bill.lines || []).map(function(l) { return {
        ...l,
        salePrice: "",
        commissionRate: "",
        lot: l.lot || bill.lot,
        shipmentId: l.shipmentId || bill.shipmentId,
        shipperName: l.shipperName || bill.shipperName,
      }; }),
      fees: (bill.fees || []).map(function(fe) { return {
        feeType: fe.feeType || "",
        feeCustom: fe.feeCustom || "",
        qty: fe.qty || 1,
        rate: fe.rate || "",
        amount: fe.amount || 0,
        pctRate: fe.pctRate || "",
        fromBill: true,
      }; }),
    };
    setEditing(null); setShowForm(false);
    setTimeout(function() { setEditing(newInv); setShowForm(true); }, 50);
  }, [invoices]);
  useEffect(function() { window.__gfiInvoiceCreate = createFromBill; return function() { delete window.__gfiInvoiceCreate; }; }, [createFromBill]);

  /* Open-by-id hook for global search */
  useEffect(function() {
    window.__gfiInvoiceOpen = function(id) {
      var ret = window.__gfiReturnModule; window.__gfiReturnModule = null;
      var src = invoicesRef.current && invoicesRef.current.length ? invoicesRef.current : loadLS(STORAGE_KEY, []);
      var inv = src.find(function(x) { return x.id === id; });
      if (inv) { returnModuleRef.current = ret; setViewing(inv); }
    };
    return function() { delete window.__gfiInvoiceOpen; };
  }, []);

  /* 2026-10-20 — Arturo: "todo lo que se abre desde aqui, al cerrarlo
     debe regresar a la factura abierta" — same window.__gfiBillReopenId
     pattern BillRegistry.js already has for its own Shipment Info Edit
     button: Linked Bill/Linked Lot/the PO link set
     window.__gfiInvoiceReopenId right before navigating away, this
     effect picks it back up once `invoices` has actually loaded and
     reopens that exact record (not just the Invoices list). */
  useEffect(function() {
    if (!window.__gfiInvoiceReopenId) return;
    if (!invoices || invoices.length === 0) return;
    var id = window.__gfiInvoiceReopenId; window.__gfiInvoiceReopenId = null;
    var inv = invoices.find(function(x) { return x.id === id; });
    if (inv) { returnModuleRef.current = null; setViewing(inv); }
  }, [invoices]);

  /* Blank-form create hook for Create New menu */
  useEffect(function() {
    window.__gfiInvoiceNew = function() {
      returnModuleRef.current = window.__gfiReturnModule; window.__gfiReturnModule = null;
      setEditing(null); setShowForm(true);
    };
    return function() { delete window.__gfiInvoiceNew; };
  }, []);

  /* Season scope, isolated from the other row filters — same pattern
     validated in PaymentRegistry.js / BillRegistry.js. `summary` (the
     status tabs below) keys off THIS array, not the fully-filtered
     `filtered`: the tabs set cardFilter on click, so if summary read
     from a list that already had cardFilter baked in, clicking a tab
     would collapse its own count instead of letting the user compare
     Draft vs Sent vs Overdue vs Paid within the chosen season(s).
     Empty selection = all seasons (no restriction). */
  var seasonFilteredInvoices = useMemo(function() {
    if (filterSeasons.length === 0) return resolvedInvoices;
    return resolvedInvoices.filter(function(x) { return filterSeasons.indexOf(resolveSeasonId(seasons, x.date, x.seasonId)) >= 0; });
  }, [resolvedInvoices, filterSeasons, seasons]);

  /* Live sale-status set for one invoice — same raw-line-first,
     resolvePendingStage-fallback logic the multi-badge Status column
     renders (2026-09-27, Arturo: pendingPickUp/pendingDelivery/
     partialProtection never matched the filter). */
  var shpsForWeekCacheInv = useMemo(function() { return loadLS("gfi-shipments", []); }, []);
  var liveStatusesForInvoice = useCallback(function(x) {
    /* 2026-10-20, Arturo: "para twin river el sales status cambia en
       el embarque y de ahi debe replicarse al bill y a la factura" —
       checked live (resolveTwinRiverLotStatus), never from a stored
       line.saleStatus, since nobody resaves the Invoice when the
       Shipment's own status changes. */
    /* 2026-10-30 — per-product (twinRiverProductStatus), not a single
       lot-wide value, same fix as the list column/view-mode above.
       2026-11-03, PU 17484: resolveTwinRiverShipment(x, ...) resolved
       ONE shipment (x's home lot) for every line — on a multi-lot
       invoice a second lot's real ClariFresh status never factored
       in. Resolved per-line now (own l.lot), falling back to the
       line's own saleStatus when that line isn't a ClariFresh lot. */
    var statuses = (x.lines || []).map(function(l) {
      var shp = resolveTwinRiverShipmentForLot(l.lot || x.lot, x, shpsForWeekCacheInv);
      return shp !== undefined ? twinRiverProductStatus(shp, l.productBase || l.productName) : l.saleStatus;
    }).filter(Boolean);
    if (statuses.length === 0) statuses = [resolvePendingStage([x], slPassingIndexList)];
    return statuses;
  }, [slPassingIndexList, shpsForWeekCacheInv]);
  var weekOfInvoice = useCallback(function(x) {
    var shp = x.lot ? shpsForWeekCacheInv.find(function(s) { return String(s.lot) === String(x.lot); }) : null;
    var d = shp ? (shp.dateArrival || shp.dateDeparture || x.date) : x.date;
    return d ? ("W" + getISOWeekInv(d)) : "";
  }, [shpsForWeekCacheInv]);

  /* Excel-style live filter set (2026-09-27, Arturo: "todos los filtros
     deben ser dinamicos y actualizarse en vivo... si filtro la
     temporada... no es necesario me muestre nombres... que no aparecen
     en la tabla, clientes es lo mismo, semanas igual, sale status
     igual" + "agregar tambien que pueda seleccionar mas de un filtro
     de un concepto... tener casillas de seleccion"). Client/Sale
     Status/Week are multi-select arrays now (same SearchSelect `multi`
     checkbox UI Season already uses); each dropdown's own option list
     is built by applying every OTHER active filter to the season
     scope, same AutoFilter mechanic as Excel/Google Sheets. */
  var applyInvFilters = useCallback(function(list, exclude) {
    if (exclude !== "search" && search) { var s = search.toLowerCase(); list = list.filter(function(x) { return String(x.puNumber || "").includes(s) || (x.clientName || "").toLowerCase().includes(s) || String(x.lot || "").includes(s) || (x.clientRef || "").toLowerCase().includes(s); }); }
    if (exclude !== "client" && filterClient.length > 0) list = list.filter(function(x) { return filterClient.indexOf(x.clientName) !== -1; });
    if (exclude !== "status" && filterStatus) list = list.filter(function(x) { return x.status === filterStatus; });
    if (exclude !== "saleStatus" && filterSaleStatus.length > 0) list = list.filter(function(x) { var live = liveStatusesForInvoice(x); return filterSaleStatus.some(function(fs) { return live.indexOf(fs) !== -1; }); });
    if (exclude !== "date" && (dateFrom || dateTo)) list = list.filter(function(x) { var d = x.date || ""; if (!d) return false; if (dateFrom && d < dateFrom) return false; if (dateTo && d > dateTo) return false; return true; });
    if (exclude !== "week" && filterWeek.length > 0) list = list.filter(function(x) { return filterWeek.indexOf(weekOfInvoice(x)) !== -1; });
    if (exclude !== "card" && cardFilter) {
      if (cardFilter === "overdue") {
        var todayD = new Date(); todayD.setHours(0, 0, 0, 0);
        list = list.filter(function(x) {
          if (x.status !== "sent") return false;
          if (!x.dueDate) return false;
          if (new Date(x.dueDate + "T00:00:00") >= todayD) return false;
          var total = calcInvoiceTotal(x);
          var paid = paidAmount(x);
          return (total - paid) > 0.01;
        });
      } else if (cardFilter === "paid") {
        list = list.filter(function(x) {
          if ((x.status || "draft") === "paid") return true;
          var total = calcInvoiceTotal(x);
          var paid = paidAmount(x);
          return paid > 0 && (total - paid) <= 0.01;
        });
      } else if (cardFilter === "sent") {
        list = list.filter(function(x) {
          if (x.status !== "sent") return false;
          var total = calcInvoiceTotal(x);
          var paid = paidAmount(x);
          return (total - paid) > 0.01;
        });
      } else {
        list = list.filter(function(x) { return (x.status || "draft") === cardFilter; });
      }
    }
    return list;
  }, [search, filterClient, filterStatus, filterSaleStatus, dateFrom, dateTo, filterWeek, cardFilter, liveStatusesForInvoice, weekOfInvoice]);

  var uniqueClients = useMemo(function() { return [...new Set(applyInvFilters(seasonFilteredInvoices, "client").map(function(i) { return i.clientName; }).filter(Boolean))].sort(); }, [seasonFilteredInvoices, applyInvFilters]);

  var uniqueWeeks = useMemo(function() {
    var weeks = new Set();
    applyInvFilters(seasonFilteredInvoices, "week").forEach(function(inv) { var w = weekOfInvoice(inv); if (w) weeks.add(w); });
    return Array.from(weeks).sort(function(a, b) { return (parseInt(b.slice(1), 10) || 0) - (parseInt(a.slice(1), 10) || 0); });
  }, [seasonFilteredInvoices, applyInvFilters, weekOfInvoice]);

  var dynamicSaleStatusOptionsInv = useMemo(function() {
    var present = new Set();
    applyInvFilters(seasonFilteredInvoices, "saleStatus").forEach(function(x) { liveStatusesForInvoice(x).forEach(function(s) { present.add(s); }); });
    return FILTER_SALE_STATUSES.filter(function(s) { return present.has(s); });
  }, [seasonFilteredInvoices, applyInvFilters, liveStatusesForInvoice]);

  var filtered = useMemo(function() { return applyInvFilters(seasonFilteredInvoices, null); }, [seasonFilteredInvoices, applyInvFilters]);

  var sorted = useMemo(function() {
    return [...filtered].sort(function(a, b) {
      var va, vb;
      switch (sortCol) {
        case "puNumber": va = parseInt(a.puNumber, 10) || 0; vb = parseInt(b.puNumber, 10) || 0; return sortDir === "asc" ? va - vb : vb - va;
        case "season": va = seasonNameForInv(a.date, a.seasonId); vb = seasonNameForInv(b.date, b.seasonId); break;
        case "date": va = a.date || ""; vb = b.date || ""; break;
        case "week": va = a.date ? getISOWeekInv(a.date) : 0; vb = b.date ? getISOWeekInv(b.date) : 0; return sortDir === "asc" ? va - vb : vb - va;
        case "dueDate": va = a.dueDate || ""; vb = b.dueDate || ""; break;
        case "clientName": va = a.clientName || ""; vb = b.clientName || ""; break;
        case "lot": va = parseInt(a.lot, 10) || 0; vb = parseInt(b.lot, 10) || 0; return sortDir === "asc" ? va - vb : vb - va;
        default: va = a[sortCol] || ""; vb = b[sortCol] || "";
      }
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
  }, [filtered, sortCol, sortDir, seasonNameForInv]);

  var toggleSort = function(col) { if (sortCol === col) setSortDir(function(d) { return d === "asc" ? "desc" : "asc"; }); else { setSortCol(col); setSortDir("asc"); } };
  var arrow = function(col) { return sortCol === col ? (sortDir === "asc" ? " \u25b2" : " \u25bc") : ""; };

  var allSelected = sorted.length > 0 && sorted.every(function(i) { return selected[i.id]; });
  var toggleAll = function() { var ns = {}; if (!allSelected) sorted.forEach(function(i) { ns[i.id] = true; }); setSelected(ns); };
  var toggleOne = function(id, e) { if (e) e.stopPropagation(); setSelected(function(p) { var ns = Object.assign({}, p); if (ns[id]) delete ns[id]; else ns[id] = true; return ns; }); };
  var selectedCount = Object.keys(selected).length;
  var clearSelection = function() { setSelected({}); };
  var selectedInvoices = useMemo(function() { return resolvedInvoices.filter(function(inv) { return selected[inv.id]; }); }, [resolvedInvoices, selected]);

  /* Aggregate breakdown for the selection bar AND the module-total
     chip. Returns { amount (gross), deductions (commission + fees +
     transfer debits), paid, due }. Used by both the selection chip
     (sum over selectedInvoices) and the module-total chip (sum over
     all resolvedInvoices) — single function, no divergence. */
  function aggregateInvoices(list) {
    var amount = 0, deductions = 0, paid = 0, due = 0, cases = 0;
    (list || []).forEach(function(inv) {
      /* Sum per-line commission (each line carries its own
         commissionRate post-migration). Legacy fallback to
         invoice-level commissionRate when a line's rate is blank. */
      var invDefault = parseFloat(inv.commissionRate) || 0;
      var gross = 0; var commission = 0;
      (inv.lines || []).forEach(function (l) {
        if (l.saleStatus === "dumped" || l.saleStatus === "donated" || l.saleStatus === "samples" || l.saleStatus === "transfer") return;
        var lineCases = parseInt(l.cases, 10) || 0;
        var lineAmt = lineCases * (parseFloat(l.salePrice) || 0);
        gross += lineAmt;
        cases += lineCases;
        var rate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
          ? invDefault
          : (parseFloat(l.commissionRate) || 0);
        commission += lineAmt * (rate / 100);
      });
      var tFees = 0; (inv.fees || []).forEach(function(fe) { tFees += Math.abs(parseFloat(fe.amount) || 0); });
      var tCredits = 0; (inv.credits || []).forEach(function(cr) { tCredits += Math.abs(parseFloat(cr.amount) || 0); });
      var p = paidAmount(inv);
      var total = calcInvoiceTotal(inv);
      amount += gross;
      /* Deductions = everything that REDUCES gross sales: commission
         + fees − credits (credits add back). Floored at 0 so a
         credit-heavy invoice doesn't show negative deductions. */
      deductions += commission + tFees - tCredits;
      paid += p;
      due += (total - p);
    });
    return { amount: amount, deductions: deductions, paid: paid, due: due, cases: cases };
  }
  var selectedTotals = useMemo(function() { return aggregateInvoices(selectedInvoices); }, [selectedInvoices]);
  var grandTotals = useMemo(function() { return aggregateInvoices(sorted); }, [sorted]);
  /* Backwards-compat aliases for any external code paths still
     referencing these. */
  var selectedDueTotal = selectedTotals.due;
  var grandDueTotal = grandTotals.due;
  var [showBulkDelete, setShowBulkDelete] = useState(false);
  var [showBulkStatus, setShowBulkStatus] = useState(false);
  var bulkLocked = useMemo(function() {
    return selectedInvoices.filter(function(inv) { return (inv.payments || []).length > 0; }).map(function(inv) { return { id: inv.id, label: "PU# " + (inv.puNumber || inv.id) }; });
  }, [selectedInvoices]);

  /* Excel export. Convention: if there is a selection, export that;
     otherwise export the full filtered view. Summary follows the spec
     exactly: PU#, Date, Client, Customer Ref, Lot, Gross Sales,
     Commission, Advance Financing Fee, Fees, Other Fees, Credits,
     Balance Transfers, Total Due. Detail expands every invoice into
     Product / Commission / Fee / Credit / Transfer rows discriminated
     by the Type column. */
  var invoicesToExport = function () { return selectedInvoices.length > 0 ? selectedInvoices : sorted; };
  var buildInvSummaryRows = function () {
    return invoicesToExport().map(function (inv) {
      var gross = 0;
      (inv.lines || []).forEach(function (l) {
        if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
          gross += (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
        }
      });
      var commRate = parseFloat(inv.commissionRate) || 0;
      var commission = gross * (commRate / 100);
      var advFee = 0; var knownFees = 0; var otherFees = 0;
      (inv.fees || []).forEach(function (fe) {
        var amt = Math.abs(parseFloat(fe.amount) || 0);
        if (isAdvanceFinancingFeeInv(fe)) advFee += amt;
        else if (isKnownFeeName(fe)) knownFees += amt;
        else otherFees += amt;
      });
      var totalCredits = 0;
      (inv.credits || []).forEach(function (cr) { totalCredits += Math.abs(parseFloat(cr.amount) || 0); });
      var totalTransfers = 0;
      (inv.balanceTransfers || []).forEach(function (tr) {
        var a = parseFloat(tr.amount) || 0;
        if (tr.type === "credit") totalTransfers += a;
        else if (tr.type === "debit") totalTransfers -= a;
      });
      var totalDue = calcInvoiceTotal(inv);
      return {
        "PU#": xs(inv.puNumber),
        Date: xs(inv.date),
        Client: xs(resolveClientName(inv.clientId) || inv.clientName),
        "Customer Ref": xs(inv.clientRef),
        Lot: xs(inv.lot),
        "Gross Sales": num2(gross),
        Commission: num2(commission),
        "Advance Financing Fee": num2(advFee),
        Fees: num2(knownFees),
        "Other Fees": num2(otherFees),
        Credits: num2(totalCredits),
        "Balance Transfers": num2(totalTransfers),
        "Total Due": num2(totalDue),
        Status: xs(inv.status),
      };
    });
  };
  var buildInvDetailRows = function () {
    var out = [];
    invoicesToExport().forEach(function (inv) {
      var meta = { "PU#": xs(inv.puNumber), Date: xs(inv.date), Client: xs(resolveClientName(inv.clientId) || inv.clientName), "Customer Ref": xs(inv.clientRef), Lot: xs(inv.lot) };
      (inv.lines || []).forEach(function (l) {
        var amt = (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
        /* Product = commodity (Blackberry / Blueberry / Raspberry).
           Description = FULL presentation (pack/label) via the
           resolver — never falls back to commodity, so a pivot by
           Description groups by real SKU. Comm % / Comm $ are the
           per-line rate and computed amount (fallback to inv-level). */
        var lineRate = (l.commissionRate === undefined || l.commissionRate === null || l.commissionRate === "")
          ? (parseFloat(inv.commissionRate) || 0)
          : (parseFloat(l.commissionRate) || 0);
        var lineComm = invLineCommissionAmount(l, inv.commissionRate);
        out.push({ ...meta, Type: "Product", Product: xs(l.productBase || l.productName), Description: xs(resolveInvLineDesc(l)), Category: xs(l.category), Cases: intOr(l.cases), Pallets: intOr(l.pallets), Rate: num2(l.salePrice), Amount: num2(amt), "Comm %": lineRate, "Comm $": num2(-lineComm), "Sale Status": xs(l.saleStatus) });
      });
      /* Aggregated total commission row (sum of per-line) for quick
         pivot consumption. */
      var totalComm = 0;
      (inv.lines || []).forEach(function (l) { totalComm += invLineCommissionAmount(l, inv.commissionRate); });
      if (totalComm > 0) {
        out.push({ ...meta, Type: "Commission", Product: "", Description: "Total Commission", Category: "", Cases: "", Pallets: "", Rate: "", Amount: num2(-totalComm), "Comm %": "", "Comm $": "", "Sale Status": "" });
      }
      (inv.fees || []).forEach(function (fe) {
        out.push({ ...meta, Type: "Fee", Product: "", Description: feeNameInv(fe), Category: "", Cases: "", Pallets: intOr(fe.qty), Rate: num2(fe.rate), Amount: num2(-Math.abs(parseFloat(fe.amount) || 0)), "Comm %": "", "Comm $": "", "Sale Status": "" });
      });
      (inv.credits || []).forEach(function (cr) {
        var label = cr.type === "fromInvoice" ? ("Credit from PU# " + (cr.refPuNumber || "—")) : (cr.concept || "Credit");
        out.push({ ...meta, Type: "Credit", Product: "", Description: label, Category: "", Cases: "", Pallets: "", Rate: "", Amount: num2(Math.abs(parseFloat(cr.amount) || 0)), "Comm %": "", "Comm $": "", "Sale Status": "" });
      });
      (inv.balanceTransfers || []).forEach(function (tr) {
        var amt = parseFloat(tr.amount) || 0;
        var signed = tr.type === "credit" ? amt : -amt;
        out.push({ ...meta, Type: "Transfer", Product: "", Description: (tr.type === "credit" ? "Credit from PU# " : "Debit to PU# ") + (tr.otherPuNumber || "?"), Category: "", Cases: "", Pallets: "", Rate: "", Amount: num2(signed), "Comm %": "", "Comm $": "", "Sale Status": "" });
      });
    });
    return out;
  };
  /* Bulk status change. Records with payments can only move to "paid". */
  /* Build the context bundle for the GFI sales docs (Inbound Sheet
     + Packing List). One read of each LS source per print so a bulk
     run doesn't re-read N times. */
  var buildSalesDocCtx = useCallback(function () {
    return {
      clients: getClients(),
      shipments: loadLS("gfi-shipments", []),
      warehouses: loadLS("gfi-warehouses", []),
      company: loadCompanyProfile(),
    };
  }, []);
  /* Pending pick-or-inbound state. The dropdown emits a placeholder
     "<brand>-pickorinbound" when the user has to choose Pick Ticket
     vs Inbound; we stash brand + scope here, show the modal, and on
     confirm dispatch the actual print with the resolved kind. */
  var [pickKindModal, setPickKindModal] = useState(null);
  /* Second-stage modal — only shown after the user picks "Pick Ticket"
     in the kind modal. Holds the same { brand, invoices } payload so
     the eventual print call carries through unchanged. */
  var [remarksModal, setRemarksModal] = useState(null);
  var doPrint = function (invs, kind, extras) {
    var ctx = buildSalesDocCtx();
    var fallbackPrint = function () {
      printInvoiceDocs(invs, kind, ctx, function (err) {
        if (err === "popup-blocked") setToast(lang === "es" ? "⚠ El navegador bloqueó la ventana. Habilita pop-ups." : "⚠ Pop-up blocked. Allow pop-ups for this site.");
      }, extras);
    };
    /* Digital-first: save each invoice doc straight to its client Drive
       folder. Sign in on the spot if there's no live session (Nexus
       drops it on reload), same as the upload button. No print window
       (it can't be dismissed on tablets/PWA). Only fall back to the
       print window when Drive isn't configured or sign-in fails. */
    if (driveConfigured()) {
      (async function () {
        var ok = 0, fail = 0;
        setToast(lang === "es" ? "Guardando en Drive…" : "Saving to Drive…");
        try {
          if (!driveSignedIn()) { await driveSignIn(); }
        } catch (e) {
          console.error("[Drive sign-in] failed", e);
          setToast((lang === "es" ? "⚠ No se pudo conectar a Drive: " : "⚠ Could not connect to Drive: ") + (e.message || String(e)));
          fallbackPrint();
          return;
        }
        for (var i = 0; i < invs.length; i++) {
          try { await uploadInvoiceDocToDrive(invs[i], kind, ctx, extras); ok++; }
          catch (e) { fail++; console.error("[Drive upload] invoice doc failed", invs[i].id, kind, e); }
        }
        setToast((lang === "es" ? "✓ Guardado en Drive: " : "✓ Saved to Drive: ") + ok + (fail ? " · " + fail + (lang === "es" ? " con error" : " failed") : ""));
      })();
    } else {
      fallbackPrint();
    }
  };
  var handleSalesDocSingle = function (inv, kind) {
    if (kind === "gfi-pickorinbound" || kind === "sl-pickorinbound") {
      var brandKey = kind.indexOf("sl-") === 0 ? "sl" : "gfi";
      setPickKindModal({ brand: brandKey, invoices: [inv] });
      return;
    }
    doPrint([inv], kind);
  };
  var handleSalesDocBulk = function (kind) {
    if (selectedInvoices.length === 0) return;
    if (kind === "gfi-pickorinbound" || kind === "sl-pickorinbound") {
      var brandKey = kind.indexOf("sl-") === 0 ? "sl" : "gfi";
      setPickKindModal({ brand: brandKey, invoices: selectedInvoices });
      return;
    }
    doPrint(selectedInvoices, kind);
  };

  var handleBulkStatusChange = async function(newStatus) {
    if (saving) return;
    setSaving(true); setError(null);
    var lockedIds = {};
    if (newStatus !== "paid") bulkLocked.forEach(function(l) { lockedIds[l.id] = true; });
    var toUpdate = invoices.filter(function(inv) { return selected[inv.id] && !lockedIds[inv.id]; }).map(function(inv) { return { ...inv, status: newStatus }; });
    try {
      await persistManyInv(toUpdate);
      setShowBulkStatus(false); setSelected({}); setToast(t.saved);
    } catch (e) {
      setError("Could not bulk update: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  var handleBulkDelete = async function() {
    if (saving) return;
    setSaving(true); setError(null);
    var lockedIds = {};
    bulkLocked.forEach(function(l) { lockedIds[l.id] = true; });
    var toDelete = invoices.filter(function(inv) { return selected[inv.id] && !lockedIds[inv.id]; });
    try {
      await Promise.all(toDelete.map(function (inv) { return deleteFromSupabase("invoices", inv.id); }));
      var deletedSet = {};
      toDelete.forEach(function (inv) { deletedSet[inv.id] = true; });
      var keep = invoices.filter(function (inv) { return !deletedSet[inv.id]; });
      setInvoices(keep); writeInvoicesCache(keep);
      setShowBulkDelete(false);
      setSelected({});
      setToast(t.deleted);
    } catch (e) {
      setError("Could not bulk delete: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  /* Summary cards: Draft (count) | Sent (count) $due | Overdue (count) $due rojo | Paid (count)
     Paid amount uses sum of payments[] (authoritative). Records with balance <= 0 count as Paid
     regardless of stored status. */
  var summary = useMemo(function() {
    var today = new Date(); today.setHours(0, 0, 0, 0);
    var r = { draft: 0, sent: 0, sentAmt: 0, overdue: 0, overdueAmt: 0, paid: 0 };
    seasonFilteredInvoices.forEach(function(inv) {
      var total = calcInvoiceTotal(inv);
      var paid = paidAmount(inv);
      var due = +(total - paid).toFixed(2);
      var st = inv.status || "draft";
      if (st === "draft") { r.draft++; return; }
      if (paid > 0 && due <= 0.01) { r.paid++; return; }
      if (st === "paid") { r.paid++; return; }
      r.sent++; r.sentAmt += due;
      if (inv.dueDate && new Date(inv.dueDate + "T00:00:00") < today && due > 0.01) {
        r.overdue++; r.overdueAmt += due;
      }
    });
    return r;
  }, [seasonFilteredInvoices]);

  if (loading) return null;
  /* Brushed Steel metal-plate spec (see App.css "BRUSHED STEEL" /
     ShipmentRegistry.js, BillRegistry.js) — brushed-metal gradient +
     1px #b9bcc0 border, embossed text (dark #1a2636 fill, light-below/
     dark-above text-shadow), square corners, 9px column headers.
     Background lives on the <thead><tr> itself (see thead render
     below) for one continuous bar; `th` keeps only text + the shared
     border. */
  var th = { padding: "7px 5px", textAlign: "left", fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", background: "transparent", borderTop: "1px solid #b9bcc0", borderBottom: "1px solid #b9bcc0" };

  /* Xero-style header layout — mirrors BillRegistry. Tabs replace
     the big summary cards; search lives in its own always-visible
     row; the rest of the filters collapse into a ⋮ popover; module
     totals render as a tight right-aligned 2-column ledger block
     above the table. */
  var tabsCfg = [
    { key: "all",     label: lang === "es" ? "Todas" : "All",    count: seasonFilteredInvoices.length, filterValue: null },
    { key: "draft",   label: t.statusDraft,                       count: summary.draft || 0,      filterValue: "draft" },
    { key: "sent",    label: t.statusSent,                        count: summary.sent || 0,       filterValue: "sent" },
    { key: "overdue", label: t.statusOverdue,                     count: summary.overdue || 0,    filterValue: "overdue" },
    { key: "paid",    label: t.statusPaid,                        count: summary.paid || 0,       filterValue: "paid" },
  ];
  var activeTabKey = cardFilter || "all";
  /* Active filter count drives the badge next to the ⋮ button.
     `cardFilter` (tabs) + `search` (always-visible row) excluded
     since their state is already self-evident in the UI. */
  var activeFilterCount =
      (filterClient.length > 0 ? 1 : 0)
    + (filterSaleStatus.length > 0 ? 1 : 0)
    + (filterSeasons.length > 0 ? 1 : 0)
    + ((dateFrom || dateTo) ? 1 : 0)
    + (filterWeek.length > 0 ? 1 : 0)
    + (filterStatus ? 1 : 0);
  var clearAllFilters = function() {
    setFilterClient([]); setFilterStatus(""); setFilterSaleStatus([]);
    setFilterSeasons([]); setDateFrom(""); setDateTo(""); setFilterWeek([]);
  };
  /* Brushed Steel action button — same spec as ShipmentRegistry.js/
     BillRegistry.js's list toolbar (square corners, brushed-metal
     gradient, embossed text). */
  var actBtn = { padding: "5px 12px", borderRadius: 0, border: "1px solid #b9bcc0", background: "linear-gradient(180deg, #f6f7f8 0%, #d8dadd 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.15)", color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.2)", cursor: "pointer", fontSize: 12, fontWeight: 600 };
  var actBtnDanger = Object.assign({}, actBtn, { borderColor: "rgba(220,38,38,0.4)", color: "#B91C1C", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.1)" });
  /* Dark-silver "title plate" variant — same gradient as the title
     bar / table header bar, used for the Filters + "⋮" triggers so
     each trigger reads as one continuous plate with the popover it
     opens. */
  var titlePlateBtn = { padding: "5px 12px", borderRadius: 0, border: "1px solid #7d838c", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 12, fontWeight: 600 };
  var totalsLabel = { textAlign: "right", color: "var(--color-text-muted)", fontSize: 11, whiteSpace: "nowrap" };
  var totalsValue = { textAlign: "right", color: "var(--color-text)", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" };

  return (
    <div>
      {/* HEADER ROW — title (left); search + filters + kebab + new
          (right); module totals ledger right below. Brushed Steel
          metal-plate treatment ported from ShipmentRegistry.js/
          BillRegistry.js's list header. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, gap: 24, flexWrap: "wrap" }}>
        {/* Title + "showing N of M" wrapped in the same dark-silver
            title-bar plate as the table's header bar / window title bar
            (#c7cbd1→#9aa0a8), black embossed text. */}
        <div style={{ lineHeight: 1.1, padding: "8px 14px", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)" }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{t.title}</h2>
          <span style={{ fontSize: 10, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{t.showing} {sorted.length} {t.of} {seasonFilteredInvoices.length} {t.total}</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
          {/* Action group: search + filters + kebab (document/export
              actions) + New Invoice. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }} ref={filtersRef}>
            {/* SEARCH — relocated before Filters, neumorphic white pill
                with an embossed magnifier icon (was its own full row
                below the tabs). */}
            <span style={{ position: "relative", display: "inline-block" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", filter: "drop-shadow(0 1px 0 rgba(255,255,255,0.9)) drop-shadow(0 -1px 1px rgba(0,0,0,0.3))" }}>
                <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={search}
                onChange={function(e) { setSearch(e.target.value); }}
                placeholder={t.search}
                style={{ width: 200, padding: "6px 10px 6px 28px", fontSize: 12, borderRadius: 999, border: "none", background: "#ffffff", boxShadow: "inset 1px 1px 3px rgba(0,0,0,0.15), inset -1px -1px 2px rgba(255,255,255,0.8), 0 1px 1px rgba(255,255,255,0.9), 0 -1px 1px rgba(0,0,0,0.1)", outline: "none", fontFamily: "inherit", color: "var(--color-text-secondary)" }}
              />
            </span>
            {search && (
              <button
                type="button"
                onClick={function() { setSearch(""); }}
                style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}
                title={lang === "es" ? "Limpiar búsqueda" : "Clear search"}
              >×</button>
            )}
            <button
              type="button"
              onClick={function() { setShowFilters(function(v) { return !v; }); }}
              style={Object.assign({}, titlePlateBtn, { display: "flex", alignItems: "center", gap: 6, padding: "5px 10px" })}
              title={lang === "es" ? "Filtros" : "Filters"}
            >
              <span style={{ fontSize: 16, lineHeight: 1, letterSpacing: 0 }}>⋮</span>
              <span>{lang === "es" ? "Filtros" : "Filters"}</span>
              {activeFilterCount > 0 && (
                <span style={{ minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9, background: "var(--color-accent)", color: "var(--color-accent-text)", fontSize: 10, fontWeight: 700, lineHeight: "18px", textAlign: "center", marginLeft: 2 }}>{activeFilterCount}</span>
              )}
            </button>
            {/* KEBAB — document/export actions (Invoice PDF / Print
                Documents / Export Excel). These are secondary/less-
                frequent actions compared to Record Deposit, which
                stays its own visible button next to Change Status.
                Export Excel embeds the real ExportButton component as
                its own row (reused as-is, never re-implemented). */}
            <span style={{ position: "relative", display: "inline-block" }} ref={moreMenuRef}>
              <button
                type="button"
                onClick={function() { setShowMoreMenu(function(v) { return !v; }); }}
                style={Object.assign({}, titlePlateBtn, { display: "flex", alignItems: "center", padding: "5px 10px" })}
                title={lang === "es" ? "Más acciones" : "More actions"}
              >
                <span style={{ fontSize: 16, lineHeight: 1, letterSpacing: 0 }}>⋮</span>
              </button>
              {showMoreMenu && (
                <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", zIndex: 50, minWidth: 220, overflow: "visible", padding: 4 }}>
                  <button
                    type="button"
                    disabled={selectedCount === 0}
                    onClick={function() {
                      if (selectedInvoices.length === 0) return;
                      setShowMoreMenu(false);
                      printMultipleInvoices(selectedInvoices, getClients(), function(err) {
                        if (err === "popup-blocked") setToast(lang === "es" ? "⚠ El navegador bloqueó la ventana. Habilita pop-ups." : "⚠ Pop-up blocked. Allow pop-ups for this site.");
                      });
                    }}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: "none", background: "transparent", textAlign: "left", cursor: selectedCount === 0 ? "not-allowed" : "pointer", opacity: selectedCount === 0 ? 0.5 : 1, fontSize: 12, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", borderRadius: 0 }}
                    onMouseEnter={function(e) { if (selectedCount > 0) e.currentTarget.style.background = "rgba(255,255,255,0.3)"; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}
                  ><span>📄</span><span>Invoice PDF</span></button>
                  {/* Print Documents keeps its own nested dropdown
                     (GFI/SweetLeaf sections) — it manages its own
                     open/close, so the kebab itself is left to close
                     only via outside-click/Escape, never forced shut
                     by a click inside this row. */}
                  <div style={{ padding: "4px 10px" }}>
                    <PrintDocsDropdown lang={lang} onPick={function(kind) { setShowMoreMenu(false); handleSalesDocBulk(kind); }} style={{ width: "100%" }} btnStyle={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: "none", borderRadius: 0, background: "transparent", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", fontSize: 12, fontWeight: 400 }} />
                  </div>
                  <div style={{ padding: "4px 10px" }}>
                    {/* 2026-10-20 — same fix as Bills'/Shipments' kebabs:
                        btnStyle matches the plain menu items above
                        exactly (transparent, black embossed text, no
                        border), instead of ExportButton's own default
                        green pill. */}
                    <ExportButton lang={lang} filenameBase="Invoices" selectedCount={selectedCount} totalCount={sorted.length} buildSummary={buildInvSummaryRows} buildDetail={buildInvDetailRows} style={{ width: "100%" }} btnStyle={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", border: "none", borderRadius: 0, background: "transparent", color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", fontSize: 12, fontWeight: 400 }} />
                  </div>
                </div>
              )}
            </span>
            {/* + New Invoice — Brushed Steel's "colored/active plate"
                variant (same blue gradient used for Save/active toggles
                elsewhere in this design), square corners. */}
            <button onClick={function() { returnModuleRef.current = null; setEditing(null); setShowForm(true); }} style={{ padding: "6px 14px", borderRadius: 0, border: "1px solid #1a3550", background: "linear-gradient(180deg, #6FA3C7 0%, #3E6E96 55%, #284d6b 100%)", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.15)", color: "#ffffff", textShadow: "0 1px 1px rgba(0,0,0,0.25)", cursor: "pointer", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 5 }}><span style={{ fontSize: 14, lineHeight: 1 }}>+</span>{t.add}</button>
            {/* FILTER POPOVER — absolute under the trigger row, same
                dark-silver plate as the title plate; fields inside
                (SearchSelect, date inputs) stay their own recessed
                plates sitting on it. */}
            {showFilters && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, zIndex: 50, width: 440, maxWidth: "94vw", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", borderRadius: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.18)", padding: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Filtros" : "Filters"}{activeFilterCount > 0 ? " (" + activeFilterCount + ")" : ""}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {activeFilterCount > 0 && (
                      <button type="button" onClick={clearAllFilters} style={Object.assign({}, actBtn, { padding: "4px 8px", fontSize: 10 })}>{lang === "es" ? "Limpiar todo" : "Clear all"}</button>
                    )}
                    <button type="button" onClick={function() { setShowFilters(false); }} style={Object.assign({}, actBtn, { padding: "4px 8px", fontSize: 10 })}>{lang === "es" ? "Cerrar" : "Close"}</button>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Cliente" : "Client"}</div>
                    <SearchSelect multi value={filterClient} onChange={setFilterClient} placeholder={t.allClients} options={uniqueClients.map(function(c) { return { value: c, label: c }; })} style={filterFieldPlate} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Temporada" : "Season"}</div>
                    <SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} groups={buildSeasonFilterOptions(lang)} style={filterFieldPlate} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Desde" : "From"}</div>
                    <input type="date" value={dateFrom} onChange={function(e) { setDateFrom(e.target.value); }} style={Object.assign({}, inp, filterFieldPlate)} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Hasta" : "To"}</div>
                    <input type="date" value={dateTo} onChange={function(e) { setDateTo(e.target.value); }} style={Object.assign({}, inp, filterFieldPlate)} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Semana" : "Week"}</div>
                    <SearchSelect multi value={filterWeek} onChange={setFilterWeek} placeholder={lang === "es" ? "Todas las semanas" : "All weeks"} options={uniqueWeeks.map(function(w) { return { value: w, label: w }; })} style={filterFieldPlate} />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lang === "es" ? "Status venta" : "Sale status"}</div>
                    <SearchSelect multi value={filterSaleStatus} onChange={setFilterSaleStatus} placeholder={t.allSaleStatuses} options={dynamicSaleStatusOptionsInv.map(function(s) { return { value: s, label: t[s] || s }; })} style={filterFieldPlate} />
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* MODULE TOTALS — same dark title-bar silver plate as the
              header bar / title plate above. Deductions is the one
              deduction-type total that keeps its own red; the rest
              (Cases, Amount, Paid, Total Due) are uniform black. */}
          <div style={{ display: "grid", gridTemplateColumns: "auto auto", columnGap: 10, rowGap: 1, alignItems: "center", alignSelf: "flex-start", padding: "4px 10px", background: "linear-gradient(180deg, #c7cbd1 0%, #9aa0a8 100%)", border: "1px solid #7d838c", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.15)" }}>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Cajas" : "Cases"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{grandTotals.cases.toLocaleString("en-US")}</span>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Monto" : "Amount"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{fmtMoney(grandTotals.amount)}</span>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Deducciones" : "Deductions"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#DC2626", textShadow: "none" }}>{fmtMoney(grandTotals.deductions)}</span>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Pagado" : "Paid"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{fmtMoney(grandTotals.paid)}</span>
            <span style={{ ...totalsLabel, fontSize: 9, color: "#1a2636", textShadow: "0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(0,0,0,0.15)" }}>{lang === "es" ? "Total a Pagar" : "Total Due"}:</span>
            <span style={{ ...totalsValue, fontSize: 10, color: "#000000", textShadow: "0 1px 0 rgba(255,255,255,0.8), 0 -1px 0 rgba(0,0,0,0.25)" }}>{fmtMoney(grandTotals.due)}</span>
          </div>
        </div>
      </div>
      {error && (
        <div style={{ marginBottom: 6, padding: "6px 10px", borderRadius: 6, background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{error}</span>
          <button onClick={function() { setError(null); reload(); }} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid #991B1B", background: "transparent", color: "#991B1B", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Reintentar" : "Retry"}</button>
        </div>
      )}
      {/* TABS — All / Draft / Sent / Overdue / Paid. Restyled as
          inset/pressed segments (no underline). Record Deposit /
          Change Status / Delete Selected / Clear used to be a
          separate ACTION BAR row below (along with the standalone
          search row above, now relocated into the header action
          group) — both moved here, right after the Paid tab, same
          compact Brushed Steel metal plate buttons (actBtn/
          actBtnDanger) as the module totals ledger above. */}
      <div style={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 6, flexWrap: "wrap" }}>
        {tabsCfg.map(function(tab) {
          var isActive = activeTabKey === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={function() { setCardFilter(tab.filterValue); }}
              style={{
                padding: "5px 12px",
                background: isActive ? "#ffffff" : "transparent",
                boxShadow: isActive ? "inset 1px 1px 2px rgba(0,0,0,0.15)" : "none",
                border: "none",
                color: "#1a2636",
                fontWeight: 500,
                fontSize: 11,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {tab.label} ({tab.count})
            </button>
          );
        })}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 10, marginBottom: 2 }}>
          <button type="button" onClick={function() { setShowBatchDeposit(true); }} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{t.recordDeposit}</button>
          <button type="button" onClick={function() { setShowBulkStatus(true); }} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Cambiar Estado" : "Change Status"}</button>
          <button type="button" onClick={function() { setShowBulkDelete(true); }} disabled={selectedCount === 0} style={Object.assign({}, actBtnDanger, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Eliminar" : "Delete Selected"}</button>
          <button type="button" onClick={clearSelection} disabled={selectedCount === 0} style={Object.assign({}, actBtn, { padding: "4px 9px", fontSize: 11, opacity: selectedCount === 0 ? 0.5 : 1, cursor: selectedCount === 0 ? "not-allowed" : "pointer" })}>{lang === "es" ? "Limpiar" : "Clear"}</button>
        </div>
        {selectedCount > 0 && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, alignItems: "center", marginBottom: 2 }}>
            <span style={{ fontWeight: 700, color: "#c65d00" }}>{selectedCount} {lang === "es" ? "seleccionados" : "selected"}</span>
            <span style={{ color: "var(--color-text-muted)" }}>|</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Cajas" : "Cases"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{selectedTotals.cases.toLocaleString("en-US")}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Monto" : "Amount"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{fmtMoney(selectedTotals.amount)}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Deducciones" : "Deductions"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#DC2626" }}>{fmtMoney(selectedTotals.deductions)}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Pagado" : "Paid"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#DC2626" }}>{fmtMoney(selectedTotals.paid)}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Total a Pagar" : "Total Due"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#c65d00" }}>{fmtMoney(selectedTotals.due)}</span></span>
          </div>
        )}
      </div>
      {sorted.length === 0 ? <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-text-muted)", fontSize: 14 }}>{t.noResults}</div> : (
        <div style={{ overflowX: "auto", borderRadius: 0, border: "1px solid #b9bcc0", background: "#ffffff" }}>
          <table className="gfi-invoices-list-table" style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
            <thead><tr style={{ background: "linear-gradient(180deg, #eef0f2 0%, #d2d5d9 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.08)" }}>
              <th style={{ ...th, width: 30, cursor: "default" }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
              <th onClick={function() { toggleSort("puNumber"); }} style={{ ...th, textAlign: "center" }}>{t.puNum}{arrow("puNumber")}</th>
              <th onClick={function() { toggleSort("season"); }} style={th}>{lang === "es" ? "Temporada" : "Season"}{arrow("season")}</th>
              <th onClick={function() { toggleSort("date"); }} style={th}>{t.date}{arrow("date")}</th>
              {/* Week # (2026-09-18, Arturo: "lo mismo en la tabla de
                  facturas de ground fresh" — same request as SweetLeaf's
                  invoice table above) — computed live from Invoice Date
                  via the same getISOWeekInv helper already used for the
                  season/week filter in this file, never persisted. */}
              <th onClick={function() { toggleSort("week"); }} style={{ ...th, textAlign: "center" }}>{lang === "es" ? "Semana" : "Week"}{arrow("week")}</th>
              <th onClick={function() { toggleSort("dueDate"); }} style={th}>{t.dueDate}{arrow("dueDate")}</th>
              <th onClick={function() { toggleSort("clientName"); }} style={th}>{t.client}{arrow("clientName")}</th>
              <th style={{ ...th, lineHeight: 1.2 }}><div>{t.clientRefColLine1}</div><div>{t.clientRefColLine2}</div></th>
              {/* Lot — link column to the matching Bill (like Bills'
                  own Invoice/PU link column), centered rather than
                  left/right-aligned. */}
              <th onClick={function() { toggleSort("lot"); }} style={{ ...th, textAlign: "center" }}>{t.lot}{arrow("lot")}</th>
              <th style={{ ...th, textAlign: "right" }}>{lang === "es" ? "Cajas" : "Cases"}</th>
              {/* Pallets — small count, centered rather than
                  right-aligned (same convention as Bills/Shipments). */}
              <th style={{ ...th, textAlign: "center" }} title={lang === "es" ? "Total de pallets de la factura" : "Total pallets on the invoice"}>{lang === "es" ? "Pallets" : "Pallets"}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.amount}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.deductions}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.paidCol}</th>
              <th style={{ ...th, textAlign: "right" }}>{t.totalDue}</th>
              <th style={{ ...th, textAlign: "center" }}>{t.saleStatus}</th>
              {/* 2026-09-14 — Four Seasons' follow-up response to the
                  Protection Status Reminder, pushed SL→GFI from
                  SweetLeafPortal.js. Only ever set on FS invoices that
                  were under Protection/PAS; blank for everything else. */}
              <th style={{ ...th, textAlign: "center", lineHeight: 1.2 }}><div>{t.protectionResponseColLine1}</div><div>{t.protectionResponseColLine2}</div></th>
              <th style={{ ...th, textAlign: "center" }}>{t.status}</th>
              {/* Trailing Xero-style icon columns — SL-copied flag,
                  docs count, email-sent flag. Headers stay empty.
                  The per-row print button was removed; printing now
                  runs through the kebab's "Invoice PDF" action on
                  checkbox selection. */}
              <th style={{ ...th, textAlign: "center", width: 24 }}></th>
              <th style={{ ...th, textAlign: "center", width: 30 }}></th>
              <th style={{ ...th, textAlign: "center", width: 30 }}></th>
            </tr></thead>
            <tbody>{sorted.map(function(inv) {
              var total = 0, tFees = 0, totalCases = 0;
              (inv.lines || []).forEach(function(l) {
                if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
                  var c = parseInt(l.cases, 10) || 0;
                  total += c * (parseFloat(l.salePrice) || 0);
                  totalCases += c;
                }
              });
              /* tFees only drives the "Deductions" column display (abs).
                 The "Total Due" column uses calcInvoiceTotal so it stays
                 in lock-step with the form/print summary (single source
                 of truth — fixes the prior list-vs-detail divergence
                 that double-applied imported fees). */
              (inv.fees || []).forEach(function(fe) { tFees += Math.abs(parseFloat(fe.amount) || 0); });
              var paid = paidAmount(inv);
              var due = +(calcInvoiceTotal(inv) - paid).toFixed(2);
              var st = inv.status || "draft";
              if (st !== "draft" && paid > 0 && due <= 0.01) st = "paid";
              var ss = INV_STATUS_STYLES[st]; var statusKey = st === "draft" ? "statusDraft" : st === "sent" ? "statusSent" : "statusPaid";
              return (
                <tr key={inv.id} onClick={function() { returnModuleRef.current = null; setViewing(inv); }} style={{ cursor: "pointer", transition: "background 0.15s", borderBottom: "1px solid var(--color-border)", background: selected[inv.id] ? "rgba(185,188,192,0.3)" : "transparent" }} onMouseEnter={function(e) { e.currentTarget.style.background = selected[inv.id] ? "rgba(185,188,192,0.4)" : "rgba(185,188,192,0.2)"; }} onMouseLeave={function(e) { e.currentTarget.style.background = selected[inv.id] ? "rgba(185,188,192,0.3)" : "transparent"; }}>
                  <td style={{ padding: "6px 8px", textAlign: "center" }} onClick={function(e) { e.stopPropagation(); }}><input type="checkbox" checked={!!selected[inv.id]} onChange={function(e) { toggleOne(inv.id, e); }} /></td>
                  {/* PU# — number stays clean. Email-sent + SL-import
                      indicators live in the trailing icon columns at
                      the end of the row per the project design rule. */}
                  <td style={{ padding: "6px 8px", fontSize: 9.5, color: "#1a2636", textAlign: "center" }}>{inv.puNumber}<RejectionBadge cases={rejectedCasesForInvoice(inv)} /></td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>{seasonNameForInv(inv.date, inv.seasonId) || "—"}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)" }}>{fmtDate(inv.date)}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-muted)", textAlign: "center" }}>{inv.date ? ("W" + getISOWeekInv(inv.date)) : "—"}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)" }}>{fmtDate(inv.dueDate)}</td>
                  {/* Client — can wrap to a 2nd line and push the
                      remaining columns right (same fix as Bills’
                      Shipper cell); kept from wrapping. */}
                  <td style={{ padding: "6px 8px", fontSize: 9.5, whiteSpace: "nowrap" }}>{resolveClientName(inv.clientId) || inv.clientName || "—"}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>{inv.clientRef || "—"}</td>
                  <td style={{ padding: "6px 8px", fontSize: 9.5, textAlign: "center", color: "var(--color-accent)" }} onClick={function(e) { e.stopPropagation(); }}>{(function() {
                    /* Collect ALL distinct lots from this invoice's
                       lines (plus the invoice-level lot as a fallback
                       when lines don't carry one). Each lot becomes
                       its own clickable link routed to the matching
                       bill \u2014 invoices that span multiple shipments
                       finally surface every lot. Seen-map dedupes
                       across lines + invoice level. */
                    var seen = {};
                    var lots = [];
                    (inv.lines || []).forEach(function(l) {
                      var v = l && l.lot;
                      if (v != null && v !== "" && !seen[String(v)]) { seen[String(v)] = true; lots.push(v); }
                    });
                    if (inv.lot && !seen[String(inv.lot)]) { seen[String(inv.lot)] = true; lots.push(inv.lot); }
                    if (lots.length === 0) return "\u2014";
                    return (
                      <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, justifyContent: "center" }}>
                        {lots.map(function(lot, i) {
                          var matchedBill = lookupBillByLot(lot);
                          var sep = i < lots.length - 1 ? <span style={{ color: "var(--color-text-muted)" }}>,</span> : null;
                          /* No bill yet \u2192 plain text, no link.
                             Bill exists \u2192 blue link, opens preview. */
                          if (!matchedBill) {
                            return <span key={lot}>{lot}{sep}</span>;
                          }
                          return (
                            <span key={lot}>
                              <span
                                onClick={function() { setLotPreviewBill(matchedBill); }}
                                onMouseEnter={function(e) { e.currentTarget.style.textDecoration = "underline"; }}
                                onMouseLeave={function(e) { e.currentTarget.style.textDecoration = "none"; }}
                                style={{ color: "#2563EB", cursor: "pointer", textDecoration: "none" }}
                                title={lang === "es" ? "Ver bill" : "View bill"}
                              >{lot}</span>
                              {sep}
                            </span>
                          );
                        })}
                      </span>
                    );
                  })()}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 9.5 }}>{totalCases > 0 ? totalCases.toLocaleString("en-US") : "—"}</td>
                  <td style={{ padding: "6px 8px", textAlign: "center", fontSize: 9.5 }}>{(function () { var p = (inv.lines || []).reduce(function (acc, l) { return acc + (parseInt(l.pallets, 10) || 0); }, 0); return p > 0 ? p.toLocaleString("en-US") : "—"; })()}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 9.5 }}>{fmtMoney(total)}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 9.5, color: "#DC2626" }}>{tFees > 0 ? "(" + fmtMoney(tFees) + ")" : "—"}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 9.5, color: paid > 0 ? "#059669" : "var(--color-text-muted)" }}>{paid > 0 ? fmtMoney(paid) : "—"}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontSize: 9.5, color: "var(--color-accent)" }}>{fmtMoney(due)}</td>
                <td style={{ padding: "6px 8px", textAlign: "center" }}>{(function() {
                  /* Show ALL distinct sale-status values across the
                     invoice's product lines \u2014 was previously collapsed
                     to a single "worst" badge (protection > partial >
                     good) which dropped any line-level claim/rejected/
                     past-due/etc. Set-dedup so two lines with the same
                     status render one badge. Wrap so 3+ badges flow
                     without breaking the row. */
                  /* 2026-10-30, Arturo (PU 17475/lot 58074): same
                     per-product fix as the Products table/view mode —
                     a single trStatusBadge blended every product's
                     status into one badge; now one distinct badge per
                     product, same dedup as the non-TR branch below.
                     2026-11-03, PU 17484: resolveTwinRiverShipment(inv, ...)
                     still resolved ONE shipment (inv's home lot) for
                     every line — on a multi-lot invoice a second
                     lot's real status never factored into this badge.
                     Resolved per-line now (own l.lot). */
                  var statuses = [];
                  var seen = {};
                  (inv.lines || []).forEach(function(l) {
                    var shp = resolveTwinRiverShipmentForLot(l.lot || inv.lot, inv, shpsForWeekCacheInv);
                    var s = shp !== undefined ? twinRiverProductStatus(shp, l.productBase || l.productName) : l.saleStatus;
                    if (s && !seen[s]) { seen[s] = true; statuses.push(s); }
                  });
                  if (statuses.length === 0) statuses = [resolvePendingStage([inv], slPassingIndexList)];
                  /* Fallback colours for statuses not in SALE_STATUS_COLORS
                     (e.g. claim, rejected, pastDue once introduced). */
                  var fallbackSc = { bg: "rgba(107,114,128,0.10)", text: "#374151", border: "rgba(107,114,128,0.25)" };
                  return <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, justifyContent: "center" }}>{statuses.map(function(s) {
                    var sc = SALE_STATUS_COLORS[s] || fallbackSc;
                    return <span key={s} style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9.5, fontWeight: 400, background: sc.bg, color: sc.text, border: "1px solid " + sc.border, boxShadow: SALE_STATUS_BEVEL, whiteSpace: "nowrap" }}>{t[s] || s}</span>;
                  })}</span>;
                })()}</td>
                  <td style={{ padding: "6px 8px", textAlign: "center" }}>{inv.protectionResponseStatus
                    ? (function() { var sc = PROTECTION_RESPONSE_COLORS[inv.protectionResponseStatus]; return <span title={inv.protectionResponseNote || ""} style={{ padding: "2px 7px", borderRadius: 0, fontSize: 9.5, fontWeight: 400, background: sc.bg, color: sc.text, border: "1px solid " + sc.border, boxShadow: SALE_STATUS_BEVEL, whiteSpace: "nowrap" }}>{t[inv.protectionResponseStatus]}</span>; })()
                    : <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>—</span>}</td>
                  <td style={{ padding: "6px 8px", textAlign: "center" }}><span style={{ padding: "3px 9px", borderRadius: 0, fontSize: 9.5, fontWeight: 400, background: ss.bg, color: ss.text, border: "1px solid " + ss.border, boxShadow: SALE_STATUS_BEVEL }}>{t[statusKey]}</span></td>
                  {/* SL-copied indicator. */}
                  <td style={{ padding: "6px 4px", textAlign: "center", width: 24 }}>
                    {inv.slImported && (
                      <img src={SL_ISOTIPO_B64} alt="SL" title={lang === "es" ? "Copiado a SweetLeaf" : "Copied to SweetLeaf"} style={{ width: 14, height: 14, objectFit: "contain", verticalAlign: "middle" }} />
                    )}
                  </td>
                  {/* Docs indicator \u2014 placeholder for future xero/bol slots
                      on GFI invoices. Stays hidden until any doc fields
                      are attached. */}
                  <td style={{ padding: "6px 4px", textAlign: "center", width: 30 }}>
                    {(function () {
                      var n = ((inv.xeroInvoiceFile && (inv.xeroInvoiceFile.url || inv.xeroInvoiceFile.data)) ? 1 : 0)
                            + ((inv.bolFile && (inv.bolFile.url || inv.bolFile.data)) ? 1 : 0);
                      if (n === 0) return null;
                      return (
                        <span title={(lang === "es" ? "Archivos adjuntos: " : "Attached files: ") + n} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                          <svg width="14" height="16" viewBox="0 0 14 16" fill="none" stroke="#6B7280" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1.5 1h7l4 4v10h-11V1z"/>
                            <path d="M8.5 1v4h4" fill="#D1D5DB"/>
                            <line x1="4" y1="8" x2="10" y2="8" strokeWidth="1"/>
                            <line x1="4" y1="10.5" x2="10" y2="10.5" strokeWidth="1"/>
                            <line x1="4" y1="13" x2="7" y2="13" strokeWidth="1"/>
                          </svg>
                          <span style={{ fontSize: 10, fontWeight: 500, color: "#6B7280" }}>{n}</span>
                        </span>
                      );
                    })()}
                  </td>
                  {/* Email-sent indicator. */}
                  <td style={{ padding: "6px 4px", textAlign: "center", width: 30 }}>
                    {inv.emailSentAt && (
                      <span title={(lang === "es" ? "Enviada por email " : "Emailed ") + new Date(inv.emailSentAt).toLocaleString()} style={{ display: "inline-flex", alignItems: "center" }}>
                        <svg width="18" height="14" viewBox="0 0 18 14" fill="none" stroke="#059669" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="0.5" y="0.5" width="14" height="11" rx="2"/>
                          <path d="M0.5 0.5L7.5 6.5L14.5 0.5"/>
                          <circle cx="14" cy="10" r="3.5" fill="#059669" stroke="none"/>
                          <path d="M12.5 10L13.5 11L15.5 9" stroke="white" strokeWidth="1.5"/>
                        </svg>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
      {viewing && <DetailPanel
        invoice={viewing}
        invoices={invoices}
        t={t} lang={lang}
        onSave={function(updated) { handleSave(updated); setViewing(updated); }}
        onDelete={handleDelete}
        onEditLock={function(rec) { setViewing(null); setLockedRecord(rec); }}
        onClose={function() { setViewing(null); closeToOrigin(); }}
        onPrint={function(inv) { setShowPrint(inv); }}
        onPrintDoc={handleSalesDocSingle}
      />}
      {showForm && !viewing && <InvoiceForm invoice={editing} invoices={invoices} t={t} lang={lang} onSave={handleSave} onDelete={handleDelete} onCancel={function() { setShowForm(false); setEditing(null); closeToOrigin(); }} />}
      {showBatchDeposit && <BatchDepositModal invoices={selectedInvoices} t={t} lang={lang} onConfirm={handleBatchDepositConfirm} onCancel={function() { setShowBatchDeposit(false); }} />}
      {lockedRecord && <EditLockDialog record={lockedRecord} t={t} onClose={function() { setLockedRecord(null); }} />}
      {showBulkDelete && <BulkDeleteDialog count={selectedCount} lockedRecords={bulkLocked} lang={lang} onCancel={function() { setShowBulkDelete(false); }} onConfirm={handleBulkDelete} />}
      {showBulkStatus && <BulkStatusDialog count={selectedCount} lockedRecords={bulkLocked} statuses={INV_STATUSES.map(function(s) { return { value: s, label: t[s === "draft" ? "statusDraft" : s === "sent" ? "statusSent" : "statusPaid"] || s }; })} lang={lang} onCancel={function() { setShowBulkStatus(false); }} onConfirm={handleBulkStatusChange} />}
      {confirmationPayment && (function() { var pr = partyAndRowsForInvoiceDeposit(confirmationPayment); return <PaymentConfirmationPreview payment={confirmationPayment} party={pr.party} rows={pr.rows} lang={lang} onClose={function() { setConfirmationPayment(null); }} />; })()}
      {showPrint && <PrintPreview invoice={showPrint} t={t} onClose={function() { setShowPrint(null); }} />}
      {lotPreviewBill && <BillPreviewModal bill={lotPreviewBill} lang={lang} onClose={function() { setLotPreviewBill(null); }} />}
      {pickKindModal && <PickKindSelector
        brandLabel={pickKindModal.brand === "sl" ? "SweetLeaf Produce" : "Ground-Fresh"}
        lang={lang}
        onCancel={function () { setPickKindModal(null); }}
        onPick={function (chosenKind) {
          /* chosenKind = "pick" | "inbound".
             - Pick Ticket → second step (remarks modal) before printing
             - Inbound     → print directly, no extra prompt */
          var payload = { brand: pickKindModal.brand, invoices: pickKindModal.invoices };
          setPickKindModal(null);
          if (chosenKind === "pick") {
            setRemarksModal(payload);
          } else {
            doPrint(payload.invoices, payload.brand + "-inbound");
          }
        }}
      />}
      {remarksModal && (function () {
        /* Default Loading Date = ETA + 1 day per spec (Sunday→Tuesday
           after Monday arrival, etc.). Resolved from the FIRST invoice's
           shipment ETA — for bulk prints the user can adjust if needed.
           Falls back to today if no shipment ETA is available. */
        var ctx = buildSalesDocCtx();
        var firstInv = remarksModal.invoices[0];
        var firstShp = firstInv ? findShipmentForInvoice(firstInv, ctx.shipments) : null;
        var eta = firstShp ? (firstShp.dateArrival || "") : "";
        var defaultLD = "";
        if (eta) {
          var d = new Date(eta + "T00:00:00");
          if (!isNaN(d.getTime())) {
            d.setDate(d.getDate() + 1);
            defaultLD = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
          }
        }
        return <PickTicketRemarksModal
          brandLabel={remarksModal.brand === "sl" ? "SweetLeaf Produce" : "Ground-Fresh"}
          lang={lang}
          defaultLoadingDate={defaultLD}
          onCancel={function () { setRemarksModal(null); }}
          onPrint={function (remarksLines, chosenLoadingDate) {
            /* User confirmed — fire the print with remarks + chosen
               Loading Date. The Loading Date overrides the auto-default
               (shipment.dateDeparture) inside the builder. */
            var invs = remarksModal.invoices;
            var brand = remarksModal.brand;
            setRemarksModal(null);
            doPrint(invs, brand + "-pick", { remarks: remarksLines, loadingDate: chosenLoadingDate });
          }}
        />;
      })()}
      {toast && <Toast message={toast} onClose={function() { setToast(null); }} />}
    </div>
  );
}